// ─── TABLE DES MATIÈRES (numéros approximatifs — grepper le nom pour précis) ───
// ~L7    CONFIG           DEMO_MODE, WORKER_URL, AdSense IDs
// ~L18   IMAGES           DEST_IMGS, SHIP_IMGS, getShipImg
// ~L166  UTILITAIRES      GF/GL (fonts), fmtDate, ORIGIN_AIRPORTS, resolveOriginCode
// ~L198  EXPEDIA AFFILIÉ  EXPEDIA_CAMREF, EXPEDIA_LINKS, REGION_*, CRUISE_LINE_IDS, SHIP_IDS
// ~L236  URL BUILDERS     buildCruiseBookingUrl, buildCruiseDirectLink,
//                         buildExpediaFlightUrl, buildExpediaHotelUrl
//                         PORT_EXPEDIA_CITY
// ~L414  WORKER READER    readStream() — lit la réponse SSE du worker
// ⚠️ 2026-06-10 : RETURN_OFFERS_TOOL + buildPrompt + CLASS_CASCADE ont DÉMÉNAGÉ
//                 dans src/promptBuilder.js (worker-only — recette hors bundle).
//                 CRUISE_COMPANIES / GENERIC_SHIP_CLASSES_FR / resolveOriginCode
//                 → src/cruiseData.js (partagé UI + worker).
// ~L802  AD BANNER        AdBanner component
// ~L844  DONNÉES FUNNEL   SHIPS_BY_COMPANY, SHIP_CLASSES_BY_COMPANY (le reste → cruiseData.js)
// ~L910  DEMO             generateDemoOffer(data)
// ~L990  STEPS            Définition des 20 étapes du funnel (tableau STEPS[])
// ~L1295 COMPOSANTS UI    PriorityRankSelector, Walkthrough, AirportInput,
//                         FamilyInput, DateInput, BudgetInput,
//                         ShipClassSelector, CompaniesSelector, SpecificShipSelector
// ~L1631 SEARCHING SCREEN SearchingScreen, CRUISE_TIPS
// ~L1780 FLIGHT BLOCK     FlightBlock
// ~L1870 HOTEL BLOCK      HotelBlock
// ~L1976 RESULT CARD      ResultCard (boutons réservation, vigie…)
// ~L2196 MODALS           RefinementModal, WatchModal, FeedbackModal
// ~L2370 EXTRACTION       isStepFilled, normalizeCompany, mergeExtractedParams
// ~L2522 MAIN APP         export default App() — machine à états principale
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from "react";
import { useWikiImage } from "./imageResolver";
import { ProjectDescriber, OfferChat } from "./TravelAgent";
import { CRUISE_COMPANIES, GENERIC_SHIP_CLASSES_FR, resolveOriginCode } from "./cruiseData";
import { AuthButton, PaywallModal, AdminCDMModal, RedeemCodeModal, AdminCodesModal, AdminStatsModal, AdminDashboardModal, useSearchUsage, useSearchGate, VIGIE_ENABLED } from "./AuthGate";
import { useClerk, useUser } from "@clerk/clerk-react";
import { useLanguage } from "./useLanguage";
import { getStrings } from "./strings";
import { track } from "./track";
import { CHANGELOG } from "./data/changelog";
import { assessLuxuryRegion } from "./data/companyCoverage";
import { assessBudget } from "./data/budgetFloor";
// Funnel configurable (no-op sur main — la config par tenant vit sur la branche
// white-label ; ces coquilles vides gardent le code du funnel identique entre
// les deux branches, donc les prochains cherry-picks restent propres).
const WL_HIDDEN_STEPS  = new Set();
const WL_STEP_DEFAULTS = {};

// ─── HISTORIQUE LOCAL DES RECHERCHES RÉCENTES ────────────────────────────
// Sauve AUTOMATIQUEMENT les dernières recherches complétées dans localStorage
// → l'user retrouve ses résultats au retour ultérieur sans reconsommer un
// crédit API. Garde les RECENT_SEARCHES_MAX plus récentes (anneau). TTL 30 j.
// Distinct des « favoris » nommés (savedList, sauvegarde manuelle via 💾).
const SAVED_SEARCH_KEY = "croisiereai_saved_search";
const SAVED_SEARCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const SAVED_SEARCH_VERSION = 2; // v2 = liste d'historique (v1 = objet unique)
const RECENT_SEARCHES_MAX = 5;

function recentSearchId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// Signature de dédup : même navire précis OU mêmes régions + mois/année + durée.
// Refaire une recherche identique remplace l'ancienne au lieu de doublonner.
function recentSearchSig(d) {
  const sd = d || {};
  const regs = (sd.regions || []).filter(r => r && r !== "any").sort().join(",");
  return [sd.specific_ship || "", regs, sd.dates?.month || "", sd.dates?.year || "", sd.duration || ""].join("|");
}

function loadRecentSearches() {
  try {
    const raw = localStorage.getItem(SAVED_SEARCH_KEY);
    if (!raw) return [];
    const payload = JSON.parse(raw);
    const cutoff = Date.now() - SAVED_SEARCH_TTL_MS;
    // Migration v1 (objet unique) → premier élément de l'historique
    if (payload && payload.v === 1 && Array.isArray(payload.offers)) {
      return (payload.ts > cutoff && payload.offers.length > 0)
        ? [{ id: recentSearchId(), ts: payload.ts, data: payload.data, offers: payload.offers, compromiseNote: payload.compromiseNote || null, refinementOptions: payload.refinementOptions || null }]
        : [];
    }
    if (!payload || payload.v !== SAVED_SEARCH_VERSION || !Array.isArray(payload.items)) return [];
    return payload.items.filter(e => e && (e.ts || 0) > cutoff && (e.offers || []).length > 0);
  } catch { return []; }
}

function saveSearchToLocal({ data, offers, compromiseNote, refinementOptions }) {
  if (!offers || offers.length === 0) return;
  try {
    const sig = recentSearchSig(data);
    const kept = loadRecentSearches().filter(e => recentSearchSig(e.data) !== sig);
    const entry = {
      id: recentSearchId(),
      ts: Date.now(),
      data,
      offers,
      compromiseNote: compromiseNote || null,
      refinementOptions: refinementOptions || null,
    };
    const items = [entry, ...kept].slice(0, RECENT_SEARCHES_MAX);
    localStorage.setItem(SAVED_SEARCH_KEY, JSON.stringify({ v: SAVED_SEARCH_VERSION, items }));
  } catch {}
}

function removeRecentSearch(id) {
  try {
    const items = loadRecentSearches().filter(e => e.id !== id);
    localStorage.setItem(SAVED_SEARCH_KEY, JSON.stringify({ v: SAVED_SEARCH_VERSION, items }));
    return items;
  } catch { return loadRecentSearches(); }
}


// ─── LISTE DE RECHERCHES NOMMÉES (sauvegarde explicite) ──────────────────────
// Anonyme → localStorage (par appareil). Connecté → KV serveur (synchro multi-
// appareils, via actions save_search / list_searches / delete_search du worker).
// Nom auto par défaut dérivé des critères, renommable. Cap SAVED_LIST_MAX.
const SAVED_LIST_KEY = "croisiereai_saved_list";
const SAVED_LIST_MAX = 20;
const SAVED_LIST_TTL_MS = 365 * 24 * 60 * 60 * 1000;

const REGION_SHORT = {
  fr: { carib_east: "Caraïbes Est", carib_west: "Caraïbes Ouest", carib_south: "Caraïbes Sud", bermuda: "Bermudes", europe: "Europe", europe_med: "Méditerranée", europe_north: "Europe du Nord", europe_british: "Îles britanniques", alaska: "Alaska", hawaii: "Hawaï", asia: "Asie", repositioning: "Repositionnement" },
  en: { carib_east: "Eastern Caribbean", carib_west: "Western Caribbean", carib_south: "Southern Caribbean", bermuda: "Bermuda", europe: "Europe", europe_med: "Mediterranean", europe_north: "Northern Europe", europe_british: "British Isles", alaska: "Alaska", hawaii: "Hawaii", asia: "Asia", repositioning: "Repositioning" },
};

function newSearchId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// Nom par défaut : « Caraïbes Est — juillet 2027 »
function defaultSearchName(data, lang) {
  const sd = data || {};
  const isEN = lang === "en";
  const map = REGION_SHORT[isEN ? "en" : "fr"];
  const bits = [];
  if (sd.specific_ship) bits.push(sd.specific_ship);
  const regs = (sd.regions || []).filter(r => r && r !== "any");
  if (!sd.specific_ship && regs.length > 0) {
    const r = map[regs[0]] || regs[0];
    bits.push(regs.length > 1 ? `${r} +${regs.length - 1}` : r);
  }
  if (sd.dates?.month && sd.dates?.year) {
    const monthsFr = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
    const monthsEn = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    bits.push(`${(isEN ? monthsEn : monthsFr)[sd.dates.month - 1]} ${sd.dates.year}`);
  }
  if (bits.length === 0) return isEN ? "Cruise search" : "Recherche croisière";
  return bits.join(" — ");
}

function loadLocalSavedList() {
  try {
    const raw = localStorage.getItem(SAVED_LIST_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const cutoff = Date.now() - SAVED_LIST_TTL_MS;
    return arr.filter(e => (e.ts || 0) > cutoff);
  } catch { return []; }
}

function writeLocalSavedList(list) {
  try { localStorage.setItem(SAVED_LIST_KEY, JSON.stringify((list || []).slice(0, SAVED_LIST_MAX))); } catch {}
}

// ── Recherches sauvegardées côté serveur (utilisateur connecté) ──────────────
async function kvListSearches(getToken) {
  try {
    const token = await getToken();
    if (!token) return null;
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "list_searches", token }) });
    const d = await r.json();
    return Array.isArray(d.searches) ? d.searches : null;
  } catch { return null; }
}
async function kvSaveSearch(getToken, entry) {
  try {
    const token = await getToken();
    if (!token) return null;
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "save_search", token, search: entry }) });
    const d = await r.json();
    return Array.isArray(d.searches) ? d.searches : null;
  } catch { return null; }
}
async function kvDeleteSearch(getToken, id) {
  try {
    const token = await getToken();
    if (!token) return null;
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "delete_search", token, id }) });
    const d = await r.json();
    return Array.isArray(d.searches) ? d.searches : null;
  } catch { return null; }
}

// ── Lien-résultat partageable (admin) ────────────────────────────────────────
// share → fige le résultat courant sous un jeton public, retourne le jeton.
// get  → lecture publique d'un résultat partagé (ouverture via ?r=<token>).
async function kvShareResult(getToken, entry) {
  try {
    const token = await getToken();
    if (!token) return { error: "Non connecté (pas de jeton)." };
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "share_result", token, search: entry }) });
    const d = await r.json().catch(() => ({}));
    if (d && d.token) return { token: d.token };
    return { error: (d && d.error && (d.error.detail || d.error.message)) || `Échec serveur (HTTP ${r.status}).` };
  } catch (e) { return { error: e.message || "Erreur réseau." }; }
}
async function kvGetSharedResult(shareToken) {
  try {
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "get_shared_result", token: shareToken }) });
    const d = await r.json();
    return d.search || null;
  } catch { return null; }
}
// ── Inspiration du moment (« Inspire-moi ») ──────────────────────────────────
// get → lecture publique de la vedette pré-cuisinée (0 coût IA, KV pure).
// publish → admin fige le résultat courant comme vedette.
async function kvGetInspire() {
  try {
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "inspire" }) });
    const d = await r.json();
    return d.search || null;
  } catch { return null; }
}
async function kvPublishInspire(getToken, entry, theme) {
  try {
    const token = await getToken();
    if (!token) return { error: "Non connecté (pas de jeton)." };
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "inspire_publish", token, search: entry, theme }) });
    const d = await r.json().catch(() => ({}));
    if (d && d.ok) return { ok: true };
    return { error: (d && d.error && (d.error.detail || d.error.message)) || `Échec serveur (HTTP ${r.status}).` };
  } catch (e) { return { error: e.message || "Erreur réseau." }; }
}
// ── Demandes de suivi (followups, avec courriel) — panneau admin Retours ──────
async function kvListFollowups(getToken) {
  try {
    const token = await getToken();
    if (!token) return null;
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "list_followups", token }) });
    const d = await r.json();
    return Array.isArray(d.followups) ? d.followups : null;
  } catch { return null; }
}
async function kvUpdateFollowup(getToken, id, op) {
  try {
    const token = await getToken();
    if (!token) return false;
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "update_followup", token, id, op }) });
    const d = await r.json();
    return !!d.ok;
  } catch { return false; }
}
async function kvDeleteTrace(getToken, id) {
  try {
    const token = await getToken();
    if (!token) return false;
    const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "delete_trace", token, id }) });
    const d = await r.json();
    return !!d.ok;
  } catch { return false; }
}
// Horodatage (epoch ms OU ISO UTC) → heure de Montréal (notre fuseau de gestion).
// Les données sont stockées en UTC ; seul l'AFFICHAGE est converti (America/Toronto
// gère automatiquement l'heure d'été/hiver).
function fmtMtl(ts) {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("fr-CA", { timeZone: "America/Toronto", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

// ─── CONFIG ────────────────────────────────────────────────────────────────────
// v3.0.0 — Freemium Clerk + Stripe
const DEMO_MODE   = false; // true = demo local, false = vraie recherche API
const WORKER_URL  = process.env.REACT_APP_WORKER_URL || "https://cruisehunter-proxy.YOUR_SUBDOMAIN.workers.dev";

// Réseaux sociaux (pied de page). Ajouter X et Instagram ici quand les URLs
// seront prêtes — un objet par réseau et le rendu s'occupe du reste.
const SOCIALS = [
  {
    name: "facebook",
    label: "Facebook",
    url: "https://www.facebook.com/profile.php?id=61590446632747",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.91h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94z" />
      </svg>
    ),
  },
];

// ─── ADSENSE CONFIG ─────────────────────────────────────────────────────────────
// Remplacer par ton Publisher ID et tes Ad Unit IDs une fois le compte approuvé.
// Laisser vide ("") pour afficher un espace réservé pendant la beta.
const ADSENSE_CLIENT   = "ca-pub-8428474881576857";
const ADSENSE_SLOT_TOP = "";  // Ad unit ID — bannière entre résultats
const ADSENSE_SLOT_BTM = "";  // Ad unit ID — bannière bas de page

// ─── IMAGES DESTINATIONS ──────────────────────────────────────────────────────
const DEST_IMGS = {
  // Royal Caribbean private islands
  "perfect day": "/destinations/cococay.jpg",
  "cococay":     "/destinations/cococay.jpg",
  "great stirrup":"/destinations/great-stirrup-cay.avif",
  "stirrup cay": "/destinations/great-stirrup-cay.avif",
  // Bahamas
  "nassau":      "/destinations/nassau.avif",
  "bahamas":     "/destinations/nassau.avif",
  "grand bahama":"/destinations/nassau.avif",
  // Bermuda
  "bermuda":     "/destinations/bermudes.jpg",
  "bermudes":    "/destinations/bermudes.jpg",
  "kings wharf": "/destinations/bermudes.jpg",
  // Jamaica
  "jamaica":     "/destinations/jamaique.jpg",
  "jamaïque":    "/destinations/jamaique.jpg",
  "jamaique":    "/destinations/jamaique.jpg",
  "ocho rios":   "/destinations/jamaique.jpg",
  "montego":     "/destinations/jamaique.jpg",
  "falmouth":    "/destinations/jamaique.jpg",
  // Cayman Islands
  "cayman":      "/destinations/grand-cayman.jpg",
  "george town": "/destinations/grand-cayman.jpg",
  // Mexico
  "cozumel":     "/destinations/cozumel.jpg",
  "costa maya":  "/destinations/costa-maya.jpg",
  "progreso":    "/destinations/cozumel.jpg",
  "mexique":     "/destinations/cozumel.jpg",
  "mexico":      "/destinations/cozumel.jpg",
  // Puerto Rico / US Virgin Islands
  "san juan":    "/destinations/san-juan.webp",
  "porto rico":  "/destinations/san-juan.webp",
  "puerto rico": "/destinations/san-juan.webp",
  "st. thomas":  "/destinations/st-thomas.jpg",
  "st thomas":   "/destinations/st-thomas.jpg",
  "charlotte amalie": "/destinations/st-thomas.jpg",
  "virgin island":"/destinations/st-thomas.jpg",
  "tortola":     "/destinations/tortola.jpg",
  "british virgin":"/destinations/tortola.jpg",
  "st. croix":   "/destinations/st-croix.webp",
  // Honduras / Belize
  "roatan":      "/destinations/roatan.png",
  "belize":      "/destinations/belize.png",
  // Aruba / Curacao / ABC
  "aruba":       "/destinations/aruba.webp",
  "oranjestad":  "/destinations/aruba.webp",
  "curacao":     "/destinations/curacao.webp",
  "curaçao":     "/destinations/curacao.webp",
  "willemstad":  "/destinations/curacao.webp",
  "bonaire":     "/destinations/bonaire.webp",
  // Eastern Caribbean
  "barbados":    "/destinations/barbados.webp",
  "bridgetown":  "/destinations/barbados.webp",
  "st. lucia":   "/destinations/st-lucia.jpg",
  "st lucia":    "/destinations/st-lucia.jpg",
  "martinique":  "/destinations/martinique.jpg",
  "guadeloupe":  "/destinations/guadeloupe.webp",
  "antigua":     "/destinations/antigua.png",
  "st. maarten": "/destinations/st-maarten.webp",
  "st maarten":  "/destinations/st-maarten.webp",
  "st. martin":  "/destinations/st-maarten.webp",
  "grenada":     "/destinations/grenade.png",
  "dominica":    "/destinations/dominique.jpg",
  // Turks & Caicos / Dominican
  "turks":       "/destinations/turks-caicos.jpg",
  "grand turk":  "/destinations/turks-caicos.jpg",
  "puerto plata":"/destinations/labadee.jpg",
  "dominican":   "/destinations/labadee.jpg",
  "amber cove":  "/destinations/labadee.jpg",
  "dominicaine": "/destinations/labadee.jpg",
  "labadee":     "/destinations/labadee.jpg",
  // Canada — ports de destination seulement (pas les ports de départ)
  "vancouver":       "/destinations/vancouver.jpg",
  "victoria":        "/destinations/vancouver.jpg",
  "quebec city":     "/destinations/quebec-city.jpg",
  "québec":          "/destinations/quebec-city.jpg",
  "saguenay":        "/destinations/saguenay.jpg",
  "charlottetown":   "/destinations/charlottetown.jpg",
  "halifax":         "/destinations/halifax.jpg",
  "sydney, ns":      "/destinations/sydney-ns.jpg",
  "sydney nova":     "/destinations/sydney-ns.jpg",
  "corner brook":    "/destinations/newfoundland.jpg",
  "st. john's":      "/destinations/newfoundland.jpg",
  "newfoundland":    "/destinations/newfoundland.jpg",
  "terre-neuve":     "/destinations/newfoundland.jpg",
  // ── ASIE ───────────────────────────────────────────────────────────────────
  "singapore":       "/destinations/singapore.jpg",
  "hong kong":       "/destinations/hongkong.jpg",
  // Japon
  "japan":           "/destinations/tokyo.jpg",
  "tokyo":           "/destinations/tokyo.jpg",
  "yokohama":        "/destinations/tokyo.jpg",
  "shimizu":         "/destinations/tokyo.jpg",
  "osaka":           "/destinations/kyoto.jpg",
  "kyoto":           "/destinations/kyoto.jpg",
  "kobe":            "/destinations/kyoto.jpg",
  "nagasaki":        "/destinations/nagasaki.jpg",
  // Vietnam
  "vietnam":         "/destinations/hanoi.jpg",
  "hanoi":           "/destinations/hanoi.jpg",
  "halong":          "/destinations/halong.jpg",
  "ha long":         "/destinations/halong.jpg",
  "ho chi minh":     "/destinations/hochiminh.jpg",
  "saigon":          "/destinations/hochiminh.jpg",
  "da nang":         "/destinations/halong.jpg",
  "chan may":        "/destinations/halong.jpg",
  "hoi an":          "/destinations/halong.jpg",
  // Thaïlande
  "thailand":        "/destinations/bangkok.jpg",
  "bangkok":         "/destinations/bangkok.jpg",
  "laem chabang":    "/destinations/bangkok.jpg",
  "phuket":          "/destinations/phuket.jpg",
  "ko samui":        "/destinations/phuket.jpg",
  // Corée
  "korea":           "/destinations/busan.jpg",
  "busan":           "/destinations/busan.jpg",
  "incheon":         "/destinations/busan.jpg",
  // Chine
  "shanghai":        "/destinations/shanghai.jpg",
  "beijing":         "/destinations/shanghai.jpg",
  "china":           "/destinations/shanghai.jpg",
  "tianjin":         "/destinations/shanghai.jpg",
  // Moyen-Orient / Asie du Sud
  "dubai":           "/destinations/dubai.jpg",
  "abu dhabi":       "/destinations/dubai.jpg",
  "india":           "/destinations/mumbai.jpg",
  "mumbai":          "/destinations/mumbai.jpg",
  "bali":            "/destinations/bali.jpg",
  "indonesia":       "/destinations/bali.jpg",
  // ── EUROPE ─────────────────────────────────────────────────────────────────
  // Grèce / Méditerranée Est
  "santorini":       "/destinations/santorini.jpg",
  "mykonos":         "/destinations/mykonos.jpg",
  "greece":          "/destinations/santorini.jpg",
  "grèce":           "/destinations/santorini.jpg",
  "piraeus":         "/destinations/athens.jpg",
  "athens":          "/destinations/athens.jpg",
  "athènes":         "/destinations/athens.jpg",
  "rhodes":          "/destinations/rhodes.jpg",
  "crete":           "/destinations/crete.jpg",
  "crète":           "/destinations/crete.jpg",
  "heraklion":       "/destinations/crete.jpg",
  "corfu":           "/destinations/corfu.jpg",
  "corfou":          "/destinations/corfu.jpg",
  "mediterran":      "/destinations/santorini.jpg",
  // Croatie / Balkans
  "dubrovnik":       "/destinations/dubrovnik.webp",
  "croatia":         "/destinations/dubrovnik.webp",
  "croatie":         "/destinations/dubrovnik.webp",
  "split":           "/destinations/split.jpg",
  "zadar":           "/destinations/zadar.jpg",
  "kotor":           "/destinations/kotor.jpg",
  "montenegro":      "/destinations/kotor.jpg",
  // Italie — villes curées (photos locales vérifiées 2026-06-03)
  "italy":           "/destinations/rome.webp",
  "italie":          "/destinations/rome.webp",
  "rome":            "/destinations/rome.webp",
  "civitavecchia":   "/destinations/rome.webp",
  "amalfi":          "/destinations/rome.webp",
  "naples":          "/destinations/naples.jpg",
  "napoli":          "/destinations/naples.jpg",
  "venice":          "/destinations/venise.webp",
  "venise":          "/destinations/venise.webp",
  "genoa":           "/destinations/genes.jpg",
  "genes":           "/destinations/genes.jpg",
  "gênes":           "/destinations/genes.jpg",
  "portofino":       "/destinations/genes.jpg",
  "la spezia":       "/destinations/cinque-terre.jpg",
  "spezia":          "/destinations/cinque-terre.jpg",
  "cinque terre":    "/destinations/cinque-terre.jpg",
  "livorno":         "/destinations/florence.jpg",
  "livourne":        "/destinations/florence.jpg",
  "florence":        "/destinations/florence.jpg",
  "firenze":         "/destinations/florence.jpg",
  "pisa":            "/destinations/florence.jpg",
  "pise":            "/destinations/florence.jpg",
  "taormina":        "/destinations/taormine.jpg",
  "taormine":        "/destinations/taormine.jpg",
  "messina":         "/destinations/taormine.jpg",
  "messine":         "/destinations/taormine.jpg",
  "sicily":          "/destinations/taormine.jpg",
  "sicile":          "/destinations/taormine.jpg",
  "palermo":         "/destinations/taormine.jpg",
  "palerme":         "/destinations/taormine.jpg",
  // Malte
  "valletta":        "/destinations/la-valette.jpg",
  "la valette":      "/destinations/la-valette.jpg",
  "valette":         "/destinations/la-valette.jpg",
  "malta":           "/destinations/la-valette.jpg",
  "malte":           "/destinations/la-valette.jpg",
  // Espagne
  "barcelona":       "/destinations/barcelone.jpg",
  "spain":           "/destinations/barcelone.jpg",
  "espagne":         "/destinations/barcelone.jpg",
  "palma":           "/destinations/palma.jpg",
  "malaga":          "/destinations/malaga.jpg",
  "valencia":        "/destinations/valencia.jpg",
  "alicante":        "/destinations/alicante.jpg",
  "cadiz":           "/destinations/cadiz.jpg",
  "cadix":           "/destinations/cadiz.jpg",
  "ibiza":           "/destinations/ibiza.jpg",
  // Canaries — photos par île (Las Palmas/Lanzarote/Fuerteventura distinctes de Tenerife)
  "canary":          "/destinations/tenerife.avif",
  "tenerife":        "/destinations/tenerife.avif",
  "las palmas":      "/destinations/las-palmas.jpg",
  "gran canaria":    "/destinations/las-palmas.jpg",
  "lanzarote":       "/destinations/lanzarote.jpg",
  "fuerteventura":   "/destinations/fuerteventura.jpg",
  // Gibraltar
  "gibraltar":       "/destinations/gibraltar.jpg",
  // Maroc
  "casablanca":      "/destinations/casablanca.jpg",
  "tanger":          "/destinations/tanger.jpg",
  "tangier":         "/destinations/tanger.jpg",
  "agadir":          "/destinations/agadir.jpg",
  "maroc":           "/destinations/casablanca.jpg",
  "morocco":         "/destinations/casablanca.jpg",
  // Portugal
  "lisbon":          "/destinations/lisbonne.jpg",
  "lisbonne":        "/destinations/lisbonne.jpg",
  "porto":           "/destinations/lisbonne.jpg",
  "portugal":        "/destinations/lisbonne.jpg",
  "madeira":         "/destinations/funchal.jpg",
  "funchal":         "/destinations/funchal.jpg",
  // France
  "marseille":       "/destinations/marseille.jpg",
  "cannes":          "/destinations/cannes.jpg",
  "nice":            "/destinations/nice.jpg",
  "france":          "/destinations/cote-azur.jpg",
  "ajaccio":         "/destinations/ajaccio.jpg",
  "corsica":         "/destinations/ajaccio.jpg",
  "corse":           "/destinations/ajaccio.jpg",
  "bastia":          "/destinations/ajaccio.jpg",
  // Turquie
  "istanbul":        "/destinations/istanbul.webp",
  "turkey":          "/destinations/istanbul.webp",
  "turquie":         "/destinations/istanbul.webp",
  "ephesus":         "/destinations/istanbul.webp",
  "bodrum":          "/destinations/istanbul.webp",
  "kusadasi":        "/destinations/istanbul.webp",
  // Norvège / Fjords
  "norway":          "/destinations/norvege.webp",
  "norvège":         "/destinations/norvege.webp",
  "fjord":           "/destinations/norvege.webp",
  "bergen":          "/destinations/bergen.jpg",
  "oslo":            "/destinations/norvege.webp",
  "geiranger":       "/destinations/geiranger.jpg",
  "flam":            "/destinations/flam.jpg",
  "flåm":            "/destinations/flam.jpg",
  "alesund":         "/destinations/alesund.jpg",
  "ålesund":         "/destinations/alesund.jpg",
  "stavanger":       "/destinations/stavanger.jpg",
  "tromso":          "/destinations/tromso.jpg",
  "tromsø":          "/destinations/tromso.jpg",
  // Baltique / Scandinavie
  "stockholm":       "/destinations/stockholm.jpg",
  "copenhagen":      "/destinations/copenhagen.jpg",
  "copenhague":      "/destinations/copenhagen.jpg",
  "helsinki":        "/destinations/helsinki.jpg",
  "tallinn":         "/destinations/tallinn.jpg",
  "riga":            "/destinations/riga.jpg",
  "baltic":          "/destinations/stockholm.jpg",
  // UK / Irlande / Islande
  "southampton":     "/destinations/southampton.jpg",
  "dover":           "/destinations/dover.jpg",
  "london":          "/destinations/southampton.jpg",
  "england":         "/destinations/southampton.jpg",
  "liverpool":       "/destinations/liverpool.jpg",
  "belfast":         "/destinations/belfast.jpg",
  "cobh":            "/destinations/cobh.jpg",
  "cork":            "/destinations/cobh.jpg",
  "dublin":          "/destinations/dublin.jpg",
  "ireland":         "/destinations/irlande.jpg",
  "irlande":         "/destinations/irlande.jpg",
  // Écosse
  "edinburgh":       "/destinations/edinburgh.jpg",
  "édimbourg":       "/destinations/edinburgh.jpg",
  "leith":           "/destinations/edinburgh.jpg",
  "south queensferry":"/destinations/edinburgh.jpg",
  "glasgow":         "/destinations/edinburgh.jpg",
  "greenock":        "/destinations/edinburgh.jpg",
  "scotland":        "/destinations/edinburgh.jpg",
  "écosse":          "/destinations/edinburgh.jpg",
  "inverness":       "/destinations/inverness.jpg",
  "invergordon":     "/destinations/inverness.jpg",
  "loch ness":       "/destinations/inverness.jpg",
  "kirkwall":        "/destinations/kirkwall.jpg",
  "orkney":          "/destinations/kirkwall.jpg",
  "orcades":         "/destinations/kirkwall.jpg",
  "iceland":         "/destinations/islande.webp",
  "islande":         "/destinations/islande.webp",
  "reykjavik":       "/destinations/islande.webp",
  // ── ALASKA ─────────────────────────────────────────────────────────────────
  // Glacier Bay (Margerie Glacier + Mt Fairweather) — local, fiable (l'ancienne
  // photo Unsplash montrait un pipeline industriel, 2026-06-07).
  "alaska":          "/destinations/alaska.jpg",
  "juneau":          "/destinations/juneau.jpg",
  "ketchikan":       "/destinations/ketchikan.jpg",
  "skagway":         "/destinations/skagway.jpg",
  "sitka":           "/destinations/sitka.jpg",
  "seward":          "/destinations/alaska.jpg",
  "whittier":        "/destinations/alaska.jpg",
  "glacier bay":     "/destinations/alaska.jpg",
  "glacier":         "/destinations/alaska.jpg",
  "hubbard":         "/destinations/alaska.jpg",
  // ── COLOMBIE-BRITANNIQUE (escales des croisières Alaska) ─────────────────────
  "colombie-britannique": "/destinations/vancouver.jpg",
  "british columbia":     "/destinations/vancouver.jpg",
  "colombie":             "/destinations/vancouver.jpg",
  // ── HAWAII ─────────────────────────────────────────────────────────────────
  "hawaii":          "/destinations/honolulu.jpg",
  "honolulu":        "/destinations/honolulu.jpg",
  "oahu":            "/destinations/honolulu.jpg",
  "maui":            "/destinations/maui.jpg",
  "lahaina":         "/destinations/maui.jpg",
  "kahului":         "/destinations/maui.jpg",
  "kauai":           "/destinations/kauai.jpg",
  "nawiliwili":      "/destinations/kauai.jpg",
  "hilo":            "/destinations/hilo.jpg",
  "kona":            "/destinations/hilo.jpg",
  // ── AMÉRIQUE DU SUD ────────────────────────────────────────────────────────
  "rio":             "/destinations/rio.jpg",
  "brazil":          "/destinations/rio.jpg",
  "brésil":          "/destinations/rio.jpg",
  "buenos aires":    "/destinations/buenosaires.jpg",
  "argentina":       "/destinations/buenosaires.jpg",
  "argentine":       "/destinations/buenosaires.jpg",
  "montevideo":      "/destinations/montevideo.jpg",
  "uruguay":         "/destinations/montevideo.jpg",
  "ushuaia":         "/destinations/ushuaia.jpg",
  "patagonia":       "/destinations/ushuaia.jpg",
  "patagonie":       "/destinations/ushuaia.jpg",
  "punta arenas":    "/destinations/ushuaia.jpg",
  "cartagena":       "/destinations/cartagena.jpg",
  "colombia":        "/destinations/cartagena.jpg",
  "chile":           "/destinations/valparaiso.jpg",
  "chili":           "/destinations/valparaiso.jpg",
  "valparaiso":      "/destinations/valparaiso.jpg",
  "santiago":        "/destinations/valparaiso.jpg",
  "panama":          "/destinations/panama.jpg",
  "canal":           "/destinations/panama.jpg",
  "peru":            "/destinations/peru.jpg",
  "pérou":           "/destinations/peru.jpg",
  "callao":          "/destinations/peru.jpg",
  "lima":            "/destinations/peru.jpg",
  // ── PACIFIQUE / AUSTRALIE ──────────────────────────────────────────────────
  "sydney":          "/destinations/sydney.jpg",
  "australia":       "/destinations/sydney.jpg",
  "australie":       "/destinations/sydney.jpg",
  "new zealand":     "/destinations/auckland.jpg",
  "nouvelle-zélande":"/destinations/auckland.jpg",
  "auckland":        "/destinations/auckland.jpg",
  "tahiti":          "/destinations/tahiti.jpg",
  "polynesia":       "/destinations/tahiti.jpg",
  "polynésie":       "/destinations/tahiti.jpg",
  "bora bora":       "/destinations/tahiti.jpg",
  "default":         "/destinations/ocean.jpg",
};

// ─── HÔTELS CURATÉS PRÉ-CROISIÈRE ────────────────────────────────────────────
// Navette aéroport ✓ + navette port de croisière ✓ — vérifiés 2025
// Tiers : budget · midrange · upscale  (correspond à data.hotel_stars du funnel)
const CURATED_HOTELS = {
  miami: {
    budget: [
      { name: "Comfort Suites Miami Airport North",               expedia_id: 4326587,  stars: 3, note_fr: "navettes gratuites · petit-déj inclus",          note_en: "free shuttles both ways · breakfast included" },
      { name: "Comfort Inn & Suites Miami International Airport", expedia_id: 34864763, stars: 3, note_fr: "navettes gratuites · petit-déj inclus",          note_en: "free shuttles both ways · breakfast included" },
    ],
    midrange: [
      { name: "Hampton Inn & Suites Miami Airport Blue Lagoon",   expedia_id: 479114,   stars: 3, note_fr: "navette port ~12 $/pers · très bien noté",       note_en: "port shuttle ~$12/person · top rated" },
    ],
    upscale: [
      { name: "Embassy Suites by Hilton Miami International Airport", expedia_id: 41394,   stars: 4, note_fr: "navette port ~12 $/pers · breakfast + happy hour", note_en: "port shuttle ~$12/person · breakfast + happy hour" },
      { name: "Regency Miami Airport by Sonesta",                     expedia_id: 11742,   stars: 4, note_fr: "navette port ~15 $/pers",                         note_en: "port shuttle ~$15/person" },
    ],
  },
  fort_lauderdale: {
    budget: [
      { name: "Rodeway Inn & Suites Fort Lauderdale Airport & Port Everglades", expedia_id: 1429514,  stars: 2, note_fr: "navette FLL gratuite 24/7 · navette port gratuite · petit-déj inclus", note_en: "free FLL shuttle 24/7 · free cruise port shuttle · breakfast included" },
      { name: "Comfort Suites Fort Lauderdale Airport & Cruise Port",           expedia_id: 20087951, stars: 3, note_fr: "navette FLL gratuite · navette port ~13 $/pers · petit-déj inclus",   note_en: "free FLL shuttle · cruise port shuttle ~$13/person · breakfast included" },
    ],
    midrange: [
      { name: "Hyatt Place Fort Lauderdale Airport - South & Cruise Port",      expedia_id: 2930016,  stars: 3, note_fr: "navette FLL gratuite 24/7 · navette port (prestataire) · petit-déj", note_en: "free FLL shuttle 24/7 · paid port shuttle · breakfast included" },
      { name: "Hilton Garden Inn Ft. Lauderdale Airport-Cruise Port",           expedia_id: 984870,   stars: 3, note_fr: "navette FLL gratuite · navette port payante · piscine · restaurant",  note_en: "free FLL shuttle · paid cruise port shuttle · pool · restaurant" },
    ],
    upscale: [
      { name: "Embassy Suites by Hilton Fort Lauderdale 17th Street",           expedia_id: 11909,    stars: 4, note_fr: "navette port ~12 $/pers · suites 2 pièces · breakfast + cocktails",   note_en: "cruise port shuttle ~$12/person · 2-room suites · breakfast + reception" },
      { name: "Hilton Fort Lauderdale Marina",                                  expedia_id: 23113,    stars: 4, note_fr: "navette port ~10 $/pers · vue marina · à 2 pas du port",              note_en: "cruise port shuttle ~$10/person · marina views · steps from port" },
    ],
  },
  port_canaveral: {
    budget: [
      { name: "Fairfield Inn & Suites Orlando Airport",                         expedia_id: 84583,    stars: 3, note_fr: "package Fly Stay Cruise · navette MCO + port incluses · départ 11h30", note_en: "Fly Stay Cruise package · MCO & port shuttles included · 11:30am port departure" },
    ],
    midrange: [
      { name: "Hampton Inn & Suites Cape Canaveral Cruise Port",                expedia_id: 48813289, stars: 3, note_fr: "navette port 12 $/pers (9h30-11h30) · petit-déj inclus · < 1 mi du port", note_en: "port shuttle $12/person (9:30am-11:30am) · breakfast included · <1 mi from terminals" },
      { name: "Homewood Suites by Hilton Cape Canaveral - Cocoa Beach",         expedia_id: 12879530, stars: 3, note_fr: "navette port ~12 $/pers · suites avec cuisine · petit-déj inclus",      note_en: "port shuttle ~$12/person · suite with kitchen · breakfast included" },
    ],
    upscale: [
      { name: "DoubleTree by Hilton Cocoa Beach Oceanfront",                    expedia_id: 4398,     stars: 4, note_fr: "navette MCO + navette port inclus · vue océan · plage privée",          note_en: "MCO & port shuttles included · oceanfront · private beach" },
      { name: "Courtyard by Marriott Cocoa Beach Cape Canaveral",               expedia_id: 574977,   stars: 4, note_fr: "package navette MCO A/R + navette port · piscine · 10 min du port",     note_en: "Fly Stay Cruise: MCO round-trip + port shuttle · pool · 10 min from port" },
    ],
  },
};

function getCuratedPortKey(port = "") {
  const p = (port || "").toLowerCase();
  if (p.includes("miami"))                                  return "miami";
  if (p.includes("canaveral"))                              return "port_canaveral";
  if (p.includes("lauderdale") || p.includes("everglades")) return "fort_lauderdale";
  return null;
}

// ─── IMAGES NAVIRES ────────────────────────────────────────────────────────────
// Wikipedia est tenté en premier (useWikiImage). Si Wikipedia échoue, placeholder dégradé.

const SHIP_IMGS = {
  // Royal Caribbean — Icon Class (méga)
  "icon of the seas":         "https://upload.wikimedia.org/wikipedia/commons/thumb/6/66/Icon_of_the_Seas_Puerto_Rico_2025_%28cropped%29.jpg/960px-Icon_of_the_Seas_Puerto_Rico_2025_%28cropped%29.jpg",
  "star of the seas":         null,
  // Royal Caribbean — Oasis Class
  "wonder of the seas":       null,
  "symphony of the seas":     null,
  "harmony of the seas":      null,
  "oasis of the seas":        null,
  "allure of the seas":       null,
  // Royal Caribbean — Quantum Class
  "quantum of the seas":      null,
  "anthem of the seas":       null,
  "ovation of the seas":      null,
  "spectrum of the seas":     null,
  // Royal Caribbean — Voyager/Freedom Class
  "navigator of the seas":    null,
  "adventure of the seas":    null,
  "explorer of the seas":     null,
  "freedom of the seas":      null,
  "independence of the seas": null,
  // Norwegian
  "norwegian prima":          null,
  "norwegian viva":           null,
  "norwegian escape":         null,
  "norwegian bliss":          null,
  "norwegian joy":            null,
  "norwegian getaway":        null,
  "norwegian breakaway":      null,
  // Carnival
  "carnival celebration":     null,
  "carnival jubilee":         null,
  "carnival mardi gras":      null,
  "carnival venezia":         null,
  "carnival vista":           null,
  "carnival horizon":         null,
  "carnival panorama":        null,
  // Celebrity
  "celebrity beyond":         null,
  "celebrity ascent":         null,
  "celebrity edge":           null,
  "celebrity apex":           null,
  // Princess
  "sun princess":             null,
  "sphere princess":          null,
  "enchanted princess":       null,
  "discovery princess":       null,
  "majestic princess":        null,
  // MSC
  "msc world europa":         null,
  "msc seashore":             null,
  "msc seascape":             null,
  "msc magnifica":            null,
  // Holland America
  "rotterdam":                null,
  "nieuw statendam":          null,
  "koningsdam":               null,
  // Virgin Voyages
  "scarlet lady":             null,
  "valiant lady":             null,
  "resilient lady":           null,
};

const getShipImg = (name = "") => {
  const n = name.toLowerCase();
  const k = Object.keys(SHIP_IMGS).find(k => n.includes(k));
  return k ? SHIP_IMGS[k] : null;
};
// Villes à photo curée FIABLE (gagnent toujours sur Wikipédia, même si nom de
// fichier ≠ clé — ex: "livourne" → florence.jpg). Évite les images Wikipédia
// hors-sujet (portrait, pancarte) sur les ports méditerranéens (bug 2026-06-03).
const DEST_TRUSTED = new Set([
  "naples", "napoli", "genoa", "genes", "gênes", "portofino",
  "la spezia", "spezia", "cinque terre", "livorno", "livourne", "florence", "firenze", "pisa", "pise",
  "taormina", "taormine", "messina", "messine", "sicily", "sicile", "palermo", "palerme",
  "valletta", "la valette", "valette", "malta", "malte",
  "st. thomas", "st thomas", "charlotte amalie", "virgin island",
  // Alaska + Colombie-Britannique : photos locales curées (sinon Wikipédia rend
  // un pipeline / une plage tropicale — 2026-06-07).
  "alaska", "juneau", "ketchikan", "skagway", "sitka", "seward", "whittier", "glacier bay",
  "vancouver", "victoria", "colombie-britannique", "british columbia", "colombie",
]);
// Retourne la clé DEST_IMGS qui matche + si la photo curée est SPÉCIFIQUE/fiable.
// Spécifique = le nom du fichier contient la clé (ex: clé "cozumel" → cozumel.jpg → ✓).
// trusted = ville de DEST_TRUSTED (photo curée fiable) → ne va JAMAIS sur Wikipédia.
// Sinon (collapse pays : "malaga" → barcelone.jpg) → on tente la VILLE sur Wikipédia.
// Pliage des accents (« Gênes » → « genes ») + on préfère le match le plus PRÉCOCE
// dans le nom (les ports s'écrivent « Ville, Pays » → la ville prime sur le pays).
const _fold    = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const _normKey = (s) => _fold(s).replace(/[^a-z0-9]/g, "");
const getDestMatch = (name = "") => {
  const n = _fold(name);
  let best = null;
  for (const k of Object.keys(DEST_IMGS)) {
    if (k === "default") continue;
    const fk = _fold(k);
    const idx = n.indexOf(fk);
    if (idx === -1) continue;
    if (!best || idx < best.idx || (idx === best.idx && fk.length > best.len)) best = { key: k, idx, len: fk.length };
  }
  if (!best) return { key: null, file: DEST_IMGS.default, specific: false, trusted: false };
  const file = DEST_IMGS[best.key];
  const base = _normKey((file.split("/").pop() || "").replace(/\.\w+$/, ""));
  // Toute photo LOCALE curée (/destinations/) est fiable par défaut : elle est sur-thème
  // (ville/région/pays) et ne doit JAMAIS être écrasée par une image Wikipédia hors-sujet
  // (carte, panneau, portrait). Wikipédia ne reste tenté que pour les replis distants
  // (Unsplash pays : Asie, Amérique du Sud). Bug 2026-06-14 : Barcelona/Valencia/Las Palmas/
  // Southampton tombaient sur Wikipédia car nom de fichier ≠ nom du port.
  const isLocal = typeof file === "string" && file.startsWith("/destinations/");
  return { key: best.key, file, specific: base.includes(_normKey(best.key)), trusted: isLocal || DEST_TRUSTED.has(best.key) };
};

// Pools régionaux — filet anti-doublon. Un itinéraire (ex. Espagne, Alaska, Caraïbes)
// peut avoir plusieurs escales d'une même région ; si deux tombent sur la MÊME photo
// (sous-port générique), la 2e bascule sur une AUTRE photo de la même région via dedupeDestSrc.
const DEST_POOLS = [
  ["/destinations/barcelone.jpg","/destinations/valencia.jpg","/destinations/malaga.jpg","/destinations/palma.jpg","/destinations/alicante.jpg","/destinations/cadiz.jpg","/destinations/ibiza.jpg","/destinations/gibraltar.jpg"],
  ["/destinations/tenerife.avif","/destinations/las-palmas.jpg","/destinations/lanzarote.jpg","/destinations/fuerteventura.jpg"],
  ["/destinations/rome.webp","/destinations/naples.jpg","/destinations/venise.webp","/destinations/genes.jpg","/destinations/florence.jpg","/destinations/taormine.jpg","/destinations/cinque-terre.jpg"],
  ["/destinations/santorini.jpg","/destinations/mykonos.jpg","/destinations/athens.jpg","/destinations/rhodes.jpg","/destinations/crete.jpg","/destinations/corfu.jpg"],
  ["/destinations/dubrovnik.webp","/destinations/split.jpg","/destinations/zadar.jpg","/destinations/kotor.jpg"],
  ["/destinations/cote-azur.jpg","/destinations/marseille.jpg","/destinations/nice.jpg","/destinations/cannes.jpg","/destinations/ajaccio.jpg"],
  ["/destinations/casablanca.jpg","/destinations/tanger.jpg","/destinations/agadir.jpg"],
  ["/destinations/lisbonne.jpg","/destinations/funchal.jpg"],
  ["/destinations/stockholm.jpg","/destinations/copenhagen.jpg","/destinations/helsinki.jpg","/destinations/tallinn.jpg","/destinations/riga.jpg"],
  ["/destinations/norvege.webp","/destinations/bergen.jpg","/destinations/geiranger.jpg","/destinations/alesund.jpg","/destinations/flam.jpg","/destinations/stavanger.jpg","/destinations/tromso.jpg"],
  ["/destinations/southampton.jpg","/destinations/dover.jpg","/destinations/liverpool.jpg","/destinations/edinburgh.jpg","/destinations/belfast.jpg","/destinations/cobh.jpg","/destinations/dublin.jpg","/destinations/irlande.jpg","/destinations/kirkwall.jpg","/destinations/inverness.jpg"],
  ["/destinations/alaska.jpg","/destinations/juneau.jpg","/destinations/ketchikan.jpg","/destinations/skagway.jpg","/destinations/sitka.jpg"],
  ["/destinations/honolulu.jpg","/destinations/maui.jpg","/destinations/kauai.jpg","/destinations/hilo.jpg"],
  ["/destinations/tokyo.jpg","/destinations/kyoto.jpg","/destinations/nagasaki.jpg"],
  ["/destinations/hanoi.jpg","/destinations/halong.jpg","/destinations/hochiminh.jpg"],
  ["/destinations/nassau.avif","/destinations/cococay.jpg","/destinations/great-stirrup-cay.avif","/destinations/labadee.jpg","/destinations/bermudes.jpg","/destinations/jamaique.jpg","/destinations/grand-cayman.jpg","/destinations/cozumel.jpg","/destinations/costa-maya.jpg","/destinations/san-juan.webp","/destinations/st-thomas.jpg","/destinations/tortola.jpg","/destinations/st-croix.webp","/destinations/aruba.webp","/destinations/curacao.webp","/destinations/bonaire.webp","/destinations/barbados.webp","/destinations/st-lucia.jpg","/destinations/martinique.jpg","/destinations/guadeloupe.webp","/destinations/antigua.png","/destinations/st-maarten.webp","/destinations/grenade.png","/destinations/dominique.jpg","/destinations/turks-caicos.jpg","/destinations/roatan.png","/destinations/belize.png"],
];
// Renvoie une photo non encore utilisée dans CET itinéraire : si `file` est déjà pris,
// on cherche une alternative dans le même pool régional ; sinon on tolère le doublon
// (jamais une photo d'une autre région).
function dedupeDestSrc(file, used) {
  if (!file || !used.has(file)) return file;
  const pool = DEST_POOLS.find(p => p.includes(file));
  if (pool) { const alt = pool.find(x => !used.has(x)); if (alt) return alt; }
  return file;
}

const GF = { fontFamily: "'Playfair Display', serif" };
const GL = { fontFamily: "Lato, sans-serif" };

// Atmosphère premium : halos dorés + marine diffus posés sur le fond marine profond
// (#07111F). Donne de la profondeur/lumière sans nuire à la lisibilité ni aux CTA.
// Appliqué aux écrans racine sans photo (accueil, résultats, funnel).
const ATMOSPHERE = "radial-gradient(ellipse 90% 55% at 50% -8%, rgba(201,168,76,0.09), transparent 60%), radial-gradient(ellipse 75% 50% at 100% 22%, rgba(26,74,122,0.16), transparent 58%), radial-gradient(ellipse 80% 60% at 0% 102%, rgba(26,74,122,0.10), transparent 60%), #07111F";

function fmtDate(str, lang = "fr") {
  if (!str) return "";
  const locale = lang === "en" ? "en-CA" : "fr-CA";
  return new Date(str + "T12:00:00").toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
}

// Affiche un nom de compagnie lisible même si l'IA renvoie un slug (« royal_caribbean »
// → « Royal Caribbean »). Laisse intact ce qui est déjà lisible (majuscule + espace).
function prettyLine(s) {
  const v = String(s || "").trim();
  if (!v) return v;
  if (!/[_-]/.test(v) && v !== v.toLowerCase()) return v; // déjà propre
  const ACR = { msc: "MSC", ncl: "NCL", po: "P&O" };
  return v.replace(/[_-]+/g, " ").split(/\s+/)
    .map(w => ACR[w.toLowerCase()] || (w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}





// ─── LIENS AFFILIÉS CRUISEDIRECT (CJ) ────────────────────────────────────────
const CD_CJ_PREFIX = 'https://www.jdoqocy.com/click-101729213-15534477?url=';

// IDs destination CruiseDirect (f[N]=destination:ID)
const CD_DESTINATIONS = {
  carib_east: 2818, carib_west: 2818, carib_south: 2818, bermuda: 2818,
  alaska:     2814,
  // europe: TBD — distinct de mediterranean sur CruiseDirect
  // hawaii: TBD
};

// IDs durée CruiseDirect (f[N]=duration:ID)
const CD_DURATIONS = {
  short: 2,  // 3-5 nuits
  week:  3,  // 6-9 nuits (on vise 7-9 via Stage 2)
  long:  4,  // 10-14 nuits
  xlong: 5,  // 15+ nuits
};

// IDs compagnie CruiseDirect (f[N]=cruiseline:ID)
const CD_CRUISELINES = {
  royal_caribbean: 115,
  msc:             121,
  norwegian:       113,
  crystal:         744848,  // filet compagnie : si un navire Crystal n'est pas dans CD_SHIPS
};

// IDs navires CruiseDirect — extraits automatiquement via scraper console 2026-04-28
const CD_SHIPS = {
  "adventure of the seas":      287,
  "allure of the seas":         310,
  "brilliant lady":             783,
  "carnival breeze":            164,
  "carnival celebration":       172,
  "carnival dream":             162,
  "carnival firenze":         176,
  "carnival horizon":           167,
  "carnival jubilee":           173,
  "carnival mardi gras":        171,
  "carnival legend":            142,
  "carnival liberty":           159,
  "carnival magic":             163,
  "carnival miracle":           143,
  "carnival paradise":          155,
  "carnival sunshine":          165,
  "carnival valor":             158,
  "carnival venezia":           175,
  "carnival vista":             166,
  "celebrity apex":             199,
  "celebrity ascent":           201,
  "celebrity beyond":           200,
  "celebrity constellation":    178,
  "celebrity eclipse":          192,
  "celebrity millennium":     183,
  "celebrity reflection":       194,
  "celebrity silhouette":       193,
  "celebrity summit":           184,
  "celebrity xcel":             744035,
  "coral princess":             262,
  "costa fascinosa":            345,
  "costa favolosa":             344,
  "crown princess":             276,
  "crystal serenity":           204,
  "crystal symphony":           203,
  "disney destiny":             790868,
  "disney dream":               214,
  "disney fantasy":             215,
  "disney magic":               212,
  "disney treasure":            744069,
  "enchanted princess":         283,
  "enchantment of the seas":    289,
  "eurodam":                    231,
  "explora i":                  805771,
  "explora iii":                823797,
  "explorer of the seas":       290,
  "freedom of the seas":        306,
  "grandeur of the seas":       291,
  "harmony of the seas":        313,
  "icon of the seas":           319,
  "independence of the seas":   308,
  "jewel of the seas":          292,
  "legend of the seas":         833914,
  "liberty of the seas":        307,
  "mardi gras":                 171,
  "mariner of the seas":        295,
  "msc armonia":              380,
  "msc grandiosa":              399,
  "msc meraviglia":             395,
  "msc opera":                  385,
  "msc poesia":                 389,
  "msc seascape":               403,
  "msc seashore":               401,
  "msc seaside":                396,
  "msc world america":          405,
  "msc world europa":           402,
  "nieuw amsterdam":            232,
  "nieuw statendam":            234,
  "norwegian aqua":             44263,
  "norwegian bliss":            256,
  "norwegian breakaway":        253,
  "norwegian epic":             252,
  "norwegian escape":           255,
  "norwegian gem":              249,
  "norwegian getaway":          254,
  "norwegian jewel":            247,
  "norwegian joy":              257,
  "norwegian luna":             792218,
  "norwegian pearl":            248,
  "norwegian prima":            259,
  "norwegian viva":             260,
  "oasis of the seas":          309,
  "oceania allura":             741699,
  "oceania marina":             375,
  // alias slugs Gangwaze — Oceania sans préfixe (mêmes navires que ci-dessus)
  "marina":                     375,
  "nautica":                    373,
  "riviera":                    376,
  "sirena":                     377,
  "oceania vista":              378,
  "odyssey of the seas":      317,
  "queen elizabeth":            370,
  "queen mary 2":             368,
  "radiance of the seas":       299,
  "regal princess":             269,
  "resilient lady":             782,
  "rhapsody of the seas":       300,
  "rotterdam":                  223,
  "scarlet lady":               780,
  "seabourn ovation":           364,
  "seabourn quest":             362,
  "seven seas grandeur":        553,
  "seven seas mariner":         549,
  "seven seas prestige":        774592,
  "seven seas splendor":        552,
  "seven seas voyager":       548,
  "silver dawn":                544,
  "silver nova":                545,
  "silver ray":                 546,
  "silver shadow":              534,
  "silver spirit":              536,
  "sky princess":               282,
  "star legend":                327,
  "star of the seas":           44262,
  "star pride":                 325,
  "star princess":              286,
  "sun princess":               744259,
  "symphony of the seas":       315,
  "utopia of the seas":         320,
  "valiant lady":               781,
  "viking sea":                 763,
  "vision of the seas":         304,
  "volendam":                   227,
  "wind surf":                  324,
  "zuiderdam":                  230,
  // Celebrity (Europe)
  "celebrity infinity":         181,
  // Costa (Europe)
  "costa pacifica":             341,
  "costa smeralda":             353,
  "costa toscana":              356,
  // Cunard
  "queen anne":                 371,
  "queen victoria":             369,
  // Explora Journeys
  "explora ii":                 805772,
  // MSC (Europe)
  "msc euribia":                404,
  "msc fantasia":               390,
  "msc lirica":                 381,
  "msc magnifica":              392,
  "msc orchestra":              386,
  "msc preziosa":               394,
  "msc sinfonia":               388,
  "msc world asia":             823917,
  // Oceania (Europe)
  "oceania sirena":             377,
  // Regent Seven Seas
  "seven seas navigator":       550,
  // AmaWaterways (rivière)
  "amabella":                   611,
  "amacello":                   603,
  "amacerto":                   612,
  "amadolce":                   598,
  "amadouro":                   605,
  "amakristina":                625,
  "amalea":                     626,
  "amalucia":                   630,
  "amalyra":                    601,
  "amamagdalena":               633,
  "amamagna":                   628,
  "amamora":                    627,
  "amamoraprima":               613,
  "amareina":                   617,
  "amaserena":                  620,
  "amasiena":                   629,
  "amasintra":                  744855,
  "amasofia":                   776275,
  "amasonata":                  616,
  "amastella":                  622,
  "amavenita":                  624,
  "amaverde":                   610,
  "amavida":                    614,
  "amaviola":                   623,
  // Avalon Waterways (rivière)
  "avalon envision":            586,
  "avalon expression":          574,
  "avalon illumination":        578,
  "avalon imagery ii":          561,
  "avalon impression":          579,
  "avalon panorama":            554,
  "avalon passion":             587,
  "avalon tranquility ii":      583,
  "avalon view":                593,
  "avalon visionary":           569,
  "avalon vista":               570,
  // Tauck (S.S. rivière)
  "ss beatrice":                749,
  "ss elisabeth":               744924,
  "ss joie de vivre":           745,
  "ss maria theresa":           744,
  "ss victoria":                744923,
  "ss antoinette":              722,
  // Rivage (rivière)
  "river duchess":              726,
  // Viking Ocean
  "viking astrild":             487,
  "viking beyla":               486,
  "viking dagur":               792659,
  "viking egdir":               529,
  "viking fjorgyn":             521,
  "viking gersemi":             523,
  "viking gymir":               528,
  "viking haki":                818360,
  "viking herja":               508,
  "viking hervor":              524,
  "viking neptune":             769,
  "viking nerthus":             792587,
  "viking saturn":              770,
  "viking skaga":               520,
  "viking star":                761,
  "viking vela":                771,
  "viking vesta":               737783,
  // Viking Longship (rivière)
  "viking longship aegir":      441,
  "viking longship alruna":     499,
  "viking longship alsvin":     477,
  "viking longship atla":       444,
  "viking longship baldur":     462,
  "viking longship bragi":      443,
  "viking longship egil":       500,
  "viking longship einar":      512,
  "viking longship eir":        496,
  "viking longship embla":      442,
  "viking longship freya":      437,
  "viking longship gefjon":     490,
  "viking longship gullveig":   464,
  "viking longship hild":       507,
  "viking longship hlin":       483,
  "viking longship idi":        466,
  "viking longship idun":       438,
  "viking longship ingvi":      480,
  "viking longship jarl":       446,
  "viking longship kara":       484,
  "viking longship kari":       519,
  "viking longship lif":        468,
  "viking longship magni":      469,
  "viking longship mani":       495,
  "viking longship mimir":      493,
  "viking longship modi":       491,
  "viking longship radgrid":    518,
  "viking longship rinda":      447,
  "viking longship rolf":       502,
  "viking longship sigrun":     514,
  "viking longship sigyn":      515,
  "viking longship skadi":      448,
  "viking longship skirnir":    497,
  "viking longship tialfi":     503,
  "viking longship tir":        510,
  "viking longship tor":        450,
  "viking longship ullur":      511,
  "viking longship vali":       513,
  "viking longship var":        449,
  "viking longship ve":         492,
  "viking longship vidar":      489,
  "viking longship vilhjalm":   506,
  "viking longship vili":       494,
  // Hawaii
  "emerald princess":           278,
  "pride of america":           245,
  "ruby princess":              279,
  // Asie
  "amadara":                    621,
  "avalon saigon":              588,
  "celebrity solstice":         190,
  "diamond princess":           264,
  "disney adventure":           790719,
  "mekong jewel":               752,
  "msc bellissima":             398,
  "navigator of the seas":      297,
  "norwegian jade":             250,
  "oceania nautica":            373,
  "sapphire princess":          271,
  "seabourn encore":            363,
  "silver muse":                541,
  "spectrum of the seas":       316,
  "star seeker":                772825,
  "viking saigon":              526,
  "viking tonle":               723493,
  "westerdam":                  228,
  // Amérique du Sud
  "amameodia":                  634,
  "aria amazon":                757,
  "celebrity equinox":          191,
  "costa diadema":              348,
  "costa serena":               340,
  "majestic princess":          281,
  "msc divina":                 393,
  "msc musica":                 384,
  "msc seaview":                397,
  "msc splendida":              391,
  "msc virtuosa":               400,
  "oceania insignia":           372,
  "oosterdam":                  221,
  "viking jupiter":             766,
  // Alaska
  "anthem of the seas":         312,
  "carnival luminosa":          174,
  "carnival spirit":            144,
  "celebrity edge":             197,
  "discovery princess":         284,
  "disney wonder":              213,
  "island princess":            267,
  "koningsdam":                 233,
  "noordam":                    220,
  "norwegian encore":           258,
  "oceania riviera":            376,
  "quantum of the seas":        311,
  "royal princess":             270,
  "serenade of the seas":       301,
  "seven seas explorer":        551,
  "silver moon":                542,
  "silver whisper":             535,
  "viking orion":               426,
  "viking venus":               767,
  "voyager of the seas":        305,
  "zaandam":                    229,
  // Ajouts manuels
  "wonder of the seas":         318,
  "brilliance of the seas":     288,
  "caribbean princess":         261,
  "grand princess":             266,
  "carnival conquest":          139,
  "carnival freedom":           160,
  "carnival glory":             141,
  "carnival pride":             156,
  "carnival splendor":          161,
  "norwegian dawn":             237,
  "norwegian sky":              251,
  "norwegian spirit":         246,
  "norwegian star":             241,
  "norwegian sun":              242,
};

function getCruiseDirectShipId(shipName) {
  if (!shipName) return null;
  const key = shipName.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();
  return CD_SHIPS[key] || null;
}

// Lignes en HOLD : CruiseDirect ne les vend pas → aucun lien de réservation possible.
// On retourne null (le bouton CruiseDirect est alors masqué) plutôt qu'un lien générique
// qui mènerait au MAUVAIS navire. azamara : cf. mémoire project_azamara_hold.
const CD_HELD_LINES = new Set(["azamara"]);

// Deep link CruiseDirect : URL directe si trouvée par Haiku, sinon recherche filtrée
function buildCruiseDirectLink(offer, data) {
  if (CD_HELD_LINES.has(offer.cruise_line)) return null;
  // Only use booking_url if it's a real CruiseDirect reservation URL (not a hallucinated search URL)
  if (offer.booking_url && offer.booking_url.includes('book.cruisedirect.com/swift/cruise/package/')) {
    return CD_CJ_PREFIX + encodeURIComponent(offer.booking_url);
  }
  const filters = [];
  if (offer.departure_date) {
    const [y, m, d] = offer.departure_date.split('-').map(Number);
    let minTs, maxTs;
    if (d >= 1) {
      // Jour EXACT connu (offre réelle) → CruiseDirect isole CE départ précis.
      // Validé multi-scénarios 2026-05-28 (Wonder 3n, MSC World America 7n, Norwegian
      // Prima 7n → 1 seul résultat, la bonne croisière, vs ~4 avec le mois entier).
      minTs = Math.floor(Date.UTC(y, m - 1, d, 0, 0, 0) / 1000);
      maxTs = Math.floor(Date.UTC(y, m - 1, d, 23, 59, 59) / 1000);
    } else {
      // Jour inconnu (ex: post FB « Croisière du Mois » = mois seul) → fenêtre du mois.
      minTs = Math.floor(Date.UTC(y, m - 1, 1) / 1000);
      maxTs = Math.floor(Date.UTC(y, m, 0, 23, 59, 59) / 1000);
    }
    filters.push(`departure_date:(min:${minTs},max:${maxTs})`);
  }
  const regions = (data?.regions || []).filter(r => r !== 'any');
  // Durée : calcul exact depuis les dates si disponibles, sinon fallback sur le choix du funnel
  if (offer.departure_date && offer.return_date) {
    const nights = Math.round((new Date(offer.return_date) - new Date(offer.departure_date)) / 86400000);
    let durId = null;
    if (nights <= 5)       durId = 2;
    else if (nights <= 9)  durId = 3;
    else if (nights <= 14) durId = 4;
    else                   durId = 5;
    if (durId) filters.push(`duration:${durId}`);
  } else if (data?.duration && CD_DURATIONS[data.duration]) {
    filters.push(`duration:${CD_DURATIONS[data.duration]}`);
  }
  const lineId = CD_CRUISELINES[offer.cruise_line];
  if (lineId) filters.push(`cruiseline:${lineId}`);
  const shipId = getCruiseDirectShipId(offer.ship_name);
  if (shipId) {
    filters.push(`ship:${shipId}`);
    // Avec un ship ID précis, ne pas ajouter destination — peut filtrer faussement
    // (ex: Oasis depuis Cape Liberty = "Bahamas" sur CD, pas "Caribbean")
  } else {
    const destId = regions.map(r => CD_DESTINATIONS[r]).find(Boolean);
    if (destId) filters.push(`destination:${destId}`);
  }
  if (!filters.length) return CD_CJ_PREFIX + encodeURIComponent('https://www.cruisedirect.com');
  const searchUrl = 'https://www.cruisedirect.com/search-results?' +
    filters.map((val, i) => `f[${i}]=${val}`).join('&');
  return CD_CJ_PREFIX + encodeURIComponent(searchUrl);
}

// ─── LIENS AFFILIÉS EXPEDIA ──────────────────────────────────────────────────
const EXPEDIA_CAMREF = "1100l5JnZe"; // Partnerize camref (du widget Creator Toolbox)

// Shortlinks statiques pour vols et hôtels (pages de recherche générales)
const EXPEDIA_LINKS = {
  hotels:  "https://expedia.com/affiliate/qjHiWfI",
  flights: "https://expedia.com/affiliate/uQAcotL",
};

// ─── POSTS FACEBOOK PRÊTS-À-COLLER (Croisière/Aventure du Mois) ──────────────
// Compose, à partir des offres CDM générées, 2 posts FB copier-coller avec les
// 3 liens affiliés (CruiseDirect + vol Expedia + hôtel Expedia). Aucun appel API
// — tout vient des champs déjà générés. Cadré « déniché avec CroisièreAI ».
function cdmDateToISO(s) {
  if (!s) return null;
  let m = String(s).match(/(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (m) return `${m[1]}-${m[2]}-${m[3] || "01"}`;
  const MO = { january:1,february:2,march:3,april:4,may:5,june:6,july:7,august:8,september:9,october:10,november:11,december:12,
    janvier:1,février:2,fevrier:2,mars:3,avril:4,mai:5,juin:6,juillet:7,août:8,aout:8,septembre:9,octobre:10,novembre:11,décembre:12,decembre:12 };
  m = String(s).toLowerCase().match(/([a-zà-ÿ]+)\s+(\d{4})/i);
  if (m && MO[m[1]]) return `${m[2]}-${String(MO[m[1]]).padStart(2, "0")}-01`;
  return null;
}

function buildFBPosts(offers) {
  if (!offers?.deal || !offers?.adventure) return null;
  const mk = (o, kind) => {
    const iso = cdmDateToISO(o.departure_date);
    const ret = iso && o.duration_days
      ? new Date(new Date(iso + "T12:00:00").getTime() + o.duration_days * 86400000).toISOString().slice(0, 10)
      : null;
    const cd = buildCruiseDirectLink(
      // On ne connaît que le MOIS pour la Croisière du Mois → départ_date en « YYYY-MM »
      // (sans jour) pour que le lien CruiseDirect filtre sur le mois, pas sur le 1er (=vide).
      { ship_name: o.ship_name, cruise_line: o.cruise_line, departure_date: iso ? iso.slice(0, 7) : null, return_date: ret, departure_port: o.departure_port },
      {}
    );
    const price  = o.price_per_person_cad ? `${Number(o.price_per_person_cad).toLocaleString("fr-CA")} $ CA / personne` : "Prix à confirmer";
    const header = kind === "deal" ? "🚢 LA CROISIÈRE DU MOIS" : "🌍 L'AVENTURE DU MOIS";
    const tag    = kind === "deal" ? "#dealcroisiere" : "#aventure";
    const text =
`${header} — ${o.departure_date}

${o.ship_name} (${prettyLine(o.cruise_line)}) · ${o.duration_days} jours au départ de ${o.departure_port}
${o.itinerary}

💰 À partir de ${price}
${o.wow_factor}

✨ Déniché avec CroisièreAI.ca — notre IA compare les vraies croisières en temps réel, sans artifice ni date inventée.

👉 Réserver la croisière : ${cd}
✈️ Trouver le vol : ${EXPEDIA_LINKS.flights}
🏨 Hôtel la veille du départ : ${EXPEDIA_LINKS.hotels}

#croisiere #voyage #croisiereai ${tag}`;
    return { kind, header, text, cd, flight: EXPEDIA_LINKS.flights, hotel: EXPEDIA_LINKS.hotels };
  };
  return { deal: mk(offers.deal, "deal"), adventure: mk(offers.adventure, "adventure") };
}

// Meilleur lien de réservation croisière disponible
function buildCruiseBookingUrl(offer, data) {
  // CruiseDirect via CJ — partenariat direct, toujours en priorité
  return buildCruiseDirectLink(offer, data);
}

// ─── IMPRESSION SOMMAIRE OFFRE ───────────────────────────────────────────────
// Ouvre une page propre (fond blanc, texte noir, format papier) avec le sommaire
// d'UNE offre puis lance l'impression. Évite d'imprimer la fiche thème foncé.
function printOfferSummary(o, data, isEN) {
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const money = (n) => `${Number(n).toLocaleString(isEN ? "en-CA" : "fr-CA")} $`;
  const infoRow = (label, val) => val ? `<tr><td class="lbl">${esc(label)}</td><td class="val">${esc(val)}</td></tr>` : "";
  const priceRow = (label, n) => (n > 0) ? `<tr><td>${esc(label)}</td><td class="num">${money(n)}</td></tr>` : "";

  const L = isEN ? {
    brand: "Cruise AI", dates: "Dates", itinerary: "Itinerary", port: "Departure port",
    hotel: "Suggested hotel", flight: "Flight", breakdown: "Price breakdown",
    cruise: "Cruise", flights: "Flights", hotelN: "Night-before hotel", taxes: "Taxes & fees", total: "TOTAL",
    estimated: "Estimated prices — confirm the exact price on CruiseDirect before booking.",
    generated: "Generated", footer: "Plan your cruise at cruiseai.ca",
  } : {
    brand: "Croisière AI", dates: "Dates", itinerary: "Itinéraire", port: "Port de départ",
    hotel: "Hôtel suggéré", flight: "Vol", breakdown: "Détail du prix",
    cruise: "Croisière", flights: "Vols", hotelN: "Hôtel la veille", taxes: "Taxes & frais", total: "TOTAL",
    estimated: "Prix estimés — confirmez le prix exact sur CruiseDirect avant de réserver.",
    generated: "Généré le", footer: "Planifiez votre croisière sur croisiereai.ca",
  };

  const today = new Date().toLocaleDateString(isEN ? "en-CA" : "fr-CA", { year: "numeric", month: "long", day: "numeric" });
  const hotelName = o.hotel_option?.hotel_name || "";
  const fo = o.flight_option;
  const flightLine = fo?.airline ? `${fo.airline}${fo.origin_airport && fo.destination_airport ? ` (${fo.origin_airport}→${fo.destination_airport})` : ""}` : "";

  const html = `<!doctype html><html lang="${isEN ? "en" : "fr"}"><head><meta charset="utf-8">
<title>${esc(o.ship_name || L.brand)}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:Georgia,"Times New Roman",serif;color:#1a1a1a;margin:36px;line-height:1.5}
  .brand{font-size:13px;letter-spacing:.14em;text-transform:uppercase;color:#9a7d28;margin-bottom:6px}
  h1{font-size:23px;margin:0 0 3px}
  .sub{color:#555;font-size:14px;margin-bottom:14px}
  .catch{font-style:italic;color:#333;font-size:15px;margin:0 0 18px}
  table{width:100%;border-collapse:collapse;margin-bottom:18px}
  td{padding:7px 0;font-size:14px;border-bottom:1px solid #eee;vertical-align:top}
  td.lbl{color:#777;width:160px}
  td.val{font-weight:bold}
  td.num{text-align:right}
  .sec{font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:#9a7d28;margin:0 0 4px}
  .total td{border-top:2px solid #1a1a1a;border-bottom:none;font-size:18px;font-weight:bold;padding-top:9px}
  .note{font-size:11px;color:#888}
  .footer{margin-top:30px;padding-top:12px;border-top:1px solid #ddd;font-size:12px;color:#666;display:flex;justify-content:space-between}
  @page{margin:18mm}
  @media print{body{margin:0}}
</style></head><body>
  <div class="brand">${esc(L.brand)}</div>
  <h1>${esc(o.ship_name || "")}</h1>
  <div class="sub">${esc([o.ship_class, prettyLine(o.cruise_line)].filter(Boolean).join(" · "))}</div>
  ${o.catchTitle ? `<p class="catch">${esc(o.catchTitle)}</p>` : ""}
  <table>
    ${infoRow(L.dates, o.dateLabel)}
    ${infoRow(L.itinerary, o.itinerary_summary)}
    ${infoRow(L.port, o.port)}
    ${hotelName ? infoRow(L.hotel, hotelName) : ""}
    ${flightLine ? infoRow(L.flight, flightLine) : ""}
  </table>
  <div class="sec">${esc(L.breakdown)}</div>
  <table>
    ${priceRow(L.cruise, o.cruise_cad)}
    ${priceRow(L.flights, o.flight_cad)}
    ${priceRow(L.hotelN, o.hotel_cad)}
    ${priceRow(L.taxes, o.taxes_cad)}
    <tr class="total"><td>${esc(L.total)}</td><td class="num">${o.total_cad > 0 ? money(o.total_cad) + " CAD" : "—"}</td></tr>
  </table>
  <div class="note">${esc(L.estimated)}</div>
  <div class="footer"><span>${esc(L.generated)} ${esc(today)}</span><span>${esc(L.footer)}</span></div>
</body></html>`;

  const w = window.open("", "_blank");
  if (!w) { alert(isEN ? "Please allow pop-ups to print this summary." : "Autorisez les fenêtres contextuelles pour imprimer ce sommaire."); return; }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch (e) {} }, 300);
}


// Deep-link prf.hn vers recherche croisières pré-filtrée (dates + durée + destination)

// Port d'embarquement → aéroport IATA le plus proche (ports de DÉPART seulement).
// Sert à reconstruire un lien vol fiable depuis les données croisière, même si
// l'IA n'a pas rempli flight_option.destination_airport (fréquent hors Caraïbes).
const PORT_TO_IATA = {
  "miami": "MIA", "port canaveral": "MCO", "canaveral": "MCO", "fort lauderdale": "FLL",
  "tampa": "TPA", "new orleans": "MSY", "galveston": "HOU", "new york": "JFK",
  "cape liberty": "EWR", "bayonne": "EWR", "baltimore": "BWI", "boston": "BOS",
  "charleston": "CHS", "jacksonville": "JAX", "norfolk": "ORF", "san francisco": "SFO",
  "los angeles": "LAX", "san pedro": "LAX", "long beach": "LGB", "san diego": "SAN",
  "seattle": "SEA", "vancouver": "YVR", "victoria": "YYJ", "montreal": "YUL",
  "quebec": "YQB", "whittier": "ANC", "seward": "ANC", "anchorage": "ANC",
  "san juan": "SJU", "barcelona": "BCN", "civitavecchia": "FCO", "rome": "FCO",
  "venice": "VCE", "naples": "NAP", "genoa": "GOA", "marseille": "MRS",
  "piraeus": "ATH", "athens": "ATH", "southampton": "LHR", "amsterdam": "AMS",
  "copenhagen": "CPH", "honolulu": "HNL", "sydney": "SYD", "singapore": "SIN",
};
// Clés triées par longueur décroissante : "cape liberty" testé avant "new york", etc.
const PORT_IATA_KEYS = Object.keys(PORT_TO_IATA).sort((a, b) => b.length - a.length);
function portToIata(portStr) {
  const s = (portStr || "").toLowerCase();
  if (!s) return null;
  for (const k of PORT_IATA_KEYS) if (s.includes(k)) return PORT_TO_IATA[k];
  return null;
}

// Nombre de voyageurs à facturer/afficher. Source de vérité unique : un GROUPE (ex: 4)
// est rangé dans data.group_size ; une FAMILLE dans family_detail.adults ; couple/solo =
// défauts. ⚠️ Bug 2026-05-31 : l'affichage du prix /pers ET les liens vol/hôtel ignoraient
// group_size → ils divisaient/réservaient pour 2 au lieu de 4 (« ≈ 4 543 $/pers · 2 pers »
// sur un groupe de 4). Ordre aligné sur le worker (groupSizeNum || fd.adults || défauts).
export function paxCount(data) {
  const fd = data?.family_detail;
  const gs = data?.group_size ? parseInt(data.group_size, 10) : 0;
  return (gs > 0 ? gs : 0) || fd?.adults || (data?.group === "couple" ? 2 : data?.group === "solo" ? 1 : 2);
}

// Composition RÉELLE du groupe pour l'affichage : le total couvre tout le monde, pas
// seulement les adultes. Distingue bébés (<2 ans), enfants (2-12/ado) à partir des âges.
export function partyComposition(data) {
  const fd   = data?.family_detail;
  const kids = (data?.group === "family" && Array.isArray(fd?.children)) ? fd.children : [];
  const babies   = kids.filter(c => (c?.age ?? 99) < 2).length;
  const children = kids.length - babies;
  return { adults: paxCount(data), children, babies, kids: kids.length };
}

// Libellé « 2 adultes + 1 enfant + 1 bébé » (FR/EN). Réutilisé pour clarifier que le
// prix total couvre toute la famille. Vide de sens si pas d'enfants → appelant gère.
export function partyLabel(data, isEN) {
  const { adults, children, babies } = partyComposition(data);
  const seg = [`${adults} ${isEN ? (adults > 1 ? "adults" : "adult") : (adults > 1 ? "adultes" : "adulte")}`];
  if (children > 0) seg.push(`${children} ${isEN ? (children > 1 ? "children" : "child") : (children > 1 ? "enfants" : "enfant")}`);
  if (babies   > 0) seg.push(`${babies} ${isEN ? (babies > 1 ? "infants" : "infant") : (babies > 1 ? "bébés" : "bébé")}`);
  return seg.join(" + ");
}

// Nombre de SIÈGES d'avion à facturer : adultes + enfants de 2 ans et + (siège
// obligatoire) + bébés <2 ans SEULEMENT s'ils prennent un siège réservé (sinon genoux).
export function flightSeats(data) {
  const fd   = data?.family_detail;
  const kids = (data?.group === "family" && Array.isArray(fd?.children)) ? fd.children : [];
  const seatKids  = kids.filter(c => (c?.age ?? 99) >= 2).length;
  const babySeats = data?.infant_flight_seat === "seat" ? kids.filter(c => (c?.age ?? 99) < 2).length : 0;
  return paxCount(data) + seatKids + babySeats;
}

// Deep-link vol pré-rempli (origine, destination, dates, passagers).
// Origine = aéroport du funnel (fiable) ; destination = port d'embarquement de la
// croisière trouvée (fiable). On ne dépend de flight_option de l'IA qu'en dernier
// recours → le lien tombe toujours sur le bon trajet + les bonnes dates.
function flightLegs(fo, offer, data) {
  let orig = resolveOriginCode(data);
  if (!/^[A-Z]{3}$/.test(orig)) orig = ((fo?.origin_airport) || "").toUpperCase().trim();
  let dst = portToIata(offer?.port) || ((fo?.destination_airport) || "").toUpperCase().trim();
  orig = orig.replace(/\bSFB\b/, "MCO");
  dst  = dst.replace(/\bSFB\b/, "MCO");
  if (!/^[A-Z]{3}$/.test(orig) || !/^[A-Z]{3}$/.test(dst) || !offer?.departure_date) return null;
  const adults = paxCount(data);
  // Aller : veille de la croisière si nuit d'hôtel réservée, sinon jour J
  let depIso = offer.departure_date;
  if (data?.overnight === "yes") {
    const d = new Date(depIso + "T12:00:00");
    d.setDate(d.getDate() - 1);
    depIso = d.toISOString().slice(0, 10);
  }
  // Retour : date de fin réelle de la croisière (offer.return_date prioritaire),
  // sinon date de recherche si plausible, sinon J+7
  let retIso = null;
  if (offer.return_date && offer.return_date > offer.departure_date) {
    const diff = (new Date(offer.return_date) - new Date(offer.departure_date)) / 86400000;
    if (diff >= 2 && diff <= 30) retIso = offer.return_date;
  }
  if (!retIso) {
    const searchTo = data?.dates?.to;
    if (searchTo && searchTo > offer.departure_date) {
      const diff = (new Date(searchTo) - new Date(offer.departure_date)) / 86400000;
      if (diff >= 3 && diff <= 30) retIso = searchTo;
    }
  }
  if (!retIso) {
    const d = new Date(offer.departure_date + "T12:00:00");
    d.setDate(d.getDate() + 7);
    retIso = d.toISOString().slice(0, 10);
  }
  return { orig, dst, depIso, retIso, adults };
}

// Google Flights — format `q=` documenté et stable (ne casse pas comme le path Kayak).
function buildGoogleFlightsUrl(fo, offer, data) {
  const l = flightLegs(fo, offer, data);
  if (!l) return null;
  const q = `Flights from ${l.orig} to ${l.dst} on ${l.depIso} returning ${l.retIso}`;
  return `https://www.google.com/travel/flights?q=${encodeURIComponent(q)}`;
}

// Kayak — format PATH /YUL-MIA/YYYY-MM-DD/YYYY-MM-DD/Nadults.
// ⚠️ Ré-validé par test-clic réel 2026-05-17 (Simon) : ce format ouvre Kayak
// avec trajet + dates pré-remplis ET résultats affichés — c'est CE qu'on veut.
// (Il avait été marqué "cassé→errorOccurred" ~2026-05 ; Kayak l'a depuis réparé.)
// → PRINCIPAL dans buildFlightUrl. Si Kayak le recasse : test-clic des replis.
function buildKayakPathUrl(fo, offer, data) {
  const l = flightLegs(fo, offer, data);
  if (!l) return null;
  return `https://www.kayak.com/flights/${l.orig}-${l.dst}/${l.depIso}/${l.retIso}/${l.adults}adults`;
}

// Kayak — format query (repli). Ne verrouille pas toujours les dates aussi
// proprement que le format path, mais reste un repli valide si le path casse.
function buildKayakUrl(fo, offer, data) {
  const l = flightLegs(fo, offer, data);
  if (!l) return null;
  const toUS = iso => { const [y, m, d] = iso.split("-"); return `${m}/${d}/${y}`; };
  const p = new URLSearchParams({
    adults: String(l.adults), oneway: "n", action: "doflights",
    depart_date: toUS(l.depIso), return_date: toUS(l.retIso),
    origin: l.orig, destination: l.dst,
  });
  return `https://www.kayak.com/flights?${p.toString()}`;
}

// Lien vol robuste — UN SEUL endroit. Principal = Kayak path (re-validé par
// test-clic 2026-05-21 : YUL→MCO 2026-08-15→22 affiche Air Canada/Delta/American
// avec dates verrouillées et résultats complets). Repli → Google Flights (q=, ok
// mais souvent moins propre côté résultats) → Kayak query → Expedia.
// Note : pour dates >12 mois, AUCUN agrégateur ne fonctionne (les compagnies
// n'ouvrent pas la vente >330j avant départ). Le repli est géré ailleurs dans
// FlightBlock (à venir : notif email quand la fenêtre s'ouvre).
// Aviasales (Travelpayouts) — deep link AFFILIÉ pré-rempli. marker = identifiant
// d'affilié (commission sur les vols réservés). sub_id = suivi par offre (quelle
// croisière génère le clic). Format /search/{ORIG}{DDMM}{DEST}{DDMM}{PAX}.
const AVIASALES_MARKER = "725454";
function buildAviasalesUrl(fo, offer, data) {
  const l = flightLegs(fo, offer, data);
  if (!l) return null;
  const ddmm = iso => { const [, m, d] = iso.split("-"); return `${d}${m}`; };
  const route = `${l.orig}${ddmm(l.depIso)}${l.dst}${ddmm(l.retIso)}${l.adults}`;
  const sub = `${(offer?.ship_name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40)}${offer?.departure_date ? `-${offer.departure_date}` : ""}`;
  const q = `marker=${AVIASALES_MARKER}` + (sub ? `&sub_id=${encodeURIComponent(sub)}` : "");
  return `https://www.aviasales.com/search/${route}?${q}`;
}

// Lien vol — PRINCIPAL = Aviasales (affilié, monétisé). Replis non monétisés
// (Kayak/Google) si flightLegs nul, puis Expedia (affilié) en dernier recours.
function buildFlightUrl(fo, offer, data) {
  return buildAviasalesUrl(fo, offer, data)
      || buildKayakPathUrl(fo, offer, data)
      || buildGoogleFlightsUrl(fo, offer, data)
      || buildKayakUrl(fo, offer, data)
      || buildExpediaFlightUrl(fo, data);
}

function buildExpediaFlightUrl(fo, data) {
  if (!fo?.origin_airport || !fo?.destination_airport) return EXPEDIA_LINKS.flights;
  const toExpDate = iso => { if (!iso) return null; const [y,m,d] = iso.split("-"); return `${d}/${m}/${y}`; };
  // Si hôtel la veille : vol départ J-1 (pas le jour de l'embarquement)
  let outboundDate = data?.dates?.from;
  if (data?.overnight === "yes" && outboundDate) {
    const d = new Date(outboundDate + "T12:00:00");
    d.setDate(d.getDate() - 1);
    outboundDate = d.toISOString().slice(0, 10);
  }
  const depDate = toExpDate(outboundDate);
  const retDate = toExpDate(data?.dates?.to);
  if (!depDate || !retDate) return EXPEDIA_LINKS.flights;
  const adults = paxCount(data);
  const orig = fo.origin_airport;
  // Port Canaveral → MCO (Orlando International) pour les vols internationaux depuis le Canada
  const dst  = fo.destination_airport === "SFB" ? "MCO" : fo.destination_airport;
  const p = new URLSearchParams({
    leg1: `from:${orig},to:${dst},departure:${depDate}TANYT,fromType:U,toType:U`,
    leg2: `from:${dst},to:${orig},departure:${retDate}TANYT,fromType:U,toType:U`,
    mode: "search",
    options: "carrier:,cabinclass:,maxhops:1,nopenalty:N",
    pageId: "0",
    passengers: `adults:${adults},children:0,infantinlap:N`,
    trip: "roundtrip",
  });
  const url = encodeURIComponent(`https://www.expedia.ca/Flights-Search?${p.toString()}`);
  return `https://prf.hn/click/camref:${EXPEDIA_CAMREF}/destination:${url}`;
}

// Mapping port de croisière → ville Expedia pour la recherche hôtel
const PORT_EXPEDIA_CITY = {
  // ── USA ────────────────────────────────────────────────────────────
  "miami":           "Miami",
  "port canaveral":  "Cape Canaveral",
  "fort lauderdale": "Fort Lauderdale",
  "tampa":           "Tampa",
  "new orleans":     "New Orleans",
  "galveston":       "Galveston",
  "new york":        "New York City",
  "cape liberty":    "Bayonne, New Jersey",
  "bayonne":         "Bayonne, New Jersey",
  "baltimore":       "Baltimore",
  "boston":          "Boston",
  "charleston":      "Charleston",
  "norfolk":         "Norfolk",
  "jacksonville":    "Jacksonville",
  "san francisco":   "San Francisco",
  "los angeles":     "Los Angeles",
  "san pedro":       "Los Angeles",
  "long beach":      "Long Beach",
  "san diego":       "San Diego",
  "seattle":         "Seattle",
  // ── ALASKA ─────────────────────────────────────────────────────────
  "juneau":          "Juneau, Alaska, United States of America",
  "ketchikan":       "Ketchikan, Alaska, United States of America",
  "skagway":         "Skagway, Alaska, United States of America",
  "sitka":           "Sitka, Alaska, United States of America",
  "whittier":        "Anchorage, Alaska, United States of America",
  "seward":          "Seward, Alaska, United States of America",
  "anchorage":       "Anchorage, Alaska, United States of America",
  // ── CANADA ─────────────────────────────────────────────────────────
  "vancouver":       "Vancouver, British Columbia, Canada",
  "victoria":        "Victoria, British Columbia, Canada",
  "montreal":        "Montreal, Quebec, Canada",
  "quebec":          "Quebec City, Quebec, Canada",
  // ── CARAÏBES / MEXIQUE ─────────────────────────────────────────────
  "san juan":        "San Juan, Puerto Rico",
  "nassau":          "Nassau, Bahamas",
  "freeport":        "Freeport, Bahamas",
  "st. thomas":      "Charlotte Amalie, US Virgin Islands",
  "st thomas":       "Charlotte Amalie, US Virgin Islands",
  "st. maarten":     "Philipsburg, Sint Maarten",
  "st maarten":      "Philipsburg, Sint Maarten",
  "ocho rios":       "Ocho Rios, Jamaica",
  "montego bay":     "Montego Bay, Jamaica",
  "falmouth":        "Falmouth, Jamaica",
  "grand cayman":    "George Town, Cayman Islands",
  "cozumel":         "Cozumel, Mexico",
  "costa maya":      "Mahahual, Mexico",
  "roatan":          "Roatan, Honduras",
  "belize city":     "Belize City, Belize",
  "aruba":           "Oranjestad, Aruba",
  "curacao":         "Willemstad, Curacao",
  "bridgetown":      "Bridgetown, Barbados",
  "barbados":        "Bridgetown, Barbados",
  // ── EUROPE MÉDITERRANÉE ────────────────────────────────────────────
  "barcelona":       "Barcelona, Spain",
  "palma":           "Palma de Mallorca, Spain",
  "malaga":          "Malaga, Spain",
  "civitavecchia":   "Rome, Italy",
  "rome":            "Rome, Italy",
  "naples":          "Naples, Italy",
  "venice":          "Venice, Italy",
  "messina":         "Messina, Italy",
  "genoa":           "Genoa, Italy",
  "marseille":       "Marseille, France",
  "nice":            "Nice, France",
  "cannes":          "Cannes, France",
  "monaco":          "Monte Carlo, Monaco",
  "piraeus":         "Athens, Greece",
  "athens":          "Athens, Greece",
  "thessaloniki":    "Thessaloniki, Greece",
  "rhodes":          "Rhodes, Greece",
  "heraklion":       "Heraklion, Greece",
  "dubrovnik":       "Dubrovnik, Croatia",
  "split":           "Split, Croatia",
  "kotor":           "Kotor, Montenegro",
  "valletta":        "Valletta, Malta",
  "istanbul":        "Istanbul, Turkey",
  "bodrum":          "Bodrum, Turkey",
  "lisbon":          "Lisbon, Portugal",
  "porto":           "Porto, Portugal",
  // ── EUROPE NORD ────────────────────────────────────────────────────
  "southampton":     "Southampton, United Kingdom",
  "dover":           "Dover, United Kingdom",
  "london":          "London, United Kingdom",
  "dublin":          "Dublin, Ireland",
  "cobh":            "Cork, Ireland",
  "amsterdam":       "Amsterdam, Netherlands",
  "hamburg":         "Hamburg, Germany",
  "copenhagen":      "Copenhagen, Denmark",
  "stockholm":       "Stockholm, Sweden",
  "helsinki":        "Helsinki, Finland",
  "tallinn":         "Tallinn, Estonia",
  "riga":            "Riga, Latvia",
  "bergen":          "Bergen, Norway",
  "stavanger":       "Stavanger, Norway",
  "oslo":            "Oslo, Norway",
  "reykjavik":       "Reykjavik, Iceland",
  // ── ASIE ───────────────────────────────────────────────────────────
  "singapore":       "Singapore",
  "hong kong":       "Hong Kong",
  "yokohama":        "Yokohama, Japan",
  "tokyo":           "Tokyo, Japan",
  "osaka":           "Osaka, Japan",
  "kobe":            "Kobe, Japan",
  "nagasaki":        "Nagasaki, Japan",
  "shanghai":        "Shanghai, China",
  "tianjin":         "Tianjin, China",
  "beijing":         "Beijing, China",
  "busan":           "Busan, South Korea",
  "incheon":         "Seoul, South Korea",
  "laem chabang":    "Bangkok, Thailand",
  "bangkok":         "Bangkok, Thailand",
  "ko samui":        "Ko Samui, Thailand",
  "phuket":          "Phuket, Thailand",
  "port klang":      "Kuala Lumpur, Malaysia",
  "kuala lumpur":    "Kuala Lumpur, Malaysia",
  "benoa":           "Bali, Indonesia",
  "bali":            "Bali, Indonesia",
  "ho chi minh":     "Ho Chi Minh City, Vietnam",
  "phu my":          "Ho Chi Minh City, Vietnam",
  "da nang":         "Da Nang, Vietnam",
  "chan may":        "Da Nang, Vietnam",
  "haiphong":        "Hanoi, Vietnam",
  "dubai":           "Dubai, United Arab Emirates",
  "abu dhabi":       "Abu Dhabi, United Arab Emirates",
  "mumbai":          "Mumbai, India",
  // ── AUSTRALIE / PACIFIQUE ──────────────────────────────────────────
  "sydney":          "Sydney, Australia",
  "auckland":        "Auckland, New Zealand",
  "honolulu":        "Honolulu, Hawaii, United States of America",
  "kahului":         "Maui, Hawaii, United States of America",
  "hilo":            "Hilo, Hawaii, United States of America",
  // ── AMÉRIQUE DU SUD ────────────────────────────────────────────────
  "buenos aires":    "Buenos Aires, Argentina",
  "rio de janeiro":  "Rio de Janeiro, Brazil",
  "santos":          "Sao Paulo, Brazil",
  "valparaiso":      "Valparaiso, Chile",
  "callao":          "Lima, Peru",
  "cartagena":       "Cartagena, Colombia",
};

// Deep-link Expedia Hôtel — URL directe si Claude l'a trouvée, sinon search par ville
function buildExpediaHotelUrl(offer, data) {
  const ho = offer.hotel_option;
  const dep = offer.departure_date; // YYYY-MM-DD
  if (!dep) return EXPEDIA_LINKS.hotels;

  // Dates : checkin = veille du départ, checkout = jour départ
  const checkinDate  = new Date(dep + "T12:00:00");
  checkinDate.setDate(checkinDate.getDate() - 1);
  const checkin  = checkinDate.toISOString().slice(0, 10);
  const checkout = dep;
  const fd       = data?.family_detail;
  const adults   = paxCount(data);
  const children = fd?.children?.length || 0;
  const roomStr  = children > 0 ? `a${adults}e${children}` : `a${adults}`;

  // URL directe hôtel trouvée par Claude → extraire la base + ajouter dates
  if (ho?.source_url && (ho.source_url.includes("expedia.ca") || ho.source_url.includes("expedia.com")) && ho.source_url.includes(".Hotel-Information")) {
    const base = ho.source_url.split("?")[0];
    const url  = `${base}?chkin=${checkin}&chkout=${checkout}&rm1=${roomStr}`;
    return `https://prf.hn/click/camref:${EXPEDIA_CAMREF}/destination:${encodeURIComponent(url)}`;
  }

  // Fallback : si on a le nom de l'hôtel, chercher par nom simplifié (brand + ville, max 4 mots)
  // Trop spécifique = risque de rater ("Hyatt Place Fort Lauderdale Airport - South & Cruise Port"
  // → "Hyatt Place Fort Lauderdale"). On coupe après le tiret, puis on garde 4 mots.
  const portKey    = (offer.port || "miami").replace(/,.*/, "").trim().toLowerCase();
  const cityName   = PORT_EXPEDIA_CITY[portKey] || (offer.port ? offer.port.split(",")[0].trim() : "Miami");
  const p = new URLSearchParams({
    destination: cityName,
    d1: checkin, startDate: checkin,
    d2: checkout, endDate: checkout,
    adults: String(adults),
    ...(children > 0 && { children: String(children) }),
    rooms: "1",
    sort: "RECOMMENDED",
    flexibility: "0_DAY",
  });
  const url = encodeURIComponent(`https://www.expedia.ca/Hotel-Search?${p.toString()}`);
  return `https://prf.hn/click/camref:${EXPEDIA_CAMREF}/destination:${url}`;
}

// Construit un lien Expedia pour un hôtel curé.
// Si hotel.expedia_id connu → deep link direct avec dates (page hôtel exact).
// Sinon → recherche par nom (fallback).
function buildExpediaCuratedHotelUrl(hotel, offer, data) {
  const dep = offer.departure_date;
  if (!dep) return EXPEDIA_LINKS.hotels;
  const checkinDate = new Date(dep + "T12:00:00");
  checkinDate.setDate(checkinDate.getDate() - 1);
  const checkin  = checkinDate.toISOString().slice(0, 10);
  const checkout = dep;
  const fd       = data?.family_detail;
  const adults   = paxCount(data);
  const children = fd?.children?.length || 0;
  const roomStr  = children > 0 ? `a${adults}e${children}` : `a${adults}`;

  if (hotel.expedia_id) {
    const url = `https://www.expedia.ca/h${hotel.expedia_id}.Hotel-Information?chkin=${checkin}&chkout=${checkout}&rm1=${roomStr}`;
    return `https://prf.hn/click/camref:${EXPEDIA_CAMREF}/destination:${encodeURIComponent(url)}`;
  }

  const p = new URLSearchParams({
    destination: hotel.name,
    d1: checkin, startDate: checkin,
    d2: checkout, endDate: checkout,
    adults: String(adults),
    ...(children > 0 && { children: String(children) }),
    rooms: "1",
    sort: "RECOMMENDED",
    flexibility: "0_DAY",
  });
  const url = encodeURIComponent(`https://www.expedia.ca/Hotel-Search?${p.toString()}`);
  return `https://prf.hn/click/camref:${EXPEDIA_CAMREF}/destination:${url}`;
}

// ─── WORKER RESPONSE READER ──────────────────────────────────────────────────
// Gère deux types de réponses du worker :
//   • application/json  → cache HIT ou erreur avant stream (réponse classique)
//   • text/event-stream → cache MISS — stream SSE; attend l'événement "result"
async function readWorkerResult(res) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e   = err?.error || {};
    const det = e.detail ? ` — ${e.detail}` : "";
    throw new Error(e.code ? `[${e.code}] ${e.message}${det}` : (e.message || `HTTP ${res.status}`));
  }

  if (!(res.headers.get("Content-Type") || "").includes("text/event-stream")) {
    return res.json();
  }

  // Lecture du stream SSE — cherche l'événement "result"
  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let   buffer  = "";
  let   currentEvent = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines  = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith("data: ") && currentEvent === "result") {
          const json = JSON.parse(line.slice(6).trim());
          if (json?.error) {
            const e   = json.error;
            const det = e.detail ? ` — ${e.detail}` : "";
            throw new Error(e.code ? `[${e.code}] ${e.message}${det}` : (e.message || "Erreur inconnue"));
          }
          return json;
        }
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }

  throw new Error("[901] Réponse incomplète du serveur.");
}


// ─── RESSERREMENT — couverture des paramètres ───────────────────────────────────
// Évalue si une recherche tombe dans ce qu'on couvre BIEN. Partagé funnel + agent
// (les deux convergent vers le même objet `data`/params → un seul garde-fou, 2 portes).
// 🔴 "out" = hors de notre focus : court (3-5n) et xlong (15n+). Décision produit :
// notre audience (QC/Canada) doit VOYAGER pour atteindre un port → le <7n (et >14n)
// ne lui sert pas (ça, c'est pour les gens habitant tout près d'un port). On propose
// d'élargir vers le 7n+ AVANT de brûler une recherche. (Le 🟡 « mince » — mois lointain
// /sous-région creuse — est déjà géré en aval par le garde-fou honnêteté.)
export function assessCoverage(data) {
  const d = data?.duration;
  if (d === "short") return { level: "out", suggest: "week", reason: "short" };
  if (d === "xlong") return { level: "out", suggest: "long",  reason: "xlong" };
  return { level: "ok" };
}

// ─── GARDE-FOU « DATES IMPOSSIBLES » ─────────────────────────────────────────
// Une fenêtre de départ AUJOURD'HUI/PASSÉE ou d'un seul jour (ex: from=to=aujourd'hui,
// vu via un drapeau thin 2026-06-04) ne peut RIEN retourner de réservable (on ne book
// pas une croisière qui lève l'ancre le jour même). Plutôt que d'honorer un jour
// impossible → on élargit vers les meilleurs départs À VENIR (philosophie « jamais
// vide » : présenter le plus proche + expliquer). Agnostique à la source (funnel,
// agent, édition manuelle). N'agit QUE sur une vraie date exacte (from) ; les modes
// mois / « meilleure offre » / sans date ne sont jamais touchés.
export function broadenImpossibleDates(data) {
  try {
    const d = data?.dates;
    if (!d || !d.from) return data;                       // pas de date exacte → rien à corriger
    const todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD (comparaison lexicale ISO)
    const impossible = d.from <= todayStr || (d.to && d.to === d.from); // passé/aujourd'hui OU 1 seul jour
    if (!impossible) return data;
    const { dates, when, ...rest } = data;                // on retire la date impossible
    return { ...rest, when: "anytime", _dateBroadened: true }; // → meilleurs départs à venir
  } catch { return data; }
}


// ─── AD BANNER ─────────────────────────────────────────────────────────────────
function AdBanner({ slot, style = {} }) {
  const ref = useRef(null);
  const ready = !!ADSENSE_CLIENT && !!slot;

  useEffect(() => {
    if (!ready || !ref.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch { /* AdSense pas encore chargé */ }
  }, [ready]);

  if (!ready) return null;

  return (
    <div style={style}>
      <ins
        ref={ref}
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}

// ─── CRUISE DATA ───────────────────────────────────────────────────────────────

const SHIPS_BY_COMPANY = {
  royal_caribbean: ["Icon of the Seas", "Star of the Seas", "Wonder of the Seas", "Oasis of the Seas", "Symphony of the Seas", "Navigator of the Seas", "Quantum of the Seas"],
  norwegian:       ["Norwegian Prima", "Norwegian Viva", "Norwegian Escape", "Norwegian Getaway", "Norwegian Bliss", "Norwegian Joy"],
  carnival:        ["Carnival Celebration", "Carnival Jubilee", "Carnival Mardi Gras", "Carnival Venezia", "Carnival Vista"],
  celebrity:       ["Celebrity Beyond", "Celebrity Edge", "Celebrity Ascent", "Celebrity Apex"],
  princess:        ["Sun Princess", "Sphere Princess", "Enchanted Princess", "Discovery Princess"],
  msc:             ["MSC Seashore", "MSC Seascape", "MSC World Europa", "MSC Magnifica"],
  holland_america: ["Rotterdam", "Nieuw Statendam", "Koningsdam", "Zuiderdam"],
  virgin:          ["Scarlet Lady", "Valiant Lady", "Resilient Lady"],
};

// Classes de navires par compagnie (pour sélection ciblée)
export const SHIP_CLASSES_BY_COMPANY = {
  royal_caribbean: [
    { v: "icon_class",    l: "Icon Class",    ships: "Icon of the Seas · Star of the Seas",    pax: "7 600 pass.", s: "Le plus grand du monde · 40 toboggans, 7 quartiers thématiques, 20+ restos, Perfect Day inclus", icon: "🏆" },
    { v: "oasis_class",   l: "Oasis Class",   ships: "Wonder · Symphony · Harmony · Oasis",    pax: "5 400 pass.", s: "Central Park, Boardwalk, AquaTheater · Classique RCL avec tonnes d'activités", icon: "🌴" },
    { v: "quantum_class", l: "Quantum Class", ships: "Quantum · Anthem · Ovation · Odyssey",    pax: "4 200 pass.", s: "NorthStar (capsule panoramique), bumper cars, iFly skydiving, ambiance high-tech", icon: "🚀" },
    { v: "voyager_class", l: "Voyager Class", ships: "Navigator · Adventure · Explorer",       pax: "3 100 pass.", s: "Classique RCL, piste de patinage, mur d'escalade, bon rapport qualité-prix", icon: "⚓" },
  ],
  norwegian: [
    { v: "prima_class",     l: "Prima Class",    ships: "Norwegian Prima · Norwegian Viva",    pax: "3 100 pass.", s: "Design premium, espaces ouverts, moins achalandé · idéal si vous fuyez les foules", icon: "⭐" },
    { v: "breakaway_class", l: "Breakaway Plus", ships: "Escape · Bliss · Joy · Getaway",      pax: "4 000 pass.", s: "Waterfront promenade, 25+ restaurants, The Bern, ambiance festive", icon: "🎊" },
  ],
  carnival: [
    { v: "excel_class", l: "Excel Class", ships: "Mardi Gras · Celebration · Jubilee",         pax: "5 200 pass.", s: "BOLT – seul rollercoaster en mer, zone Excel, 20+ restos, meilleur rapport animé", icon: "🎡" },
    { v: "vista_class", l: "Vista Class", ships: "Vista · Horizon · Panorama · Radiance",      pax: "3 900 pass.", s: "SkyRide, plage privée, excellent prix familial, très populaire Caraïbes", icon: "🌅" },
  ],
  celebrity: [
    { v: "edge_class", l: "Edge Class", ships: "Beyond · Ascent · Edge · Apex",                pax: "2 900 pass.", s: "Design haut de gamme, Eden (3 niveaux), Magic Carpet, cuisine gastronomique · clientèle adulte", icon: "✨" },
  ],
  princess: [
    { v: "sphere_class", l: "Sphere Class", ships: "Sun Princess · Sphere Princess",           pax: "4 300 pass.", s: "Tout neuf, Piazza centrale, 30+ restaurants, technologie MedallionClass, idéal couple", icon: "🌟" },
    { v: "royal_class",  l: "Royal Class",  ships: "Enchanted · Discovery · Majestic",         pax: "3 600 pass.", s: "Élégant et familial, Sky Suites, Theatre Under the Stars, bonne valeur", icon: "👸" },
  ],
  msc: [
    { v: "world_class",   l: "World Class",   ships: "MSC World Europa",                       pax: "6 700 pass.", s: "Gigantesque, toboggan transparent, nombreux spectacles · prix très compétitif", icon: "🌍" },
    { v: "seashore_class",l: "Seashore Class",ships: "MSC Seashore · MSC Seascape",            pax: "5 600 pass.", s: "Plages intégrées, passerelle Marine, excellente valeur Caraïbes / Méditerranée", icon: "🏖️" },
  ],
  holland_america: [
    { v: "pinnacle_class", l: "Pinnacle Class", ships: "Rotterdam · Nieuw Statendam · Koningsdam", pax: "2 650 pass.", s: "Élégance classique, music live partout, cuisine gastronomique, clientèle 40+ calme", icon: "🎼" },
  ],
  virgin: [
    { v: "scarlet_class", l: "Scarlet Class", ships: "Scarlet Lady · Valiant Lady · Resilient Lady", pax: "2 770 pass.", s: "Adultes seulement (18+), design audacieux, toutes boissons incluses, ambiance rock", icon: "🔥" },
  ],
};



// ─── DEMO OFFER ────────────────────────────────────────────────────────────────
function generateDemoOffer(data) {
  const fd      = data.family_detail;
  const adults  = fd?.adults || 2;
  const children = fd?.children || [];
  const budget  = data.budget || 10000;
  const target  = data.cruise_target || {};
  const pax     = adults + children.length;
  const bpp     = Math.round(budget / pax);
  const depDate = data.dates?.from || "2026-03-15";
  const retDate = data.dates?.to   || "2026-03-22";

  return {
    ship_name:     target.ship_name || "Icon of the Seas",
    ship_class:    "Icon Class",
    cruise_line:   target.cruise_line || "Royal Caribbean",
    ship_image_url: getShipImg(target.ship_name || "icon"),
    catchTitle:    `7 nuits à bord de l'${target.ship_name || "Icon of the Seas"} — Perfect Day at CocoCay`,
    dateLabel:     `${fmtDate(depDate)} → ${fmtDate(retDate)}`,
    port:          "Miami, FL",
    itinerary_summary: "Miami → Perfect Day at CocoCay → Nassau → Miami",
    destinations: [{ name: "Perfect Day at CocoCay" }, { name: "Nassau, Bahamas" }],
    cruise_cad:   Math.round(bpp * 0.55) * pax,
    flight_cad:   adults * 690 + children.length * 450,
    hotel_cad:    data.overnight === "yes" ? 290 : 0,
    taxes_cad:    Math.round(pax * 180),
    total_cad:    Math.round(bpp * 0.55) * pax + adults * 690 + (data.overnight === "yes" ? 290 : 0) + pax * 180,
    price_per_person: Math.round(bpp * 0.55 + 690 + 180),
    booking_url:  "https://www.royalcaribbean.com",
    flight_url:   "https://www.aircanada.com",
    flight_option: data.departure === "drive_ny" ? null : {
      origin_airport:      resolveOriginCode(data),
      destination_airport: "MIA",
      airline:             "Air Canada",
      stops_out:           0,
      stops_return:        0,
      depart_out:          "07:15",
      arrive_out:          "11:40",
      depart_return:       "14:30",
      price_cad:           adults * 690 + children.length * 450,
      source_url:          "https://www.aircanada.com",
      alternative: {
        airline:   "United (via Newark)",
        stops_out: 1,
        price_cad: Math.max(0, adults * 690 + children.length * 450 - 140),
        trade_off: "Avec 1 escale à EWR, -140$ mais +4h de voyage",
      },
    },
    source:       "Royal Caribbean + Air Canada (DÉMO)",
    hotel_option: data.overnight === "yes" ? {
      hotel_name:      "Marriott Miami Biscayne Bay",
      hotel_stars:     4,
      price_cad:       290,
      airport_shuttle: true,
      port_shuttle:    true,
      address:         "1633 N Bayshore Dr, Miami, FL",
      source_url:      "https://www.booking.com/hotel/us/miami-marriott-biscayne-bay.html",
      alternative: {
        hotel_name:      "Holiday Inn Port of Miami",
        hotel_stars:     3,
        price_cad:       175,
        airport_shuttle: true,
        port_shuttle:    false,
        trade_off:       "Sans navette port, -115$/nuit — navette aéroport seulement",
      },
    } : null,
    kids_promo:   children.length > 0 ? "Enfants 2-12 ans naviguent gratuitement" : null,
    score:        9,
    price_level:  "great",
    price_vs_avg: "18% moins cher que la haute saison",
    cabins: [
      { type: "Balcon Océan", price_pp: bpp },
      { type: "Intérieure",   price_pp: Math.round(bpp * 0.55) },
    ],
    last_checked: new Date().toISOString(),
    analysis:     "DÉMO — Classée #1 car meilleur rapport qualité/prix : prix de croisière dans la moyenne MAIS vol Air Canada direct depuis YUL à 690$/adulte (vs ~830$ pour un vol via escale). Total optimisé pour votre profil.",
  };
}

// ─── LISTE PORTS DE DÉPART ────────────────────────────────────────────────────

const DEPARTURE_PORT_LIST = [
  { region: "Canada",         ports: [{ v: "Montréal, QC", l: "Montréal" }, { v: "Québec, QC", l: "Québec" }, { v: "Vancouver, BC", l: "Vancouver" }] },
  { region: "Nord-Est USA",   ports: [{ v: "New York, NY", l: "New York" }, { v: "Cape Liberty, NJ", l: "Cape Liberty (NJ)" }, { v: "Boston, MA", l: "Boston" }, { v: "Baltimore, MD", l: "Baltimore" }] },
  { region: "Floride",        ports: [{ v: "Miami, FL", l: "Miami" }, { v: "Fort Lauderdale, FL", l: "Fort Lauderdale" }, { v: "Port Canaveral, FL", l: "Port Canaveral" }, { v: "Tampa, FL", l: "Tampa" }] },
  { region: "Golfe",          ports: [{ v: "New Orleans, LA", l: "New Orleans" }, { v: "Galveston, TX", l: "Galveston" }] },
  { region: "Côte Pacifique", ports: [{ v: "Seattle, WA", l: "Seattle" }, { v: "Los Angeles, CA", l: "Los Angeles" }, { v: "San Diego, CA", l: "San Diego" }] },
  { region: "Caraïbes",       ports: [{ v: "San Juan, PR", l: "San Juan" }] },
];

// ─── FUNNEL  ──────────────────────────────────────────────────────────────
const STEPS = [
  // 1. Experience level
  {
    id: "experience", type: "cards",
    q: "Quelle est votre expérience des croisières ?",
    sub: "Cela guide notre approche de recherche.",
    opts: [
      { v: "first",  icon: "🌟", l: "Première croisière",   s: "Je n'ai jamais navigué" },
      { v: "some",   icon: "⚓", l: "Quelques croisières",   s: "2 à 4 expériences" },
      { v: "expert", icon: "🏆", l: "Croisiériste aguerri",  s: "5 croisières et plus" },
    ],
  },
  // 2a. Vibe (first-timers only)
  {
    id: "vibe", type: "cards",
    showIf: d => d.experience === "first",
    q: "Quelle ambiance recherchez-vous ?",
    sub: "Nous choisirons la meilleure compagnie selon votre style.",
    opts: [
      { v: "party",     icon: "🎉", l: "Festif & animé",  s: "Piscines, spectacles, bars" },
      { v: "family",    icon: "👨‍👩‍👧", l: "Famille",        s: "Activités pour tous les âges" },
      { v: "romantic",  icon: "💑", l: "Romantique",       s: "Raffiné, paisible, gastronomie" },
      { v: "adventure", icon: "🏄", l: "Aventure",         s: "Sports, excursions, nature" },
    ],
  },
  // (Étape « intent » à 3 cartes RETIRÉE 2026-06-07 : faux choix — value/company menaient au
  // même flux. Le « navire précis » est désormais replié dans l'étape compagnies, optionnel.)
  // 3a. Région / Destination
  {
    id: "regions", type: "cards", multi: true,
    showIf: d => d.intent !== "by_ship",
    q: "Quelle destination vous attire ?",
    sub: "Sélectionnez une ou plusieurs régions.",
    opts: [
      { v: "carib_east",  icon: "🏝️", l: "Caraïbes Est",    s: "Puerto Rico, USVI, St-Martin, Bahamas" },
      { v: "carib_west",  icon: "🌊", l: "Caraïbes Ouest",   s: "Mexique, Jamaïque, Grand Cayman, Belize" },
      { v: "carib_south", icon: "🌺", l: "Caraïbes Sud",     s: "Aruba, Curaçao, Barbade, Îles du Vent" },
      { v: "bermuda",     icon: "🏖️", l: "Bermudes",         s: "Île unique, eaux turquoise, British charm" },
      { v: "europe_med",     icon: "🏛️", l: "Méditerranée",            s: "Barcelone, Rome, Grèce, Croatie" },
      { v: "europe_north",   icon: "❄️", l: "Europe du Nord & Baltique", s: "Copenhague, Stockholm, fjords norvégiens" },
      { v: "europe_british", icon: "🏰", l: "Îles britanniques & Islande", s: "Royaume-Uni, Irlande, Reykjavik" },
      { v: "alaska",      icon: "🏔️", l: "Alaska",           s: "Glaciers, fjords, faune sauvage" },
      { v: "hawaii",      icon: "🌸", l: "Hawaï / Pacifique", s: "Îles hawaïennes, Polynésie" },
      { v: "asia",        icon: "🏯", l: "Asie",              s: "Japon, Vietnam, Singapour, Dubaï" },
      { v: "repositioning", icon: "🔄", l: "Repositionnement / Traversée", s: "À sens unique, saisonnier (printemps/automne), départs limités" },
      { v: "any",         icon: "🎯", l: "Peu importe",       s: "Surprenez-moi — meilleure offre" },
    ],
  },
  // 3b. Escales précises (optionnel) — l'usager peut nommer des îles à visiter.
  // Texte libre, normalisé/matché côté worker (filtre escales + repli honnête).
  {
    id: "ports", type: "ports", optional: true,
    showIf: d => d.intent !== "by_ship",
    q: "Une île ou une escale en particulier ?",
    sub: "Optionnel — ex. Aruba, Curaçao, Bonaire. Laissez vide si peu importe.",
  },
  // (Étape « port_activities » RETIRÉE 2026-06-07 : signal trop mou — la région + les escales
  // nommées capturent déjà l'intention de destination, sans cet écran de friction.)
  // 3. Companies (TOUS — optionnel) — héberge le filtre Lignes de luxe + le sélecteur
  // de navire précis replié, donc doit être accessible à tous les niveaux d'expérience
  // (avant 2026-06-07 : réservé some/expert → débutants n'atteignaient jamais le luxe).
  {
    id: "companies", type: "companies",
    showIf: d => d.intent !== "by_ship",
    q: "Une compagnie en tête ? (optionnel)",
    sub: "Laissez vide pour chercher toutes les compagnies.",
  },
  // (Étapes « company_for_ship » + « specific_ship » RETIRÉES 2026-06-07 : le navire précis
  // est maintenant un sélecteur optionnel REPLIÉ dans l'étape compagnies, narrowé par les
  // compagnies sélectionnées. Capacité conservée, une étape de moins pour tout le monde.)
  // 4b. Ship class (tous les expérimentés)
  {
    id: "ship_class", type: "ship_class",
    showIf: d => (d.experience === "some" || d.experience === "expert") && d.intent !== "by_ship",
    q: "Quelle classe de navire vous attire ?",
    sub: "La classe détermine l'ambiance, les activités et la clientèle.",
  },
  // (Étape « ship_freshness » RETIRÉE 2026-06-07 : redondante — la fraîcheur du navire est
  // déjà couverte par la priorité « Le bateau » (classe/taille/fraîcheur) + l'étape classe.)
  // 4c. Cabin (options filtrées selon le contexte — Central Park/Boardwalk seulement sur Royal Icon/Oasis)
  {
    id: "cabin", type: "cards", multi: true,
    q: "Quelle cabine préférez-vous ?",
    sub: "Type d'hébergement à bord — vous pouvez en sélectionner plusieurs.",
    opts: d => {
      const base = [
        { v: "balcony_ocean", icon: "🌊", l: "Balcon Océan", s: "Vue sur la mer" },
        { v: "oceanview",     icon: "🪟", l: "Vue Océan",    s: "Fenêtre sans balcon" },
        { v: "inside",        icon: "🚪", l: "Intérieure",   s: "Si deal exceptionnel" },
        { v: "suite",         icon: "👑", l: "Suite",        s: "Occasion spéciale" },
      ];
      const shipClassesVal = Array.isArray(d.ship_class) ? d.ship_class : (d.ship_class ? [d.ship_class] : []);
      const isRoyalIconOasis =
        ((d.companies || []).includes("royal_caribbean") &&
          shipClassesVal.some(v => ["icon_class", "oasis_class"].includes(v))) ||
        (d.specific_ship && /icon|star of the seas|wonder|symphony|harmony|oasis|utopia|allure/i.test(d.specific_ship));
      if (isRoyalIconOasis) {
        base.splice(1, 0,
          { v: "balcony_centralpark", icon: "🌳", l: "Central Park", s: "Jardin intérieur" },
          { v: "balcony_boardwalk",   icon: "🎡", l: "Boardwalk",    s: "Vue parc d'attractions" },
        );
      }
      return base;
    },
  },
  // 4d. Classement des priorités (sauf navire précis — priorité = meilleur prix pour ce navire)
  {
    id: "priorities", type: "priority_rank",
    showIf: d => d.intent !== "by_ship",
    q: "Classez ces critères du plus au moins important.",
    sub: "Cliquez dans l'ordre — votre #1 guide notre scoring.",
    opts: d => {
      const base = [
        { v: "prix",       icon: "💰", l: "Prix / Budget",    s: "Meilleur rapport qualité-prix" },
        { v: "itineraire", icon: "🗺️", l: "Itinéraire",       s: "Ports, destinations, escales" },
        { v: "bateau",     icon: "🚢", l: "Le bateau",         s: "Classe, taille, fraîcheur" },
      ];
      if (d.vibe) {
        base.push({ v: "experience", icon: "🎉", l: "Expérience à bord", s: "Ambiance, activités, gastronomie" });
      } else {
        base.push({ v: "compagnie",  icon: "⚓", l: "La compagnie",      s: "Fidélité, style, service à bord" });
      }
      return base;
    },
  },
  // (Étape « duration » FUSIONNÉE dans « when » 2026-06-10 — GO Simon, revue funnel
  // B2 : chips de durée + nuits exactes apparaissent inline dans l'écran « quand »
  // (même mécanique que les dates fusionnées le 2026-06-07). Libellés via strings.js,
  // clé duration intacte. Un écran de moins pour tout le monde.)
  // 6. When (skip seulement pour best_deal — first-timer doit pouvoir préciser)
  {
    id: "when", type: "cards",
    showIf: d => d.intent !== "best_deal",
    q: "Quand souhaitez-vous partir ?",
    sub: "Vos dates influencent les promotions disponibles.",
    opts: [
      { v: "exact",    icon: "📅", l: "Dates précises",       s: "Je sais quand je pars" },
      { v: "flexible", icon: "🔄", l: "Flexible ±1–3 j",      s: "Autour de mes dates" },
      { v: "month",    icon: "🗓️", l: "Un mois précis",      s: "Je cible une période" },
      { v: "range",    icon: "📆", l: "Une plage de dates",   s: "Entre deux dates — ex: juillet à août" },
      { v: "anytime",  icon: "🎯", l: "La meilleure offre",   s: "Surprenez-moi !" },
    ],
  },
  // (Étape « dates » FUSIONNÉE dans « when » 2026-06-07 : le sélecteur de dates apparaît
  // maintenant inline sous le choix « quand » — un écran de moins.)
  // 8. Group
  {
    id: "group", type: "cards",
    q: "Qui voyagera avec vous ?",
    sub: "Chaque voyageur compte pour les promotions.",
    opts: [
      { v: "solo",   icon: "🧍",    l: "Seul·e",    s: "Juste moi" },
      { v: "couple", icon: "👫",    l: "En couple",  s: "2 adultes" },
      { v: "family", icon: "👨‍👩‍👧‍🍼", l: "Famille",  s: "Avec enfants" },
      { v: "group",  icon: "👥",    l: "Groupe",     s: "Amis / famille élargie" },
    ],
  },
  // (Étapes « group_size » + « cabin_occupancy » RETIRÉES comme écrans 2026-06-10 —
  // GO Simon, revue funnel A3/A4 : repliées en CHIPS INLINE dans l'étape « group »
  // (taille puis occupation si ≥3) et sous « family_detail » (occupation si ≥3).
  // Libellés réutilisés depuis strings.js (clés group_size / cabin_occupancy intactes).)
  // 9. Family detail
  {
    id: "family_detail", type: "family",
    showIf: d => d.group === "family",
    q: "Parlez-moi de vos enfants.",
    sub: "Les âges déterminent les promotions disponibles.",
  },
  // 10. Departure
  {
    id: "departure", type: "cards",
    q: "Comment arriverez-vous au port ?",
    sub: "Détermine les sources de recherche.",
    opts: [
      { v: "drive_ny",  icon: "🚗", l: "En voiture",           s: "Je me rends au port par la route" },
      { v: "fly_south", icon: "✈️", l: "En avion",             s: "L'IA trouve les meilleurs vols" },
      { v: "either",    icon: "🔀", l: "Peu importe",          s: "Comparez les deux" },
    ],
  },
  // 10a. Port de départ (si voiture ou peu importe)
  {
    id: "departure_port", type: "port_picker",
    showIf: d => ["drive_ny", "either"].includes(d.departure),
    q: "Quel port de départ vous convient ?",
    sub: "Vous pouvez sélectionner plusieurs ports.",
  },
  // (Étape « infant_flight_seat » RETIRÉE comme écran 2026-06-10 — GO Simon, revue
  // funnel A2 : repliée en chips inline sous « family_detail » quand un bébé <2 ans
  // est saisi. Défaut « lap » (sur les genoux). Libellés via strings.js, clé intacte.)
  // 10a-bis. Région d'origine (si "peu importe") — pour proposer 1 offre voiture + 1 offre avion
  {
    id: "drive_origin", type: "cards",
    showIf: d => d.departure === "either",
    q: "De quelle région partez-vous ?",
    sub: "Permet de vous proposer une option en voiture et une en avion.",
    opts: [
      { v: "mtl",    icon: "🏙️", l: "Montréal / Québec",  s: "Cape Liberty, NY et Boston à 5–7h de route" },
      { v: "ottawa", icon: "🏛️", l: "Ottawa / Kingston",  s: "Cape Liberty, NY et Boston à 6–8h de route" },
      { v: "toronto",icon: "🌆", l: "Toronto / Ontario",  s: "Ports du nord-est à 9h+ — mieux en avion" },
      { v: "autres", icon: "✈️", l: "Autre région",        s: "Toutes les options en avion" },
    ],
  },
  // 10c. Origin airport (if flying) — collected BEFORE the flight search
  {
    id: "origin_airport", type: "airport",
    showIf: d => ["fly_south", "either"].includes(d.departure),
    q: "D'où partez-vous en avion ?",
    sub: "Nous utiliserons cet aéroport pour trouver les vols les moins chers.",
  },
  // (Étape « overnight » RETIRÉE 2026-06-10 — GO Simon, revue funnel A1 : depuis que
  // l'hôtel est ESTIMÉ par le worker (lazy_hotel, focus croisière), cette question ne
  // pilote plus ni coût ni recherche. Défaut silencieux « oui » en avion (sécurité —
  // appliqué au submit), l'agent peut toujours extraire overnight:"no" du texte libre.)
  // 13. Budget
  {
    id: "budget", type: "budget",
    q: "Quel est votre budget total ?",
    sub: "En dollars canadiens, pour tout le groupe, transport inclus.",
  },
];

// ─── I18N STEP TRANSLATION ─────────────────────────────────────────────────────
// Superpose les traductions sur STEPS sans le dupliquer.
// Les valeurs internes (v:) ne changent pas — seuls q, sub, l, s changent.
function applyStepTranslations(steps, t) {
  return steps.map(step => {
    const s = t.steps?.[step.id];
    if (!s) return step;
    const newStep = { ...step };
    if (s.q)   newStep.q   = s.q;
    if (s.sub) newStep.sub = s.sub;
    if (s.opts) {
      if (typeof step.opts === "function") {
        // cabin a une fonction opts — on wrape pour appliquer les traductions au résultat
        const originalFn = step.opts;
        newStep.opts = (d) => originalFn(d).map(opt => {
          const o = s.opts?.[opt.v];
          return o ? { ...opt, l: o.l || opt.l, s: o.s !== undefined ? o.s : opt.s } : opt;
        });
      } else if (Array.isArray(step.opts)) {
        newStep.opts = step.opts.map(opt => {
          const o = s.opts?.[opt.v];
          return o ? { ...opt, l: o.l || opt.l, s: o.s !== undefined ? o.s : opt.s } : opt;
        });
      }
    }
    return newStep;
  });
}

// ─── COMPONENTS ────────────────────────────────────────────────────────────────


// ─── PRIORITY RANK ─────────────────────────────────────────────────────────────
// Classement par clic : l'usager clique les cartes dans l'ordre d'importance.
// Un badge numéroté (1→2→3→4) apparaît sur chaque carte cliquée.
function PriorityRankSelector({ value = [], onChange, opts = [] }) {
  const RANK_COLORS = ["#C9A84C", "#7EB3D4", "#8BBFA8", "#9E9E9E"];
  const toggle = (v) => {
    const idx = value.indexOf(v);
    if (idx >= 0) {
      onChange(value.filter(x => x !== v));
    } else {
      onChange([...value, v]);
    }
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      {opts.map(o => {
        const rank = value.indexOf(o.v);
        const selected = rank >= 0;
        const color = selected ? RANK_COLORS[rank] || "#9E9E9E" : null;
        return (
          <button
            key={o.v}
            onClick={() => toggle(o.v)}
            style={{
              position: "relative",
              background: selected ? `${color}18` : "rgba(255,255,255,0.03)",
              border: `2px solid ${selected ? color : "rgba(255,255,255,0.08)"}`,
              borderRadius: 14, padding: "14px 13px 12px", cursor: "pointer",
              textAlign: "left", display: "flex", flexDirection: "column", gap: 4,
            }}
          >
            {selected && (
              <div style={{
                position: "absolute", top: 8, right: 10,
                background: color, color: "#07111F",
                borderRadius: "50%", width: 22, height: 22,
                display: "flex", alignItems: "center", justifyContent: "center",
                ...GL, fontSize: 14, fontWeight: 900,
              }}>{rank + 1}</div>
            )}
            <div style={{ fontSize: 22 }}>{o.icon}</div>
            <div style={{ ...GF, fontSize: 15, color: selected ? color : "#E8DCC8", fontWeight: 700 }}>{o.l}</div>
            <div style={{ ...GL, fontSize: 11, color: "#5A7A95", lineHeight: 1.4 }}>{o.s}</div>
          </button>
        );
      })}
    </div>
  );
}

// ─── FUNNEL INTRO POPUP ────────────────────────────────────────────────────────
// ─── WALKTHROUGH — tour guidé pour la 1ère visite ──────────────────────────────
// 4 étapes : (1) intro centrée avec le pitch "IA = vrai agent de voyage",
//            (2) flèche sur le funnel guidé (Recommandé),
//            (3) flèche sur l'agent IA conversationnel,
//            (4) flèche sur le bouton compte/packs dans le header.
// Trigger : localStorage `cai_walkthrough_seen` absent → s'affiche au montage du
// welcome. Les cibles utilisent `data-tour="funnel|agent|account"`.
function Walkthrough({ onClose, lang = "fr" }) {
  const isEN = lang === "en";
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  useEffect(() => { try { track("walkthrough_started"); } catch {} }, []);

  const STEPS = isEN ? [
    {
      type: "modal",
      icon: "🧭",
      title: "Quick tour",
      body: <>Let me show you around — <strong style={{ color: "#E8DCC8" }}>30 seconds</strong> to spot the key things on the site.<br /><br />Press <strong style={{ color: "#E8DCC8" }}>Next</strong> to start, or <strong>Skip</strong> if you'd rather jump in.</>,
    },
    { type: "tooltip", target: '[data-tour="funnel"]', icon: "📋", title: "Guided form", badge: "Recommended", body: "Precise questions, sharper results. Best for a first search — most users start here." },
    { type: "tooltip", target: '[data-tour="agent"]', icon: "🧳", title: "AI Travel Agent", body: "Prefer to chat? Describe your trip in your own words — the AI extracts the parameters for you." },
    { type: "tooltip", target: '[data-tour="account"]', icon: "👤", title: "Your account", body: "A free account gives you 2 free searches every month — plus you can save your searches and find them on any device. Your free quota renews each month." },
    { type: "tooltip", target: '[data-tour="redeem"]', icon: "🎁", title: "Have a code?", body: "Got a code to apply? Click here to redeem it and add credits to your account." },
    { type: "tooltip", target: '[data-tour="feedback"]', icon: "💬", title: "Feedback & bugs", body: "Spotted a bug or have a suggestion? Tell me here — I read every message." },
  ] : [
    {
      type: "modal",
      icon: "🧭",
      title: "Petite visite guidée",
      body: <>Je vous fais le tour du site — <strong style={{ color: "#E8DCC8" }}>30 secondes</strong> pour repérer l'essentiel.<br /><br />Cliquez sur <strong style={{ color: "#E8DCC8" }}>Suivant</strong> pour démarrer, ou sur <strong>Passer</strong> si vous préférez foncer direct.</>,
    },
    { type: "tooltip", target: '[data-tour="funnel"]', icon: "📋", title: "Formulaire guidé", badge: "Recommandé", body: "Questions précises, résultats mieux ciblés. Idéal pour une première recherche — la plupart des gens commencent par ici." },
    { type: "tooltip", target: '[data-tour="agent"]', icon: "🧳", title: "Agent de voyage IA", body: "Vous préférez jaser ? Décrivez votre projet dans vos mots — l'IA extrait les paramètres pour vous." },
    { type: "tooltip", target: '[data-tour="account"]', icon: "👤", title: "Votre compte", body: "Un compte gratuit vous donne 2 recherches gratuites chaque mois — et vous pouvez sauvegarder vos recherches pour les retrouver sur tous vos appareils. Votre quota gratuit se renouvelle chaque mois." },
    { type: "tooltip", target: '[data-tour="redeem"]', icon: "🎁", title: "Vous avez un code ?", body: "Un code à appliquer ? Cliquez ici pour l'utiliser et créditer votre compte." },
    { type: "tooltip", target: '[data-tour="feedback"]', icon: "💬", title: "Commentaires & bugs", body: "Un bug repéré ou une suggestion ? Dites-le-moi ici — je lis chaque message." },
  ];

  const cur     = STEPS[step];
  const isLast  = step === STEPS.length - 1;
  const isFirst = step === 0;

  // Calcule la position de la cible (ou recule si introuvable)
  useEffect(() => {
    if (cur.type !== "tooltip") { setRect(null); return; }
    const el = document.querySelector(cur.target);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const measure = () => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom });
    };
    const id = setTimeout(measure, 320);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { clearTimeout(id); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [step, cur.type, cur.target]);

  const skip = (reason) => {
    const r = typeof reason === "string" ? reason : "skipped";
    try { track("walkthrough_" + r); } catch {}
    try { localStorage.setItem("cai_walkthrough_seen", "1"); } catch {}
    onClose();
  };
  const finish = () => skip("completed");

  // ── ÉTAPE 1 : modal centré ───────────────────────────────────────────────────
  if (cur.type === "modal") {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, padding: 20 }}>
        <div style={{ background: "#091929", border: "1px solid #C9A84C66", borderRadius: 22, maxWidth: 440, width: "100%", padding: "30px 26px 24px", boxShadow: "0 40px 80px rgba(0,0,0,0.8), 0 0 60px rgba(201,168,76,0.1)" }}>
          <div style={{ fontSize: 42, textAlign: "center", marginBottom: 14 }}>{cur.icon}</div>
          <div style={{ ...GF, fontSize: 20, color: "#C9A84C", textAlign: "center", marginBottom: 14, fontWeight: 700 }}>{cur.title}</div>
          <div style={{ ...GL, fontSize: 14, color: "#C8D8E8", lineHeight: 1.75, marginBottom: 22, textAlign: "center" }}>{cur.body}</div>
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16 }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{ width: i === step ? 22 : 7, height: 7, borderRadius: 4, background: i === step ? "#C9A84C" : "#1A3050", transition: "all 0.25s" }} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={skip} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "1px solid #1A2F45", background: "transparent", color: "#7A9BB5", ...GL, fontSize: 15, cursor: "pointer" }}>
              {isEN ? "Skip tour" : "Passer la visite"}
            </button>
            <button onClick={() => setStep(s => s + 1)} style={{ flex: 2, padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontWeight: 900, fontSize: 14, cursor: "pointer" }}>
              {isEN ? "Start tour →" : "C'est parti →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── ÉTAPES 2-4 : tooltip pointé sur élément ──────────────────────────────────
  // Position du tooltip : sous la cible si assez d'espace, sinon au-dessus.
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const vw = typeof window !== "undefined" ? window.innerWidth : 400;
  const tooltipWidth = Math.min(340, vw - 32);
  const showBelow    = rect ? (rect.bottom + 180 < vh) : true;

  // Si pas de rect (cible introuvable / pas encore mesurée) → fallback modal centré
  if (!rect) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, padding: 20 }}>
        <div style={{ background: "#091929", border: "1px solid #C9A84C66", borderRadius: 18, maxWidth: tooltipWidth, width: "100%", padding: "22px 20px", boxShadow: "0 30px 60px rgba(0,0,0,0.7)" }}>
          <TooltipBody cur={cur} step={step} totalSteps={STEPS.length} isFirst={isFirst} isLast={isLast} onPrev={() => setStep(s => s - 1)} onNext={() => setStep(s => s + 1)} onSkip={skip} onFinish={finish} isEN={isEN} />
        </div>
      </div>
    );
  }

  const tooltipTop = showBelow ? (rect.bottom + 18) : Math.max(16, rect.top - 220);
  const tooltipLeft = Math.max(16, Math.min(vw - tooltipWidth - 16, rect.left + rect.width / 2 - tooltipWidth / 2));

  return (
    <>
      {/* Spotlight inversé via box-shadow XXL — assombrit tout sauf la cible */}
      <div
        onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
        style={{
          position: "fixed",
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          borderRadius: 14,
          boxShadow: "0 0 0 10000px rgba(0,0,0,0.78)",
          border: "2px solid #C9A84C",
          pointerEvents: "auto",
          cursor: "pointer",
          zIndex: 350,
          transition: "all 0.35s ease",
        }}
      />
      {/* Tooltip */}
      <div style={{
        position: "fixed",
        top: tooltipTop,
        left: tooltipLeft,
        width: tooltipWidth,
        background: "#091929",
        border: "1px solid #C9A84C66",
        borderRadius: 16,
        padding: "18px 18px 16px",
        boxShadow: "0 30px 60px rgba(0,0,0,0.7)",
        zIndex: 360,
      }}>
        <TooltipBody cur={cur} step={step} totalSteps={STEPS.length} isFirst={isFirst} isLast={isLast} onPrev={() => setStep(s => s - 1)} onNext={() => setStep(s => s + 1)} onSkip={skip} onFinish={finish} isEN={isEN} />
      </div>
    </>
  );
}

function TooltipBody({ cur, step, totalSteps, isFirst, isLast, onPrev, onNext, onSkip, onFinish, isEN }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 22 }}>{cur.icon}</div>
        <div style={{ ...GF, fontSize: 15, color: "#C9A84C", fontWeight: 700, flex: 1 }}>{cur.title}</div>
        {cur.badge && (
          <div style={{ background: "#C9A84C", color: "#07111F", ...GL, fontSize: 9, fontWeight: 900, padding: "3px 7px", borderRadius: 6, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            ⭐ {cur.badge}
          </div>
        )}
      </div>
      <div style={{ ...GL, fontSize: 15, color: "#C8D8E8", lineHeight: 1.65, marginBottom: 14 }}>{cur.body}</div>
      <div style={{ display: "flex", justifyContent: "center", gap: 5, marginBottom: 12 }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} style={{ width: i === step ? 18 : 6, height: 6, borderRadius: 3, background: i === step ? "#C9A84C" : "#1A3050", transition: "all 0.25s" }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {!isFirst && (
          <button onClick={onPrev} style={{ padding: "9px 12px", borderRadius: 10, border: "1px solid #1A2F45", background: "transparent", color: "#7A9BB5", ...GL, fontSize: 14, cursor: "pointer" }}>
            ← {isEN ? "Back" : "Retour"}
          </button>
        )}
        <button onClick={onSkip} style={{ padding: "9px 12px", borderRadius: 10, border: "none", background: "transparent", color: "#5A7A95", ...GL, fontSize: 14, cursor: "pointer", textDecoration: "underline" }}>
          {isEN ? "Skip" : "Passer"}
        </button>
        <div style={{ flex: 1 }} />
        {!isLast ? (
          <button onClick={onNext} style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontWeight: 900, fontSize: 15, cursor: "pointer" }}>
            {isEN ? "Next →" : "Suivant →"}
          </button>
        ) : (
          <button onClick={onFinish} style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#5CB85C,#3A8A3A)", color: "#07111F", ...GL, fontWeight: 900, fontSize: 15, cursor: "pointer" }}>
            ✓ {isEN ? "Done" : "Terminé"}
          </button>
        )}
      </div>
    </>
  );
}

// ─── AIRPORT INPUT ─────────────────────────────────────────────────────────────
// Sélection YUL/YQB + option "Autre" avec champ libre (IATA ou ville).
function AirportInput({ value, custom, onChange, onAutoAdvance, t }) {
  const airports = t?.airports || [
    { v: "YUL", icon: "🛫", l: "YUL — Montréal-Trudeau",      s: "Aéroport principal du Québec" },
    { v: "YQB", icon: "🛫", l: "YQB — Québec (Jean Lesage)",   s: "Hub de la Capitale-Nationale" },
    { v: "other", icon: "✈️", l: "Autre aéroport",             s: "Préciser le code IATA ou la ville" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
      {airports.map(o => {
        const on = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => {
              onChange({ origin_airport: o.v, origin_airport_custom: o.v === "other" ? (custom || "") : "" });
              if (o.v !== "other" && onAutoAdvance) setTimeout(onAutoAdvance, 300);
            }}
            style={{
              background: on ? "rgba(201,168,76,0.12)" : "rgba(255,255,255,0.03)",
              border: `2px solid ${on ? "#C9A84C" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 14, padding: "12px 13px", cursor: "pointer",
              textAlign: "left", display: "flex", flexDirection: "column", gap: 4,
              gridColumn: o.v === "other" ? "span 2" : undefined,
            }}
          >
            <div style={{ fontSize: 22 }}>{o.icon}</div>
            <div style={{ ...GF, fontSize: 15, color: on ? "#C9A84C" : "#E8DCC8", fontWeight: 700 }}>{o.l}</div>
            <div style={{ ...GL, fontSize: 11, color: "#5A7A95", lineHeight: 1.4 }}>{o.s}</div>
          </button>
        );
      })}
      {value === "other" && (
        <div style={{ gridColumn: "span 2", marginTop: 4 }}>
          <input
            type="text"
            placeholder={t?.airport_other_placeholder || "ex: YOW, Ottawa, YYZ..."}
            value={custom || ""}
            maxLength={40}
            onChange={e => onChange({ origin_airport: "other", origin_airport_custom: e.target.value.toUpperCase() })}
            style={{
              background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 10,
              padding: "9px 12px", color: "#C8D8E8", ...GL, fontSize: 15,
              width: "100%", outline: "none", boxSizing: "border-box",
            }}
          />
          <div style={{ ...GL, fontSize: 11, color: "#3A5A72", marginTop: 4 }}>
            {t?.airport_other_hint || "💡 Le code IATA (ex: YOW, YYZ) donne les meilleurs résultats de recherche."}
          </div>
        </div>
      )}
    </div>
  );
}

function FamilyInput({ value, onChange, isEN }) {
  const d = value || { adults: 2, children: [] };
  const cBtn = { background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.3)", color: "#C9A84C", width: 36, height: 36, borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 18 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "14px 16px" }}>
        <div style={{ ...GL, fontSize: 11, color: "#7A9BB5", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>{isEN ? "Adults (18+)" : "Adultes (18+)"}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button style={cBtn} onClick={() => onChange({ ...d, adults: Math.max(1, d.adults - 1) })}>−</button>
          <span style={{ ...GF, fontSize: 30, color: "#C9A84C", minWidth: 36, textAlign: "center" }}>{d.adults}</span>
          <button style={cBtn} onClick={() => onChange({ ...d, adults: Math.min(8, d.adults + 1) })}>+</button>
        </div>
      </div>
      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ ...GL, fontSize: 11, color: "#7A9BB5", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>{isEN ? "Children & infants" : "Enfants & bébés"}</div>
          <button onClick={() => d.children.length < 6 && onChange({ ...d, children: [...d.children, { age: 3 }] })} style={{ background: "#C9A84C22", border: "1px solid #C9A84C55", color: "#C9A84C", borderRadius: 8, padding: "4px 12px", cursor: "pointer", ...GL, fontSize: 13, fontWeight: 700 }}>+ {isEN ? "Add" : "Ajouter"}</button>
        </div>
        {d.children.length === 0 && <div style={{ ...GL, fontSize: 13, color: "#3A5A72", fontStyle: "italic" }}>{isEN ? "No children" : "Aucun enfant"}</div>}
        {d.children.map((ch, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
            <span style={{ ...GL, fontSize: 13, color: "#A8C0D0", minWidth: 58 }}>{isEN ? `Child ${i + 1}` : `Enfant ${i + 1}`}</span>
            <input type="range" min={0} max={17} value={ch.age} onChange={e => { const c = [...d.children]; c[i] = { age: parseInt(e.target.value) }; onChange({ ...d, children: c }); }} style={{ flex: 1, accentColor: "#C9A84C" }} />
            <span style={{ ...GL, fontSize: 14, color: "#C9A84C", minWidth: 46, textAlign: "right" }}>{ch.age === 0 ? (isEN ? "Infant" : "Bébé") : (isEN ? `${ch.age} y.o.` : `${ch.age} ans`)}</span>
            <button onClick={() => { const c = [...d.children]; c.splice(i, 1); onChange({ ...d, children: c }); }} style={{ background: "transparent", border: "none", color: "#3A5A72", cursor: "pointer", fontSize: 16, padding: 0 }}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DateInput({ value, onChange, when, isEN = false }) {
  const v = value || {};
  const iSt = { background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 10, padding: "9px 12px", color: "#C8D8E8", ...GL, fontSize: 14, width: "100%", outline: "none" };
  const now = new Date();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;
  // Année affichée dans le sélecteur par mois (onglets d'années → grille 12 mois).
  const [yr, setYr] = useState(v.year || nowYear);
  if (when === "month") {
    const months = isEN
      ? ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
      : ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
    const years = [nowYear, nowYear + 1, nowYear + 2];
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Onglets d'années — remplace le mur de 24 pastilles par 1 année visible à la fois */}
        <div style={{ display: "flex", gap: 8 }}>
          {years.map(y => {
            const active = yr === y;
            return (
              <button key={y} onClick={() => setYr(y)} style={{ flex: 1, padding: "9px 0", borderRadius: 11, border: `1px solid ${active ? "#C9A84C" : "#1A2F45"}`, background: active ? "linear-gradient(135deg,#C9A84C,#A8882A)" : "transparent", color: active ? "#07111F" : "#8AA8C0", cursor: "pointer", ...GL, fontSize: 15, fontWeight: 800 }}>{y}</button>
            );
          })}
        </div>
        {/* Grille des 12 mois de l'année choisie */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {months.map((m, mi) => {
            const monthNum = mi + 1;
            const past = yr < nowYear || (yr === nowYear && monthNum < nowMonth);
            const sel = v.month === monthNum && v.year === yr;
            return (
              <button key={mi} disabled={past} onClick={() => onChange({ month: monthNum, year: yr })}
                style={{ padding: "12px 0", borderRadius: 11, border: `1px solid ${sel ? "#C9A84C" : past ? "#12202E" : "#1A2F45"}`, background: sel ? "#C9A84C22" : "rgba(255,255,255,0.02)", color: past ? "#2A3A48" : sel ? "#C9A84C" : "#B8CCDC", cursor: past ? "not-allowed" : "pointer", opacity: past ? 0.45 : 1, ...GL, fontSize: 13, fontWeight: sel ? 800 : 500, transition: "all 0.15s" }}>
                {m}
              </button>
            );
          })}
        </div>
        {v.month && v.year && (
          <div style={{ ...GL, fontSize: 13, color: "#C9A84C", textAlign: "center", fontWeight: 700, padding: "8px 12px", background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.25)", borderRadius: 10 }}>
            📅 {isEN ? "Departing" : "Départ en"} {months[v.month - 1]} {v.year}
          </div>
        )}
      </div>
    );
  }
  if (when === "range") {
    return (
      <div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 5 }}>{isEN ? "Earliest" : "Au plus tôt"}</div>
            <input type="date" value={v.from || ""} onChange={e => onChange({ ...v, from: e.target.value })} style={iSt} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 5 }}>{isEN ? "Latest" : "Au plus tard"}</div>
            <input type="date" value={v.to || ""} min={v.from || ""} onChange={e => onChange({ ...v, to: e.target.value })} style={{ ...iSt, borderColor: v.to && v.from ? "#5CB85C55" : "#1A2F45" }} />
          </div>
        </div>
        <div style={{ ...GL, fontSize: 13, color: "#C9A84C", marginTop: 10, padding: "8px 12px", background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.25)", borderRadius: 8 }}>
          {isEN ? "📆 The AI will find the best offers departing within this range." : "📆 L'IA trouvera les meilleures offres dont le départ tombe dans cette plage."}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 5 }}>{isEN ? "Departure" : "Départ"}</div>
          <input type="date" value={v.from || ""} onChange={e => {
            if (!e.target.value) { onChange({ ...v, from: "" }); return; }
            const d = new Date(e.target.value + "T12:00:00");
            d.setDate(d.getDate() + 7);
            onChange({ ...v, from: e.target.value, to: d.toISOString().split("T")[0] });
          }} style={iSt} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 5 }}>{isEN ? "Return" : "Retour"}</div>
          <input type="date" value={v.to || ""} onChange={e => onChange({ ...v, to: e.target.value })} style={{ ...iSt, borderColor: v.to && v.from ? "#5CB85C55" : "#1A2F45" }} />
        </div>
      </div>
      {when === "flexible" && (
        <div style={{ ...GL, fontSize: 13, color: "#C9A84C", marginTop: 10, padding: "8px 12px", background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.25)", borderRadius: 8 }}>
          {isEN ? "🔄 The AI will also look ±1 to 3 days around these dates to find the best offers." : "🔄 L'IA cherchera aussi ±1 à 3 jours autour de ces dates pour trouver les meilleures offres."}
        </div>
      )}
    </div>
  );
}

// `value`/`onChange` portent TOUJOURS le budget TOTAL du groupe (aucun changement en
// aval). Le toggle « Par personne » ne change que la SAISIE : on affiche/édite un montant
// par personne et on stocke total = pp × voyageurs. Répond à la confusion testeur.
function BudgetInput({ value, onChange, basis = "group", onBasisChange, travelers = 2, lang = "fr" }) {
  const isEN  = lang === "en";
  const total = value || 10000;
  const pax   = Math.max(1, travelers);
  const isPP  = basis === "person";
  const cur   = (n) => Number(n).toLocaleString(isEN ? "en-CA" : "fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
  const min  = isPP ? 1000  : 2000;
  const max  = isPP ? 15000 : 30000;
  const step = isPP ? 250   : 500;
  const shown = isPP ? Math.min(max, Math.max(min, Math.round(total / pax / step) * step)) : total;
  const zone = total < 5000 ? (isEN ? "Tight budget" : "Petit budget") : total < 12000 ? (isEN ? "Reasonable" : "Raisonnable") : total < 20000 ? (isEN ? "Comfortable" : "Confortable") : "Premium";
  const tab = (b, label) => (
    <button key={b} onClick={() => onBasisChange && onBasisChange(b)} style={{
      flex: 1, padding: "8px 0", borderRadius: 8, cursor: "pointer", ...GL, fontSize: 13, fontWeight: basis === b ? 700 : 400,
      border: `1px solid ${basis === b ? "#C9A84C" : "#1A2F45"}`, background: basis === b ? "rgba(201,168,76,0.13)" : "transparent",
      color: basis === b ? "#C9A84C" : "#7A9BB5",
    }}>{label}</button>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 8 }}>
        {tab("group",  isEN ? "Whole group" : "Total du groupe")}
        {tab("person", isEN ? "Per person"  : "Par personne")}
      </div>
      <div style={{ textAlign: "center" }}>
        <div style={{ ...GF, fontSize: 40, color: "#C9A84C", fontWeight: 700 }}>{cur(shown)}</div>
        <div style={{ ...GL, fontSize: 13, color: "#5A7A95", marginTop: 3 }}>
          {isPP ? (isEN ? `per person · × ${pax} = ${cur(total)} total` : `par personne · × ${pax} = ${cur(total)} au total`) : `${zone} · ${isEN ? "for the whole group" : "pour tout le groupe"}`}
        </div>
      </div>
      <input type="range" min={min} max={max} step={step} value={shown}
        onChange={e => { const v = parseInt(e.target.value); onChange(isPP ? v * pax : v); }}
        style={{ width: "100%", accentColor: "#C9A84C" }} />
      <div style={{ display: "flex", justifyContent: "space-between", ...GL, fontSize: 11, color: "#3A5A72" }}>
        <span>{cur(min)}</span><span>{cur(max)}</span>
      </div>
    </div>
  );
}

function ShipClassSelector({ value, onChange, companies, isFirstTimer, t }) {
  const selectedCompanies = companies || [];
  const genericClasses = t?.generic_ship_classes || GENERIC_SHIP_CLASSES_FR;
  let classes = genericClasses;
  let companyName = null;

  if (!isFirstTimer && selectedCompanies.length === 1) {
    const key = selectedCompanies[0];
    if (SHIP_CLASSES_BY_COMPANY[key]) {
      classes = SHIP_CLASSES_BY_COMPANY[key];
      companyName = CRUISE_COMPANIES.find(c => c.v === key)?.l;
    }
  }
  // ShipClassSelector still uses genericClasses for FR labels in buildPrompt — no change needed there

  // value = toujours un array (multi-sélection pour les 2 modes : générique et par compagnie)
  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  const toggle = (v) => {
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {companyName && (
        <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 2 }}>
          Classes disponibles pour <strong style={{ color: "#C9A84C" }}>{companyName}</strong>
        </div>
      )}
      <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 2 }}>
        {t?.select_multiple || "Sélectionnez tout ce qui s'applique"}
      </div>
      {isFirstTimer && (
        <div style={{ background: "rgba(201,168,76,0.07)", border: "1px solid #C9A84C33", borderRadius: 10, padding: "10px 12px", ...GL, fontSize: 13, color: "#A8C0D0", lineHeight: 1.6, marginBottom: 4 }}>
          💡 La taille du navire change complètement l'expérience. Un méga-paquebot, c'est une ville flottante avec toboggans et Broadway. Un navire moyen, c'est plus intime et plus calme.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {classes.map(c => {
          const on = selected.includes(c.v);
          return (
            <button key={c.v} onClick={() => toggle(c.v)} style={{
              background: on ? "rgba(201,168,76,0.12)" : "rgba(255,255,255,0.03)",
              border: `2px solid ${on ? "#C9A84C" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 14, padding: "12px 14px", cursor: "pointer",
              textAlign: "left", display: "flex", alignItems: "flex-start", gap: 12,
            }}>
              <span style={{ fontSize: 22, flexShrink: 0, marginTop: 1 }}>{c.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 3 }}>
                  <span style={{ ...GF, fontSize: 14, color: on ? "#C9A84C" : "#E8DCC8", fontWeight: 700 }}>{c.l}</span>
                  <span style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>{c.pax}</span>
                </div>
                {c.ships && <div style={{ ...GL, fontSize: 11, color: "#4A6A82", marginBottom: 3 }}>{c.ships}</div>}
                <div style={{ ...GL, fontSize: 13, color: "#7A9BB5", lineHeight: 1.5 }}>{c.s}</div>
              </div>
              {on && <span style={{ color: "#C9A84C", fontSize: 16, flexShrink: 0 }}>✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CompaniesSelector({ value, onChange, shipValue, onShipChange }) {
  const selected = value || [];
  const [showOthers, setShowOthers] = useState(false);
  const [showShip, setShowShip]     = useState(!!shipValue);
  const main    = CRUISE_COMPANIES.filter(c => !c.secondary);
  const others  = CRUISE_COMPANIES.filter(c => c.secondary && !c.luxury);
  const luxury  = CRUISE_COMPANIES.filter(c => c.luxury);
  const nOthersSelected = CRUISE_COMPANIES.filter(c => c.secondary && selected.includes(c.v)).length;
  const companyBtn = (c) => {
    const on = selected.includes(c.v);
    return (
      <button key={c.v} onClick={() => onChange(on ? selected.filter(x => x !== c.v) : [...selected, c.v])} style={{
        background: on ? "rgba(201,168,76,0.12)" : "rgba(255,255,255,0.03)",
        border: `2px solid ${on ? "#C9A84C" : "rgba(255,255,255,0.08)"}`,
        borderRadius: 14, padding: "10px 12px", cursor: "pointer",
        textAlign: "left", display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 18 }}>{c.icon}</span>
        <span style={{ ...GL, fontSize: 13, color: on ? "#C9A84C" : "#E8DCC8", fontWeight: on ? 700 : 400, lineHeight: 1.3, flex: 1 }}>{c.l}</span>
        {on && <span style={{ fontSize: 14, color: "#C9A84C" }}>✓</span>}
      </button>
    );
  };
  const luxeVals  = luxury.map(c => c.v);
  const allLuxeOn = luxeVals.length > 0 && luxeVals.every(v => selected.includes(v));
  const toggleLuxe = () => onChange(allLuxeOn
    ? selected.filter(v => !luxeVals.includes(v))                    // tout retirer
    : [...new Set([...selected, ...luxeVals])]);                     // tout ajouter
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 4 }}>
        Sélectionnez une ou plusieurs compagnies (toutes si aucune sélection)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
        {main.map(companyBtn)}
      </div>
      {/* CATÉGORIE LUXE — bouton 1-clic bien visible (un acheteur luxe pense « haut de gamme »,
          pas « Regent vs Seabourn »). Sélectionne toutes les lignes luxe d'un coup → cache prêt
          + filet luxe→luxe. Les lignes individuelles restent dispo dans « Autres compagnies ». */}
      <button onClick={toggleLuxe} style={{
        background: allLuxeOn ? "linear-gradient(135deg,rgba(201,168,76,0.22),rgba(201,168,76,0.10))" : "rgba(201,168,76,0.06)",
        border: `2px solid ${allLuxeOn ? "#C9A84C" : "#C9A84C66"}`, borderRadius: 14, padding: "12px 14px",
        cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontSize: 20 }}>✦</span>
        <span style={{ flex: 1 }}>
          <span style={{ ...GL, fontSize: 14, color: "#C9A84C", fontWeight: 700, display: "block" }}>Croisière de luxe</span>
          <span style={{ ...GL, fontSize: 11, color: "#8AA0B5" }}>Regent · Seabourn · Oceania · Cunard · Silversea</span>
        </span>
        {allLuxeOn && <span style={{ fontSize: 15, color: "#C9A84C" }}>✓</span>}
      </button>
      <button onClick={() => setShowOthers(s => !s)} style={{ ...GL, fontSize: 13, color: "#5A7A95", background: "transparent", border: "1px solid #1A2F45", borderRadius: 10, padding: "7px 12px", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 6 }}>
        <span>{showOthers || nOthersSelected > 0 ? "▾" : "▸"}</span>
        <span>Autres compagnies{nOthersSelected > 0 ? ` (${nOthersSelected} sélectionnée${nOthersSelected > 1 ? "s" : ""})` : ""}</span>
      </button>
      {(showOthers || nOthersSelected > 0) && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
            {others.map(companyBtn)}
          </div>
          <div style={{ ...GL, fontSize: 11, color: "#C9A84C", fontWeight: 700, marginTop: 6, marginBottom: 2, letterSpacing: 0.4 }}>
            ✦ Lignes de luxe / haut de gamme
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
            {luxury.map(companyBtn)}
          </div>
        </>
      )}
      {/* NAVIRE PRÉCIS (optionnel, replié) — remplace l'ancienne étape « intent » à 3 cartes.
          Le rare connaisseur choisit ici son navire ; les 95% l'ignorent. Liste réduite par
          les compagnies sélectionnées ci-dessus. */}
      {onShipChange && (
        <>
          <button onClick={() => { const n = !showShip; setShowShip(n); if (!n) onShipChange(""); }} style={{ ...GL, fontSize: 13, color: "#5A7A95", background: "transparent", border: "1px solid #1A2F45", borderRadius: 10, padding: "7px 12px", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 6 }}>
            <span>{showShip ? "▾" : "▸"}</span>
            <span>⚓ Je connais déjà mon navire (optionnel){shipValue ? ` · ${shipValue}` : ""}</span>
          </button>
          {showShip && <SpecificShipSelector value={shipValue} onChange={onShipChange} companies={selected} />}
        </>
      )}
    </div>
  );
}

function CompanyForShipSelector({ value, onChange, onAutoAdvance, isEN }) {
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
        {CRUISE_COMPANIES.map(c => {
          const on = value === c.v;
          return (
            <button key={c.v} onClick={() => {
              const next = on ? null : c.v;
              onChange(next);
              if (!on) setTimeout(onAutoAdvance, 300);
            }} style={{
              background: on ? "rgba(201,168,76,0.12)" : "rgba(255,255,255,0.03)",
              border: `2px solid ${on ? "#C9A84C" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 14, padding: "10px 12px", cursor: "pointer",
              textAlign: "left", display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontSize: 18 }}>{c.icon}</span>
              <span style={{ ...GL, fontSize: 14, color: on ? "#C9A84C" : "#E8DCC8", fontWeight: on ? 700 : 400, flex: 1 }}>{c.l}</span>
              {on && <span style={{ fontSize: 14, color: "#C9A84C" }}>✓</span>}
            </button>
          );
        })}
      </div>
      <div style={{ ...GL, fontSize: 11, color: "#3A5A72", marginTop: 8, textAlign: "center" }}>
        {isEN ? "Not sure? Skip this step and click Next to see all ships." : "Pas sûr ? Laissez vide et cliquez « Suivant » pour voir tous les navires."}
      </div>
    </div>
  );
}

function SpecificShipSelector({ value, onChange, companies }) {
  const selectedCompanies = companies || [];
  const ships = selectedCompanies.length > 0
    ? selectedCompanies.flatMap(c => SHIPS_BY_COMPANY[c] || [])
    : Object.values(SHIPS_BY_COMPANY).flat();
  const iSt = { background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 10, padding: "9px 12px", color: "#C8D8E8", ...GL, fontSize: 14, width: "100%", outline: "none", appearance: "none" };
  return (
    <div>
      <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 8 }}>Choisissez votre navire</div>
      <select value={value || ""} onChange={e => onChange(e.target.value)} style={iSt}>
        <option value="">— Sélectionnez un navire —</option>
        {ships.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      {selectedCompanies.length > 0 && (
        <div style={{ ...GL, fontSize: 11, color: "#3A5A72", marginTop: 6 }}>
          Filtré pour : {selectedCompanies.map(c => CRUISE_COMPANIES.find(x => x.v === c)?.l).join(", ")}
        </div>
      )}
    </div>
  );
}

// ─── SEARCHING SCREEN ──────────────────────────────────────────────────────────
const CRUISE_TIPS = [
  "💡 Réserver 6 à 9 mois à l'avance donne souvent les meilleurs prix — surtout pour les grandes cabines.",
  "💡 Le mardi et le mercredi sont les meilleurs jours pour trouver des promotions flash.",
  "💡 Les cabines intérieures peuvent coûter 40% moins cher — idéal si vous passez vos journées à explorer.",
  "💡 Sur un méga-paquebot, la cabine Boardwalk donne sur le parc d'attractions — très animé le soir.",
  "💡 Air Canada Vacations propose souvent des packages vol+croisière avec siège inclus dès l'aéroport.",
  "💡 Les promotions « Kids Sail Free » de Royal Caribbean peuvent économiser des milliers de dollars en famille.",
  "💡 Si vous êtes à distance raisonnable d'un port, conduire élimine le coût des vols.",
];

const OCEAN_BG_PHOTOS = [
  "/destinations/aruba.webp",
  "/destinations/bermudes.jpg",
  "/destinations/turks-caicos.jpg",
  "/destinations/cococay.jpg",
  "/destinations/labadee.jpg",
  "/destinations/st-lucia.jpg",
];

function SearchingScreen({ target, data, usage, onPaywall, t = {} }) {
  const [phase, setPhase]   = useState(0);
  const [pct, setPct]       = useState(0);
  const [tipIdx, setTipIdx] = useState(0);
  const [deepIdx, setDeepIdx] = useState(0);
  const [bgPhoto]           = useState(() => OCEAN_BG_PHOTOS[Math.floor(Math.random() * OCEAN_BG_PHOTOS.length)]);

  const hasFlights = data?.departure !== "drive_ny";
  const hasHotel   = data?.overnight === "yes";
  const tips = t.tips || CRUISE_TIPS;
  // Micro-étapes de la phase finale (« Analyse ») — la plus longue (l'IA rédige).
  // Elles tournent pour que l'écran ait l'air vivant au lieu de figé à 98%.
  const analyseSteps = t.analyse_steps || [
    "Je compare les itinéraires et les escales…",
    "Je vérifie le prix réel de chaque départ…",
    "Je calcule les vols et l'hôtel la veille…",
    "Je classe les meilleures offres pour toi…",
    "Je rédige ton analyse personnalisée…",
  ];

  const phases = [
    { icon: "🚢", title: t.searching_cruise   || "Recherche de croisières",              detail: t.searching_cruise_d  || "Analyse des disponibilités et des prix en temps réel…" },
    ...(hasFlights ? [{ icon: "✈️", title: t.searching_flights  || "Recherche de vols",  detail: t.searching_flights_d || "Meilleurs tarifs aériens vers le port d'embarquement…" }] : []),
    ...(hasHotel   ? [{ icon: "🏨", title: t.searching_hotel    || "Recherche d'hôtel",  detail: t.searching_hotel_d   || "Hébergement avec navette aéroport et navette port…" }]   : []),
    { icon: "🏆", title: t.searching_results  || "Analyse et préparation des résultats", detail: t.searching_results_d || "Comparaison, classement et mise en forme de votre sélection…" },
  ];

  // Timings étalés sur ~27 s selon le nombre de phases
  const totalMs = 27000;
  const timings = phases.map((_, i) => Math.round((i / phases.length) * totalMs));

  useEffect(() => {
    const timers = timings.map((t, i) => setTimeout(() => setPhase(i), t));
    const iv = setInterval(() => {
      setPct(p => p + (98 - p) * 0.012); // asymptotique : approche 98% sans jamais s'y figer
    }, 150);
    const tipIv = setInterval(() => setTipIdx(i => (i + 1) % tips.length), 7000);
    const deepIv = setInterval(() => setDeepIdx(i => i + 1), 4500);
    return () => { timers.forEach(clearTimeout); clearInterval(iv); clearInterval(tipIv); clearInterval(deepIv); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = phases[Math.min(phase, phases.length - 1)];
  // Sur la dernière phase (analyse), on fait défiler des micro-messages concrets
  // pour signaler que l'IA travaille (sinon l'écran semble gelé sur la fin).
  const isLastPhase = phase >= phases.length - 1;
  const curDetail = isLastPhase ? analyseSteps[deepIdx % analyseSteps.length] : cur.detail;

  return (
    <div style={{
      background: "#07111F",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      backgroundImage: `linear-gradient(rgba(7,17,31,0.80), rgba(7,17,31,0.88)), url(${bgPhoto})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;700;900&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes ping  { 0%{transform:scale(1);opacity:.6} 100%{transform:scale(2.4);opacity:0} }
        @keyframes ping2 { 0%{transform:scale(1);opacity:.4} 100%{transform:scale(2.0);opacity:0} }
        @keyframes sail  { 0%{transform:translateX(-8px)} 50%{transform:translateX(8px)} 100%{transform:translateX(-8px)} }
        @keyframes wave  { 0%,100%{transform:scaleY(1)} 50%{transform:scaleY(0.5)} }
        @keyframes fade  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "16px 22px", borderBottom: "1px solid #0D1F30" }}>
        <img src="/croisiere-ai-mark-gold.svg" alt="" style={{ height: 26, width: 26, flexShrink: 0 }} />
        <span style={{ ...GF, fontSize: 16, color: "#C9A84C", fontWeight: 700 }}>{t?.app_title === "Cruise AI" ? "Cruise AI" : "Croisière AI"}</span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#5CB85C", animation: "ping2 1.4s ease-out infinite" }} />
            <span style={{ ...GL, fontSize: 11, color: "#5CB85C", fontWeight: 700 }}>{t.live_badge || "EN DIRECT"}</span>
          </div>
          <AuthButton usage={usage} workerUrl={WORKER_URL} />
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "28px 20px", gap: 28 }}>

        {/* Sonar + ship */}
        <div style={{ position: "relative", width: 120, height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", width: 120, height: 120, borderRadius: "50%", border: "2px solid #C9A84C33", animation: "ping 2s ease-out infinite" }} />
          <div style={{ position: "absolute", width: 120, height: 120, borderRadius: "50%", border: "2px solid #C9A84C22", animation: "ping 2s ease-out infinite", animationDelay: "0.6s" }} />
          <div style={{ position: "absolute", width: 80, height: 80, borderRadius: "50%", border: "1px solid #2A4A6A", background: "rgba(10,30,53,0.9)" }} />
          <span style={{ fontSize: 36, animation: "sail 3s ease-in-out infinite", position: "relative", zIndex: 1 }}>🚢</span>
        </div>

        {/* Titre + phase active */}
        <div style={{ textAlign: "center", maxWidth: 400 }}>
          <div style={{ ...GF, fontSize: 22, color: "#fff", fontWeight: 700, marginBottom: 8 }}>
            Recherche en cours…
          </div>
          <div key={phase} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, animation: "fade 0.5s ease" }}>
            <span style={{ fontSize: 18 }}>{cur.icon}</span>
            <div>
              <div style={{ ...GL, fontSize: 15, color: "#C9A84C", fontWeight: 700 }}>{cur.title}</div>
              <div key={curDetail} style={{ ...GL, fontSize: 13, color: "#4A6A82", animation: isLastPhase ? "fade 0.5s ease" : "none" }}>{curDetail}</div>
            </div>
          </div>
        </div>

        {/* Barre de progression */}
        <div style={{ width: "100%", maxWidth: 420 }}>
          <div style={{ height: 6, background: "#0A1E35", borderRadius: 99, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${pct}%`, borderRadius: 99,
              background: "linear-gradient(90deg, #1A4A7A, #C9A84C, #E8DCC8, #C9A84C, #1A4A7A)",
              backgroundSize: "300% auto",
              animation: "shimmer 1.8s linear infinite",
              transition: "width 1.2s ease-out",
            }} />
          </div>
        </div>

        {/* Étapes de recherche */}
        <div style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", gap: 7 }}>
          {phases.map((p, i) => {
            const done    = phase > i;
            const current = phase === i;
            return (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                background: done ? "rgba(92,184,92,0.07)" : current ? "rgba(201,168,76,0.08)" : "rgba(255,255,255,0.02)",
                border: `1px solid ${done ? "#5CB85C33" : current ? "#C9A84C44" : "#1A2F45"}`,
                borderRadius: 11, transition: "all 0.5s",
              }}>
                <div style={{
                  width: 9, height: 9, borderRadius: "50%", flexShrink: 0,
                  background: done ? "#5CB85C" : current ? "#C9A84C" : "#1A3050",
                  boxShadow: done ? "0 0 6px #5CB85C99" : current ? "0 0 8px #C9A84C88" : "none",
                  transition: "all 0.5s",
                }} />
                <span style={{ fontSize: 14 }}>{p.icon}</span>
                <span style={{ ...GL, fontSize: 13, color: done ? "#5CB85C" : current ? "#C9A84C" : "#2A4A62", fontWeight: done || current ? 700 : 400, flex: 1 }}>{p.title}</span>
                {done && <span style={{ fontSize: 14, color: "#5CB85C" }}>✓</span>}
              </div>
            );
          })}
        </div>

        {/* Tip rotatif */}
        <div key={tipIdx} style={{
          width: "100%", maxWidth: 420,
          background: "rgba(201,168,76,0.06)", border: "1px solid #C9A84C22",
          borderRadius: 14, padding: "14px 16px", animation: "fade 0.6s ease",
        }}>
          <div style={{ ...GL, fontSize: 13, color: "#8A9BB0", lineHeight: 1.7 }}>
            {tips[tipIdx % tips.length]}
          </div>
        </div>

        <div style={{ ...GL, fontSize: 11.5, color: "#5A7290", textAlign: "center", maxWidth: 420, lineHeight: 1.6 }}>
          {t.searching_footer || "Notre IA vérifie chaque croisière en temps réel — comptez jusqu'à 1 minute. Surtout ne rafraîchissez pas la page, on y est presque !"}
        </div>
      </div>
    </div>
  );
}

// ─── FLIGHT BLOCK ──────────────────────────────────────────────────────────────
// Affiche le vol identifié (compagnie, escales, heures, fourchette prix) + bouton Kayak.
function flightBracket(price_cad) {
  if (!price_cad || price_cad <= 0) return null;
  const lower = Math.ceil(price_cad / 500) * 500;
  const upper = lower + 1000;
  return `~${lower.toLocaleString()} – ${upper.toLocaleString()} $`;
}

function FlightBlock({ data, offer, t = {} }) {
  const [showAlt, setShowAlt] = useState(false);
  const [faEmail, setFaEmail]   = useState("");
  const [faStatus, setFaStatus] = useState("idle"); // idle | sending | sent
  const [faError, setFaError]   = useState("");
  const isEN       = t?.app_title === "Cruise AI";
  const fo         = offer.flight_option || null;
  const sameDayRisk = fo?.same_day_boarding === true;
  const hasReturn  = fo && (fo.depart_return || fo.arrive_return);
  const overnight  = data?.overnight === "yes";
  // Suggestion hôtel "arrive la veille" : montrée pour TOUT départ avion où l'usager
  // n'a pas pris de nuit la veille (overnight≠yes), même si Sonnet n'a pas confirmé
  // le flag same_day_boarding (fréquent hors Caraïbes — d'où l'absence d'hôtel en
  // Méditerranée). Honore "toujours offrir un hôtel + suggérer poliment d'arriver la
  // veille". Zéro coût : lien Expedia search par ville du port (pas de web_search).
  const isFlyOffer     = (fo != null) || (offer.flight_cad > 0);
  const sameDaySuggest = !overnight && !sameDayRisk && isFlyOffer;
  const stopsLabel = n => n === 0 || n === "0"
    ? (t.results_flight_direct || "direct")
    : n == null ? "" : `${n} escale${n > 1 ? "s" : ""}`;
  const airline    = fo?.airline || "";
  const bracketTxt = flightBracket(fo?.price_cad);
  // Prix vol Aviasales (Data API, attaché côté worker). Plus net que le bracket
  // estimé du web_search → on l'affiche en priorité quand il existe. `dated` =
  // collé au mois de la croisière ; sinon = tarif indicatif toutes dates.
  const fp        = offer.flight_price || null;
  const fpPrice   = fp && typeof fp.price === "number" && fp.price > 0 ? Math.round(fp.price) : null;
  const fpAirline = airline || fp?.airline || "";
  // Aéroport-proxy : prix d'un aéroport voisin (FLL→Miami) quand le port est vide.
  // On l'affiche en clair (« via Miami ») — transparent + montre l'option moins chère.
  const fpProxyCity = fp?.proxy ? ({ MIA: "Miami", LAX: "Los Angeles", MCO: "Orlando" }[fp.proxy] || fp.proxy) : null;
  // SFB (Orlando Sanford) → MCO (Orlando International) pour l'affichage
  const normAirport = code => code === "SFB" ? "MCO" : code;
  // Repli fiable : si l'IA n'a pas rempli les aéroports, on les déduit du funnel
  // + du port d'embarquement (même source que le lien vol) — jamais de "?".
  const legs   = flightLegs(fo, offer, data);
  const origAp = fo?.origin_airport || legs?.orig || "?";
  const dstAp  = normAirport(fo?.destination_airport) || legs?.dst || "?";
  // Vente de vols ouverte? (>330 j avant départ → fenêtre pas ouverte → notif courriel
  // au lieu du lien). Remonté ici pour que la carte devienne elle-même le bouton.
  const depDateFB      = legs?.depIso || offer?.departure_date;
  const daysUntilFlight = depDateFB ? Math.floor((new Date(depDateFB + "T12:00:00") - new Date()) / 86400000) : 0;
  const flightTooFar   = daysUntilFlight > 330;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>

      {/* Jour même en avion : risque CONFIRMÉ (rouge) OU suggestion d'arriver la veille (or) */}
      {(sameDayRisk || sameDaySuggest) && (
        <div style={{ background: sameDayRisk ? "#E8524A18" : "#C9A84C14", border: `1px solid ${sameDayRisk ? "#E8524A60" : "#C9A84C55"}`, borderRadius: 8, padding: "9px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ ...GL, fontSize: 11, color: sameDayRisk ? "#E8924A" : "#C9A84C", lineHeight: 1.6 }}>
            {sameDayRisk
              ? (t.results_same_day_risk || "⚠️ Vol le jour de l'embarquement — risque de manquer le bateau si retard.")
              : (isEN
                  ? "💡 You're set to arrive on embarkation day. Many travelers prefer arriving the night before for a stress-free start — the ship won't wait if a flight is delayed."
                  : "💡 Tu arrives le jour même de l'embarquement. Plusieurs voyageurs préfèrent arriver la veille pour un départ sans stress — le bateau n'attend pas en cas de retard de vol.")}
          </div>
          <a
            href={buildExpediaHotelUrl(offer, data)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { try { track("hotel_from_sameday_warning"); } catch {} }}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start", padding: "6px 12px", borderRadius: 8, background: "#0A1E35", border: "1px solid #C9A84C55", color: "#C9A84C", ...GL, fontSize: 12, fontWeight: 600, textDecoration: "none" }}
          >
            🏨 {isEN ? "Find a hotel near the port for the night before" : "Trouver un hôtel près du port pour la veille"}
          </a>
        </div>
      )}

      {/* Conseil timing — nuit avant */}
      {overnight && !sameDayRisk && (
        <div style={{ ...GL, fontSize: 11, color: "#7AB0D4" }}>
          ✓ {isEN ? "Night before recommended — protect yourself against delays or cancellations so you never miss your ship" : "Nuit avant recommandée — protégez-vous contre les retards et annulations de vol pour ne jamais manquer votre navire"}
        </div>
      )}

      {/* Carte vol — cliquable (= le bouton de réservation) si vente ouverte, sinon statique (suivie de la notif) */}
      {(() => {
        const inner = (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ ...GL, fontSize: 14, color: "#C8D8E8", fontWeight: 700 }}>
                ✈️ {origAp} → {dstAp}
                {fo?.depart_out && <span style={{ color: "#7A9BB5", fontWeight: 400 }}> · {fo.depart_out}{fo.arrive_out ? ` → ${fo.arrive_out}` : ""}</span>}
              </div>
              <span style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>{stopsLabel(fo?.stops_out)}</span>
            </div>
            {hasReturn && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ ...GL, fontSize: 14, color: "#C8D8E8", fontWeight: 700 }}>
                  ✈️ {dstAp} → {origAp}
                  {fo.depart_return && <span style={{ color: "#7A9BB5", fontWeight: 400 }}> · {fo.depart_return}{fo.arrive_return ? ` → ${fo.arrive_return}` : ""}</span>}
                </div>
                <span style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>{stopsLabel(fo?.stops_return)}</span>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {fpPrice ? (
                <>
                  <span style={{ ...GL, fontSize: 13, color: "#C9A84C", fontWeight: 800 }}>
                    {fpAirline ? `${fpAirline} · ` : ""}{isEN ? "from" : "à partir de"} ~{fpPrice} $/pers
                    {fp.transfers === 0 ? (isEN ? " · nonstop" : " · direct") : ""}
                    {fpProxyCity ? ` · via ${fpProxyCity}` : ""}
                  </span>
                  <span style={{ ...GL, fontSize: 8, color: "#3A5A72", fontStyle: "italic" }}>
                    {isEN
                      ? `round-trip${fp.dated ? ", this month" : ", indicative fare"} — varies by date & availability`
                      : `aller-retour${fp.dated ? ", ce mois-ci" : ", tarif indicatif"} — varie selon date et dispo`}
                  </span>
                </>
              ) : (
                <>
                  <span style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>
                    {airline}{bracketTxt ? ` · ${bracketTxt}` : ""}
                  </span>
                  {bracketTxt && (
                    <span style={{ ...GL, fontSize: 8, color: "#3A5A72", fontStyle: "italic" }}>
                      {isEN ? "estimated — varies by date & availability" : "estimé — varie selon date et dispo"}
                    </span>
                  )}
                </>
              )}
            </div>
          </>
        );
        return flightTooFar ? (
          <div style={{ background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 10, padding: "10px 13px", display: "flex", flexDirection: "column", gap: 5 }}>
            {inner}
          </div>
        ) : (
          <a href={buildFlightUrl(fo, offer, data)} target="_blank" rel="noopener noreferrer"
            onClick={() => track("offer_clicked", { type: "flight" })}
            style={{ display: "block", textDecoration: "none", background: "#0A1E35", border: "1.5px solid #2F4A66", borderRadius: 12, padding: "11px 14px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>{inner}</div>
            <div style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #ffffff10", ...GL, fontSize: 13, fontWeight: 800, color: "#7AB8E8", textAlign: "center" }}>
              ✈️ {isEN ? "Tap to book →" : "Cliquer pour réserver →"}
            </div>
          </a>
        );
      })()}

      {/* Notif courriel si départ >330 j (vente pas ouverte) — sinon la carte ci-dessus EST le bouton de réservation */}
      {flightTooFar && (() => {
        const depDate = depDateFB;
        const opensDate = new Date(new Date(depDate + "T12:00:00").getTime() - 330 * 86400000);
        const opensLabel = opensDate.toLocaleDateString(isEN ? "en-CA" : "fr-CA", { month: "long", year: "numeric" });
        const depFmt = fmtDate(depDate, isEN ? "en" : "fr");
        const adults = legs?.adults || 2;

        const submitAlert = async () => {
          if (!faEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(faEmail)) {
            setFaError(isEN ? "Please enter a valid email." : "Entre un courriel valide.");
            return;
          }
          setFaError(""); setFaStatus("sending");
          try {
            const res = await fetch(WORKER_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                action: "flight_alert",
                email: faEmail,
                origin: origAp, destination: dstAp,
                departure_date: depDate, return_date: legs?.retIso || null,
                opens_label: opensLabel,
                ship_name: offer.ship_name || "", cruise_line: offer.cruise_line || "",
                adults, lang: isEN ? "en" : "fr",
              }),
            });
            const json = await res.json();
            if (!res.ok || json?.error) throw new Error(json?.error?.message || `Error ${res.status}`);
            try { track("flight_alert_requested", { route: `${origAp}-${dstAp}`, departure: depDate }); } catch {}
            setFaStatus("sent");
          } catch (e) {
            setFaError(isEN ? "Network error. Please try again." : "Erreur réseau. Réessaie.");
            setFaStatus("idle");
          }
        };

        return (
          <>
            <div style={{ background: "#1A3A5C22", border: "1px solid #4A90C455", borderRadius: 10, padding: "10px 13px", ...GL, fontSize: 12.5, color: "#A8C0D0", lineHeight: 1.65 }}>
              📅 {isEN
                ? `Flight booking typically opens ~11 months before departure. For your ${depFmt} trip, airlines will release seats around ${opensLabel}.`
                : `La réservation des vols ouvre généralement ~11 mois avant le départ. Pour ton voyage du ${depFmt}, les compagnies vont mettre les places en vente vers ${opensLabel}.`}
            </div>
            {faStatus === "sent" ? (
              <div style={{ background: "#4A90C418", border: "1px solid #4A90C455", borderRadius: 10, padding: "12px 14px", ...GL, fontSize: 13, color: "#7AB8E8", lineHeight: 1.6, textAlign: "center" }}>
                ✅ {isEN
                  ? `You're on the list! We'll email ${faEmail} when flights open for this route.`
                  : `C'est noté ! On écrira à ${faEmail} dès que les vols ouvrent pour ce trajet.`}
              </div>
            ) : (
              <>
                <div style={{ ...GL, fontSize: 11.5, color: "#A8C0D0", textAlign: "center" }}>
                  {isEN ? "Get a free heads-up by email when flights open — no spam." : "Reçois un avis gratuit par courriel quand les vols ouvrent — pas de spam."}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input type="email" inputMode="email" placeholder={isEN ? "your@email.com" : "votre@courriel.com"}
                    value={faEmail} onChange={e => setFaEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && submitAlert()}
                    style={{ flex: 1, background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 10, padding: "11px 12px", color: "#C8D8E8", ...GL, fontSize: 15, outline: "none", boxSizing: "border-box", minWidth: 0 }} />
                  <button onClick={submitAlert} disabled={faStatus === "sending"}
                    style={{ flexShrink: 0, padding: "11px 16px", borderRadius: 10, background: "linear-gradient(135deg,#C9A84C,#A8882A)", border: "none", color: "#07111F", ...GL, fontWeight: 900, fontSize: 14, cursor: faStatus === "sending" ? "wait" : "pointer", whiteSpace: "nowrap" }}>
                    {faStatus === "sending" ? "…" : `🔔 ${isEN ? "Notify me" : "M'aviser"}`}
                  </button>
                </div>
                {faError && <div style={{ ...GL, fontSize: 11.5, color: "#E8524A", textAlign: "center" }}>{faError}</div>}
              </>
            )}
          </>
        );
      })()}

      {/* Alternative vol */}
      {fo?.alternative && (fo.alternative.airline || fo.alternative.price_cad) && (
        <>
          <button onClick={() => setShowAlt(s => !s)}
            style={{ background: "transparent", border: "1px dashed #4A90C455", borderRadius: 8, padding: "6px 10px", color: "#7AB8E8", ...GL, fontSize: 13, cursor: "pointer", textAlign: "left" }}>
            {showAlt ? "▾" : (t.results_alt_flight || "▸ Alternative vol")}{fo.alternative.trade_off ? ` — ${fo.alternative.trade_off}` : ""}
          </button>
          {showAlt && (
            <div style={{ background: "#0A1E35", border: "1px solid #1A2F4580", borderRadius: 10, padding: "8px 11px", display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={{ display: "flex", justifyContent: "space-between", ...GL, fontSize: 13, color: "#A8C0D0" }}>
                <span>{fo.alternative.airline || "Autre vol"} · {stopsLabel(fo.alternative.stops_out)}</span>
                {fo.alternative.price_cad > 0 && <span style={{ color: "#C9A84C", fontWeight: 700 }}>{flightBracket(fo.alternative.price_cad)}</span>}
              </div>
              {fo.alternative.trade_off && <div style={{ ...GL, fontSize: 11, color: "#5A7A95", fontStyle: "italic" }}>{fo.alternative.trade_off}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── HOTEL BLOCK ───────────────────────────────────────────────────────────────
// Affiche l'hôtel identifié pour la nuit avant l'embarquement + toggle alternative.
// Priorité au lien Booking.com deep-link retourné par l'IA, sinon lien généré.
function HotelBlock({ data, offer, secondary = false, t = {}, isSuggestion = false }) {
  const [showAlt, setShowAlt] = useState(false);
  const ho       = offer.hotel_option || null;
  const portCity = (offer.port || "Miami").replace(/,.*/, "").trim();
  const isEN     = t?.app_title === "Cruise AI";

  // Hôtels curatés : si port connu → remplace le nom trouvé par Claude (fiable + navettes garanties)
  const portKey     = getCuratedPortKey(portCity);
  const tier        = data?.hotel_stars || "midrange";
  const curatedList = portKey ? (CURATED_HOTELS[portKey]?.[tier] || []) : [];
  const curated     = curatedList[0] || null;
  const curatedAlt  = curatedList[1] || null;

  const displayName  = curated ? curated.name  : ho?.hotel_name;
  const displayStars = curated ? curated.stars : ho?.hotel_stars;
  const displayNote  = curated ? (isEN ? curated.note_en : curated.note_fr) : null;
  const displayPrice = ho?.price_cad;
  const displayUrl   = curated
    ? buildExpediaCuratedHotelUrl(curated, offer, data)
    : buildExpediaHotelUrl(offer, data);

  const altName  = curatedAlt ? curatedAlt.name  : ho?.alternative?.hotel_name;
  const altStars = curatedAlt ? curatedAlt.stars : ho?.alternative?.hotel_stars;
  const altNote  = curatedAlt
    ? (isEN ? curatedAlt.note_en : curatedAlt.note_fr)
    : ho?.alternative?.trade_off;
  const altPrice = curatedAlt ? null : ho?.alternative?.price_cad;
  const altUrl   = curatedAlt
    ? buildExpediaCuratedHotelUrl(curatedAlt, offer, data)
    : buildExpediaHotelUrl({ ...offer, hotel_option: ho?.alternative }, data);
  const hasAlt = !!(altName || altNote);

  const starsStr = n => n ? "★".repeat(n) : "";
  const shuttleTag = (airport, port) => {
    const tags = [];
    if (airport) tags.push(t.results_shuttle_airport || "navette aéroport ✓");
    if (port)    tags.push(t.results_shuttle_port    || "navette port ✓");
    if (!airport && !port) tags.push(t.results_shuttle_check || "vérifier navettes");
    return tags.join(" · ");
  };

  const suggestionReason = isSuggestion
    ? (isEN
        ? "💡 Arrive the night before to avoid road surprises"
        : "💡 Arriver la veille évite les imprévus de la route")
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div style={{ ...GL, fontSize: 9, color: isSuggestion ? "#C9A84C99" : "#3A5A72", textTransform: "uppercase", letterSpacing: "0.08em", paddingLeft: 2 }}>
        🏨 {isSuggestion ? (isEN ? "Hotel near the port" : "Hôtel près du port") : secondary ? (t.results_hotel_label ? `${t.results_hotel_label} (option)` : "Hôtel seul (option)") : (t.results_hotel_label || "Hôtel la veille")} — {portCity}
      </div>

      {displayName ? (
        <a href={displayUrl} target="_blank" rel="noopener noreferrer"
          onClick={() => track("offer_clicked", { type: "hotel" })}
          style={{ display: "block", textDecoration: "none", background: "#0A1E35", border: "1.5px solid #2A4A33", borderRadius: 12, padding: "11px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
            <div style={{ ...GL, fontSize: 14, color: "#C8D8E8", fontWeight: 700 }}>
              {displayName}
              {displayStars && <span style={{ color: "#C9A84C", fontWeight: 400, marginLeft: 6 }}>{starsStr(displayStars)}</span>}
            </div>
            {displayPrice > 0 && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
                <div style={{ ...GL, fontSize: 14, color: "#C9A84C", fontWeight: 900 }}>~{displayPrice.toLocaleString()} $</div>
                <div style={{ ...GL, fontSize: 9, color: "#3A5A72" }}>{t.results_estimated || "prix estimé"}</div>
              </div>
            )}
          </div>
          <div style={{ ...GL, fontSize: 11, lineHeight: 1.55, marginTop: 4 }}>
            {suggestionReason && (<span style={{ color: "#E8C870", fontWeight: 700 }}>{suggestionReason}</span>)}
            {suggestionReason && <span style={{ color: "#5A7A95" }}> · </span>}
            <span style={{ color: "#5A7A95" }}>{displayNote || shuttleTag(ho?.airport_shuttle, ho?.port_shuttle)}</span>
          </div>
          {!curated && ho?.address && (<div style={{ ...GL, fontSize: 11, color: "#3A5A72", marginTop: 2 }}>{ho.address}</div>)}
          <div style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #ffffff10", ...GL, fontSize: 13, fontWeight: 800, color: "#7AE890", textAlign: "center" }}>
            🏨 {isEN ? "Tap to book →" : "Cliquer pour réserver →"}
          </div>
        </a>
      ) : (
        <>
          <div style={{ ...GL, fontSize: 11, color: "#3A5A72", paddingLeft: 2 }}>
            {t.results_filtered || "Filtré : navette aéroport · navette port incluse"}
          </div>
          <a href={displayUrl} target="_blank" rel="noopener noreferrer"
            onClick={() => track("offer_clicked", { type: "hotel" })}
            style={{ display: "block", padding: "12px 14px", borderRadius: 10, background: "linear-gradient(135deg,#1A4A2A,#0D2A15)", border: "1px solid #4A9C5A", color: "#7AE890", ...GL, fontWeight: 700, fontSize: 15, textDecoration: "none", textAlign: "center" }}>
            {t.results_book_hotel || "🏨 Réserver l'hôtel sur Expedia →"}
          </a>
        </>
      )}

      {hasAlt && (
        <>
          <button
            onClick={() => setShowAlt(s => !s)}
            style={{ background: "transparent", border: "1px dashed #C9A84C44", borderRadius: 8, padding: "6px 10px", color: "#A89050", ...GL, fontSize: 13, cursor: "pointer", textAlign: "left" }}
          >
            {showAlt ? "▾" : (t.results_alt_hotel || "▸ Voir alternative hôtel")}{!curatedAlt && altNote ? ` — ${altNote}` : ""}
          </button>
          {showAlt && (
            <div style={{ background: "#0A1E35", border: "1px solid #1A2F4580", borderRadius: 10, padding: "8px 11px", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ display: "flex", justifyContent: "space-between", ...GL, fontSize: 13, color: "#A8C0D0" }}>
                <span>
                  {altName || "Autre hôtel"}
                  {altStars && <span style={{ color: "#C9A84C99" }}> {starsStr(altStars)}</span>}
                </span>
                {altPrice > 0 && <span style={{ color: "#C9A84C", fontWeight: 700 }}>~{altPrice.toLocaleString()} $ <span style={{ fontSize: 9, color: "#3A5A72", fontWeight: 400 }}>estimé</span></span>}
              </div>
              <div style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>
                {curatedAlt
                  ? (isEN ? curatedAlt.note_en : curatedAlt.note_fr)
                  : shuttleTag(ho?.alternative?.airport_shuttle, ho?.alternative?.port_shuttle)}
              </div>
              {!curatedAlt && altNote && (
                <div style={{ ...GL, fontSize: 11, color: "#5A7A95", fontStyle: "italic" }}>{altNote}</div>
              )}
              <a href={altUrl} target="_blank" rel="noopener noreferrer"
                onClick={() => track("offer_clicked", { type: "hotel", alt: true })}
                style={{ display: "block", padding: "8px 12px", borderRadius: 8, background: "linear-gradient(135deg,#1A4A2A,#0D2A15)", border: "1px solid #4A9C5A", color: "#7AE890", ...GL, fontWeight: 700, fontSize: 14, textDecoration: "none", textAlign: "center" }}>
                {t.results_hotel_alt_book || "🏨 Réserver cet hôtel sur Expedia →"}
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── DESTINATION THUMB ─────────────────────────────────────────────────────────
// Petit composant isolé pour pouvoir appeler `useWikiImage` à l'intérieur d'un
// `.map()` (les hooks doivent être appelés au top-level d'un composant).
function DestThumb({ d, srcOverride }) {
  // Photo curée SPÉCIFIQUE (ex: Cozumel, Costa Maya, Santorini) → elle gagne, elle est
  // hand-pick et fiable. Seulement quand la curée n'est qu'un repli PAYS générique
  // (Málaga/Naples/Kyoto → photo du pays) on va chercher la VILLE réelle sur Wikipédia.
  // useWikiImage retombe sur `staticUrl` si Wikipédia n'a rien d'utilisable (isUsablePhoto
  // filtre cartes/drapeaux/logos) et onError retombe dessus si l'URL casse.
  // EXCEPTION : îles privées (Wikipédia peu fiable) → curée DOIT gagner même si clé≠fichier.
  // srcOverride : photo imposée par le filet anti-doublon de l'itinéraire (gagne sur tout).
  const match     = getDestMatch(d.name);
  const staticUrl = srcOverride || d.image_url || match.file;
  const DEST_PINNED = ["perfect day", "cococay", "coco cay", "great stirrup", "stirrup cay", "labadee"];
  const nameLC  = (d.name || "").toLowerCase();
  const pinned  = DEST_PINNED.some(k => nameLC.includes(k));
  const useCurated = !!srcOverride || pinned || match.specific || match.trusted;   // curée fiable → pas de Wikipédia
  const wikiUrl = useWikiImage(d.name, staticUrl, "destination");
  const [broken, setBroken] = useState(false);
  const src = broken ? staticUrl : (useCurated ? staticUrl : wikiUrl);
  return (
    <div style={{ flex: 1, position: "relative", borderRadius: 10, overflow: "hidden", height: 75 }}>
      <img
        src={src}
        alt={d.name}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
        onError={() => setBroken(true)}
      />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 55%)" }} />
      <div style={{ position: "absolute", bottom: 5, left: 7, ...GL, fontSize: 8, color: "#fff", fontWeight: 700 }}>{d.name}</div>
    </div>
  );
}

// ─── OFFRES DU MOMENT (accueil) ──────────────────────────────────────────────
// Section « Offres du moment » : promos affiliées CruiseDirect (liens CJ vérifiés,
// ID éditeur 101729213 = commission à nous). Liens permanents : on remplace le
// contenu quand une nouvelle promo arrive. Le lien « dernière minute » reste
// pertinent en continu. Clics tracés (track deal_clicked) pour mesurer l'intérêt.
const CURRENT_DEALS = [
  {
    id: "royal_btc",
    url: "https://www.jdoqocy.com/click-101729213-15534639",
    emoji: "🚢",
    title:   { fr: "Royal Caribbean — Vente « Beat the Clock »", en: "Royal Caribbean — Beat the Clock Sale" },
    desc:    { fr: "Économies instantanées, crédit à bord, souper spécialité gratuit et rabais sur le 2ᵉ passager.",
               en: "Instant savings, onboard credit, free specialty dining and a discount on the 2nd guest." },
    cta:     { fr: "Voir l'offre →", en: "See the deal →" },
    tag:     { fr: "Durée limitée", en: "Limited time" },
  },
  {
    id: "last_minute",
    url: "https://www.tkqlhce.com/click-101729213-15534673",
    emoji: "⏰",
    title:   { fr: "Croisières de dernière minute", en: "Last-Minute Cruise Deals" },
    desc:    { fr: "Des départs proches à prix réduits, mis à jour en continu par CruiseDirect.",
               en: "Near-term departures at reduced prices, updated continuously by CruiseDirect." },
    cta:     { fr: "Voir les départs →", en: "See departures →" },
    tag:     { fr: "Mis à jour", en: "Updated" },
  },
];

function CurrentDealsSection({ lang = "fr" }) {
  const isEN = lang === "en";
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 12 }}>
        <span style={{ ...GF, fontWeight: 800, fontSize: 16, color: "#E8D9A8" }}>
          🔥 {isEN ? "Current deals" : "Offres du moment"}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {CURRENT_DEALS.map(d => (
          <a
            key={d.id}
            href={d.url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={() => { try { track("deal_clicked", { deal: d.id }); } catch {} }}
            style={{
              display: "flex", alignItems: "center", gap: 14, textDecoration: "none",
              background: "linear-gradient(135deg, rgba(201,168,76,0.12), rgba(201,168,76,0.04))",
              border: "1px solid #C9A84C66", borderRadius: 14, padding: "14px 16px",
            }}
          >
            <span style={{ fontSize: 26, lineHeight: 1 }}>{d.emoji}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ ...GF, fontWeight: 700, fontSize: 15, color: "#F0E6C8" }}>{d.title[lang] || d.title.fr}</span>
                <span style={{ ...GL, fontSize: 11, fontWeight: 700, color: "#07111F", background: "#C9A84C", borderRadius: 999, padding: "2px 8px" }}>{d.tag[lang] || d.tag.fr}</span>
              </span>
              <span style={{ display: "block", ...GL, fontSize: 13, color: "#9DB2C7", marginTop: 4 }}>{d.desc[lang] || d.desc.fr}</span>
            </span>
            <span style={{ ...GL, fontWeight: 700, fontSize: 13, color: "#C9A84C", whiteSpace: "nowrap" }}>{d.cta[lang] || d.cta.fr}</span>
          </a>
        ))}
      </div>
      <div style={{ ...GL, fontSize: 11, color: "#5A7A95", textAlign: "center", marginTop: 8 }}>
        {isEN
          ? "Affiliate links — we may earn a commission, at no extra cost to you. Prices in USD on CruiseDirect."
          : "Liens partenaires — nous pouvons toucher une commission, sans coût supplémentaire pour vous. Prix en USD sur CruiseDirect."}
      </div>
    </div>
  );
}

// ─── EXEMPLE DE RÉSULTAT (accueil) ───────────────────────────────────────────
// Montre CONCRÈTEMENT un résultat (photo navire Wikipédia + détail croisière/vol/
// hôtel) AVANT que l'usager investisse l'effort du funnel — attaque la fuite
// accueil→recherche (25 visiteurs → 7 recherches, 2026-06-01). Clairement étiqueté
// « Exemple » : jamais présenté comme un prix live (philosophie anti-invention).
// Composant isolé pour pouvoir appeler le hook useWikiImage (photo réelle du navire).
function ExampleResultCard({ lang = "fr" }) {
  const isEN      = lang === "en";
  const SHIP      = "Symphony of the Seas";
  const staticImg = getShipImg(SHIP);
  const wikiImg   = useWikiImage(SHIP, staticImg, "ship");
  const [broken, setBroken] = useState(false);
  const src = broken ? staticImg : wikiImg;
  const rows = [
    [isEN ? "🚢 Cruise (inside cabin)"        : "🚢 Croisière (cabine intérieure)", "~1 690 $"],
    [isEN ? "✈️ Flight (estimate)"            : "✈️ Vol (estimé)",                  "~520 $"],
    [isEN ? "🏨 Port hotel, night before"     : "🏨 Hôtel près du port, la veille",  "~130 $"],
  ];
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ textAlign: "center", ...GL, fontSize: 12.5, color: "#5A7A95", marginBottom: 10, letterSpacing: "0.04em" }}>
        👀 {isEN ? "What a result looks like" : "À quoi ressemble un résultat"}
      </div>
      <div style={{ background: "#091929", border: "1px solid #C9A84C33", borderRadius: 18, overflow: "hidden", boxShadow: "0 16px 36px rgba(0,0,0,0.45)" }}>
        <div style={{ position: "relative", height: 150 }}>
          {src ? (
            <img src={src} alt={SHIP} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={() => setBroken(true)} />
          ) : (
            <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#0A1E35,#1A3A5A)", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ fontSize: 46, opacity: 0.15 }}>🚢</span></div>
          )}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, #091929 0%, rgba(9,25,41,0.15) 55%, transparent 100%)" }} />
          <div style={{ position: "absolute", top: 0, left: 0, display: "inline-flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", borderRadius: "0 0 10px 0", padding: "5px 13px", ...GL, fontSize: 12, fontWeight: 900, letterSpacing: "0.04em" }}>
            🥇 {isEN ? "Recommended" : "Recommandé"}
          </div>
          <span style={{ position: "absolute", top: 9, right: 10, background: "rgba(9,25,41,0.85)", border: "1px solid #2A4A66", color: "#7AB8E8", ...GL, fontSize: 10.5, fontWeight: 800, padding: "3px 9px", borderRadius: 7, letterSpacing: "0.06em" }}>
            {isEN ? "EXAMPLE" : "EXEMPLE"}
          </span>
        </div>
        <div style={{ padding: "0 16px 16px", marginTop: -2 }}>
          <div style={{ ...GF, fontSize: 17, color: "#E8D9A8", fontWeight: 700, marginBottom: 2 }}>{SHIP}</div>
          <div style={{ ...GL, fontSize: 12.5, color: "#7A9BB5", marginBottom: 9 }}>Royal Caribbean · 7 {isEN ? "nights" : "nuits"}</div>
          <div style={{ ...GL, fontSize: 13, color: "#A8C0D0", lineHeight: 1.7, marginBottom: 11 }}>
            🗓️ {isEN ? "Jan 18 → Jan 25, 2027" : "18 janv → 25 janv 2027"}<br />
            🚢 Miami → CocoCay → St. Thomas → St. Maarten → Miami
          </div>
          <div style={{ background: "#0A1E33", border: "1px solid #1A3050", borderRadius: 10, padding: "10px 13px", marginBottom: 11 }}>
            {rows.map(([k, v], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", ...GL, fontSize: 12.5, color: "#9AB8D0", padding: "2px 0" }}>
                <span>{k}</span><span style={{ color: "#C8D8E8" }}>{v}</span>
              </div>
            ))}
            <div style={{ borderTop: "1px solid #1A3050", marginTop: 6, paddingTop: 7, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ ...GL, fontSize: 12.5, color: "#7A9BB5" }}>{isEN ? "Total / person" : "Total / personne"}</span>
              <span style={{ ...GF, fontSize: 21, color: "#7AE890", fontWeight: 800 }}>~2 340 $</span>
            </div>
          </div>
          <div style={{ background: "#0A1E33", border: "1px solid #C9A84C33", borderRadius: 10, padding: "9px 12px", ...GL, fontSize: 12.5, color: "#9AB8D0", lineHeight: 1.6 }}>
            💡 {isEN
              ? "Best balance of price, ship freshness and itinerary for a January family trip — confirmed departure date, real ports, flight + port hotel handled."
              : "Meilleur équilibre prix / fraîcheur du navire / itinéraire pour un voyage familial en janvier — date de départ confirmée, vrais ports, vol + hôtel près du port gérés."}
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", ...GL, fontSize: 11, color: "#3A5A72", marginTop: 8, lineHeight: 1.5, padding: "0 8px" }}>
        {isEN
          ? "Illustrative example. Your real results show live prices and confirmed departures for your own criteria."
          : "Exemple illustratif. Vos vrais résultats affichent les prix du jour et les départs confirmés selon vos propres critères."}
      </div>
    </div>
  );
}

// ─── RESULT CARD ───────────────────────────────────────────────────────────────

// Opt-in « jamais vide » : sur un résultat élargi/compromis, l'usager laisse son courriel.
// On analyse le soir (digest) et on lui écrit gratuitement si on trouve mieux. Avertissement
// pourriel inclus (Hotmail/Outlook capricieux). 100 % opt-in, aucune relance non sollicitée.
// Modale admin : affiche le lien-résultat partageable + un gabarit de courriel
// prêt à copier-coller (envoi manuel depuis bonjour@croisiereai.ca). Sert les
// retours usagers : on relance la recherche, on partage le bon résultat, l'usager
// reste sur notre site avec nos liens affiliés.
function ShareResultModal({ info, data, lang, onClose, onRetry }) {
  const [copied, setCopied] = useState(""); // "link" | "mail"
  const url = info?.url || "";
  const defaultMail =
    "Bonjour,\n\n" +
    "Merci d'avoir pris le temps de nous faire un retour — vous aviez raison, l'offre proposée ne correspondait pas tout à fait. J'ai repris votre recherche personnellement.\n\n" +
    "Voici ce qu'on trouve de mieux pour vos critères :\n" +
    "👉 " + url + "\n\n" +
    "Le lien ouvre directement vos résultats sur Croisière AI. Dites-moi si une de ces croisières vous parle — je peux affiner avec vous.\n\n" +
    "Au plaisir,\nSimon — Croisière AI\nbonjour@croisiereai.ca";
  const [mail, setMail] = useState(defaultMail);
  const copy = (text, which) => {
    try { navigator.clipboard.writeText(text); setCopied(which); setTimeout(() => setCopied(""), 1800); } catch {}
  };
  const overlay = { position: "fixed", inset: 0, background: "rgba(4,9,16,0.78)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
  const box = { background: "#0C1726", border: "1px solid #2A4A68", borderRadius: 16, padding: "22px 22px 18px", maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" };
  const btn = (active) => ({ background: active ? "rgba(122,232,144,0.12)" : "transparent", border: `1px solid ${active ? "#7AE890" : "#7AE89066"}`, borderRadius: 9, padding: "7px 13px", color: "#7AE890", ...GL, fontSize: 12.5, cursor: "pointer", fontWeight: 600 });
  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ ...GL, fontSize: 17, color: "#E8DCC8", fontWeight: 700 }}>🔗 Lien-résultat partageable</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8AA", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        {info?.error ? (
          <div style={{ ...GL, fontSize: 14, color: "#E8A0A0", lineHeight: 1.5 }}>
            La génération du lien a échoué.
            {typeof info.error === "string" && <div style={{ fontSize: 12.5, color: "#C99", marginTop: 6, fontFamily: "monospace" }}>Détail : {info.error}</div>}
            <div style={{ marginTop: 10 }}><button onClick={onRetry} style={btn(false)}>Réessayer</button></div>
          </div>
        ) : (
          <>
            <div style={{ ...GL, fontSize: 13, color: "#8FB0C8", lineHeight: 1.55, marginBottom: 14 }}>
              Ce lien ouvre <b>ce résultat figé</b> sur notre site (offres + nos liens affiliés). Colle-le dans ta réponse à l'usager. Valable ~4 mois.
            </div>

            <div style={{ ...GL, fontSize: 11.5, color: "#7A9BB5", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>Le lien</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
              <input readOnly value={url} onFocus={e => e.target.select()} style={{ flex: 1, background: "#08111E", border: "1px solid #2A4A68", borderRadius: 9, padding: "8px 10px", color: "#CFE0EE", ...GL, fontSize: 12.5, minWidth: 0 }} />
              <button onClick={() => copy(url, "link")} style={btn(copied === "link")}>{copied === "link" ? "✓ Copié" : "Copier"}</button>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <div style={{ ...GL, fontSize: 11.5, color: "#7A9BB5", textTransform: "uppercase", letterSpacing: 0.5 }}>Gabarit de courriel (modifiable)</div>
              <button onClick={() => copy(mail, "mail")} style={btn(copied === "mail")}>{copied === "mail" ? "✓ Copié" : "Copier le courriel"}</button>
            </div>
            <textarea value={mail} onChange={e => setMail(e.target.value)} rows={11} style={{ width: "100%", background: "#08111E", border: "1px solid #2A4A68", borderRadius: 9, padding: "10px 12px", color: "#CFE0EE", ...GL, fontSize: 13, lineHeight: 1.5, resize: "vertical", boxSizing: "border-box" }} />
          </>
        )}
      </div>
    </div>
  );
}

// Panneau admin « Retours usagers » : liste les recherches 👎 ou à drapeau
// (action worker `traces` only_problems). Pour chaque cas : texte original (si
// agent), critères captés, offres montrées. Deux actions : « Relancer » (charge
// les critères dans le récap modifiable) et « Copier le contexte » (pour me le
// coller en terminal et que je diagnostique avec accès cache + code).
function summarizeTraceParams(p) {
  if (!p) return "—";
  const parts = [];
  if (p.specific_ship) parts.push("🚢 " + p.specific_ship);
  if ((p.regions || []).filter(r => r && r !== "any").length) parts.push("📍 " + p.regions.filter(r => r && r !== "any").join("/"));
  if ((p.companies || []).length) parts.push("🏷️ " + p.companies.join("/"));
  if ((p.ship_class || []).length) parts.push("💎 " + p.ship_class.join("/"));
  if (p.exact_nights) parts.push(p.exact_nights + " nuits");
  else if (p.duration) parts.push(p.duration);
  if (p.dates?.from) parts.push(p.dates.from + (p.dates.to ? "→" + p.dates.to : ""));
  else if (p.dates?.month) parts.push(p.dates.month + "/" + (p.dates.year || ""));
  if (p.budget) parts.push(p.budget + "$");
  if (p.family_detail?.adults) parts.push(p.family_detail.adults + " ad.");
  else if (p.group_size) parts.push(p.group_size + " pers.");
  if (p.departure) parts.push("✈️ " + p.departure + (p.origin_airport ? " (" + p.origin_airport + ")" : ""));
  if ((p.priorities || []).length) parts.push("⭐ " + p.priorities.join(","));
  return parts.join(" · ") || "—";
}

function AdminReturnsModal({ getToken, onClose, onRelaunch }) {
  const [state, setState] = useState("loading"); // loading | ok | error
  const [traces, setTraces] = useState([]);
  const [followups, setFollowups] = useState([]);  // demandes de suivi (avec courriel)
  const [copied, setCopied] = useState("");
  const [relaunching, setRelaunching] = useState(null); // id de la trace en préparation
  const [busyFu, setBusyFu] = useState(null);       // id du followup en cours de maj
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const [rt, fu] = await Promise.all([
          fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "traces", only_problems: "1", limit: 50, token }) }).then(x => x.json()).catch(() => ({})),
          kvListFollowups(getToken),
        ]);
        if (cancelled) return;
        if (rt.ok && Array.isArray(rt.traces)) { setTraces(rt.traces); setState("ok"); }
        else setState(Array.isArray(fu) ? "ok" : "error");
        if (Array.isArray(fu)) setFollowups(fu);
      } catch { if (!cancelled) setState("error"); }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const markFollowup = async (id, op) => {
    setBusyFu(id);
    const ok = await kvUpdateFollowup(getToken, id, op);
    setBusyFu(null);
    if (!ok) return;
    if (op === "delete") setFollowups(prev => prev.filter(f => f.id !== id));
    else setFollowups(prev => prev.map(f => f.id === id ? { ...f, handled: op === "done" } : f));
  };
  const fuSummary = (f) => {
    const s = f.summary || {};
    const parts = [];
    if (s.specific_ship) parts.push("🚢 " + s.specific_ship);
    if ((s.regions || []).length) parts.push("📍 " + s.regions.join("/"));
    if ((s.companies || []).length) parts.push("🏷️ " + s.companies.join("/"));
    if (s.duration) parts.push(s.duration);
    if (s.dates?.from) parts.push(s.dates.from + (s.dates.to ? "→" + s.dates.to : ""));
    else if (s.dates?.month) parts.push(s.dates.month + "/" + (s.dates.year || ""));
    if (s.budget) parts.push(s.budget + "$");
    return parts.join(" · ") || "—";
  };

  const buildContext = (t) => {
    const lines = [];
    lines.push("=== RETOUR USAGER (" + (t.rating === "down" ? "👎" : "🚩 drapeau") + ") ===");
    lines.push("Date : " + (t.ts || "?") + " · lancé via : " + (t.init || "?"));
    if (t.agent_text) lines.push('Texte original : "' + t.agent_text + '"');
    lines.push("Critères captés : " + summarizeTraceParams(t.params));
    lines.push("Critères (brut) : " + JSON.stringify(t.params || {}));
    lines.push("Résultat : " + (t.result?.count ?? "?") + " offre(s)" + ((t.result?.flags || []).length ? " · drapeaux: " + t.result.flags.join(",") : ""));
    (t.result?.offers || []).slice(0, 8).forEach((o, i) => {
      lines.push("  " + (i + 1) + ". " + (o.ship || "?") + " " + (o.date || "") + " — " + (o.itin || "").slice(0, 90));
    });
    return lines.join("\n");
  };
  const copy = (t) => {
    try { navigator.clipboard.writeText(buildContext(t)); setCopied(t.id); setTimeout(() => setCopied(""), 1800); } catch {}
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(4,9,16,0.82)", zIndex: 9999, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "4vh 12px", overflowY: "auto" };
  const box = { background: "#0C1726", border: "1px solid #2A4A68", borderRadius: 16, padding: "22px", maxWidth: 760, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" };
  const sbtn = (active, color) => ({ background: active ? color + "22" : "transparent", border: `1px solid ${color}66`, borderRadius: 9, padding: "6px 11px", color, ...GL, fontSize: 12, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ ...GL, fontSize: 17, color: "#E8DCC8", fontWeight: 700 }}>📋 Retours usagers <span style={{ fontSize: 13, color: "#7A9BB5", fontWeight: 400 }}>(👎 + résultats à drapeau · 30 derniers jours)</span></div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8AA", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        {state === "loading" && <div style={{ ...GL, fontSize: 14, color: "#8FB0C8", padding: "20px 0", textAlign: "center" }}>Chargement…</div>}
        {state === "error" && <div style={{ ...GL, fontSize: 14, color: "#E8A0A0", padding: "20px 0", textAlign: "center" }}>Échec du chargement des retours.</div>}
        {state === "ok" && traces.length === 0 && followups.length === 0 && <div style={{ ...GL, fontSize: 14, color: "#8FCFA5", padding: "20px 0", textAlign: "center" }}>✅ Aucun retour. Tout roule.</div>}

        {/* ── 📬 À RÉPONDRE : demandes de suivi avec courriel (les vraies réponses clients) ── */}
        {state === "ok" && followups.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...GL, fontSize: 13, color: "#C9A84C", fontWeight: 700, marginBottom: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>📬 À répondre ({followups.filter(f => !f.handled).length})</div>
            {followups.map((f) => (
              <div key={f.id} style={{ background: f.handled ? "rgba(90,122,149,0.04)" : "rgba(201,168,76,0.07)", border: `1px solid ${f.handled ? "#2A4A6840" : "#C9A84C44"}`, borderRadius: 12, padding: "13px 14px", marginBottom: 10, opacity: f.handled ? 0.6 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                  <span style={{ ...GL, fontSize: 13.5, color: "#E8DCC8", fontWeight: 700 }}>
                    {f.handled ? "✓ " : ""}{f.rating === "down" ? "👎 Offre pas aimée" : f.context === "empty" ? "🚫 Aucun résultat" : "📬 Résultat élargi"}
                    <span style={{ fontWeight: 400, color: "#7A9BB5", marginLeft: 8, fontSize: 12 }}>{fmtMtl(f.ts)}</span>
                  </span>
                </div>
                {/* COURRIEL — cliquable pour répondre directement */}
                <a href={`mailto:${f.email}`} style={{ ...GL, fontSize: 13.5, color: "#7AB8E8", fontWeight: 600, textDecoration: "none", display: "inline-block", marginBottom: 6 }}>📧 {f.email}</a>
                {f.reason && <div style={{ ...GL, fontSize: 12.5, color: "#D4B86A", fontStyle: "italic", lineHeight: 1.5, marginBottom: 6 }}>« {f.reason} »</div>}
                <div style={{ ...GL, fontSize: 12.5, color: "#9FC0D8", lineHeight: 1.55, marginBottom: 6 }}>{fuSummary(f)}</div>
                {f.offer?.ship && <div style={{ ...GL, fontSize: 11.5, color: "#7A9BB5", marginBottom: 9 }}>🚢 Offre visée : {f.offer.ship}{f.offer.date ? " · " + f.offer.date : ""}</div>}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={async () => { setRelaunching("fu_" + f.id); try { await onRelaunch({ params: f.summary || {} }); } catch { setRelaunching(null); } }} disabled={relaunching === "fu_" + f.id} style={sbtn(false, "#C9A84C")}>{relaunching === "fu_" + f.id ? "⏳…" : "↻ Relancer (récap)"}</button>
                  <button onClick={() => markFollowup(f.id, f.handled ? "reopen" : "done")} disabled={busyFu === f.id} style={sbtn(f.handled, "#7AE890")}>{f.handled ? "↩ Rouvrir" : "✓ Fait"}</button>
                  <button onClick={() => markFollowup(f.id, "delete")} disabled={busyFu === f.id} style={{ ...sbtn(false, "#C77"), marginLeft: "auto" }}>🗑 Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {state === "ok" && traces.length > 0 && <div style={{ ...GL, fontSize: 13, color: "#7A9BB5", fontWeight: 700, marginBottom: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>🔎 Diagnostic (sans courriel)</div>}
        {state === "ok" && traces.map((t) => (
          <div key={t.id} style={{ background: "rgba(90,122,149,0.07)", border: "1px solid #2A4A6855", borderRadius: 12, padding: "13px 14px", marginBottom: 11 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6, marginBottom: 7 }}>
              <span style={{ ...GL, fontSize: 13.5, color: "#E8DCC8", fontWeight: 700 }}>
                {t.rating === "down" ? "👎 Offre pas aimée" : "🚩 Résultat à drapeau"}
                <span style={{ fontWeight: 400, color: "#7A9BB5", marginLeft: 8, fontSize: 12 }}>{fmtMtl(t.ts)} · {t.init}</span>
              </span>
              <span style={{ ...GL, fontSize: 12, color: t.result?.count ? "#8FCFA5" : "#E8A0A0" }}>{t.result?.count ?? "?"} offre(s){(t.result?.flags || []).length ? " · " + t.result.flags.join(",") : ""}</span>
            </div>
            {t.agent_text && <div style={{ ...GL, fontSize: 12.5, color: "#B8C9D8", fontStyle: "italic", lineHeight: 1.5, marginBottom: 7, padding: "7px 10px", background: "#08111E", borderRadius: 8 }}>« {t.agent_text} »</div>}
            <div style={{ ...GL, fontSize: 12.5, color: "#9FC0D8", lineHeight: 1.55, marginBottom: 7 }}>{summarizeTraceParams(t.params)}</div>
            {(t.result?.offers || []).length > 0 && (
              <div style={{ ...GL, fontSize: 11.5, color: "#7A9BB5", lineHeight: 1.5, marginBottom: 9 }}>
                {(t.result.offers || []).slice(0, 4).map((o, i) => (
                  <div key={i}>• {o.ship} {o.date ? "· " + o.date : ""} — {(o.itin || "").slice(0, 70)}{(o.itin || "").length > 70 ? "…" : ""}</div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={async () => { setRelaunching(t.id); try { await onRelaunch(t); } catch { setRelaunching(null); } }} disabled={relaunching === t.id} style={sbtn(false, "#C9A84C")}>{relaunching === t.id ? "⏳ Préparation…" : (t.agent_text ? "↻ Relancer (corrigé)" : "↻ Relancer (récap)")}</button>
              <button onClick={() => copy(t)} style={sbtn(copied === t.id, "#7AE890")}>{copied === t.id ? "✓ Copié" : "📋 Copier le contexte"}</button>
              <button onClick={async () => { setBusyFu("tr_" + t.id); const ok = await kvDeleteTrace(getToken, t.id); setBusyFu(null); if (ok) setTraces(prev => prev.filter(x => x.id !== t.id)); }} disabled={busyFu === "tr_" + t.id} style={{ ...sbtn(false, "#C77"), marginLeft: "auto" }}>🗑 Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FollowupOptIn({ data, lang, context }) {
  const isEN = lang === "en";
  const isEmpty = context === "empty";
  const [email, setEmail] = useState("");
  const [st, setSt] = useState("idle"); // idle | sending | done | error
  const submit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setSt("error"); return; }
    setSt("sending");
    try {
      const r = await fetch(WORKER_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "search_followup", email, search_params: data, lang, ...(context ? { context } : {}) }),
      });
      const j = await r.json();
      if (j && j.ok) { setSt("done"); try { track("followup_optin", { context: context || "broadened" }); } catch {} }
      else setSt("error");
    } catch { setSt("error"); }
  };
  if (st === "done") return (
    <div style={{ background: "rgba(111,191,127,0.10)", border: "1px solid #6FBF7F55", borderRadius: 12, padding: "12px 14px", marginBottom: 12, ...GL, fontSize: 13.5, color: "#8FCFA5", lineHeight: 1.55 }}>
      ✅ {isEN
        ? "Got it! We'll analyze your search tonight and email you — for free — if we find a better match. 📬 Check your spam folder just in case, and add info@croisiereai.ca to your contacts."
        : "C'est noté ! On analyse votre recherche ce soir et on vous écrit gratuitement si on trouve mieux. 📬 Vérifiez vos indésirables au cas où, et ajoutez info@croisiereai.ca à vos contacts."}
    </div>
  );
  return (
    <div style={{ background: "rgba(90,122,149,0.08)", border: "1px solid #2A4A6855", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
      <div style={{ ...GL, fontSize: 13.5, color: "#B8C9D8", lineHeight: 1.55, marginBottom: 8 }}>
        {isEmpty
          ? (isEN
              ? "We couldn't find a confirmed match for exactly what you wanted. Leave your email — we'll keep looking and write you (free) as soon as we find something good."
              : "On n'a pas trouvé de départ confirmé correspondant exactement à ce que vous vouliez. Laissez votre courriel — on continue de chercher et on vous écrit (gratuitement) dès qu'on trouve quelque chose de bien.")
          : (isEN
              ? "Not quite what you hoped for? Leave your email — we'll analyze your search tonight and, if we find a better match, we'll send it to you for free."
              : "Pas tout à fait ce que vous espériez ? Laissez votre courriel — on analyse votre recherche ce soir et, si on trouve mieux, on vous l'envoie gratuitement.")}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input type="email" inputMode="email" placeholder={isEN ? "your@email.com" : "votre@courriel.com"} value={email}
          onChange={e => { setEmail(e.target.value); if (st === "error") setSt("idle"); }}
          onKeyDown={e => e.key === "Enter" && submit()}
          style={{ flex: 1, minWidth: 180, background: "#0A1E35", border: "1px solid #2A4A68", borderRadius: 9, padding: "9px 11px", color: "#E8DCC8", ...GL, fontSize: 14 }} />
        <button onClick={submit} disabled={st === "sending"}
          style={{ background: "#C9A84C", border: "none", borderRadius: 9, padding: "9px 16px", color: "#07111F", ...GL, fontWeight: 800, fontSize: 13.5, cursor: "pointer" }}>
          {st === "sending" ? "…" : (isEN ? "Notify me" : "Me prévenir")}
        </button>
      </div>
      {st === "error" && <div style={{ ...GL, fontSize: 12, color: "#E08A7A", marginTop: 6 }}>{isEN ? "Please enter a valid email." : "Entrez un courriel valide."}</div>}
      <div style={{ ...GL, fontSize: 11.5, color: "#7A8AA0", marginTop: 7, fontStyle: "italic" }}>
        📬 {isEN ? "Our emails sometimes land in spam — add info@croisiereai.ca to your contacts." : "Nos courriels tombent parfois dans les indésirables — ajoutez info@croisiereai.ca à vos contacts."}
      </div>
    </div>
  );
}

// Suivi 👎 : quand l'usager n'a pas aimé une offre, on lui demande CE QUI clochait +
// son courriel → on revient vers lui. Accompagnement clientèle = notre différenciateur
// (« on n'est pas là pour prendre l'argent du monde, mais pour les aider à trouver le
// meilleur voyage »). Réutilise l'action worker search_followup (avec rating/reason/offer).
function DownFollowup({ offer, data, lang }) {
  const isEN = lang === "en";
  const [reason, setReason] = useState("");
  const [email, setEmail] = useState("");
  const [st, setSt] = useState("idle"); // idle | sending | done | error
  const submit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setSt("error"); return; }
    setSt("sending");
    try {
      const r = await fetch(WORKER_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "search_followup", email, lang, search_params: data, rating: "down",
          reason: reason.slice(0, 400), offer: { ship: offer.ship_name || "", date: offer.departure_date || "" } }),
      });
      const j = await r.json();
      if (j && j.ok) { setSt("done"); try { track("feedback_followup", {}); } catch {} } else setSt("error");
    } catch { setSt("error"); }
  };
  if (st === "done") return (
    <span style={{ ...GL, fontSize: 12, color: "#8FCFA5", textAlign: "center", lineHeight: 1.5 }}>
      🙏 {isEN ? "Thank you — we'll look into it and get back to you. 📬 Check your spam folder." : "Merci — on regarde ça et on revient vers vous. 📬 Vérifiez vos indésirables."}
    </span>
  );
  return (
    <div style={{ width: "100%", maxWidth: 460, margin: "0 auto", display: "flex", flexDirection: "column", gap: 7 }}>
      <div style={{ ...GL, fontSize: 12.5, color: "#9FB3C6", lineHeight: 1.5, textAlign: "center" }}>
        {isEN ? "🙏 Noted — thanks. Tell us what was off and we'll get back to you (we genuinely want to help):"
              : "🙏 Noté — merci. Dites-nous ce qui clochait et on revient vers vous (on veut vraiment vous aider) :"}
      </div>
      <input value={reason} onChange={e => setReason(e.target.value)} placeholder={isEN ? "What was missing or off? (optional)" : "Qu'est-ce qui manquait ou clochait ? (optionnel)"}
        style={{ background: "#0A1E35", border: "1px solid #2A4A68", borderRadius: 9, padding: "8px 11px", color: "#E8DCC8", ...GL, fontSize: 13 }} />
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
        <input type="email" inputMode="email" value={email} onChange={e => { setEmail(e.target.value); if (st === "error") setSt("idle"); }}
          onKeyDown={e => e.key === "Enter" && submit()} placeholder={isEN ? "your@email.com" : "votre@courriel.com"}
          style={{ flex: 1, minWidth: 150, background: "#0A1E35", border: "1px solid #2A4A68", borderRadius: 9, padding: "8px 11px", color: "#E8DCC8", ...GL, fontSize: 13 }} />
        <button onClick={submit} disabled={st === "sending"} style={{ background: "#C9A84C", border: "none", borderRadius: 9, padding: "8px 15px", color: "#07111F", ...GL, fontWeight: 800, fontSize: 13, cursor: "pointer" }}>
          {st === "sending" ? "…" : (isEN ? "Send" : "Envoyer")}
        </button>
      </div>
      {st === "error" && <span style={{ ...GL, fontSize: 11.5, color: "#E08A7A" }}>{isEN ? "Please enter a valid email." : "Entrez un courriel valide."}</span>}
    </div>
  );
}

// Taux USD→CAD du jour (CruiseDirect facture en $US ; nos prix sont en $CA). Récupéré une
// fois, mis en cache localStorage par date, fallback raisonnable si l'API échoue. Source
// gratuite sans clé (open.er-api.com). Sert uniquement à AFFICHER l'équivalent honnête —
// aucun prix concurrent inventé.
const USD_CAD_FALLBACK = 1.40;
function useUsdCadRate() {
  const readCache = () => {
    try {
      const raw = localStorage.getItem("usdcad_rate");
      if (raw) {
        const o = JSON.parse(raw);
        if (o && o.date === new Date().toISOString().slice(0, 10) && o.rate > 0) return o.rate;
      }
    } catch {}
    return null;
  };
  const [rate, setRate] = useState(() => readCache() || USD_CAD_FALLBACK);
  useEffect(() => {
    if (readCache()) return; // déjà à jour aujourd'hui
    let cancelled = false;
    fetch("https://open.er-api.com/v6/latest/USD")
      .then((r) => r.json())
      .then((d) => {
        const r = d && d.rates && d.rates.CAD;
        if (r > 0 && !cancelled) {
          setRate(r);
          try { localStorage.setItem("usdcad_rate", JSON.stringify({ date: new Date().toISOString().slice(0, 10), rate: r })); } catch {}
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return rate;
}

function ResultCard({ offer: o, data, onReset, onWatch, onAskQuestion, rank = 0, t = {}, priceHistory = [], isMobileView = false, traceId = null }) {

  const [fb, setFb] = useState(null);   // feedback 👍/👎 PAR OFFRE → null | "up" | "down"
  const usdCadRate = useUsdCadRate();   // taux du jour pour l'équivalent $US (lien CruiseDirect)
  const staticShipImg = o.ship_image_url || getShipImg(o.ship_name);
  const wikiShipImg   = useWikiImage(o.ship_name, staticShipImg, "ship");
  const [shipBroken, setShipBroken] = useState(false);
  const [whyOpen, setWhyOpen]       = useState(false); // « Pourquoi cette offre » repliable
  const [priceOpen, setPriceOpen]   = useState(false); // détail du prix repliable
  const imgSrc = shipBroken ? staticShipImg : wikiShipImg;

  const isEN = t?.app_title === "Cruise AI";
  const priceCfg = {
    great:   { color: "#5CB85C", bg: "#5CB85C15", border: "#5CB85C50", emoji: "🟢", label: isEN ? "Excellent deal" : "Excellent deal" },
    average: { color: "#C9A84C", bg: "#C9A84C15", border: "#C9A84C50", emoji: "🟡", label: isEN ? "Average price" : "Prix dans la moyenne" },
    high:    { color: "#D9924A", bg: "#D9924A15", border: "#D9924A50", emoji: "✨", label: isEN ? "Premium fare" : "Tarif premium" },
  };
  const cfg = priceCfg[o.price_level] || priceCfg.average;

  // « dropping » (baisse probable) ne dit JAMAIS « attends » — ça pousserait l'usager à
  // reporter ou booker ailleurs (vigie de prix en suspens). On REFORMULE en « réserve pour
  // bloquer ce prix » avec note rassurante (dépôt souvent remboursable) → conversion immédiate
  // sans mentir. Hausse/stable inchangés (la note vient de Sonnet via price_trend_note).
  const trendCfg = {
    dropping: { color: "#5CB85C", bg: "#5CB85C15", border: "#5CB85C50", emoji: "🔒", label: isEN ? "Lock in this price" : "Bloque ce prix maintenant",
                note: isEN ? "The deposit is often refundable depending on the cruise line — booking now secures this fare and your cabin without a firm commitment."
                           : "Le dépôt est souvent remboursable selon la compagnie — réserver maintenant bloque ce tarif et ta cabine, sans engagement ferme." },
    rising:   { color: "#D9924A", bg: "#D9924A15", border: "#D9924A50", emoji: "📈", label: isEN ? "Rising — book soon"     : "Prix en hausse — réserve bientôt", note: null },
    stable:   { color: "#7A9BB5", bg: "#7A9BB515", border: "#7A9BB550", emoji: "➡️", label: isEN ? "Price stable"          : "Prix stable",     note: null },
  };
  const trendEntry = o.price_trend ? (trendCfg[o.price_trend] || null) : null;

  const checkedAt = o.last_checked
    ? new Date(o.last_checked).toLocaleTimeString(isEN ? "en-CA" : "fr-CA", { hour: "2-digit", minute: "2-digit" })
    : "—";

  // Prix de vol Aviasales (par personne) → total GROUPE = ×nb voyageurs. Quand
  // disponible, il remplace l'estimé flou de l'IA dans le détail ET on recalcule
  // le total/par-pers affichés pour rester cohérent (affichage seulement —
  // o.total_cad reste la base de la logique budget/badge).
  const cardAdults = paxCount(data);
  const cardSeats  = flightSeats(data); // sièges d'avion : adultes + enfants 2+ (+ bébé-siège)
  const fpGroup    = o.flight_price && o.flight_price.price > 0 ? Math.round(o.flight_price.price) * cardSeats : null;
  const displayTotal = fpGroup != null
    ? (o.cruise_cad || 0) + fpGroup + (o.hotel_cad || 0) + (o.taxes_cad || 0)
    : o.total_cad;
  const displayPP = (fpGroup != null && cardAdults > 0) ? Math.round(displayTotal / cardAdults) : o.price_per_person;

  return (
    <div style={{ background: "#091929", border: "1px solid #C9A84C33", borderRadius: 22, overflow: "hidden", boxShadow: "0 24px 48px rgba(0,0,0,0.5)" }}>

      {/* PHOTO NAVIRE — plus courte sur mobile pour remonter prix/verdict dans le viewport */}
      <div style={{ position: "relative", height: isMobileView ? 168 : 220 }}>
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={o.ship_name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={() => setShipBroken(true)}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, #0A1E35 0%, #1A3A5A 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 52, opacity: 0.15 }}>🚢</span>
          </div>
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, #091929 0%, rgba(9,25,41,0.2) 50%, transparent 100%)" }} />
        {rank === 0 && (
          <div style={{ position: "absolute", top: 12, left: 12, background: "#C9A84C22", border: "1px solid #C9A84C88", borderRadius: 20, padding: "4px 12px", ...GL, fontSize: 11, fontWeight: 700, color: "#C9A84C" }}>
            🏆 {isEN ? "Top pick" : "Meilleure offre"}{DEMO_MODE ? " (DEMO)" : ""}
          </div>
        )}
        {o.score && (
          <div style={{ position: "absolute", top: 12, right: 12, width: 44, height: 44, borderRadius: "50%", background: "rgba(9,25,41,0.92)", border: `2px solid ${o.score >= 8 ? "#5CB85C" : o.score >= 6 ? "#C9A84C" : "#E8524A"}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span style={{ ...GF, fontSize: 16, color: o.score >= 8 ? "#5CB85C" : o.score >= 6 ? "#C9A84C" : "#E8524A", fontWeight: 700, lineHeight: 1 }}>{o.score}</span>
            <span style={{ ...GL, fontSize: 7, color: "#5A7A95" }}>/10</span>
          </div>
        )}
        <div style={{ position: "absolute", bottom: 12, left: 14, right: 60 }}>
          <div style={{ ...GF, fontSize: 18, color: "#fff", fontWeight: 700, textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>{o.ship_name}</div>
          <div style={{ ...GL, fontSize: 13, color: "#C9A84Ccc" }}>{o.ship_class} · {prettyLine(o.cruise_line)}</div>
          {(o.ship_built_year || o.ship_refurb_year) && (
            <div style={{ ...GL, fontSize: 9, color: "#C9A84C99", marginTop: 1, textShadow: "0 1px 4px rgba(0,0,0,0.9)" }}>
              {o.ship_built_year ? `${isEN ? "Built" : "Construit"} ${o.ship_built_year}` : ""}
              {o.ship_built_year && o.ship_refurb_year ? " · " : ""}
              {o.ship_refurb_year ? `${isEN ? "Renovated" : "Rénové"} ${o.ship_refurb_year}` : ""}
            </div>
          )}
        </div>
      </div>

      {/* TITRE + PRIX */}
      <div style={{ padding: "16px 16px 0" }}>
        <div style={{ ...GF, fontSize: 17, color: "#E8DCC8", fontWeight: 700, lineHeight: 1.35, marginBottom: 8 }}>{o.catchTitle}</div>
        {/* Badge ESCALES DEMANDÉES — l'offre visite une/des îles que l'usager a nommées.
            Les escales sont un SCORE (pas un filtre) → ce badge dit lesquelles cochent. */}
        {Array.isArray(o.ports_matched) && o.ports_matched.length > 0 && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(111,191,127,0.12)", border: "1px solid #6FBF7F66", borderRadius: 8, padding: "4px 9px", marginBottom: 9, ...GL, fontSize: 12, color: "#8FCFA5", fontWeight: 600 }}>
            ✅ {isEN ? "Visits" : "Visite"} {o.ports_matched.join(", ")}
          </div>
        )}
        {/* ITINÉRAIRE — sorti de la photo (illisible bleu-sur-bleu) → ligne lisible dans le corps */}
        {o.itinerary_summary && (
          <div style={{ display: "flex", gap: 7, alignItems: "flex-start", background: "rgba(255,255,255,0.03)", border: "1px solid #1A2F45", borderRadius: 10, padding: "8px 11px", marginBottom: 9 }}>
            <span style={{ fontSize: 12, lineHeight: 1.5, flexShrink: 0 }}>🗺️</span>
            <span style={{ ...GL, fontSize: 12.5, color: "#A8C0D0", lineHeight: 1.5 }}>{o.itinerary_summary}</span>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span style={{ background: "#1A2F45", borderRadius: 6, padding: "3px 10px", ...GL, fontSize: 11, color: "#7A9BB5" }}>📅 {o.dateLabel}</span>
          {o.date_estimated && (
            <span style={{ background: "#3A2A0A", borderRadius: 6, padding: "3px 8px", ...GL, fontSize: 9, color: "#C9A84C", border: "1px solid #C9A84C44" }}>
              {isEN ? "Predicted date — verify" : "Date prévue — à vérifier"}
            </span>
          )}
          <span style={{ background: "#C9A84C22", borderRadius: 6, padding: "3px 10px", ...GL, fontSize: 14, fontWeight: 900, color: "#C9A84C" }}>
            {displayTotal > 0 ? `${displayTotal.toLocaleString()} $ CAD` : "—"}
          </span>
          {displayTotal > 0 && (o.flight_cad > 0 || o.hotel_cad > 0) && (
            <span style={{ background: "transparent", borderRadius: 6, padding: "3px 4px", ...GL, fontSize: 10, fontWeight: 700, color: "#5CB85C", alignSelf: "center" }}>
              {isEN ? "all-in" : "tout compris"}
            </span>
          )}
          {displayPP > 0 && (
            <span style={{ background: "#1A2F45", borderRadius: 6, padding: "3px 10px", ...GL, fontSize: 11, color: "#7A9BB5" }}>
              ≈ {displayPP.toLocaleString()} $/pers
            </span>
          )}
        </div>
      </div>

      {/* VERDICT PRIX — une ligne compacte (détail + tendance dans « Pourquoi cette offre ») */}
      {/* Offre-plancher Stage 0 (départ confirmé sans prix) → chip honnête « prix à valider »
          au lieu d'un verdict great/average/high trompeur (pas de prix à juger). */}
      <div style={{ padding: "10px 14px 0" }}>
        {o._reconciled ? (
          // Offre-plancher Stage 0 : départ confirmé par le code, pas un verdict de valeur
          // Sonnet → chip honnête (avec prix croisière exact si le cache l'avait, sinon « à valider »).
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#0E2438", border: "1px solid #2A4A66", borderRadius: 8, padding: "5px 11px", ...GL, fontSize: 12.5, fontWeight: 700, color: "#7AB8E8" }}>
            ✅ {displayTotal > 0
              ? (isEN ? "Confirmed departure · exact cruise price + estimated flight" : "Départ confirmé · prix croisière exact + vol estimé")
              : (isEN ? "Confirmed departure · price to verify" : "Départ confirmé · prix à valider")}
          </span>
        ) : displayTotal > 0 ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8, padding: "5px 11px", ...GL, fontSize: 12.5, fontWeight: 700, color: cfg.color }}>
            {cfg.emoji} {cfg.label}
          </span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#0E2438", border: "1px solid #2A4A66", borderRadius: 8, padding: "5px 11px", ...GL, fontSize: 12.5, fontWeight: 700, color: "#7AB8E8" }}>
            ✅ {isEN ? "Confirmed departure · price to verify" : "Départ confirmé · prix à valider"}
          </span>
        )}
      </div>

      {/* HISTORIQUE DES PRIX (« Vu Nx · Fourchette ») retiré 2026-05-27 : note interne
          sans valeur pour le client (décision Simon). Le suivi de prix reste côté vigie. */}

      {/* ANALYSE — raisonnement de classement, REPLIABLE (teaser 1 ligne → clic) */}
      {o.analysis && (
        <div style={{ margin: "10px 14px 0", background: "#091929", border: "1px solid #C9A84C33", borderRadius: 12, padding: "12px 14px" }}>
          <div onClick={() => setWhyOpen(v => !v)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <span style={{ ...GF, fontSize: 13, color: "#C9A84C", letterSpacing: "0.05em" }}>💡 {isEN ? "WHY THIS OFFER" : "POURQUOI CETTE OFFRE"}</span>
            <span style={{ ...GL, fontSize: 11, color: "#7AB8E8", whiteSpace: "nowrap" }}>{whyOpen ? (isEN ? "Hide ▴" : "Réduire ▴") : (isEN ? "Read analysis ▾" : "Lire l'analyse ▾")}</span>
          </div>
          {whyOpen ? (
            <>
              <div style={{ ...GL, fontSize: 14, color: "#9AB8D0", lineHeight: 1.75, marginTop: 7 }}>{o.analysis}</div>
              {(o.price_vs_avg || (trendEntry && (trendEntry.note || o.price_trend_note))) && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #1A2F4455", display: "flex", flexDirection: "column", gap: 6 }}>
                  {o.price_vs_avg && <div style={{ ...GL, fontSize: 12.5, color: cfg.color, lineHeight: 1.5 }}>{cfg.emoji} {o.price_vs_avg}</div>}
                  {trendEntry && (trendEntry.note || o.price_trend_note) && <div style={{ ...GL, fontSize: 12.5, color: trendEntry.color, lineHeight: 1.5 }}>{trendEntry.emoji} {trendEntry.label} — {trendEntry.note || o.price_trend_note}</div>}
                </div>
              )}
            </>
          ) : (
            <div style={{ ...GL, fontSize: 13, color: "#9AB8D0", lineHeight: 1.6, marginTop: 7, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{o.analysis}</div>
          )}
        </div>
      )}

      {/* TIP FLEXIBILITÉ — deal hors-période */}
      {o.flexible_tip && (
        <div style={{ margin: "8px 14px 0", background: "#1A2F0A", border: "1px solid #5CB85C44", borderRadius: 10, padding: "10px 14px" }}>
          <div style={{ ...GL, fontSize: 13, color: "#5CB85C", fontWeight: 700, marginBottom: 4 }}>💰 {isEN ? "FLEXIBILITY TIP" : "CONSEIL FLEXIBILITÉ"}</div>
          <div style={{ ...GL, fontSize: 14, color: "#9AB8D0", lineHeight: 1.65 }}>{o.flexible_tip.text}</div>
          {o.flexible_tip.booking_url && (
            <a href={o.flexible_tip.booking_url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", marginTop: 7, ...GL, fontSize: 13, color: "#5CB85C", fontWeight: 700, textDecoration: "none" }}>
              {isEN ? "View this deal →" : "Voir cette offre →"}
            </a>
          )}
        </div>
      )}

      {/* Port d'embarquement — compact */}
      {o.port && <div style={{ padding: "6px 16px 0", ...GL, fontSize: 11, color: "#3A5A72" }}>🚢 {o.port}</div>}

      {/* PHOTOS DESTINATIONS — destinations uniquement, grille dynamique */}
      {(() => {
        const seen = new Set();
        const dests = (o.destinations || []).filter(d => {
          if (!d.name || seen.has(d.name)) return false;
          seen.add(d.name);
          return true;
        }).slice(0, 4);
        if (dests.length === 0) return null;
        // Grille 2×2 pour 4 destinations, sinon une rangée
        const gridCols = dests.length === 4 ? "1fr 1fr" : `repeat(${dests.length}, 1fr)`;
        return (
          <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 5, padding: "10px 14px 0" }}>
            {(() => {
              // Filet anti-doublon : aucune photo répétée dans la bande d'un itinéraire.
              const usedSrc = new Set();
              return dests.map((d, i) => {
                const base = getDestMatch(d.name).file || DEST_IMGS.default;
                const src  = dedupeDestSrc(base, usedSrc);
                usedSrc.add(src);
                return <DestThumb key={i} d={d} srcOverride={src} />;
              });
            })()}
          </div>
        );
      })()}

      {/* PROMOS ENFANTS */}
      {o.kids_promo && (
        <div style={{ padding: "8px 14px 0" }}>
          <div style={{ background: "#5CB85C18", border: "1px solid #5CB85C44", borderRadius: 8, padding: "6px 11px", ...GL, fontSize: 11, color: "#5CB85C" }}>🎉 {o.kids_promo}</div>
        </div>
      )}

      {/* PRIX — total du voyage au coup d'œil, détail (+ cabines) au clic */}
      <div style={{ margin: "10px 14px 0", background: "rgba(201,168,76,0.06)", border: "1px solid #C9A84C22", borderRadius: 12, overflow: "hidden" }}>
        <div onClick={() => setPriceOpen(v => !v)} style={{ cursor: "pointer", padding: "13px 15px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div>
            <div style={{ ...GL, fontSize: 10, color: "#5A7A95", letterSpacing: "0.08em", textTransform: "uppercase" }}>{isEN ? "Total trip" : "Total du voyage"}</div>
            <div style={{ ...GF, fontSize: 23, color: "#C9A84C", fontWeight: 700, lineHeight: 1.1, marginTop: 2 }}>{displayTotal > 0 ? `${displayTotal.toLocaleString()} $` : "—"} <span style={{ fontSize: 13 }}>CAD</span></div>
            {displayPP > 0 && (() => {
              const pc = partyComposition(data);
              // Famille avec enfants → on clarifie : prix par ADULTE, et le total couvre
              // tout le monde (« 2 adultes + 1 enfant + 1 bébé »). Sinon, par personne.
              return pc.kids > 0
                ? <div style={{ ...GL, fontSize: 11, color: "#7A9BB5", marginTop: 3 }}>≈ {displayPP.toLocaleString()} {isEN ? "$/adult" : "$/adulte"} · {partyLabel(data, isEN)} · {isEN ? "kids at reduced rate included" : "enfants à tarif réduit inclus"}</div>
                : <div style={{ ...GL, fontSize: 11, color: "#7A9BB5", marginTop: 3 }}>≈ {displayPP.toLocaleString()} $/pers · {cardAdults} {isEN ? (cardAdults > 1 ? "people" : "person") : (cardAdults > 1 ? "personnes" : "personne")}</div>;
            })()}
          </div>
          <span style={{ ...GL, fontSize: 11, color: "#7AB8E8", whiteSpace: "nowrap" }}>{priceOpen ? (isEN ? "Hide ▴" : "Réduire ▴") : (isEN ? "Price details ▾" : "Détail du prix ▾")}</span>
        </div>
        {priceOpen && (
          <div style={{ padding: "0 15px 14px", borderTop: "1px solid #1A2F4455" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 10 }}>
              {o.cruise_cad > 0 && <div style={{ display: "flex", justifyContent: "space-between", ...GL, fontSize: 14, color: "#A8C0D0", borderBottom: "1px solid #1A2F4440", paddingBottom: 5 }}><span>🚢 {isEN ? "Cruise" : "Croisière"} <span style={{ fontSize: 9, color: o.price_exact ? "#4A9C5A" : "#3A5A72" }}>{o.price_exact ? (isEN ? "(exact)" : "(exact)") : (isEN ? "(estimated)" : "(estimé)")}</span></span><span style={{ fontWeight: 700, color: "#C8D8E8" }}>{o.cruise_cad.toLocaleString()} $</span></div>}
              {(o.flight_cad > 0 || fpGroup != null) && <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", ...GL, fontSize: 14, color: "#A8C0D0", borderBottom: "1px solid #1A2F4440", paddingBottom: 5 }}><span>✈️ {isEN ? "Flights" : "Vols"} <span style={{ fontSize: 9, color: "#3A5A72" }}>{fpGroup != null ? (isEN ? `(${cardSeats} seat${cardSeats > 1 ? "s" : ""})` : `(${cardSeats} siège${cardSeats > 1 ? "s" : ""})`) : (isEN ? "(estimated)" : "(estimé)")}</span></span><span style={{ fontWeight: 700, color: "#C8D8E8", fontSize: 13 }}>{fpGroup != null ? `${fpGroup.toLocaleString()} $` : flightBracket(o.flight_cad)}</span></div>}
              {o.hotel_cad > 0 && <div style={{ display: "flex", justifyContent: "space-between", ...GL, fontSize: 14, color: "#A8C0D0", borderBottom: "1px solid #1A2F4440", paddingBottom: 5 }}><span>🏨 {isEN ? "Night before hotel" : "Hôtel veille"}</span><span style={{ fontWeight: 700, color: "#C8D8E8" }}>{o.hotel_cad.toLocaleString()} $</span></div>}
              {o.taxes_cad > 0 && <div style={{ display: "flex", justifyContent: "space-between", ...GL, fontSize: 14, color: "#A8C0D0", borderBottom: "1px solid #1A2F4440", paddingBottom: 5 }}><span>🧾 {isEN ? "Taxes & fees" : "Taxes & frais"}</span><span style={{ fontWeight: 700, color: "#C8D8E8" }}>{o.taxes_cad.toLocaleString()} $</span></div>}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", ...GF, fontSize: 19, color: "#C9A84C", fontWeight: 700, marginTop: 3 }}>
                <span style={{ display: "flex", flexDirection: "column" }}>
                  <span>TOTAL</span>
                  {partyComposition(data).kids > 0 && <span style={{ ...GL, fontSize: 10, fontWeight: 400, color: "#7A9BB5" }}>{isEN ? `for ${partyLabel(data, isEN)}` : `pour ${partyLabel(data, isEN)}`}</span>}
                </span>
                <span>{displayTotal > 0 ? `${displayTotal.toLocaleString()} $ CAD` : "—"}</span>
              </div>
            </div>

            {/* CABINES — prix par type (intérieure / vue mer / balcon / suite) */}
            {o.cabins?.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ ...GL, fontSize: 9, color: "#5A7A95", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 7 }}>{isEN ? "Price per cabin (cruise, per person)" : "Prix par cabine (croisière, par personne)"}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  {o.cabins.map((c, i) => (
                    <div key={i} style={{ background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 9, padding: "7px 10px" }}>
                      <div style={{ ...GL, fontSize: 10, color: "#7A9BB5" }}>{c.type}</div>
                      <div style={{ ...GL, fontSize: 15, color: "#C9A84C", fontWeight: 800, marginTop: 1 }}>{c.price_pp?.toLocaleString()} $</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ ...GL, fontSize: 9, color: o.price_exact ? "#6FBF7F" : "#3A5A72", marginTop: 12, textAlign: "center", lineHeight: 1.5 }}>
              {o.price_exact
                ? (isEN ? "Exact cruise price — flights & hotel estimated. Verify on CruiseDirect before booking" : "Prix de croisière exact — vols et hôtel estimés. Vérifier sur CruiseDirect avant de réserver")
                : (isEN ? "Estimated prices — verify exact price on CruiseDirect before booking" : "Prix estimés — vérifier le prix exact sur CruiseDirect avant de réserver")}
            </div>
          </div>
        )}
      </div>

      {/* BOUTONS */}
      <div style={{ padding: "12px 14px 0", display: "flex", flexDirection: "column", gap: 8 }}>

        {/* 1. Bouton croisière — CruiseDirect (partenariat CJ) */}
        {(() => {
          const bookUrl = buildCruiseBookingUrl(o, data);
          if (!bookUrl) return null;   // ligne en hold (CruiseDirect ne la vend pas) → pas de bouton cassé
          return (
            <>
              <a href={bookUrl} target="_blank" rel="noopener noreferrer"
                onClick={() => track("offer_clicked", { type: "cruise" })}
                style={{ display: "block", padding: "14px 16px", borderRadius: 12, background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontWeight: 900, fontSize: 14, textDecoration: "none", textAlign: "center" }}>
                🚢 {isEN ? "Book this cruise — CruiseDirect →" : "Réserver cette croisière — CruiseDirect →"}
              </a>
              {/* Ligne de soutien affiliation — discrète, sous le CTA. Pas de "même prix"
                  (la garantie de prix est juste en dessous via le bouton "Meilleur prix garanti"). */}
              <div style={{ ...GL, fontSize: 11, color: "#5A7A95", textAlign: "center", lineHeight: 1.45, padding: "1px 6px" }}>
                {isEN
                  ? "💙 Booking through this link directly supports Croisière AI and lets us keep offering free searches every month. Thank you!"
                  : "💙 Réserver via ce lien soutient directement Croisière AI et nous permet d'offrir des recherches gratuites chaque mois. Merci !"}
              </div>
              {/* Équivalent $US — CruiseDirect facture en $US. On affiche la conversion honnête
                  au taux du jour (aucun prix concurrent inventé). */}
              {o.cruise_cad > 0 && (
                <div style={{ ...GL, fontSize: 11, color: "#7A9BB5", textAlign: "center", lineHeight: 1.45, padding: "0 6px" }}>
                  {isEN
                    ? `💱 Prices on CruiseDirect are in USD — this cruise ≈ ${Math.round(o.cruise_cad / usdCadRate).toLocaleString()} USD (today's rate: 1 USD = ${usdCadRate.toFixed(2)} CAD)`
                    : `💱 Sur CruiseDirect, les prix sont en $US — cette croisière ≈ ${Math.round(o.cruise_cad / usdCadRate).toLocaleString()} $US (taux du jour : 1 $US = ${usdCadRate.toFixed(2)} $CA)`}
                </div>
              )}
              <details style={{ border: "1px solid #1A4A2A55", borderRadius: 8, overflow: "hidden" }}>
                <summary style={{ ...GL, fontSize: 13, color: "#4A9A6A", padding: "5px 10px", cursor: "pointer", listStyle: "none", display: "flex", alignItems: "center", gap: 5 }}>
                  ✅ {isEN ? "Why CruiseDirect? Best price guaranteed ›" : "Pourquoi CruiseDirect? Meilleur prix garanti ›"}
                </summary>
                <div style={{ ...GL, fontSize: 13, color: "#6AAA80", padding: "6px 10px 10px", lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: "#7ABA90" }}>
                    {isEN ? "More than a great price — the whole package:" : "Plus qu'un bon prix — le forfait complet :"}
                  </div>
                  <ol style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 5 }}>
                    {(isEN ? [
                      ["Best price guarantee", "See it advertised cheaper elsewhere? They match it."],
                      ["Freebies & discounts", "Most sailings include a freebie (onboard credit, insurance) or a discount."],
                      ["Price protection", "Fare drops before final payment? They readjust it."],
                      ["No CruiseDirect cancellation fees", "Nothing on their end if you cancel."],
                      ["Flexible payments", "Refundable deposit at booking, the rest later."],
                      ["Cruise specialists", "A team that does only this, ready to help."],
                      ["Always reachable", "Phone, email or live chat."],
                    ] : [
                      ["Garantie du meilleur prix", "Vous voyez moins cher annoncé ailleurs? Ils s'alignent."],
                      ["Bonis et rabais", "La plupart des départs incluent un boni (crédit à bord, assurance) ou un rabais."],
                      ["Protection de prix", "Le tarif baisse avant le paiement final? Ils réajustent."],
                      ["Aucuns frais d'annulation de CruiseDirect", "Rien de leur côté si vous annulez."],
                      ["Paiements flexibles", "Dépôt remboursable à la réservation, le reste plus tard."],
                      ["Spécialistes croisière", "Une équipe qui ne fait que ça, prête à répondre."],
                      ["Service en tout temps", "Téléphone, courriel ou clavardage en direct."],
                    ]).map(([title, desc], i) => (
                      <li key={i}><strong style={{ color: "#8FCFA5" }}>{title}</strong> — {desc}</li>
                    ))}
                  </ol>
                </div>
              </details>
            </>
          );
        })()}

        {/* 2. Vol — caché si voyage voiture (drive_ny) OU si offre voiture dans un either (pas de flight_option/cad fourni par l'IA) */}
        {data?.departure !== "drive_ny" && (o.flight_option || o.flight_cad > 0) && (
          <>
            <div style={{ height: 1, background: "linear-gradient(to right, transparent, #1A3050, transparent)", margin: "4px 0" }} />
            <FlightBlock data={data || {}} offer={o} t={t} />
          </>
        )}

        {/* 3. Hôtel — Expedia (source de commission principale). Suggestion = drive_ny OU offre voiture dans either */}
        {(() => {
          const isDriveOffer = !o.flight_option && !(o.flight_cad > 0);
          const showHotel = data?.overnight === "yes" || o.hotel_cad > 0
            || (data?.departure === "drive_ny" && o.hotel_option)
            || (data?.departure === "either" && isDriveOffer);
          const isSuggestion = (data?.departure === "drive_ny" && data?.overnight !== "yes")
            || (data?.departure === "either" && isDriveOffer);
          if (!showHotel) return null;
          return (
            <>
              <div style={{ height: 1, background: "linear-gradient(to right, transparent, #1A3050, transparent)", margin: "4px 0" }} />
              <HotelBlock data={data || {}} offer={o} t={t} isSuggestion={isSuggestion} />
            </>
          );
        })()}

        {/* BOUTON VIGIE */}
        {VIGIE_ENABLED && <>
        <div style={{ height: 1, background: "linear-gradient(to right, transparent, #C9A84C55, transparent)", margin: "4px 0" }} />
        <button onClick={onWatch} style={{ padding: "13px 16px", borderRadius: 12, border: "2px solid #C9A84C99", background: "linear-gradient(135deg,rgba(201,168,76,0.22),rgba(201,168,76,0.10))", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", width: "100%", animation: "ca-glow-pulse 3s ease-in-out infinite" }}>
          <div>
            <div style={{ ...GL, fontSize: 15, color: "#E2C060", fontWeight: 800 }}>{t.results_watch_price || "🔔 Surveiller ce prix"}</div>
            <div style={{ ...GL, fontSize: 11, color: "#A0BACE", marginTop: 2 }}>{t.results_watch_upsell || "💰 Économisez jusqu'à 500$+ avec la vigie Pro"}</div>
          </div>
          <span style={{ ...GL, fontSize: 9, background: "#C9A84C", borderRadius: 5, padding: "3px 8px", color: "#07111F", fontWeight: 900, letterSpacing: "0.05em", flexShrink: 0 }}>{t.results_watch_pro || "PRO"}</span>
        </button>
        </>}

        {onAskQuestion && (
          <button onClick={onAskQuestion} style={{
            padding: "14px 16px", borderRadius: 14, border: "1px solid #C9A84C88",
            background: "linear-gradient(135deg,rgba(201,168,76,0.12),rgba(201,168,76,0.04))",
            cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 14,
            animation: "ca-glow-pulse 3s ease-in-out infinite",
          }}>
            <span style={{ fontSize: 26 }}>💬</span>
            <div style={{ flex: 1 }}>
              <div style={{ ...GF, fontSize: 14, color: "#C9A84C", fontWeight: 700, marginBottom: 3 }}>
                {isEN ? "Questions before booking?" : "Des questions avant de réserver ?"}
              </div>
              <div style={{ ...GL, fontSize: 13, color: "#7A9BB5", lineHeight: 1.5 }}>
                {isEN ? "Ideal cabin · Stops · Logistics · Comparisons" : "Cabine idéale · Escales · Logistique de voyage · Comparaisons"}
              </div>
            </div>
            <div style={{ ...GL, fontSize: 20, color: "#C9A84C" }}>→</div>
          </button>
        )}

        {/* IMPRIMER LE SOMMAIRE — page propre fond blanc */}
        <button onClick={() => { track("offer_printed", {}); printOfferSummary(o, data, isEN); }} style={{
          padding: "11px 16px", borderRadius: 12, border: "1px solid #1A3050",
          background: "transparent", cursor: "pointer", width: "100%",
          ...GL, fontSize: 13, color: "#7A9BB5", fontWeight: 700,
        }}>
          🖨️ {isEN ? "Print this summary" : "Imprimer ce sommaire"}
        </button>

      </div>

      {/* FEEDBACK PAR OFFRE — 1 clic, individuel : on sait QUELLE offre cloche (debug
          case-par-cas) et chaque carte = un point de contact (plus de feedback). */}
      <div style={{ padding: "4px 14px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
        {fb === null ? (
          <>
            <span style={{ ...GL, fontSize: 12, color: "#5A7A95" }}>{isEN ? "This offer?" : "Cette offre ?"}</span>
            {[["up", "👍"], ["down", "👎"]].map(([v, emo]) => (
              <button key={v} aria-label={v} onClick={() => {
                setFb(v);
                const ctx = { region: (data.regions || []).filter(r => r && r !== "any").join(",") || (data.specific_ship ? "ship:" + data.specific_ship : ""), ship: o.ship_name || "", rank };
                try { track("result_feedback_" + v, ctx); } catch {}
                if (traceId) { try { fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "rate", trace_id: traceId, rating: v, offer: { ship: o.ship_name || "", date: o.departure_date || "", rank } }), keepalive: true }).catch(() => {}); } catch {} }
              }} style={{ background: "transparent", border: "1px solid #2A4A66", borderRadius: 9, padding: "4px 11px", fontSize: 15, cursor: "pointer", lineHeight: 1 }}>
                {emo}
              </button>
            ))}
          </>
        ) : fb === "up" ? (
          <span style={{ ...GL, fontSize: 12, color: "#6FAF8A" }}>{isEN ? "🙏 Thanks!" : "🙏 Merci !"}</span>
        ) : (
          <DownFollowup offer={o} data={data} lang={isEN ? "en" : "fr"} />
        )}
      </div>

      {/* TIMESTAMP */}
      <div style={{ padding: "8px 14px 14px", textAlign: "right" }}>
        <span style={{ ...GL, fontSize: 9, color: "#2A3F55" }}>{isEN ? `Last checked: ${checkedAt}` : `Dernière vérification : ${checkedAt}`}</span>
      </div>
    </div>
  );
}

// ─── VIGIE MODAL ───────────────────────────────────────────────────────────────
// ─── REFINEMENT MODAL (B) ──────────────────────────────────────────────────────
// Fallback raffinement : quand 0 offre confirmée et que Sonnet n'a PAS rempli
// refinement_options (ex: il avait sorti des offres "estimées" toutes filtrées
// par le filet zéro-invention), on synthétise des leviers d'élargissement
// COHÉRENTS avec ce que l'utilisateur a réellement contraint — on ne propose
// de relâcher que des filtres qu'il a posés. Les ids correspondent au mapping
// onApplyLever existant.
function buildFallbackRefinement(data, lang, someOffers = false) {
  const en = lang === "en";
  const regions   = (data.regions   || []).filter(r => r && r !== "any");
  const companies = (data.companies || []).filter(c => c && c !== "any");
  const levers = [];

  if (data.dates?.from) levers.push({ id: "dates_shift",
    label: en ? "Shift dates by ±2 weeks" : "Décaler les dates de ±2 semaines",
    description: en ? "More departures are confirmed just before or after your dates."
                    : "Plus de départs sont confirmés juste avant ou après vos dates." });
  if (companies.length > 0) levers.push({ id: "other_company",
    label: en ? "Open to all cruise lines" : "Ouvrir à toutes les compagnies",
    description: en ? "Other lines sail this region on dates we can confirm."
                    : "D'autres compagnies naviguent cette région à des dates confirmables." });
  if (regions.length > 0) levers.push({ id: "other_region",
    label: en ? "Explore nearby regions" : "Explorer des régions voisines",
    description: en ? "Confirmed departures exist in adjacent regions for this period."
                    : "Des départs confirmés existent dans des régions voisines pour cette période." });
  if (data.ship_freshness && data.ship_freshness !== "any") levers.push({ id: "older_ship",
    label: en ? "Include proven older ships" : "Inclure des navires éprouvés plus anciens",
    description: en ? "The newest ships are scarce; established ships have far more confirmed sailings."
                    : "Les navires récents sont rares ; les navires établis ont bien plus de départs confirmés." });
  if (data.duration && data.duration !== "any") levers.push({ id: "any_duration",
    label: en ? "Flexible on trip length" : "Flexible sur la durée",
    description: en ? "Allowing other lengths reveals more confirmed departures."
                    : "Autoriser d'autres durées révèle plus de départs confirmés." });

  if (levers.length === 0) levers.push({ id: "other_region",
    label: en ? "Explore nearby regions" : "Explorer des régions voisines",
    description: en ? "Widening the destination reveals confirmed departures."
                    : "Élargir la destination révèle des départs confirmés." });

  return {
    message: en
      ? (someOffers
          ? "Only a few departures could be confirmed for these exact criteria — we never invent options just to fill the page. Here's how to see more, based on your choices:"
          : "No departure could be confirmed for these exact criteria — and we never invent options to fill the page. Here is how to widen, based on your choices:")
      : (someOffers
          ? "Seulement quelques départs ont pu être confirmés pour ces critères exacts — on n'invente jamais d'options pour remplir la page. Voici comment en voir plus, selon vos choix :"
          : "Aucun départ n'a pu être confirmé pour ces critères exacts — et on n'invente jamais d'options pour remplir la page. Voici comment élargir, selon vos choix :"),
    levers: levers.slice(0, 3),
  };
}

function AutoRefinementBanner({ options, onApplyLever, lang = "fr" }) {
  const isEN = lang === "en";
  const leverLabels = {
    dates_shift:    isEN ? "Shift dates ±2 weeks"        : "Décaler les dates de ±2 semaines",
    older_ship:     isEN ? "Accept an older ship"         : "Accepter un navire plus ancien",
    other_region:   isEN ? "Explore another region"       : "Explorer une autre région",
    other_company:  isEN ? "Open to all cruise lines"     : "Ouvrir à toutes les compagnies",
    any_duration:   isEN ? "Any duration"                 : "Toute durée",
  };
  const leverIcons = { dates_shift: "📅", older_ship: "🚢", other_region: "🗺️", other_company: "⚓", any_duration: "⏱️" };

  return (
    <div style={{ background: "#0D1F2D", border: "1px solid #C9A84C44", borderRadius: 16, padding: "18px 20px", marginBottom: 16 }}>
      <div style={{ ...GF, fontSize: 15, color: "#C9A84C", fontWeight: 700, marginBottom: 6 }}>
        {isEN ? "⚠️ Limited confirmed offers" : "⚠️ Offres confirmées limitées"}
      </div>
      <div style={{ ...GL, fontSize: 14, color: "#7A9BB5", marginBottom: 14 }}>{options.message}</div>
      <div style={{ ...GL, fontSize: 13, color: "#5A7A95", marginBottom: 10 }}>
        {isEN ? "Adjust one criterion to find more options:" : "Ajustez un critère pour trouver plus d'options :"}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(options.levers || []).map(lever => (
          <button key={lever.id} onClick={() => onApplyLever(lever.id)} style={{
            background: "rgba(201,168,76,0.06)", border: "1px solid #C9A84C44",
            borderRadius: 12, padding: "11px 14px", cursor: "pointer",
            textAlign: "left", display: "flex", alignItems: "flex-start", gap: 10,
          }}>
            <span style={{ fontSize: 18, marginTop: 1 }}>{leverIcons[lever.id] || "🔄"}</span>
            <div>
              <div style={{ ...GF, fontSize: 14, color: "#E8DCC8", fontWeight: 700 }}>
                {lever.label || leverLabels[lever.id]}
              </div>
              <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginTop: 2 }}>{lever.description}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function RefinementModal({ onRefine, onClose }) {
  const opts = [
    { v: "cheaper",     icon: "💰", l: "Trop cher",            s: "Cherche des options plus abordables" },
    { v: "destination", icon: "🗺️", l: "Mauvaise destination",  s: "Propose des itinéraires différents" },
    { v: "ship",        icon: "🚢", l: "Mauvais navire",        s: "Essaie d'autres types de navires" },
    { v: "dates",       icon: "📅", l: "Mauvaises dates",       s: "Cherche des périodes alternatives" },
  ];
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#091929", border: "1px solid #1A2F45", borderRadius: 20, padding: 24, width: "100%", maxWidth: 420 }}>
        <div style={{ ...GF, fontSize: 18, color: "#fff", fontWeight: 700, marginBottom: 6 }}>Qu'est-ce qui ne convient pas ?</div>
        <div style={{ ...GL, fontSize: 14, color: "#5A7A95", marginBottom: 18 }}>On relance une recherche ciblée selon votre réponse.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {opts.map(o => (
            <button key={o.v} onClick={() => onRefine(o.v)} style={{
              background: "rgba(255,255,255,0.03)", border: "1px solid #1A2F45",
              borderRadius: 14, padding: "13px 16px", cursor: "pointer",
              textAlign: "left", display: "flex", alignItems: "center", gap: 12,
            }}>
              <span style={{ fontSize: 22 }}>{o.icon}</span>
              <div>
                <div style={{ ...GF, fontSize: 15, color: "#E8DCC8", fontWeight: 700 }}>{o.l}</div>
                <div style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>{o.s}</div>
              </div>
            </button>
          ))}
        </div>
        <button onClick={onClose} style={{ marginTop: 16, width: "100%", padding: "10px", background: "transparent", border: "1px solid #1A2F45", borderRadius: 10, color: "#5A7A95", ...GL, fontSize: 14, cursor: "pointer" }}>
          Annuler
        </button>
      </div>
    </div>
  );
}

function WatchModal({ offer, searchParams, onClose, t = {}, lang = "fr" }) {
  const [email, setEmail]       = useState("");
  const [pricePaid, setPricePaid] = useState("");
  const [notBooked, setNotBooked] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [saved, setSaved]       = useState(false);
  const isEN = lang === "en";

  const save = async () => {
    if (!email || loading) return;
    setLoading(true);
    setError(null);
    try {
      const priceVal = notBooked ? null : (parseFloat(pricePaid.replace(/[^0-9.]/g, "")) || null);
      const res = await fetch(WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "watch", email, offer, search_params: searchParams || {}, price_paid: priceVal, lang }),
      });
      const json = await res.json();
      if (!res.ok || json?.error) throw new Error(json?.error?.message || `Error ${res.status}`);
      setSaved(true);
    } catch (e) {
      setError(e.message || (isEN ? "Network error. Please try again." : "Erreur réseau. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 10, padding: "10px 12px", color: "#C8D8E8", ...GL, fontSize: 15, width: "100%", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
      <div style={{ background: "#091929", border: "1px solid #5CB85C44", borderRadius: 20, padding: 24, maxWidth: 400, width: "100%" }}>
        {!saved ? (
          <>
            <div style={{ ...GF, fontSize: 20, color: "#5CB85C", marginBottom: 8 }}>{isEN ? "🔔 Set price alert" : "🔔 Activer la vigie"}</div>
            <div style={{ ...GL, fontSize: 14, color: "#7A9BB5", lineHeight: 1.7, marginBottom: 16 }}>
              {isEN ? "I'll monitor prices for" : "Je surveille le prix de"} <strong style={{ color: "#E8DCC8" }}>{offer.ship_name}</strong> {isEN ? "and alert you by email when a drop of 10%+ is detected." : "et vous alerte par courriel dès qu'une baisse de 10%+ est détectée."}
            </div>

            <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 6 }}>{isEN ? "Your email for alerts" : "Votre courriel pour les alertes"}</div>
            <input type="email" placeholder={isEN ? "your@email.com" : "votre@email.com"} value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && save()} style={{ ...inputStyle, marginBottom: 12 }} />

            <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginBottom: 6 }}>{t.watch_modal_price_label || (isEN ? "Price you paid (CAD $)" : "Prix que vous avez payé ($ CAD)")}</div>
            <input type="text" placeholder={t.watch_modal_price_placeholder || (isEN ? "e.g. 3,200" : "ex: 3 200")} value={pricePaid} onChange={e => setPricePaid(e.target.value)} disabled={notBooked} style={{ ...inputStyle, marginBottom: 8, opacity: notBooked ? 0.4 : 1 }} />
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 14 }}>
              <input type="checkbox" checked={notBooked} onChange={e => setNotBooked(e.target.checked)} style={{ accentColor: "#5CB85C" }} />
              <span style={{ ...GL, fontSize: 13, color: "#7A9BB5" }}>{t.watch_modal_not_booked || (isEN ? "I haven't booked yet" : "Je n'ai pas encore réservé")}</span>
            </label>

            {error && <div style={{ ...GL, fontSize: 13, color: "#E8524A", marginBottom: 10, padding: "8px 12px", background: "#E8524A11", borderRadius: 8, border: "1px solid #E8524A33" }}>{error}</div>}
            <div style={{ background: "#5CB85C11", border: "1px solid #5CB85C33", borderRadius: 10, padding: "10px 12px", ...GL, fontSize: 13, color: "#5CB85C", marginBottom: 16, lineHeight: 1.6 }}>
              {t.watch_modal_tip || (isEN ? "💡 If the price drops after booking, call the cruise line — they almost always match the new price." : "💡 Si le prix baisse après réservation, appelez la compagnie — ils ajustent presque toujours.")}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 12, border: "1px solid #1A2F45", background: "transparent", color: "#5A7A95", ...GL, cursor: "pointer" }}>{isEN ? "Cancel" : "Annuler"}</button>
              <button onClick={save} disabled={!email || loading} style={{ flex: 2, padding: "11px", borderRadius: 12, border: "none", background: (email && !loading) ? "linear-gradient(135deg,#5CB85C,#4A9A4A)" : "#1A2F45", color: (email && !loading) ? "#fff" : "#3A5A72", ...GL, fontWeight: 700, cursor: (email && !loading) ? "pointer" : "not-allowed" }}>
                {loading ? (t.watch_modal_activating || (isEN ? "Activating…" : "Activation…")) : (isEN ? "Set alert →" : "Activer la vigie →")}
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <div style={{ ...GF, fontSize: 20, color: "#5CB85C", marginBottom: 8 }}>{isEN ? "Alert set!" : "Vigie activée !"}</div>
            <div style={{ ...GL, fontSize: 14, color: "#7A9BB5", lineHeight: 1.7, marginBottom: 12 }}>
              {t.watch_modal_success_ship || (isEN ? "We are watching" : "Nous surveillons")} <strong style={{ color: "#E8DCC8" }}>{offer.ship_name}</strong> {t.watch_modal_success_for || (isEN ? "for you." : "pour vous.")}<br />
              {t.watch_modal_success_sub || (isEN ? "You'll receive an email as soon as a price drop is detected." : "Vous recevrez un courriel dès qu'une baisse de prix est détectée.")}
            </div>
            <div style={{ ...GL, fontSize: 13, color: "#C9A84C", background: "#C9A84C11", border: "1px solid #C9A84C33", borderRadius: 8, padding: "8px 12px", marginBottom: 20, lineHeight: 1.6 }}>
              {t.watch_modal_spam || (isEN ? "⚠️ If you don't receive the confirmation email, please check your spam folder." : "⚠️ Si vous ne recevez pas le courriel de confirmation, vérifiez votre dossier spam.")}
            </div>
            <button onClick={onClose} style={{ padding: "11px 24px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontWeight: 700, cursor: "pointer" }}>{isEN ? "Close" : "Fermer"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProUpsellModal({ onClose, t = {}, lang = "fr" }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
      <div style={{ background: "#091929", border: "1px solid #C9A84C44", borderRadius: 20, padding: 24, maxWidth: 400, width: "100%" }}>
        <div style={{ ...GF, fontSize: 20, color: "#C9A84C", marginBottom: 12 }}>{t.upsell_watch_title || "🔔 Vigie de prix — Fonctionnalité Pro"}</div>
        <div style={{ background: "#5CB85C11", border: "1px solid #5CB85C33", borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ ...GF, fontSize: 14, color: "#5CB85C", fontWeight: 700, marginBottom: 6 }}>{t.upsell_watch_savings || "Les prix des croisières peuvent chuter de 15-30% dans les 60 jours avant le départ."}</div>
          <div style={{ ...GL, fontSize: 14, color: "#7A9BB5", lineHeight: 1.7 }}>{t.upsell_watch_desc || "La vigie surveille automatiquement le prix chaque semaine et vous alerte par courriel dès qu'une baisse est détectée."}</div>
        </div>
        <div style={{ ...GL, fontSize: 13, color: "#5CB85C", background: "#5CB85C0A", border: "1px solid #5CB85C22", borderRadius: 10, padding: "10px 14px", marginBottom: 20, lineHeight: 1.8 }}>
          {t.upsell_watch_includes || "✅ Plan Pro à 24,99 $/mois : 30 recherches · 1 vigie de prix · Croisière du Mois accès anticipé"}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 12, border: "1px solid #1A2F45", background: "transparent", color: "#5A7A95", ...GL, cursor: "pointer" }}>{t.upsell_watch_cancel || "Plus tard"}</button>
          <button onClick={onClose} style={{ flex: 2, padding: "11px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontWeight: 700, cursor: "pointer" }}>
            {t.upsell_watch_cta || "Compris"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FEEDBACK MODAL ────────────────────────────────────────────────────────────
function WelcomeModal({ onClose, lang = "fr" }) {
  const [step, setStep] = useState(0);
  const isEN = lang === "en";
  useEffect(() => { try { track("welcome_modal_shown"); } catch {} }, []);

  const SCREENS = isEN ? [
    {
      icon: "🚢",
      title: "Built for cruisers, by a cruiser",
      body: "Like many busy people, I never had time to sit down with a travel agent or spend hours comparing cruise packages. I looked for a smart tool to do it — it didn't exist.\n\nSo I built one. After using it myself, I decided to share it with fellow travelers. Welcome to Cruise AI.",
      note: null,
      list: null,
    },
    {
      icon: "🤖",
      title: "Real AI research — not just filters",
      body: "Every search triggers a genuine AI analysis: it scans the web live to find confirmed departure dates, verified itineraries, flights, and a hotel near the port with shuttle service.",
      note: "💡 Cruise prices are often the exact per-cabin fare; flights and hotel are estimates — verify before booking.",
      list: "Because every search has a real cost (the AI genuinely works for you), a free account gives you 2 free searches every month — plus a 1st search to try without even signing up. Your free quota renews each month.",
    },
    {
      icon: "🤝",
      title: "Our partners & promise",
      body: "We partner with CruiseDirect (cruises), Expedia (hotels) and Aviasales (flights) — chosen for their intuitive platforms and best-price guarantees. One click and you land on the flights for your cruise, already pre-filled.",
      note: null,
      list: "✓  Identifies your needs and budget\n✓  Finds the best cruise for you\n✓  Simplifies flights & hotel (with shuttle)\n✓  Saves your searches so you can find them later\n\nThe tool evolves constantly — as AI improves, so do our recommendations.",
    },
  ] : [
    {
      icon: "🚢",
      title: "Créé par un passionné de croisières",
      body: "Comme bien des gens occupés, je n'ai jamais le temps de m'asseoir avec un agent de voyage ou de passer des heures à comparer des forfaits. J'ai cherché un outil intelligent pour le faire — il n'existait pas.\n\nAlors je l'ai créé. Après l'avoir utilisé pour moi-même, j'ai décidé d'en faire profiter d'autres voyageurs. Bienvenue sur Croisière AI.",
      note: null,
      list: null,
    },
    {
      icon: "🤖",
      title: "Une vraie recherche IA — pas des filtres",
      body: "Chaque recherche déclenche une vraie analyse : l'IA parcourt le web en temps réel pour trouver des dates confirmées, des itinéraires vérifiés, des vols et un hôtel avec navette près du port.",
      note: "💡 Les prix de croisière sont souvent le tarif exact par cabine ; les vols et l'hôtel restent des estimations — à valider avant de réserver.",
      list: "Parce que chaque recherche représente un vrai coût (l'IA travaille vraiment pour vous), un compte gratuit vous donne 2 recherches gratuites chaque mois — plus une 1re recherche à essayer sans même créer de compte. Votre quota gratuit se renouvelle chaque mois.",
    },
    {
      icon: "🤝",
      title: "Nos partenaires & notre promesse",
      body: "Nous travaillons avec CruiseDirect (croisières), Expedia (hôtels) et Aviasales (vols) — choisis pour leurs plateformes intuitives et leurs garanties de meilleur prix. Un clic et tu tombes directement sur les vols de ta croisière, déjà pré-remplis.",
      note: null,
      list: "✓  Cerne vos besoins et votre budget\n✓  Trouve la meilleure croisière pour vous\n✓  Simplifie vols et hôtel (avec navette)\n✓  Sauvegarde vos recherches pour les retrouver plus tard\n\nL'outil évolue constamment — plus l'IA s'améliore, plus nos recommandations aussi.",
    },
  ];

  const s = SCREENS[step];
  const isLast = step === SCREENS.length - 1;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: 20 }}>
      <div style={{ background: "#091929", border: "1px solid #C9A84C44", borderRadius: 24, padding: "28px 24px 24px", maxWidth: 460, width: "100%", maxHeight: "90vh", position: "relative", display: "flex", flexDirection: "column" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 16, background: "transparent", border: "none", color: "#3A5A72", fontSize: 18, cursor: "pointer", lineHeight: 1, padding: 4, zIndex: 1 }}>✕</button>

        <div style={{ textAlign: "center", marginBottom: 18, flexShrink: 0 }}>
          <div style={{ fontSize: 38, marginBottom: 10 }}>{s.icon}</div>
          <div style={{ ...GF, fontSize: 17, color: "#C9A84C", fontWeight: 700 }}>{s.title}</div>
        </div>

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, marginBottom: 18, paddingRight: 4 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p style={{ ...GL, fontSize: 15, color: "#C8D8E8", lineHeight: 1.75, margin: 0, whiteSpace: "pre-line" }}>{s.body}</p>
            {s.note && (
              <div style={{ background: "#C9A84C12", border: "1px solid #C9A84C33", borderRadius: 10, padding: "10px 13px", ...GL, fontSize: 14, color: "#C8B870", lineHeight: 1.65 }}>{s.note}</div>
            )}
            {s.list && (
              <div style={{ background: "#0A1E35", borderRadius: 10, padding: "10px 13px", ...GL, fontSize: 14, color: "#A8C0D0", lineHeight: 1.75, whiteSpace: "pre-line" }}>{s.list}</div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 14, flexShrink: 0 }}>
          {SCREENS.map((_, i) => (
            <div key={i} onClick={() => setStep(i)} style={{ width: i === step ? 22 : 7, height: 7, borderRadius: 4, background: i === step ? "#C9A84C" : "#1A3050", cursor: "pointer", transition: "all 0.25s" }} />
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)} style={{ flex: 1, padding: "12px", borderRadius: 14, border: "1px solid #1A2F45", background: "transparent", color: "#5A7A95", ...GL, fontSize: 15, cursor: "pointer" }}>
              ← {isEN ? "Back" : "Retour"}
            </button>
          )}
          {!isLast ? (
            <button onClick={() => setStep(s => s + 1)} style={{ flex: 2, padding: "14px", borderRadius: 14, border: "1px solid #4A9C5A", background: "linear-gradient(135deg,#1A4A2A,#0D2A15)", color: "#7AE890", ...GL, fontWeight: 700, fontSize: 16, cursor: "pointer" }}>
              {isEN ? "Next →" : "Suivant →"}
            </button>
          ) : (
            <button onClick={onClose} style={{ flex: 2, padding: "14px", borderRadius: 14, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GF, fontWeight: 700, fontSize: 16, cursor: "pointer" }}>
              {isEN ? "Start searching →" : "Commencer ma recherche →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── WHAT'S NEW MODAL ─────────────────────────────────────────────────────────
// Liste les dernières évolutions visibles côté user (cf. src/data/changelog.js)

const LATEST_CHANGELOG_DATE = CHANGELOG[0]?.date || "";

function hasUnseenChangelog() {
  try {
    const seen = localStorage.getItem("lastSeenChangelog") || "";
    return LATEST_CHANGELOG_DATE > seen;
  } catch { return false; }
}

function WhatsNewModal({ onClose, lang = "fr" }) {
  const isEN = lang === "en";
  useEffect(() => {
    try { localStorage.setItem("lastSeenChangelog", LATEST_CHANGELOG_DATE); } catch {}
    try { track("whats_new_opened"); } catch {}
  }, []);

  const typeStyle = {
    new:           { label_fr: "Nouveau",       label_en: "New",          color: "#7AE890", bg: "#1A4A2A33" },
    improvement:   { label_fr: "Amélioration",  label_en: "Improvement",  color: "#C9A84C", bg: "#C9A84C22" },
    "behind-scenes": { label_fr: "Coulisses",  label_en: "Behind the scenes", color: "#7A9BB5", bg: "#1A305022" },
  };

  function fmtDate(iso) {
    const [y, m, d] = iso.split("-");
    const months_fr = ["janv.", "févr.", "mars", "avril", "mai", "juin", "juill.", "août", "sept.", "oct.", "nov.", "déc."];
    const months_en = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const months = isEN ? months_en : months_fr;
    return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: 20 }}>
      <div style={{ background: "#091929", border: "1px solid #C9A84C44", borderRadius: 24, padding: "24px 22px", maxWidth: 540, width: "100%", maxHeight: "90vh", position: "relative", display: "flex", flexDirection: "column" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 16, background: "transparent", border: "none", color: "#3A5A72", fontSize: 18, cursor: "pointer", lineHeight: 1, padding: 4, zIndex: 1 }}>✕</button>

        <div style={{ marginBottom: 16, flexShrink: 0 }}>
          <div style={{ ...GF, fontSize: 19, color: "#C9A84C", fontWeight: 700 }}>
            🆕 {isEN ? "What's new" : "Nouveautés"}
          </div>
          <div style={{ ...GL, fontSize: 13, color: "#7A9BB5", marginTop: 4 }}>
            {isEN ? "What changed on the site recently" : "Ce qui a évolué récemment sur le site"}
          </div>
        </div>

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, paddingRight: 4 }}>
          {CHANGELOG.map((entry, i) => {
            const ts = typeStyle[entry.type] || typeStyle.improvement;
            return (
              <div key={i} style={{ background: "#0A1E35", borderRadius: 12, padding: "14px 16px", marginBottom: 10, border: "1px solid #1A305088" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18 }}>{entry.icon}</span>
                    <span style={{ ...GL, fontSize: 11, color: ts.color, background: ts.bg, padding: "3px 8px", borderRadius: 6, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {isEN ? ts.label_en : ts.label_fr}
                    </span>
                  </div>
                  <span style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>{fmtDate(entry.date)}</span>
                </div>
                <div style={{ ...GF, fontSize: 15, color: "#E8D8B8", fontWeight: 700, marginBottom: 4 }}>
                  {isEN ? entry.title_en : entry.title_fr}
                </div>
                <div style={{ ...GL, fontSize: 13.5, color: "#A8C0D0", lineHeight: 1.6 }}>
                  {isEN ? entry.body_en : entry.body_fr}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ flexShrink: 0, marginTop: 16 }}>
          <button onClick={onClose} style={{ width: "100%", padding: "12px", borderRadius: 14, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GF, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
            {isEN ? "Got it" : "Compris"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Vitrine "capacités" — sans dates (≠ changelog). Cadrée comme les fonctions
// du produit pour démontrer la valeur sans marqueur temporel récent.
function CapabilitiesModal({ onClose, lang = "fr" }) {
  const isEN = lang === "en";
  useEffect(() => { try { track("capabilities_opened"); } catch {} }, []);

  const CAPS = isEN ? [
    { icon: "🧠", title: "The power of Anthropic's AI", body: "Anthropic's AI deeply analyzes the web to find the best cruise for YOUR exact criteria — real reasoning and analysis, not just a filter." },
    { icon: "⚡", title: "Hours of research in moments", body: "In seconds, it sifts through dozens of options and ships (Royal Caribbean, Carnival, Norwegian, MSC and more) — the equivalent of hours of manual shopping, done for you." },
    { icon: "✈️", title: "Flight + hotel near the port", body: "The analysis includes a flight from your city and a hotel with shuttle near the port — so you leave with peace of mind." },
    { icon: "💬", title: "A conversational agent on every option", body: "A question about a cabin, a port stop or travel logistics? Chat with the AI — it remembers the full context of your search." },
    { icon: "💾", title: "Saved, named searches", body: "Keep several searches, name them, and find them again on every device — without paying twice." },
    { icon: "🎯", title: "Smart criteria broadening", body: "When your criteria are too specific to return satisfying results, the tool suggests smart ways to broaden them — without starting over." },
    { icon: "🤝", title: "Partners with a best-price guarantee", body: "Prices shown are estimates to compare; we then send you straight to our selected partners (CruiseDirect, Expedia), chosen for their best-price guarantee." },
    { icon: "🖨️", title: "Clean printable summary", body: "Print a tidy summary of the offer you like — only what you need, nothing extra." },
    { icon: "📖", title: "A blog to shop smart", body: "Honest guides: travel agent vs traditional sites vs AI, understanding fees, picking the right cruise line. Worth a read before booking.", href: "/blog/en/" },
  ] : [
    { icon: "🧠", title: "La puissance de l'IA d'Anthropic", body: "L'intelligence artificielle d'Anthropic analyse le web en profondeur pour trouver la meilleure croisière selon TES critères précis — une vraie analyse qui raisonne, pas un simple filtre." },
    { icon: "⚡", title: "Des heures de recherche en quelques instants", body: "En quelques secondes, l'outil passe au crible des dizaines d'options et de navires (Royal Caribbean, Carnival, Norwegian, MSC et plus) — l'équivalent d'heures de magasinage manuel, fait pour toi." },
    { icon: "✈️", title: "Vol + hôtel près du port", body: "L'analyse inclut un vol depuis ta ville et un hôtel avec navette près du port — pour partir l'esprit tranquille." },
    { icon: "💬", title: "Un agent conversationnel sur chaque option", body: "Une question sur une cabine, une escale ou la logistique de voyage ? Discute avec l'IA — elle se souvient du contexte complet de ta recherche." },
    { icon: "💾", title: "Recherches sauvegardées et nommées", body: "Garde plusieurs recherches, donne-leur un nom, et retrouve-les sur tous tes appareils — sans repayer." },
    { icon: "🎯", title: "Élargissement intelligent des critères", body: "Quand tes critères sont trop précis pour donner des résultats satisfaisants, l'outil te propose des élargissements intelligents — sans tout recommencer." },
    { icon: "🤝", title: "Des partenaires au meilleur prix garanti", body: "Les prix affichés sont des estimés pour comparer ; on t'envoie ensuite directement chez nos partenaires sélectionnés (CruiseDirect, Expedia), choisis pour leur garantie du meilleur prix." },
    { icon: "🖨️", title: "Sommaire imprimable et épuré", body: "Imprime un sommaire propre de l'offre qui t'intéresse — seulement ce dont tu as besoin, rien de superflu." },
    { icon: "📖", title: "Un blogue pour magasiner futé", body: "Des guides honnêtes : agent de voyage vs sites traditionnels vs IA, comprendre les frais, choisir la bonne compagnie. À lire avant de réserver.", href: "/blog/" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: 20 }}>
      <div style={{ background: "#091929", border: "1px solid #C9A84C44", borderRadius: 24, padding: "24px 22px", maxWidth: 540, width: "100%", maxHeight: "90vh", position: "relative", display: "flex", flexDirection: "column" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 16, background: "transparent", border: "none", color: "#3A5A72", fontSize: 18, cursor: "pointer", lineHeight: 1, padding: 4, zIndex: 1 }}>✕</button>
        <div style={{ marginBottom: 16, flexShrink: 0 }}>
          <div style={{ ...GF, fontSize: 19, color: "#C9A84C", fontWeight: 700 }}>
            ✨ {isEN ? "What this tool does for you" : "Ce que l'outil fait pour vous"}
          </div>
          <div style={{ ...GL, fontSize: 13, color: "#7A9BB5", marginTop: 4 }}>
            {isEN ? "A real AI travel scout — here's everything it handles." : "Un vrai éclaireur de voyage IA — voici tout ce qu'il prend en charge."}
          </div>
        </div>
        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, paddingRight: 4 }}>
          {CAPS.map((c, i) => {
            const inner = (
              <>
                <div style={{ ...GF, fontSize: 15, color: "#E8D8B8", fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{c.icon}</span> {c.title}
                  {c.href && <span style={{ marginLeft: "auto", color: "#C9A84C", fontSize: 16 }}>→</span>}
                </div>
                <div style={{ ...GL, fontSize: 13.5, color: "#A8C0D0", lineHeight: 1.6 }}>{c.body}</div>
              </>
            );
            const cardStyle = { background: "#0A1E35", borderRadius: 12, padding: "14px 16px", marginBottom: 10, border: `1px solid ${c.href ? "#C9A84C55" : "#1A305088"}`, display: "block" };
            return c.href
              ? <a key={i} href={c.href} target="_blank" rel="noopener noreferrer" style={{ ...cardStyle, textDecoration: "none", cursor: "pointer" }}>{inner}</a>
              : <div key={i} style={cardStyle}>{inner}</div>;
          })}
        </div>
        <div style={{ flexShrink: 0, marginTop: 16 }}>
          <button onClick={onClose} style={{ width: "100%", padding: "12px", borderRadius: 14, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GF, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
            {isEN ? "Got it" : "Compris"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FeedbackModal({ onClose }) {
  const [text, setText] = useState("");
  const [type, setType] = useState("suggestion");
  const [sent, setSent] = useState(false);

  const handleSend = () => {
    if (!text.trim()) return;
    const subject = encodeURIComponent(`Croisière AI — ${type === "bug" ? "Bug signalé" : "Suggestion"}`);
    const body    = encodeURIComponent(text);
    window.open(`mailto:contact@croisiereai.ca?subject=${subject}&body=${body}`, "_blank");
    setSent(true);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
      <div style={{ background: "#091929", border: "1px solid #C9A84C44", borderRadius: 20, padding: 24, maxWidth: 400, width: "100%" }}>
        {!sent ? (
          <>
            <div style={{ ...GF, fontSize: 18, color: "#C9A84C", marginBottom: 6 }}>💬 Votre feedback</div>
            <div style={{ ...GL, fontSize: 13, color: "#5A7A95", marginBottom: 14, lineHeight: 1.6 }}>
              Aidez-nous à améliorer Croisière AI — chaque suggestion compte !
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[["suggestion", "💡 Suggestion"], ["bug", "🐛 Bug"]].map(([v, l]) => (
                <button key={v} onClick={() => setType(v)} style={{
                  flex: 1, padding: "8px", borderRadius: 10,
                  border: `1px solid ${type === v ? "#C9A84C" : "#1A2F45"}`,
                  background: type === v ? "#C9A84C22" : "transparent",
                  color: type === v ? "#C9A84C" : "#5A7A95",
                  ...GL, fontSize: 14, cursor: "pointer",
                }}>{l}</button>
              ))}
            </div>
            <textarea
              placeholder={type === "bug" ? "Décrivez le problème rencontré…" : "Votre suggestion d'amélioration…"}
              value={text}
              onChange={e => setText(e.target.value)}
              rows={4}
              style={{ background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 10, padding: "10px 12px", color: "#C8D8E8", ...GL, fontSize: 14, width: "100%", outline: "none", resize: "vertical", marginBottom: 14, boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 12, border: "1px solid #1A2F45", background: "transparent", color: "#5A7A95", ...GL, cursor: "pointer" }}>Annuler</button>
              <button onClick={handleSend} disabled={!text.trim()} style={{ flex: 2, padding: "11px", borderRadius: 12, border: "none", background: text.trim() ? "linear-gradient(135deg,#C9A84C,#A8882A)" : "#1A2F45", color: text.trim() ? "#07111F" : "#3A5A72", ...GL, fontWeight: 700, cursor: text.trim() ? "pointer" : "not-allowed" }}>
                Envoyer →
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🙏</div>
            <div style={{ ...GF, fontSize: 18, color: "#C9A84C", marginBottom: 8 }}>Merci !</div>
            <div style={{ ...GL, fontSize: 14, color: "#7A9BB5", lineHeight: 1.7, marginBottom: 16 }}>
              Votre message a été transmis. Nous l'examinerons avec attention.
            </div>
            <button onClick={onClose} style={{ padding: "11px 24px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontWeight: 700, cursor: "pointer" }}>Fermer</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── EXTRACTION HELPERS ────────────────────────────────────────────────────────
// Un step est « déjà répondu » quand sa valeur est significative.
// Doit rester aligné avec la fonction ok() du funnel.
function isStepFilled(step, v, data) {
  if (step.type === "priority_rank")        return Array.isArray(v) && v.length === 4;
  // Step durée : exact_nights valide (≥2) tient lieu de réponse aussi.
  // Étape « when » fusionnée (dates 06-07 + durée 06-10) : dates valides si l'option
  // en exige, ET durée présente (carte ou nuits exactes) — sinon l'avance intelligente
  // sauterait la durée jamais répondue.
  if (step.id === "when") {
    if (!v) return false;
    // Navire précis : durée non exigée (parité avec l'ex-étape duration, showIf by_ship).
    const durWaived = data?.intent === "by_ship" || !!data?.specific_ship;
    const hasDur = durWaived || !!data?.duration || (typeof data?.exact_nights === "number" && data.exact_nights >= 2);
    if (!hasDur) return false;
    if (["exact", "flexible", "month", "range"].includes(v))
      return v === "range" ? !!(data?.dates?.from && data?.dates?.to) : !!(data?.dates?.from || data?.dates?.month != null);
    return true;
  }
  // Groupe : « group » n'est rempli qu'avec sa taille (+ occupation si ≥3) — inline.
  if (step.id === "group") {
    if (!v) return false;
    if (v === "group") {
      const n = parseInt(data?.group_size, 10) || 0;
      if (!n) return false;
      if (n >= 3 && !data?.cabin_occupancy) return false;
    }
    return true;
  }
  if (step.type === "cards" && !step.multi) return !!v;
  if (step.type === "cards" && step.multi)  return Array.isArray(v) && v.length > 0;
  // Famille : adultes requis + occupation (inline) dès 3 voyageurs.
  if (step.type === "family") {
    if (!(v && v.adults != null)) return false;
    const pax = (v.adults || 0) + ((v.children || []).length);
    if (pax >= 3 && !data?.cabin_occupancy) return false;
    return true;
  }
  if (step.type === "dates")         return data?.when === "range" ? !!(v?.from && v?.to) : !!(v?.from || v?.month != null);
  if (step.type === "ship_class")    return Array.isArray(v) ? v.length > 0 : !!v;
  // ⚠️ « rempli » sert à l'avance intelligente (saut des étapes déjà répondues).
  // Pour les étapes OPTIONNELLES, « rempli » DOIT vouloir dire « a une vraie valeur »,
  // sinon le funnel vierge les saute (bug 2026-06-07 : étapes escales + compagnies
  // jamais affichées). Le bouton « Suivant » reste franchissable sans réponse via ok().
  if (step.type === "companies")         return Array.isArray(v) && v.length > 0;
  if (step.type === "company_for_ship") return !!v;
  if (step.type === "specific_ship")    return !!v;
  if (step.type === "ports")         return Array.isArray(data?.desired_ports) && data.desired_ports.length > 0;
  if (step.type === "budget")        return v != null;
  if (step.type === "port_picker")   return Array.isArray(v) && v.length > 0;
  if (step.type === "airport")       return !!v && (v !== "other" || !!(data?.origin_airport_custom || "").trim());
  return !!v;
}

function findFirstUnfilledIdx(data) {
  const vis = STEPS.filter(s => !WL_HIDDEN_STEPS.has(s.id) && (!s.showIf || s.showIf(data)));
  for (let i = 0; i < vis.length; i++) {
    if (!isStepFilled(vis[i], data[vis[i].id], data)) return i;
  }
  return Math.max(0, vis.length - 1);
}

// Haiku peut renvoyer un slug imparfait (ex: "royal-caribbean", "ncl", "rcl").
// On normalise vers les valeurs du funnel.
function normalizeCompany(raw) {
  const s = String(raw || "").toLowerCase().replace(/[-\s]/g, "_");
  const valid = CRUISE_COMPANIES.map(c => c.v);
  if (valid.includes(s)) return s;
  if (s.includes("royal") || s === "rcl" || s === "rc") return "royal_caribbean";
  if (s.includes("norwegian") || s === "ncl")           return "norwegian";
  if (s.includes("carnival") || s === "ccl")            return "carnival";
  if (s.includes("celebrity"))                          return "celebrity";
  if (s.includes("princess"))                           return "princess";
  if (s === "msc" || s.includes("msc"))                 return "msc";
  if (s.includes("holland"))                            return "holland_america";
  if (s.includes("virgin"))                             return "virgin";
  return null;
}

// Retrouve la compagnie d'un navire (fuzzy: "star of the sea" → "Star of the Seas" → royal_caribbean).
// Renvoie { company, shipName } ou null.
function resolveShipToCompany(rawName) {
  const q = String(rawName || "").toLowerCase().trim();
  if (!q) return null;
  for (const [company, ships] of Object.entries(SHIPS_BY_COMPANY)) {
    for (const ship of ships) {
      const sLow = ship.toLowerCase();
      if (sLow === q || sLow.includes(q) || q.includes(sLow)) {
        return { company, shipName: ship };
      }
    }
  }
  return null;
}

function addSevenDays(iso) {
  try {
    const d = new Date(iso + "T12:00:00");
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + 7);
    return d.toISOString().split("T")[0];
  } catch { return null; }
}

// Garde-fou DATES PASSÉES (critique) : l'agent/extracteur peut renvoyer une année
// passée (biais « 2025 » du modèle) → l'usager voit « dates dans le passé » alors
// qu'il n'a jamais dit 2025. On AVANCE toute date passée à la prochaine occurrence
// FUTURE. C'est un outil de réservation : seul le futur existe. Idempotent sur une
// date déjà future (ne touche à rien). Couvre from/to précis ET mois+année.
export function clampDatesToFuture(dates) {
  if (!dates || typeof dates !== "object") return dates;
  const d = { ...dates };
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const curY = now.getFullYear(), curM = now.getMonth() + 1;
  if (d.from) {
    // Dates précises : avancer par années ENTIÈRES jusqu'à ce que `from` soit >= aujourd'hui.
    let add = 0;
    while (add < 6) {
      const t = new Date(d.from + "T12:00:00"); t.setFullYear(t.getFullYear() + add);
      if (t.toISOString().slice(0, 10) >= today) break;
      add++;
    }
    if (add > 0) {
      const shift = iso => { if (!iso) return iso; const x = new Date(iso + "T12:00:00"); x.setFullYear(x.getFullYear() + add); return x.toISOString().slice(0, 10); };
      d.from = shift(d.from);
      if (d.to) d.to = shift(d.to);
      if (d.year != null) d.year = Number(d.year) + add;
    }
  } else if (d.month != null) {
    // Mois (+ année éventuelle). On recalcule l'année À PARTIR D'AUJOURD'HUI au lieu de
    // faire confiance à celle de l'agent : une extraction aberrante (ex: « décembre 1976 »)
    // ne doit jamais survivre. L'ancien garde-fou plafonnait l'avance à +6 ans → 1976
    // devenait 1982, toujours dans le passé (bug testeur 2026-05-31).
    const m = Number(d.month);
    let y = curY;
    if (m < curM) y++; // ce mois est déjà passé cette année → prochaine occurrence = l'an prochain
    // On n'honore l'année fournie que si elle est réellement dans le futur.
    if (d.year != null && Number(d.year) > y) y = Number(d.year);
    d.year = y;
  }
  return d;
}

export function mergeExtractedParams(existing, extracted) {
  const merged = { ...existing };
  const valid = {
    experience:      ["first", "some", "expert"],
    vibe:            ["party", "family", "romantic", "adventure"],
    intent:          ["best_deal", "by_date", "by_company", "by_ship"],
    ship_freshness:   ["recent", "any", "classic_ok"],
    loyalty:          ["royal_caribbean", "carnival", "celebrity", "norwegian", "princess", "holland_america", "msc", "none"],
    duration:    ["short", "week", "long", "xlong", "any"],
    when:        ["exact", "flexible", "month", "range", "anytime"],
    group:       ["solo", "couple", "family", "group"],
    cabin_occupancy: ["one", "multiple", "unsure"],
    departure:   ["drive_ny", "fly_south", "either"],
    overnight:   ["yes", "no"],
    cabin:       ["balcony_ocean", "balcony_centralpark", "balcony_boardwalk", "oceanview", "inside", "suite"],
    ship_class:  ["icon_class", "oasis_class", "quantum_class", "voyager_class", "prima_class", "breakaway_class", "excel_class", "vista_class", "edge_class", "sphere_class", "royal_class", "world_class", "seashore_class", "pinnacle_class", "scarlet_class"],
    origin_airport: ["YUL", "YQB", "other"],
    flight_type:    ["direct", "connections", "any"], // sinon « vol direct seulement » dit à l'agent est jeté (lu par buildPrompt)
    // Précisions collectées par l'agent conversationnel — DOIVENT remonter
    // jusqu'à buildPrompt, sinon la recherche ignore région/occasion/priorités.
    regions:        ["carib_east", "carib_west", "carib_south", "bermuda", "europe", "europe_med", "europe_north", "europe_british", "alaska", "hawaii", "asia", "repositioning", "any"],
    past_regions:   ["carib_east", "carib_west", "carib_south", "bermuda", "europe", "europe_med", "europe_north", "europe_british", "alaska", "hawaii", "asia", "none"],
    port_activities: ["beach", "culture", "food", "shopping", "adventure", "any"],
    sea_days_pref:  ["max_ports", "balanced", "sea_days", "any"],
    occasion:       ["honeymoon", "anniversary", "birthday", "graduation", "none"],
    priorities:     ["prix", "itineraire", "bateau", "cabine"],
  };
  for (const key of Object.keys(valid)) {
    const v = extracted[key];
    if (v == null) continue;
    if (Array.isArray(v)) {
      const filtered = v.filter(x => valid[key].includes(x));
      if (filtered.length > 0) merged[key] = filtered;
    } else if (valid[key].includes(v)) {
      merged[key] = v;
    }
  }
  if (typeof extracted.budget === "number" && extracted.budget > 0) {
    merged.budget = Math.round(extracted.budget);
  }
  if (typeof extracted.group_size === "number" && extracted.group_size > 0) {
    merged.group_size = Math.round(extracted.group_size);
  }
  if (typeof extracted.exact_nights === "number" && Number.isFinite(extracted.exact_nights)) {
    const n = Math.round(extracted.exact_nights);
    if (n >= 2 && n <= 21) merged.exact_nights = n;
  }
  // departure_port = texte libre (n'importe quel port mondial, ex: « Rome ») → pas
  // d'enum. On nettoie et on borne à 4. Honoré comme contrainte dure dans buildPrompt.
  {
    const raw = Array.isArray(extracted.departure_port)
      ? extracted.departure_port
      : (typeof extracted.departure_port === "string" && extracted.departure_port.trim() ? [extracted.departure_port] : []);
    const ports = raw.filter(p => typeof p === "string" && p.trim()).map(p => p.trim()).slice(0, 4);
    if (ports.length) merged.departure_port = ports;
  }
  if (extracted.dates && typeof extracted.dates === "object") {
    const d = {};
    if (extracted.dates.from)  d.from  = extracted.dates.from;
    if (extracted.dates.to)    d.to    = extracted.dates.to;
    // Convention unique de l'app : mois 1-12 (janvier=1). L'agent peut
    // déraper et renvoyer 0 (ancien schéma 0-based) → 0 serait "falsy"
    // côté worker (p.dates.month || fallback) et viserait le MOIS COURANT.
    // On garde-fou ici : 0 → janvier, et on borne à 1-12.
    if (extracted.dates.month != null) {
      let m = Number(extracted.dates.month);
      if (Number.isFinite(m)) {
        if (m === 0) m = 1;
        m = Math.min(12, Math.max(1, Math.round(m)));
        d.month = m;
      }
    }
    if (extracted.dates.year  != null) d.year  = extracted.dates.year;
    if (Object.keys(d).length > 0) merged.dates = { ...(merged.dates || {}), ...d };
  }
  // Garde-fou : aucune date passée n'entre dans la recherche (biais « 2025 » du modèle).
  if (merged.dates) merged.dates = clampDatesToFuture(merged.dates);
  if (extracted.family_detail && typeof extracted.family_detail === "object") {
    const fd = {
      adults:   extracted.family_detail.adults ?? 2,
      children: Array.isArray(extracted.family_detail.children) ? extracted.family_detail.children : [],
    };
    merged.family_detail = fd;
  }
  if (Array.isArray(extracted.companies)) {
    const normalized = extracted.companies.map(normalizeCompany).filter(Boolean);
    if (normalized.length > 0) merged.companies = Array.from(new Set(normalized));
  }
  // Escales précises captées par l'agent (texte libre, normalisé/matché côté worker).
  if (Array.isArray(extracted.desired_ports)) {
    const ports = extracted.desired_ports.map(p => String(p || "").trim()).filter(Boolean);
    if (ports.length > 0) merged.desired_ports = Array.from(new Set(ports));
  }
  if (typeof extracted.specific_ship === "string" && extracted.specific_ship.trim()) {
    const resolved = resolveShipToCompany(extracted.specific_ship);
    if (resolved) {
      merged.specific_ship = resolved.shipName;
      if (!merged.companies || merged.companies.length === 0) {
        merged.companies = [resolved.company];
      }
    } else {
      merged.specific_ship = extracted.specific_ship.trim();
    }
  }
  if (typeof extracted.origin_airport_custom === "string" && extracted.origin_airport_custom.trim()) {
    merged.origin_airport_custom = extracted.origin_airport_custom.trim().toUpperCase();
    if (!merged.origin_airport) merged.origin_airport = "other";
  }

  // Inférences: si l'utilisateur nomme un navire ou une compagnie, il sait ce qu'il veut.
  if (merged.specific_ship && !merged.intent)     merged.intent     = "by_ship";
  if (merged.specific_ship && !merged.experience) merged.experience = "expert";
  if (!merged.specific_ship && merged.companies?.length && !merged.intent)     merged.intent     = "by_company";
  if (!merged.specific_ship && merged.companies?.length && !merged.experience) merged.experience = "some";

  // Si date de départ fournie sans retour → retour = départ + 7 jours.
  if (merged.dates?.from && !merged.dates?.to) {
    const to = addSevenDays(merged.dates.from);
    if (to) merged.dates = { ...merged.dates, to };
  }
  // Si dates exactes fournies → when = "exact" par défaut.
  if (merged.dates?.from && !merged.when) merged.when = "exact";

  return merged;
}

// ─── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setS]   = useState("welcome");
  const ref              = useRef("welcome");
  const go               = useCallback(s => { ref.current = s; setS(s); }, []);
  const [idx, setIdx]    = useState(0);
  // « L'utilisateur a reculé manuellement » → on passe en navigation LINÉAIRE.
  // Le saut "prochaine question vide" (nextUnfilledIdx) est fait pour le funnel
  // pré-rempli par l'agent ; dès qu'on recule dans des étapes déjà remplies, il
  // propulse l'usager à la fin / lance la recherche au milieu. Ce drapeau corrige.
  const [wentBack, setWentBack] = useState(false);
  const [editReturn, setEditReturn] = useState(false);  // édition d'UN critère depuis le récap → revenir au récap (pas avancer dans le funnel)
  const [data, setData]  = useState(() => ({ ...WL_STEP_DEFAULTS }));
  const [offers, setOffers] = useState([]);
  const [showRefinement, setShowRefinement] = useState(false);
  const [refinementOptions, setRefinementOptions] = useState(null);
  const [compromiseNote, setCompromiseNote] = useState(null);
  const [error, setError] = useState(null);
  const [watchOffer, setWatchOffer]         = useState(null);
  const [showProUpsell, setShowProUpsell]   = useState(false);
  const [offerHistories, setOfferHistories] = useState({});
  const [showFeedback, setShowFeedback]     = useState(false);
  const [showAdmin, setShowAdmin]           = useState(false);
  const [showStats, setShowStats]           = useState(false);
  const [showDashboard, setShowDashboard]   = useState(false);
  const [showReturns, setShowReturns]       = useState(false); // panneau admin « Retours usagers » (👎 + résultats à drapeau)
  const [showCapabilities, setShowCapabilities] = useState(false);
  const [showRedeem, setShowRedeem]         = useState(false);
  const [showCodes, setShowCodes]           = useState(false);
  const [cdmLoading, setCdmLoading]         = useState(false);
  const [cdmOffers, setCdmOffers]           = useState(null);
  const [cdmVigie, setCdmVigie]             = useState("");
  const [cdmSending, setCdmSending]         = useState(false);
  const [cdmResult, setCdmResult]           = useState(null);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showTourPrompt, setShowTourPrompt]   = useState(false); // bannière « visite guidée » proéminente 1re visite (non bloquante)
  const [showPaywall, setShowPaywall]         = useState(false);
  const [autoSearch,  setAutoSearch]          = useState(false);
  const [coverageGate, setCoverageGate]       = useState(null); // {level,suggest,reason} si recherche hors-scope (court/xlong)
  const [showWelcome, setShowWelcome]         = useState(false);
  const [inspireMode, setInspireMode]         = useState(false); // affiche la « vedette » + CTA conversion (pas une recherche perso)
  const [inspireAvailable, setInspireAvailable] = useState(false); // une vedette est-elle publiée ? (sinon on cache le bouton)
  const [inspirePublishing, setInspirePublishing] = useState(false);
  const [showWhatsNew, setShowWhatsNew]       = useState(false);
  const [recentSearches, setRecentSearches]   = useState(() => loadRecentSearches());
  const [recentOpen, setRecentOpen]           = useState(false); // panneau recherches récentes replié par défaut
  const [manuallySaved, setManuallySaved]     = useState(false);
  const [shareInfo, setShareInfo]             = useState(null); // {url, busy} — lien-résultat partageable (admin)
  const [savedList, setSavedList]             = useState(() => loadLocalSavedList());
  const [showSavedList, setShowSavedList]     = useState(false);
  const [navOpen, setNavOpen]                 = useState(false);   // menu ☰ accueil
  const [showDeals, setShowDeals]             = useState(false);   // modale « 🔥 Offres »
  const [renamingId, setRenamingId]           = useState(null);
  const [renameValue, setRenameValue]         = useState("");
  const chatRef    = useRef(null);
  const searchRef  = useRef(null);
  const [chatForceOpen, setChatForceOpen] = useState(false);
  // Vue mobile RÉACTIVE (state, se met à jour au resize) → pilote le carrousel d'offres.
  const [isMobileView, setIsMobileView] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [currentSlide, setCurrentSlide] = useState(0);   // index de l'offre visible dans le carrousel
  const [showAllOffers, setShowAllOffers] = useState(false); // par défaut on montre 3 offres (focus) → « voir plus » révèle les offres-plancher
  const [traceId, setTraceId] = useState(null); // id de la recherche courante (en-tête worker) → lie le rating au cas
  const searchMetaRef = useRef({ init: "funnel", text: "" }); // comment la recherche a été initiée (funnel/agent + texte brut)
  const offersScrollRef = useRef(null);
  // Découvrabilité du swipe : nudge animé + coach-mark tant que l'user n'a jamais swipé.
  // Une fois swipé (1re fois), `cai_swipe_seen` → on n'aide plus (peek discret reste).
  const [swipeSeen, setSwipeSeen] = useState(() => { try { return localStorage.getItem("cai_swipe_seen") === "1"; } catch { return false; } });
  const markSwipeSeen = useCallback(() => {
    setSwipeSeen(true);
    try { localStorage.setItem("cai_swipe_seen", "1"); } catch {}
  }, []);
  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Lien-résultat partagé (?r=<token>) : l'usager ouvre un résultat figé par
  // l'admin → on charge les offres et on va direct aux résultats (sur NOTRE site,
  // avec NOS liens affiliés). On laisse le paramètre dans l'URL pour que le lien
  // reste rechargeable.
  useEffect(() => {
    let cancelled = false;
    try {
      const tok = new URLSearchParams(window.location.search).get("r");
      if (!tok) return;
      (async () => {
        const shared = await kvGetSharedResult(tok);
        if (cancelled || !shared || !Array.isArray(shared.offers)) return;
        setData(shared.data || {});
        setOffers(shared.offers || []);
        setCompromiseNote(shared.compromiseNote || null);
        setRefinementOptions(shared.refinementOptions || null);
        go("results");
        try { track("shared_result_opened"); } catch {}
      })();
    } catch {}
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Deep-link CAMPAGNE LUXE (?luxe=1) : la landing/pub luxe ouvre l'app avec les lignes de
  // luxe DÉJÀ pré-sélectionnées et lance le funnel → l'usager n'a plus qu'à donner région/dates.
  // (Ignoré si ?r= présent : le résultat partagé a priorité.)
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("r")) return;
      if (q.get("luxe") !== "1") return;
      const luxeVals = CRUISE_COMPANIES.filter(c => c.luxury).map(c => c.v);
      setData(d => ({ ...d, companies: luxeVals }));
      searchMetaRef.current = { init: "funnel", text: "luxe_campaign" };
      try { track("luxe_landing_enter"); } catch {}
      go("funnel");
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Nouvelle liste d'offres → ramène le carrousel à la 1re carte + replie les offres-plancher.
  useEffect(() => {
    setCurrentSlide(0);
    setShowAllOffers(false);
    if (offersScrollRef.current) offersScrollRef.current.scrollLeft = 0;
  }, [offers]);
  // Nudge de découverte : si l'user n'a jamais swipé, la carte « rebondit » une fois
  // (glisse ~52px puis revient) → signale qu'on peut swiper. Skip si déjà vu / 1 offre / desktop.
  useEffect(() => {
    if (!isMobileView || swipeSeen || offers.length < 2) return;
    const el = offersScrollRef.current;
    if (!el) return;
    const t1 = setTimeout(() => { if (offersScrollRef.current) offersScrollRef.current.scrollTo({ left: 52, behavior: "smooth" }); }, 700);
    const t2 = setTimeout(() => { if (offersScrollRef.current) offersScrollRef.current.scrollTo({ left: 0, behavior: "smooth" }); }, 1250);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [offers, isMobileView, swipeSeen]);

  // Language detection (cruiseai.com → EN, croisiereai.com → FR)
  const { lang } = useLanguage();
  const t = getStrings(lang);
  const translatedSteps = applyStepTranslations(STEPS, t);

  // 1ère visite : on NE force PLUS la modale « À propos » ni le walkthrough — ils
  // créaient DEUX murs avant la 1re recherche (fuite accueil→recherche mesurée :
  // 25 visiteurs → 7 recherches, 2026-06-01). Le visiteur tombe direct sur l'accueil,
  // qui se suffit (bullets + couverture + exemple de résultat + CTA 1 clic). Le
  // contexte reste ACCESSIBLE À LA DEMANDE : « À propos » (pied de page), bouton
  // « ✨ Découvre tout ce que l'outil fait », et le tour guidé via ces déclencheurs.
  // Réactiver l'auto-lancement = remettre les setShowWelcome/setShowWalkthrough ici.
  // 1ère visite : on propose la visite guidée via une bannière PROÉMINENTE et clignotante
  // en haut de l'accueil (non bloquante, dismissable). Après l'avoir prise OU rejetée, elle
  // disparaît et le tour ne reste accessible que par le lien discret du pied de page.
  useEffect(() => {
    try {
      const seenTour    = localStorage.getItem("cai_walkthrough_seen");
      const dismissedTip = localStorage.getItem("cai_tour_prompt_dismissed");
      if (!seenTour && !dismissedTip) setShowTourPrompt(true);
    } catch {}
  }, []);
  const dismissTourPrompt = () => {
    try { localStorage.setItem("cai_tour_prompt_dismissed", "1"); } catch {}
    setShowTourPrompt(false);
  };
  const startTourFromPrompt = () => {
    try { track("tour_prompt_accepted"); } catch {}
    dismissTourPrompt();
    setShowWalkthrough(true);
  };
  const closeWelcome = () => {
    try { track("welcome_modal_closed"); } catch {}
    localStorage.setItem("croisiere_ai_welcome_seen", "1");
    setShowWelcome(false);
  };

  // Auth Clerk + quota freemium
  const { usage, refreshUsage, getToken, isSignedIn } = useSearchUsage(WORKER_URL);
  const checkSearchGate = useSearchGate(usage, (reason) => setShowPaywall(reason || "limit"), isSignedIn);
  const { redirectToSignUp } = useClerk();
  const { user } = useUser();

  // ── « Inspire-moi » — vedette pré-cuisinée (cache-first, gratuite) ──────────
  // Au montage : la vedette existe-t-elle ? (sinon on cache le bouton de l'accueil).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ins = await kvGetInspire();
      if (!cancelled && ins && Array.isArray(ins.offers) && ins.offers.length) setInspireAvailable(true);
    })();
    return () => { cancelled = true; };
  }, []);
  // Clic public : charge la vedette dans l'écran résultats (mode inspiration). Repli
  // doux vers le funnel si rien n'est publié.
  const openInspire = async () => {
    try { track("inspire_click"); } catch {}
    const ins = await kvGetInspire();
    if (!ins || !Array.isArray(ins.offers) || !ins.offers.length) { go("funnel"); return; }
    setData(ins.data || {});
    setOffers(ins.offers || []);
    setCompromiseNote(ins.compromiseNote || null);
    setRefinementOptions(ins.refinementOptions || null);
    setInspireMode(true);
    go("results");
  };
  // CTA conversion : « la même pour TES dates » → repart des critères de la vedette,
  // vide les offres, ouvre le récap pour ajuster puis lancer une VRAIE recherche.
  const exitInspireToSearch = () => {
    try { track("inspire_to_search"); } catch {}
    setInspireMode(false);
    setOffers([]); setCompromiseNote(null); setRefinementOptions(null);
    go("recap");
  };
  // Admin : fige le résultat affiché comme vedette du moment.
  const publishInspire = async () => {
    if (!offers.length) { alert(lang === "en" ? "No offers to feature." : "Aucune offre à publier."); return; }
    setInspirePublishing(true);
    const res = await kvPublishInspire(getToken, { data, offers, compromiseNote, refinementOptions }, data?.region || "");
    setInspirePublishing(false);
    if (res.ok) { setInspireAvailable(true); alert(lang === "en" ? "✨ Featured as Inspiration of the moment." : "✨ Publié comme inspiration du moment."); }
    else alert((lang === "en" ? "Failed: " : "Échec : ") + (res.error || "?"));
  };

  // Log d'INSCRIPTION : quand un compte FRAÎCHEMENT créé (<15 min) se connecte, on logue
  // un event `signup` une seule fois (drapeau localStorage par usager) → les inscriptions
  // apparaissent enfin dans les stats/digest (avant : invisibles, gérées par Clerk seul).
  useEffect(() => {
    if (!isSignedIn || !user) return;
    try {
      const created = user.createdAt ? new Date(user.createdAt).getTime() : 0;
      const fresh = created && (Date.now() - created < 15 * 60 * 1000);
      const key = `cai_signup_logged_${user.id}`;
      if (fresh && !localStorage.getItem(key)) { track("signup", {}); localStorage.setItem(key, "1"); }
    } catch {}
  }, [isSignedIn, user]);

  // Restore funnel state after auth redirect (sign in / create account)
  useEffect(() => {
    if (!isSignedIn) return;
    const saved = localStorage.getItem('cai_pending_search');
    if (!saved) return;
    localStorage.removeItem('cai_pending_search');
    try {
      const parsed = JSON.parse(saved);
      if (Date.now() - (parsed.ts || 0) > 30 * 60 * 1000) return; // expiré après 30 min
      setData(parsed.data);
      setIdx(parsed.idx);
      if (parsed.shouldSearch) {
        setAutoSearch(true);
      } else {
        go("funnel");
      }
    } catch {}
  }, [isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Déclenche la recherche dès que autoSearch=true et data est mis à jour
  useEffect(() => {
    if (!autoSearch) return;
    setAutoSearch(false);
    search(); // data est déjà mis à jour dans le même batch de render
  }, [autoSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBeforeAuth = (shouldSearch = false) => {
    try { localStorage.setItem('cai_pending_search', JSON.stringify({ data, idx, shouldSearch, ts: Date.now() })); } catch {}
  };

  // ── Recherches sauvegardées : source = localStorage (anonyme) ou KV (connecté) ──
  // À la connexion : on charge le serveur, on y migre les recherches faites en
  // anonyme, et on exécute une éventuelle sauvegarde en attente (CTA d'inscription).
  useEffect(() => {
    if (!isSignedIn) { setSavedList(loadLocalSavedList()); return; }
    let cancelled = false;
    (async () => {
      let server = await kvListSearches(getToken);
      if (server == null) { if (!cancelled) setSavedList(loadLocalSavedList()); return; }
      const local = loadLocalSavedList();
      if (local.length > 0) {
        const ids = new Set(server.map(e => e.id));
        for (const e of local) if (!ids.has(e.id)) { const u = await kvSaveSearch(getToken, e); if (u) server = u; }
        writeLocalSavedList([]);
      }
      const pend = localStorage.getItem('cai_pending_save');
      if (pend) {
        localStorage.removeItem('cai_pending_save');
        try {
          const parsed = JSON.parse(pend);
          if (Date.now() - (parsed.ts || 0) <= 30 * 60 * 1000 && parsed.entry?.offers?.length) {
            const u = await kvSaveSearch(getToken, parsed.entry); if (u) server = u;
          }
        } catch {}
      }
      if (!cancelled) setSavedList(server);
      try {
        if (localStorage.getItem('cai_open_searches_after_auth')) {
          localStorage.removeItem('cai_open_searches_after_auth');
          if (!cancelled && server.length > 0) setShowSavedList(true);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCurrentSearch = async (customName) => {
    if (!offers || offers.length === 0) return;
    const entry = {
      id: newSearchId(),
      name: customName || defaultSearchName(data, lang),
      ts: Date.now(),
      data, offers, compromiseNote, refinementOptions,
    };
    if (isSignedIn) {
      const updated = await kvSaveSearch(getToken, entry);
      setSavedList(updated || [entry, ...savedList].slice(0, SAVED_LIST_MAX));
    } else {
      setSavedList(prev => { const next = [entry, ...prev].slice(0, SAVED_LIST_MAX); writeLocalSavedList(next); return next; });
    }
  };

  // Admin : fige le résultat courant et génère un lien public à coller dans un
  // courriel à l'usager. L'usager ouvre le lien → notre site → nos liens affiliés.
  const shareCurrentResult = async () => {
    if (!offers || offers.length === 0) return;
    setShareInfo({ url: null, busy: true });
    const res = await kvShareResult(getToken, { data, offers, compromiseNote, refinementOptions });
    if (!res || !res.token) { setShareInfo({ url: null, busy: false, error: (res && res.error) || "inconnue" }); return; }
    const url = `${window.location.origin}/?r=${res.token}`;
    setShareInfo({ url, busy: false });
    try { track("share_result_created"); } catch {}
  };

  const deleteSavedSearch = async (id) => {
    if (isSignedIn) {
      const updated = await kvDeleteSearch(getToken, id);
      setSavedList(updated || savedList.filter(e => e.id !== id));
    } else {
      setSavedList(prev => { const next = prev.filter(e => e.id !== id); writeLocalSavedList(next); return next; });
    }
  };

  const renameSavedSearch = async (id, name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) { setRenamingId(null); return; }
    if (isSignedIn) {
      const entry = savedList.find(e => e.id === id);
      if (entry) {
        const updated = await kvSaveSearch(getToken, { ...entry, name: trimmed });
        setSavedList(updated || savedList.map(e => e.id === id ? { ...e, name: trimmed } : e));
      }
    } else {
      setSavedList(prev => { const next = prev.map(e => e.id === id ? { ...e, name: trimmed } : e); writeLocalSavedList(next); return next; });
    }
    setRenamingId(null);
  };

  const openSavedSearch = (entry) => {
    setData(entry.data || {});
    setOffers(entry.offers || []);
    setCompromiseNote(entry.compromiseNote || null);
    setRefinementOptions(entry.refinementOptions || null);
    setShowSavedList(false);
    setManuallySaved(true);
    setInspireMode(false);
    try { track("saved_search_opened", { signed_in: isSignedIn }); } catch {}
    go("results");
  };

  // CTA anonyme : sauve la recherche en attente puis redirige vers l'inscription.
  const requestSignupToSave = () => {
    try {
      localStorage.setItem('cai_pending_save', JSON.stringify({
        entry: { id: newSearchId(), name: defaultSearchName(data, lang), ts: Date.now(), data, offers, compromiseNote, refinementOptions },
        ts: Date.now(),
      }));
    } catch {}
    try { track("save_signup_cta"); } catch {}
    redirectToSignUp({ redirectUrl: window.location.href });
  };

  const vis  = translatedSteps.filter(s => !WL_HIDDEN_STEPS.has(s.id) && (!s.showIf || s.showIf(data)));
  const cur  = vis[idx];
  const last = idx === vis.length - 1;

  // Avance "intelligente" : saute les étapes déjà renseignées (cas funnel
  // pré-rempli par l'agent) et ne s'arrête que sur la prochaine question vide.
  // S'il n'en reste aucune → lance la recherche. Pour un funnel vierge, le
  // comportement est identique à idx+1 (rien n'est rempli en avant).
  // `d` peut être une projection (sélection de carte pas encore commitée).
  const nextUnfilledIdx = (d = data) => {
    const v = translatedSteps.filter(s => !WL_HIDDEN_STEPS.has(s.id) && (!s.showIf || s.showIf(d)));
    const startId = cur?.id;
    let pos = v.findIndex(s => s.id === startId);
    if (pos < 0) pos = idx;
    for (let j = pos + 1; j < v.length; j++) {
      if (!isStepFilled(v[j], d[v[j].id], d)) return j;
    }
    return -1;
  };
  const noQuestionsLeft = nextUnfilledIdx() === -1;
  const goNext = (d = data) => {
    if (editReturn) { setEditReturn(false); go("recap"); return; }  // édition ciblée → retour au récap, on ne poursuit pas le funnel
    if (wentBack) {
      // Après un retour manuel : avance LINÉAIRE vers l'étape visible suivante
      // (pas de saut "prochaine vide", sinon on rocketerait jusqu'à la recherche).
      const v = translatedSteps.filter(s => !WL_HIDDEN_STEPS.has(s.id) && (!s.showIf || s.showIf(d)));
      let pos = v.findIndex(s => s.id === cur?.id);
      if (pos < 0) pos = idx;
      if (pos + 1 >= v.length) search(); else setIdx(pos + 1);
      return;
    }
    const j = nextUnfilledIdx(d);
    if (j === -1) search(); else setIdx(j);
  };

  const ok = () => {
    if (!cur) return false;
    const v = data[cur.id];
    // Étape « when » fusionnée (dates 06-07 + durée 06-10) : Suivant exige des dates
    // valides quand l'option en demande, ET une durée (carte ou nuits exactes ≥2).
    if (cur.id === "when") {
      if (!v) return false;
      // Navire précis : la durée vient de l'itinéraire du navire → pas exigée (parité
      // avec l'ancienne étape duration qui avait showIf intent !== "by_ship").
      const durWaived = data.intent === "by_ship" || !!data.specific_ship;
      const hasDur = durWaived || !!data.duration || (typeof data.exact_nights === "number" && data.exact_nights >= 2);
      if (!hasDur) return false;
      if (["exact", "flexible", "month", "range"].includes(v))
        return v === "range" ? !!(data.dates?.from && data.dates?.to) : !!(data.dates?.from || data.dates?.month != null);
      return true;
    }
    // Groupe : taille requise si « groupe » (inline) + occupation dès 3 voyageurs.
    if (cur.id === "group") {
      if (!v) return false;
      if (v === "group") {
        const n = parseInt(data.group_size, 10) || 0;
        if (!n) return false;
        if (n >= 3 && !data.cabin_occupancy) return false;
      }
      return true;
    }
    if (cur.type === "cards" && !cur.multi) return !!v;
    if (cur.type === "cards" && cur.multi)  return v?.length > 0;
    // Famille : adultes requis + occupation de cabine (inline) dès 3 voyageurs.
    if (cur.type === "family") {
      if (!v || v.adults == null) return false;
      const pax = (v.adults || 0) + ((v.children || []).length);
      if (pax >= 3 && !data.cabin_occupancy) return false;
      return true;
    }
    if (cur.type === "dates")         return data?.when === "range" ? !!(v?.from && v?.to) : !!(v?.from || v?.month);
    if (cur.type === "ship_class")    return Array.isArray(v) ? v.length > 0 : !!v;
    if (cur.type === "companies")         return true;
    if (cur.type === "company_for_ship") return true;
    if (cur.type === "specific_ship")    return !!v;
    if (cur.type === "budget")        return true;
    if (cur.type === "port_picker")   return !!v;
    if (cur.type === "airport")       return !!v && (v !== "other" || !!(data.origin_airport_custom || "").trim());
    if (cur.type === "target")        return true;
    if (cur.type === "priority")      return true;
    return true;
  };

  const card = v => {
    if (cur.multi) {
      const c = data[cur.id] || [];
      setData(d => ({ ...d, [cur.id]: c.includes(v) ? c.filter(x => x !== v) : [...c, v] }));
    } else {
      const patch = { [cur.id]: v };
      // Changer de type de groupe NETTOIE les champs dépendants : un group_size ou un
      // family_detail périmé fausserait paxCount/le prompt (ex. « couple » gardant
      // group_size=4 d'un essai précédent).
      if (cur.id === "group") {
        if (v !== "group")  patch.group_size = undefined;
        if (v !== "family") { patch.family_detail = undefined; patch.infant_flight_seat = undefined; }
        if (v === "solo" || v === "couple") patch.cabin_occupancy = undefined;
      }
      setData(d => ({ ...d, ...patch }));
      // PAS d'auto-avance quand l'étape révèle des sous-questions INLINE : « when »
      // (dates 2026-06-07 + durée 2026-06-10 fusionnées) et « group » quand l'option
      // Groupe exige la taille (+ occupation si ≥3). L'usager remplit puis Suivant.
      const staysOnStep = cur.id === "when" || (cur.id === "group" && v === "group");
      if (!staysOnStep) setTimeout(() => goNext({ ...data, ...patch }), 300);
    }
  };

  const reset = () => { go("welcome"); setIdx(0); setWentBack(false); setEditReturn(false); setData({ ...WL_STEP_DEFAULTS }); setOffers([]); setError(null); setShowRefinement(false); setRefinementOptions(null); setCompromiseNote(null); setInspireMode(false); };

  // Anon ayant déjà épuisé sa recherche gratuite : on bloque AVANT le funnel
  // (pas à la fin) pour ne pas le faire remplir 8 questions pour rien.
  const blockAnonIfExhausted = () => {
    if (isSignedIn) return false;
    const anonCount = parseInt(localStorage.getItem('anonSearchCount') || '0', 10);
    if (anonCount >= 1) { setShowPaywall("anon"); return true; }
    return false;
  };

  const generateCDM = async () => {
    setCdmLoading(true); setCdmOffers(null); setCdmResult(null);
    try {
      const token = await getToken();
      const res = await fetch(WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cdm_generate", token }),
      });
      const data = await res.json();
      if (data.offers) setCdmOffers(data.offers);
      else setCdmResult({ error: [data.error?.message, data.error?.detail].filter(Boolean).join(" — ") || "Erreur génération" });
    } catch (e) {
      setCdmResult({ error: e.message });
    } finally {
      setCdmLoading(false);
    }
  };

  const sendCDM = async () => {
    if (!cdmOffers) return;
    setCdmSending(true);
    try {
      const token = await getToken();
      const res = await fetch(WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cdm_send", token, offers: cdmOffers, vigie_savings: cdmVigie || null }),
      });
      const data = await res.json();
      setCdmResult(data);
    } catch (e) {
      setCdmResult({ error: e.message });
    } finally {
      setCdmSending(false);
    }
  };

  const handleExtracted = (params, _text, opts) => {
    const merged = mergeExtractedParams(data, params || {});
    setData(merged);
    setError(null);
    // Trace : recherche initiée par l'AGENT → on retient le texte brut + ce qu'il a capté.
    searchMetaRef.current = { init: "agent", text: _text || "" };
    if (opts && opts.launch) {
      // Lance la recherche directement (saute le funnel). L'effet autoSearch
      // déclenche search() avec `merged` déjà commité dans le même batch.
      setAutoSearch(true);
    } else {
      setWentBack(false); // funnel pré-rempli par l'agent → saut intelligent permis
      setIdx(findFirstUnfilledIdx(merged));
      go("funnel");
    }
  };

  const search = async (retryAttempt = 0) => {
    // Gates (anon/quota) UNIQUEMENT au 1er essai — un auto-retry après timeout ne doit
    // ni re-déclencher le paywall ni re-décompter le quota (cf. is_refinement plus bas).
    if (retryAttempt === 0) {
      // Gate anonyme : 1 recherche gratuite, puis inscription obligatoire
      if (!isSignedIn) {
        const anonCount = parseInt(localStorage.getItem('anonSearchCount') || '0', 10);
        if (anonCount >= 1) { setShowPaywall("anon"); return; }
      }
      // Vérifie le quota avant de lancer (si user connecté et quota épuisé → paywall)
      if (!checkSearchGate()) return;

      // RESSERREMENT : court/xlong = hors de notre focus (audience QC voyage pour le port).
      // On propose d'élargir vers le 7n+ AVANT de brûler une recherche. _coverageAck =
      // l'usager a choisi « chercher quand même ». Couvre funnel ET agent (point commun).
      if (!data._coverageAck) {
        const cov = assessCoverage(data);
        if (cov.level === "out") { setCoverageGate(cov); return; }
        // Règle A — ligne de luxe × région non couverte (data-driven, cache).
        // On prévient AVANT de brûler une recherche, avec un correctif ciblé.
        const lux = assessLuxuryRegion(data);
        if (lux) { try { track("coherence_gate", { reason: "lux_region" }); } catch {} setCoverageGate(lux); return; }
        // Règle B — budget clairement trop bas pour le groupe/saison (heuristique conservatrice).
        const pc = partyComposition(data);
        const bud = assessBudget(data, { adults: pc.adults, children: pc.children, flightSeats: flightSeats(data) });
        if (bud) { try { track("coherence_gate", { reason: "budget_low" }); } catch {} setCoverageGate(bud); return; }
      }
    }

    // Défaut hôtel veille + vol J-1 : sauf refus explicite ("no"), on recommande
    // d'arriver la veille (plus safe contre les retards de vol + revenu hôtel Expedia).
    // Mode voiture inchangé (logique "suggestion optionnelle" existante).
    const searchData = broadenImpossibleDates({
      ...data,
      overnight: data.overnight === "no"
        ? "no"
        : (data.departure === "drive_ny" ? data.overnight : "yes"),
    });
    setData(searchData);

    go("searching");
    setError(null);

    if (DEMO_MODE) {
      await new Promise(r => setTimeout(r, 6000));
      if (ref.current === "searching") { setOffers([generateDemoOffer(data)]); go("results"); }
      return;
    }

    // ── VRAIE RECHERCHE API (via Cloudflare Worker proxy) ──
    try {
      const res = await fetch(WORKER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Pour forcer une recherche fraîche (ex: bouton "Rafraîchir"),
          // ajouter "X-Refresh": "1" ou passer refresh:true dans le body.
        },
        body: JSON.stringify({
          // Recette côté serveur (2026-06-10) : le prompt complet, le system et le
          // schéma return_cruise_offers sont construits PAR LE WORKER à partir de
          // searchParams (src/promptBuilder.js, bundlé worker-only). Le navigateur
          // n'expose plus la recette ; il n'envoie que les critères + la langue.
          lang,
          // Paramètres normalisés → clé de cache côté worker + construction du prompt.
          // Deux utilisateurs avec les mêmes critères partagent le même résultat.
          searchParams: searchData,
          // Traçabilité : comment la recherche a été lancée + texte brut si agent (le
          // worker stocke la trace pour inspection case-par-cas). N'affecte PAS le cache.
          init: searchMetaRef.current.init,
          ...(searchMetaRef.current.init === "agent" ? { agent_text: searchMetaRef.current.text } : {}),
          // Appel de raffinement OU auto-retry après timeout — exempt de quota côté
          // worker (la 1re tentative a déjà été comptée) + exempt du log analytics.
          ...((data.is_refinement || retryAttempt > 0) ? { is_refinement: true } : {}),
          // JWT Clerk pour identification + quota freemium côté worker.
          ...(isSignedIn ? { token: await getToken() } : {}),
        }),
      });

      try { const _tid = res.headers.get("X-Cruisehunter-Trace-Id"); if (_tid) setTraceId(_tid); } catch {}

      const json = await readWorkerResult(res);

      // Mode maintenance (interrupteur worker) → message propre, pas une erreur cryptique.
      if (json && json.maintenance) {
        setError(lang === "en"
          ? "🛠️ Cruise AI is temporarily unavailable — we're doing a quick tune-up. Please try again in a few minutes."
          : "🛠️ Croisière AI est temporairement indisponible — petite mise au point en cours. Réessayez dans quelques minutes.");
        go("funnel");
        return;
      }

      // ── Méthode 1 (PRIMAIRE): lire le tool_use return_cruise_offers ──────────
      // Garantit un JSON structuré — le modèle ne peut pas divaguer en texte libre.
      let parsed = null;
      const toolBlock = (json.content || []).find(b => b.type === "tool_use" && b.name === "return_cruise_offers");
      if (toolBlock?.input && Array.isArray(toolBlock.input.offers)) {
        parsed = toolBlock.input;
      }

      // ── Méthode 2 (FALLBACK): parser le texte si le modèle a ignoré le tool ──
      const text = parsed ? "" : (json.content || []).map(b => b.type === "text" ? b.text : "").filter(Boolean).join("\n");
      if (!parsed && text) {
        const codeBlock = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
        if (codeBlock?.[1]) {
          try { parsed = JSON.parse(codeBlock[1]); } catch { /* continue */ }
        }
        if (!parsed) {
          const offerIdx = text.search(/"offers?"\s*:/);
          if (offerIdx !== -1) {
            let braceStart = text.lastIndexOf("{", offerIdx);
            if (braceStart !== -1) {
              let depth = 0, end = -1;
              for (let i = braceStart; i < text.length; i++) {
                if (text[i] === "{") depth++;
                else if (text[i] === "}") { depth--; if (depth === 0) { end = i; break; } }
              }
              if (end !== -1) {
                try { parsed = JSON.parse(text.slice(braceStart, end + 1)); } catch { /* continue */ }
              }
            }
          }
        }
      }
      if (!parsed) {
        const first = text.indexOf("{"), last = text.lastIndexOf("}");
        if (first !== -1 && last > first) {
          try { parsed = JSON.parse(text.slice(first, last + 1)); } catch { /* échoué */ }
        }
      }
      // Normalise: accepte {"offers":[...]} ou ancien format {"offer":{...}}
      // Un tableau offers VIDE est une réponse honnête valide (0 départ
      // confirmé) — on le laisse passer tel quel (la bannière refinement
      // expliquera). null = aucune réponse structurée du tout (vrai échec).
      let parsedOffers = Array.isArray(parsed?.offers)
        ? parsed.offers
        : parsed?.offer ? [parsed.offer] : null;

      // Garde-fou: si navire précis + date demandée, s'assurer que offers[0] est la bonne date.
      if (parsedOffers && data.specific_ship && data.dates?.from) {
        const target = data.dates.from; // "YYYY-MM-DD"
        const exactIdx = parsedOffers.findIndex(o => {
          // Vérifie departure_date ou présence de la date dans dateLabel
          if (o.departure_date === target) return true;
          // Fallback: cherche YYYY-MM-DD ou "3 jan" / "jan 3" dans dateLabel
          const dl = (o.dateLabel || "").toLowerCase();
          const d  = new Date(target);
          const day = d.getUTCDate();
          const mon = d.toLocaleString("fr-CA", { month: "short", timeZone: "UTC" }).replace(".", "");
          return dl.includes(target) || dl.includes(`${day} ${mon}`) || dl.includes(`${mon} ${day}`);
        });
        if (exactIdx > 0) {
          // Déplace l'offre avec la bonne date en position 0
          const reordered = [...parsedOffers];
          const [exact] = reordered.splice(exactIdx, 1);
          reordered.unshift(exact);
          parsedOffers = reordered;
        }
      }

      // Hard filter: reject past departures (model or cache may return stale dates)
      if (parsedOffers) {
        const cutoff = new Date().toISOString().slice(0, 10);
        const future = parsedOffers.filter(o => !o.departure_date || o.departure_date >= cutoff);
        if (future.length > 0) parsedOffers = future;
        else if (parsedOffers.length > 0) {
          console.warn("[dates] All offers have past departure dates — nullifying:", parsedOffers.map(o => o.departure_date));
          parsedOffers = null;
        }
        // tableau déjà vide → reste vide (réponse honnête "0 confirmé")
      }

      // Filet anti-invention (backstop frontend) : une date estimée n'atteint
      // JAMAIS l'utilisateur — zéro middle-ground. Idem itinéraire vague
      // « à confirmer ». Si tout est filtré → réponse honnête « 0 confirmé »
      // (la bannière d'élargissement s'affiche) ; on ne réinjecte JAMAIS une
      // offre bancale juste pour éviter un écran vide.
      if (parsedOffers) {
        parsedOffers = parsedOffers.filter(o => {
          if (o.date_estimated) {
            console.warn(`[zero-invention] Exclu "${o.ship_name}" — date_estimated:true (date non confirmée):`, o.departure_date);
            return false;
          }
          const itin = (o.itinerary_summary || "").toLowerCase();
          if (itin.includes("confirmer") || itin.includes("to confirm")) {
            console.warn(`[zero-invention] Exclu "${o.ship_name}" — itinéraire vague / à confirmer`);
            return false;
          }
          // Pseudo-offre (message déguisé en offre) : pas de vraie chaîne
          // d'escales (Port → Escale → … → Port). Sans flèche = fabriqué.
          if (!itin.includes("→")) {
            console.warn(`[zero-invention] Exclu "${o.ship_name}" — pas de chaîne d'itinéraire réelle (pseudo-offre)`);
            return false;
          }
          return true;
        });
      }

      // Garde-fou DURÉE STRICTE — Sonnet a déjà été pris à inclure une 8 nuits
      // pour "week" (7 nuits) en l'avouant dans analysis ("le seul 8 nuits trouvé...").
      // On force le respect du critère côté code, peu importe ce que dit le prompt.
      // Pour "long" et "xlong" (fourchettes ouvertes), on laisse passer car
      // Sonnet peut légitimement faire un compromis 7n expliqué dans compromise_note.
      const exactN_strict = Number(data.exact_nights);
      const hasExactN_strict = Number.isFinite(exactN_strict) && exactN_strict >= 2 && exactN_strict <= 21;
      if (parsedOffers && (hasExactN_strict || (data.duration && data.duration !== "any"))) {
        const nightsOf = (o) => {
          if (typeof o.duration_nights === "number") return o.duration_nights;
          if (o.departure_date && o.return_date) {
            return Math.round((new Date(o.return_date) - new Date(o.departure_date)) / 86400000);
          }
          return null;
        };
        parsedOffers = parsedOffers.filter(o => {
          const n = nightsOf(o);
          if (n == null) return true;
          if (hasExactN_strict && Math.abs(n - exactN_strict) > 1) {
            console.warn(`[duration-strict] Rejet "${o.ship_name}" — ${n}n vs cible ${exactN_strict}n (±1)`);
            return false;
          }
          if (!hasExactN_strict && data.duration === "week" && n !== 7) {
            console.warn(`[duration-strict] Rejet "${o.ship_name}" — ${n}n vs critère 7n (week)`);
            return false;
          }
          if (!hasExactN_strict && data.duration === "short" && (n < 3 || n > 5)) {
            console.warn(`[duration-strict] Rejet "${o.ship_name}" — ${n}n vs critère 3-5n (short)`);
            return false;
          }
          return true;
        });
      }

      // Garde-fou PORT DE DÉPART — contrainte dure code-side (le prompt seul ne suffit
      // pas : Sonnet peut désobéir). Si l'usager a EXIGÉ un/des port(s) d'embarquement
      // (funnel OU extraction conversationnelle « partir de Rome »), on rejette toute
      // offre hors-port. Match robuste par code IATA OU nom de ville :
      // « Miami »≈« Miami, FL », « Rome »≈« Civitavecchia (Rome) ».
      {
        const portConstraint = Array.isArray(data.departure_port)
          ? data.departure_port : data.departure_port ? [data.departure_port] : [];
        if (parsedOffers && portConstraint.length > 0) {
          const cIatas  = portConstraint.map(portToIata).filter(Boolean);
          const cTokens = portConstraint.map(p => String(p).toLowerCase().split(",")[0].trim()).filter(Boolean);
          parsedOffers = parsedOffers.filter(o => {
            const oport = o.port || o.departure_port || "";
            const oi = portToIata(oport);
            const op = oport.toLowerCase();
            const matchIata = oi && cIatas.includes(oi);
            const matchTok  = cTokens.some(t => t && op.includes(t));
            if (matchIata || matchTok) return true;
            console.warn(`[port-strict] Rejet "${o.ship_name}" — port "${oport}" hors contrainte ${portConstraint.join(" / ")}`);
            return false;
          });
        }
      }

      // Garde-fou OVER_BUDGET — Sonnet peut oublier de mettre le flag. On force
      // côté code : si total_cad > budget user, over_budget = true (cohérence
      // visuelle garantie même si Sonnet désobéit).
      if (parsedOffers && data.budget) {
        const budget = Number(data.budget) || 0;
        if (budget > 0) {
          parsedOffers = parsedOffers.map(o => {
            if (typeof o.total_cad === "number" && o.total_cad > budget) {
              return { ...o, over_budget: true };
            }
            return o;
          });
        }
      }

      // Garde-fou COHÉRENCE SCORE↔ORDRE — Sonnet attribue un score /10 par offre
      // ET choisit un ordre d'affichage comme deux décisions séparées : d'où un
      // 8.2 (badge vert) affiché en #3 sous deux 7.x. On force l'ordre = score
      // décroissant (tri STABLE → préserve l'ordre de Sonnet en cas d'égalité).
      // Ce n'est PAS un classement arbitraire React : on rend seulement cohérents
      // les PROPRES scores de Claude avec ce que voit l'utilisateur. On NE touche
      // PAS aux modes où l'ordre a une autre sémantique : navire précis (ordre =
      // date/prix) et date exacte demandée (offers[0] = départ le plus proche).
      if (parsedOffers && parsedOffers.length > 1 && !data.specific_ship && !data.dates?.from) {
        const scoreOf = (o) => (typeof o.score === "number" ? o.score : -1);
        parsedOffers = parsedOffers
          .map((o, i) => ({ o, i }))
          .sort((a, b) => scoreOf(b.o) - scoreOf(a.o) || a.i - b.i)
          .map(x => x.o);
      }

      // Réponse structurée mais 0 offre confirmée + refinement → réponse
      // HONNÊTE valide (pas une erreur). On affiche la bannière d'élargissement.
      if (parsedOffers === null && parsed?.refinement_options) parsedOffers = [];

      if (!parsedOffers) {
        console.error("[951] Full response text:", text);
        throw new Error(lang === "en"
          ? "The search didn't return results — please try again or rephrase your request."
          : "La recherche n'a pas abouti — réessayez ou reformulez votre demande.");
      }

      if (ref.current === "searching") {
        setOffers(parsedOffers); setShowRefinement(false);
        const newRefinement = parsed?.refinement_options
          || (parsedOffers.length < 3 && !data.specific_ship ? buildFallbackRefinement(data, lang, parsedOffers.length > 0) : null);
        const newCompromiseNote = parsed?.compromise_note || null;
        setRefinementOptions(newRefinement);
        setCompromiseNote(newCompromiseNote);
        setOfferHistories({});
        // Historique local auto pour retrouver la recherche au retour (max 5)
        saveSearchToLocal({ data, offers: parsedOffers, compromiseNote: newCompromiseNote, refinementOptions: newRefinement });
        setRecentSearches(loadRecentSearches());
        setManuallySaved(false); // nouvelle recherche → bouton repasse à "Sauvegarder"
        setInspireMode(false);   // vraie recherche → on quitte le mode vedette
        go("results");
        refreshUsage();
        // Charger l'historique des prix en arrière-plan (non bloquant)
        Promise.all(parsedOffers.map(async (offer) => {
          try {
            const r = await fetch(WORKER_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "get_price_history", ship_name: offer.ship_name, departure_date: offer.departure_date }),
            });
            const d = await r.json();
            return { key: `${offer.ship_name}:${offer.departure_date}`, history: d.history || [] };
          } catch { return null; }
        })).then(results => {
          const map = {};
          for (const r of results) if (r) map[r.key] = r.history;
          setOfferHistories(map);
        });
        if (!isSignedIn) {
          const anonCount = parseInt(localStorage.getItem('anonSearchCount') || '0', 10);
          localStorage.setItem('anonSearchCount', String(anonCount + 1));
        }
      }
    } catch (e) {
      console.error("Search error:", e);
      if (e.message?.includes("[203]")) {
        setShowPaywall(true);
        go("funnel");
      } else if (e.message?.includes("[204]")) {
        // Plafond mensuel global des recherches GRATUITES atteint (pas une
        // limite perso). Mur informatif : quota renouvelé le mois prochain.
        setShowPaywall("budget");
        go("funnel");
      } else if (e.message?.includes("[303]") || e.message?.includes("[304]")) {
        setError(lang === "en"
          ? "⏳ The AI service (Anthropic) is temporarily overloaded. This attempt was not counted — please try again in a few minutes."
          : "⏳ Le service IA (Anthropic) est temporairement surchargé. Cette tentative ne vous a pas été décomptée — réessayez dans quelques minutes.");
        go("funnel");
      } else if (e.message === "Load failed" || e.message === "Failed to fetch" || e.message === "NetworkError when attempting to fetch resource." || e.message?.includes("Réponse incomplète")) {
        // TIMEOUT (limite 30s Cloudflare sur une recherche live à froid). Deux symptômes du
        // MÊME problème, tous deux relancés ici : (1) rupture réseau « Load failed » (le client
        // perd la connexion), (2) « Réponse incomplète » = le worker a été coupé EN PLEIN STREAM
        // avant l'événement `result` → l'usager voyait alors un faux « Aucun résultat » alors que
        // la cache se remplissait côté serveur (incident Méditerranée 2026-06-09). La 1re tentative
        // a réchauffé l'index via write-through (Stage 0 écrit les voilages confirmés au fil
        // de l'eau) → on RELANCE AUTOMATIQUEMENT une fois : la 2e lecture passe par la cache
        // (rapide) et l'usager obtient ses résultats au lieu de revenir bredouille au funnel.
        // Exempt de quota (retryAttempt>0 → is_refinement). Reste sur l'écran de recherche.
        // La 1re tentative finit côté serveur ~quelques secondes APRÈS la rupture client
        // (le worker continue d'écrire le cache après la coupure). Si on relance tout de
        // suite, le retry lit le cache AVANT qu'il soit écrit → 2e timeout. On laisse donc
        // le cache atterrir (~4s) puis on relance : la 2e lecture tombe sur un cache-hit
        // rapide → l'usager voit ses résultats au lieu d'une erreur. Race corrigée 2026-05-31.
        if (retryAttempt < 2 && ref.current === "searching") {
          await new Promise(r => setTimeout(r, 4000));
          if (ref.current !== "searching") return; // l'usager a quitté l'écran entre-temps
          return search(retryAttempt + 1);
        }
        setError(lang === "en"
          ? "⚡ Connection interrupted — the search took too long. Please try again (it may be faster this time thanks to the cache)."
          : "⚡ Connexion interrompue — la recherche a pris trop de temps. Réessayez (ce sera probablement plus rapide grâce au cache).");
        go("funnel");
      } else {
        setError(`${t.error_prefix || "Erreur : "}${e.message}`);
        go("funnel");
      }
    }
  };

  // Keeps searchRef pointing to the latest search closure (avoids stale captures in RefinementModal)
  useEffect(() => { searchRef.current = search; });

  // ── Panneau « Mes recherches » (rendu dans plusieurs écrans) ────────────────
  const savedSearchesBtn = savedList.length > 0 ? (
    <button
      onClick={() => setShowSavedList(true)}
      style={{ background: "transparent", border: "1px solid #C9A84C66", borderRadius: 10, padding: "6px 12px", color: "#C9A84C", ...GL, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
    >
      📁 {lang === "en" ? "My searches" : "Mes recherches"} ({savedList.length})
    </button>
  ) : null;

  // Menu de navigation accueil (☰) : Offres / Mes recherches / Blogue. Dropdown unique
  // (desktop + mobile) — sobre, ne concurrence pas le CTA de recherche.
  const navItemStyle = { display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", color: "#E8D9A8", ...GL, fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "10px 12px", borderRadius: 8, textDecoration: "none", boxSizing: "border-box" };
  const navMenu = (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {/* Blogue mis EN ÉVIDENCE à côté du menu (décision Simon 2026-06-06) — sert notre cause
          éducative au lieu d'être caché dans le ☰. */}
      <a href={lang === "en" ? "/blog/en/" : "/blog/"} onClick={() => { try { track("nav_click", { item: "blog" }); } catch {} }}
        style={{ background: "rgba(201,168,76,0.10)", border: "1px solid #C9A84C66", borderRadius: 10, padding: "6px 12px", color: "#C9A84C", ...GL, fontSize: 13, fontWeight: 700, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
        📖 {lang === "en" ? "Blog" : "Blogue"}
      </a>
    <div style={{ position: "relative" }}>
      <button onClick={() => setNavOpen(o => !o)} aria-label="Menu" aria-expanded={navOpen}
        style={{ background: "transparent", border: "1px solid #C9A84C66", borderRadius: 10, padding: "6px 12px", color: "#C9A84C", ...GL, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
        ☰ Menu
      </button>
      {navOpen && (
        <>
          <div onClick={() => setNavOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
          <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 31, background: "#0A1E35", border: "1px solid #C9A84C55", borderRadius: 12, padding: 6, minWidth: 210, boxShadow: "0 12px 32px rgba(0,0,0,0.45)" }}>
            <button onClick={() => { setShowDeals(true); setNavOpen(false); try { track("nav_click", { item: "deals" }); } catch {} }} style={navItemStyle}>
              🔥 {lang === "en" ? "Deals" : "Offres"}
            </button>
            <button onClick={() => { setShowSavedList(true); setNavOpen(false); try { track("nav_click", { item: "saved" }); } catch {} }} style={navItemStyle}>
              ⭐ {lang === "en" ? "My searches" : "Mes recherches"}{savedList.length > 0 ? ` (${savedList.length})` : ""}
            </button>
          </div>
        </>
      )}
    </div>
    </div>
  );

  // Modale « 🔥 Offres » — réutilise la section Offres du moment (mêmes liens affiliés).
  const dealsModal = showDeals ? (
    <div onClick={() => setShowDeals(false)} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(7,17,31,0.82)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 560, background: "#0A1E35", border: "1px solid #C9A84C55", borderRadius: 18, padding: "8px 20px 24px", position: "relative" }}>
        <button onClick={() => setShowDeals(false)} aria-label={lang === "en" ? "Close" : "Fermer"} style={{ position: "absolute", top: 8, right: 14, background: "transparent", border: "none", color: "#9DB2C7", fontSize: 26, cursor: "pointer", lineHeight: 1, zIndex: 1 }}>×</button>
        <CurrentDealsSection lang={lang} />
      </div>
    </div>
  ) : null;

  const monthsShortFr = ["janv.","févr.","mars","avril","mai","juin","juill.","août","sept.","oct.","nov.","déc."];
  const monthsShortEn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const savedListPanel = showSavedList ? (
    <div
      onClick={() => setShowSavedList(false)}
      style={{ position: "fixed", inset: 0, background: "rgba(7,17,31,0.82)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}
    >
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 560, background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 18, padding: "22px 22px 26px" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <div style={{ ...GF, fontSize: 19, color: "#C9A96A", fontWeight: 700, flex: 1 }}>
            📁 {lang === "en" ? "My saved searches" : "Mes recherches sauvegardées"}
          </div>
          <button onClick={() => setShowSavedList(false)} style={{ background: "none", border: "none", color: "#7A9BB5", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        {!isSignedIn && (
          <div style={{ ...GL, fontSize: 12, color: "#A8C0D0", background: "rgba(201,168,76,0.10)", border: "1px solid #C9A84C44", borderRadius: 10, padding: "9px 12px", marginBottom: 14 }}>
            💡 {lang === "en"
              ? "Saved on this device only. Create a free account to keep them and find them on any device."
              : "Sauvegardées sur cet appareil seulement. Crée un compte gratuit pour les garder et les retrouver sur tous tes appareils."}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {savedList.map(entry => {
            const ts = new Date(entry.ts);
            const months = lang === "en" ? monthsShortEn : monthsShortFr;
            const dateLabel = `${ts.getDate()} ${months[ts.getMonth()]}`;
            const count = (entry.offers || []).length;
            return (
              <div key={entry.id} style={{ border: "1px solid #1A2F45", borderRadius: 12, padding: "12px 14px", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {renamingId === entry.id ? (
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onBlur={() => renameSavedSearch(entry.id, renameValue)}
                        onKeyDown={e => { if (e.key === "Enter") renameSavedSearch(entry.id, renameValue); if (e.key === "Escape") setRenamingId(null); }}
                        style={{ width: "100%", background: "#07111F", border: "1px solid #C9A84C66", borderRadius: 8, padding: "6px 9px", color: "#EAF2F8", ...GL, fontSize: 14 }}
                      />
                    ) : (
                      <div style={{ ...GL, fontSize: 14.5, color: "#EAF2F8", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{entry.name}</div>
                    )}
                    <div style={{ ...GL, fontSize: 11.5, color: "#7A9BB5", marginTop: 3 }}>
                      {dateLabel} · {count} {lang === "en" ? "offer(s)" : "offre(s)"}
                    </div>
                  </div>
                  <button onClick={() => openSavedSearch(entry)} style={{ padding: "7px 13px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontSize: 12.5, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                    {lang === "en" ? "Open →" : "Ouvrir →"}
                  </button>
                </div>
                <div style={{ display: "flex", gap: 14, marginTop: 9 }}>
                  <button onClick={() => { setRenamingId(entry.id); setRenameValue(entry.name); }} style={{ background: "none", border: "none", color: "#7A9BB5", ...GL, fontSize: 12, cursor: "pointer", padding: 0 }}>
                    ✏️ {lang === "en" ? "Rename" : "Renommer"}
                  </button>
                  <button onClick={() => deleteSavedSearch(entry.id)} style={{ background: "none", border: "none", color: "#C77", ...GL, fontSize: 12, cursor: "pointer", padding: 0 }}>
                    🗑️ {lang === "en" ? "Delete" : "Supprimer"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {!isSignedIn && (
          <button
            onClick={() => { try { localStorage.setItem('cai_open_searches_after_auth', '1'); } catch {} try { track("saved_list_signup_cta"); } catch {} redirectToSignUp({ redirectUrl: window.location.href }); }}
            style={{ width: "100%", marginTop: 16, padding: "11px", borderRadius: 11, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
          >
            {lang === "en" ? "💾 Create a free account to keep them everywhere" : "💾 Crée un compte gratuit pour les garder partout"}
          </button>
        )}
      </div>
    </div>
  ) : null;

  // ── WELCOME ────────────────────────────────────────────────────────────────
  if (screen === "welcome") return (
    <div className={usage?.plan === "admin" ? "cai-home cai-home-admin" : "cai-home"} style={{
      backgroundImage: "radial-gradient(ellipse 95% 45% at 50% 0%, rgba(201,168,76,0.12), transparent 62%), linear-gradient(rgba(7,17,31,0.72), rgba(7,17,31,0.85)), url(/destinations/cococay.jpg)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundAttachment: "fixed",
      backgroundColor: "#07111F",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      paddingLeft: 20,
      paddingRight: 20,
      paddingBottom: 24,
      position: "relative",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Lato:wght@300;400;700;900&display=swap" rel="stylesheet" />
      <style>{`
        * { box-sizing: border-box; }
        select option { background: #0A1E35; }
        /* Dégagement sous le bandeau fixe (boutons en haut à droite). Plus haut en
           mode admin (3 boutons de plus → le bandeau passe à 2-3 lignes sur mobile). */
        .cai-home       { padding-top: 76px; }
        .cai-home-admin { padding-top: 132px; }
        @media (min-width: 900px) { .cai-home, .cai-home-admin { padding-top: 30px; } }
        .cai-welcome-card    { max-width: 520px; }
        .cai-welcome-options { display: flex; flex-direction: column; gap: 12px; align-items: stretch; }
        .cai-welcome-sep     { display: flex; }
        /* Hero plus court sur mobile pour remonter l'action au-dessus du pli. */
        .cai-hero            { height: 160px; }
        @media (min-width: 900px) {
          .cai-welcome-card    { max-width: 920px; }
          .cai-welcome-options { flex-direction: row; gap: 16px; }
          .cai-welcome-sep     { display: none; }
          .cai-welcome-option  { flex: 1; min-width: 0; }
          .cai-hero            { height: 220px; }
        }
      `}</style>
      <div style={{ position: "fixed", top: 12, right: 16, zIndex: 20, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", maxWidth: "calc(100vw - 32px)" }}>
        {usage?.plan === "admin" && (
          <button onClick={() => { setShowAdmin(true); setCdmOffers(null); setCdmResult(null); setCdmVigie(""); }}
            style={{ padding: "6px 12px", borderRadius: 8, background: "rgba(201,168,76,0.15)", border: "1px solid #C9A84C55", color: "#C9A84C", fontSize: 13, cursor: "pointer", fontFamily: "'Inter','Helvetica Neue',sans-serif" }}>
            ⚙️ Admin
          </button>
        )}
        {usage?.plan === "admin" && (
          <button onClick={() => setShowStats(true)}
            style={{ padding: "6px 12px", borderRadius: 8, background: "rgba(201,168,76,0.15)", border: "1px solid #C9A84C55", color: "#C9A84C", fontSize: 13, cursor: "pointer", fontFamily: "'Inter','Helvetica Neue',sans-serif" }}>
            📊 Stats
          </button>
        )}
        {usage?.plan === "admin" && (
          <button onClick={() => setShowDashboard(true)}
            style={{ padding: "6px 12px", borderRadius: 8, background: "rgba(201,168,76,0.15)", border: "1px solid #C9A84C55", color: "var(--wl-primary, #C9A84C)", fontSize: 13, cursor: "pointer", fontFamily: "'Inter','Helvetica Neue',sans-serif" }}>
            📈 Tableau visuel
          </button>
        )}
        {usage?.plan === "admin" && (
          <button onClick={() => setShowReturns(true)}
            style={{ padding: "6px 12px", borderRadius: 8, background: "rgba(201,168,76,0.15)", border: "1px solid #C9A84C55", color: "#C9A84C", fontSize: 13, cursor: "pointer", fontFamily: "'Inter','Helvetica Neue',sans-serif" }}>
            📋 Retours
          </button>
        )}
        {navMenu}
        <div data-tour="account">
          <AuthButton usage={usage} workerUrl={WORKER_URL} />
        </div>
      </div>
      {savedListPanel}
      {dealsModal}

      {/* Panneau "Vos recherches récentes" — historique auto (max 5, TTL 30j).
          Chaque recherche complétée s'y ajoute toute seule ; cliquable pour rouvrir
          sans reconsommer de crédit. Distinct des favoris nommés (📁 Mes recherches). */}
      {recentSearches.length > 0 && (() => {
        const monthsFr = ["janv.","févr.","mars","avril","mai","juin","juill.","août","sept.","oct.","nov.","déc."];
        const monthsEn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        const months = lang === "en" ? monthsEn : monthsFr;
        const regMap = REGION_SHORT[lang === "en" ? "en" : "fr"];
        const summarize = (sd) => {
          const s = sd || {};
          const parts = [];
          if (s.specific_ship) parts.push(s.specific_ship);
          const regs = (s.regions || []).filter(r => r && r !== "any");
          if (!s.specific_ship && regs.length > 0) parts.push((regMap[regs[0]] || regs[0]) + (regs.length > 1 ? ` +${regs.length - 1}` : ""));
          if (s.dates?.month && s.dates?.year) parts.push(`${months[s.dates.month - 1]} ${s.dates.year}`);
          return parts.join(" · ") || (lang === "en" ? "Cruise search" : "Recherche croisière");
        };
        const resume = (entry) => {
          setData(entry.data || {});
          setOffers(entry.offers || []);
          setCompromiseNote(entry.compromiseNote || null);
          setRefinementOptions(entry.refinementOptions || null);
          setInspireMode(false);
          try { track("recent_search_resumed"); } catch {}
          go("results");
        };
        return (
          <div className="cai-welcome-card" style={{ width: "100%", maxWidth: 520, marginBottom: 16, background: "linear-gradient(135deg, rgba(201,168,76,0.18), rgba(201,168,76,0.06))", border: "1px solid #C9A84C77", borderRadius: 16, padding: recentOpen ? "14px 18px" : "12px 18px", position: "relative", zIndex: 2 }}>
            <div
              onClick={() => setRecentOpen(v => !v)}
              style={{ ...GF, fontSize: 14, color: "#C9A96A", fontWeight: 700, marginBottom: recentOpen ? 10 : 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, cursor: "pointer" }}
            >
              <span>📂 {lang === "en" ? "Your recent searches" : "Vos recherches récentes"} <span style={{ ...GL, fontWeight: 400, color: "#C8D8E8AA" }}>({recentSearches.length})</span></span>
              <span style={{ fontSize: 13, color: "#C8D8E8AA" }}>{recentOpen ? "▴" : "▾"}</span>
            </div>
            {recentOpen && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {recentSearches.map(entry => {
                const ts = new Date(entry.ts);
                const dateLabel = ts.toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", { day: "numeric", month: "short" });
                const count = (entry.offers || []).length;
                return (
                  <div key={entry.id} style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.04)", border: "1px solid #C9A84C33", borderRadius: 11, padding: "9px 12px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...GL, fontSize: 13, color: "#EAF2F8", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {summarize(entry.data)}
                      </div>
                      <div style={{ ...GL, fontSize: 11, color: "#A8C0D0", marginTop: 1 }}>
                        {dateLabel} · {count} {lang === "en" ? (count > 1 ? "offers" : "offer") : (count > 1 ? "offres" : "offre")}
                      </div>
                    </div>
                    <button
                      onClick={() => resume(entry)}
                      style={{ padding: "7px 12px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      {lang === "en" ? "Resume →" : "Reprendre →"}
                    </button>
                    <button
                      onClick={() => setRecentSearches(removeRecentSearch(entry.id))}
                      aria-label={lang === "en" ? "Remove" : "Retirer"}
                      style={{ background: "none", border: "none", color: "#7A9BB5", fontSize: 18, lineHeight: 1, cursor: "pointer", padding: "0 2px" }}
                    >×</button>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        );
      })()}

      {/* Welcome card */}
      <div className="cai-welcome-card" style={{ background: "#091929", border: "1px solid #C9A84C55", borderRadius: 24, width: "100%", boxShadow: "0 40px 80px rgba(0,0,0,0.7), 0 0 60px rgba(201,168,76,0.07)", position: "relative", zIndex: 2, overflow: "hidden" }}>

        {/* Hero image — port de Nassau avec paquebots */}
        {/* Pour changer la photo : remplace l'URL ci-dessous par n'importe quelle image 1200×500 */}
        <div className="cai-hero" style={{ position: "relative", overflow: "hidden", borderRadius: "24px 24px 0 0" }}>
          <img
            src="/destinations/nassau.avif"
            alt={t.welcome_hero_alt || "Port de croisière"}
            onError={e => { e.target.style.display = "none"; }}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 60%", display: "block" }}
          />
          {/* Gradient overlay — toujours visible même si photo absente */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(7,17,31,0.15) 0%, rgba(7,17,31,0.55) 60%, #091929 100%)" }} />
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 100%, rgba(201,168,76,0.12) 0%, transparent 70%)" }} />
          {/* Logo par-dessus l'image */}
          <div style={{ position: "absolute", bottom: 14, left: 0, right: 0, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <img src="/croisiere-ai-mark-gold.svg" alt="" style={{ width: 56, height: 56, filter: "drop-shadow(0 2px 12px rgba(0,0,0,0.9))" }} />
            <div style={{ ...GF, fontSize: 26, fontWeight: 700, color: "#C9A96A", textShadow: "0 2px 16px rgba(0,0,0,0.8)", letterSpacing: "-0.01em" }}>{t.app_title || "Croisière AI"}</div>
            <div style={{ ...GL, fontSize: 9, fontWeight: 700, color: "#C9A96A", letterSpacing: "4px", opacity: 0.7, textShadow: "0 1px 8px rgba(0,0,0,0.9)", textTransform: "uppercase" }}>CONCIERGE</div>
            <div style={{ ...GL, fontSize: 13, color: "#C8D8E8BB", textShadow: "0 1px 8px rgba(0,0,0,0.9)", marginTop: 2 }}>
              {t.app_tagline || "Votre agent IA — croisière, vols et hôtel en quelques minutes."}
            </div>
          </div>
        </div>

        {/* Corps de la carte */}
        <div style={{ padding: "22px 28px 28px" }}>

        {/* Intro bullets */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 18 }}>
          {(t.welcome_bullets || [
            "🔍 Recherche web en temps réel — prix du jour, pas un catalogue",
            "✈️ Vols + hôtel la veille inclus dans chaque recommandation",
            "🧠 Les meilleures offres selon vos critères, classées et justifiées",
          ]).map((b, i) => (
            <div key={i} style={{ ...GL, fontSize: 14, color: "#A8C0D0", lineHeight: 1.6 }}>{b}</div>
          ))}
        </div>

        {/* Deux façons de commencer — choix explicite (évite la confusion) */}
        <div style={{ textAlign: "center", ...GL, fontSize: 15, color: "#C9A84C", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 14 }}>
          {t.welcome_how || "Comment voulez-vous commencer ?"}
        </div>

        {/* Options — funnel D'ABORD (recommandé), agent ensuite. Mobile = empilé,
            desktop ≥900px = 2 colonnes côte à côte (sans séparateur "ou"). */}
        <div className="cai-welcome-options">
          {/* Option A — Funnel guidé (Recommandé) */}
          <div className="cai-welcome-option" data-tour="funnel" style={{
            background: "#091929", border: "1px solid #C9A84C", borderRadius: 16,
            padding: "16px 16px 14px", position: "relative",
            boxShadow: "0 0 24px rgba(201,168,76,0.15)",
            display: "flex", flexDirection: "column",
          }}>
            <div style={{ position: "absolute", top: -10, left: 14, background: "#C9A84C", color: "#07111F", ...GL, fontSize: 11, fontWeight: 900, padding: "3px 9px", borderRadius: 7, letterSpacing: "0.08em" }}>
              ⭐ {lang === "en" ? "RECOMMENDED" : "RECOMMANDÉ"}
            </div>
            <div style={{ ...GF, fontSize: 15, color: "#C9A84C", fontWeight: 700, marginTop: 4, marginBottom: 6 }}>
              📋 {lang === "en" ? "Guided form" : "Formulaire guidé"}
            </div>
            <div style={{ ...GL, fontSize: 15, color: "#A8C0D0", lineHeight: 1.6, marginBottom: 12, flex: 1 }}>
              {lang === "en"
                ? "Precise questions, sharper results. ~8 questions, ~2 min."
                : "Questions précises, résultats mieux ciblés. ~8 questions, ~2 min."}
            </div>
            <button onClick={() => { if (blockAnonIfExhausted()) return; searchMetaRef.current = { init: "funnel", text: "" }; track("welcome_funnel_click"); go("funnel"); }} style={{
              width: "100%", padding: "13px 20px", borderRadius: 12, border: "none",
              cursor: "pointer", background: "linear-gradient(135deg,#C9A84C,#A8882A)",
              color: "#07111F", ...GL, fontWeight: 900, fontSize: 14, letterSpacing: "0.02em",
            }}>
              {t.welcome_start || "📋 Répondre aux questions →"}
            </button>
          </div>

          {/* Séparateur "ou" (mobile uniquement) */}
          <div className="cai-welcome-sep" style={{ alignItems: "center", gap: 12, margin: "2px 0" }}>
            <div style={{ flex: 1, height: 1, background: "#1A2F45" }} />
            <span style={{ ...GL, fontSize: 14, color: "#5A7A95" }}>{t.welcome_or || "ou"}</span>
            <div style={{ flex: 1, height: 1, background: "#1A2F45" }} />
          </div>

          {/* Option B — Agent IA conversationnel */}
          <div className="cai-welcome-option" data-tour="agent" style={{ display: "flex", flexDirection: "column" }}>
            <ProjectDescriber workerUrl={WORKER_URL} onExtracted={handleExtracted} onSkipToForm={() => { if (blockAnonIfExhausted()) return; go("funnel"); }} />
          </div>
        </div>

        {/* « Inspire-moi » — raccourci WOW : montre une croisière vedette pré-cuisinée
            INSTANTANÉMENT, sans formulaire, gratuit (cache-first). Caché si aucune
            vedette n'est encore publiée. */}
        {inspireAvailable && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button onClick={openInspire} style={{
              display: "inline-flex", alignItems: "center", gap: 9,
              background: "linear-gradient(135deg, rgba(201,168,76,0.18), rgba(201,168,76,0.06))",
              border: "1px solid #C9A84C", color: "#E8D9A8", ...GF, fontWeight: 700, fontSize: 15,
              cursor: "pointer", borderRadius: 999, padding: "11px 24px",
              boxShadow: "0 0 22px rgba(201,168,76,0.14)",
            }}>
              ✨ {lang === "en" ? "Inspire me" : "Inspire-moi"}
            </button>
            <div style={{ ...GL, fontSize: 12, color: "#5A7A95", marginTop: 6 }}>
              {lang === "en" ? "A featured cruise, instantly — no form" : "Une croisière vedette, à l'instant — sans formulaire"}
            </div>
          </div>
        )}

        {/* Offres du moment — promos affiliées CruiseDirect (section permanente). */}
        <CurrentDealsSection lang={lang} />

        {/* Exemple de résultat — DESCENDU sous l'action 2026-06-03 (preuve concrète
            pour ceux qui scrollent, sans pousser le CTA sous le pli mobile). */}
        <div style={{ marginTop: 18 }}>
          <ExampleResultCard lang={lang} />
        </div>

        {/* Vitrine capacités — bouton mis en évidence (pill doré) */}
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button onClick={() => setShowCapabilities(true)} style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(201,168,76,0.10)", border: "1px solid #C9A84C66",
            color: "#C9A84C", ...GL, fontSize: 14, fontWeight: 700, cursor: "pointer",
            borderRadius: 999, padding: "10px 20px",
          }}>
            ✨ {lang === "en" ? "See everything this tool does for you" : "Découvre tout ce que l'outil fait pour toi"}
          </button>
        </div>

        {/* Pitch freemium */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 14, marginBottom: 12, flexWrap: "wrap" }}>
          <span style={{ ...GL, fontSize: 14, color: "#5A7A95" }}>{t.welcome_cta_sub || "✓ 2 recherches gratuites/mois · 1re sans compte requis"}</span>
          <span style={{ color: "#1A2F45" }}>·</span>
          <button data-tour="redeem" onClick={() => setShowRedeem(true)} style={{ background: "none", border: "none", color: "#C9A84C", ...GL, fontSize: 14, cursor: "pointer", textDecoration: "underline", padding: 0 }}>{lang === "en" ? "🎁 I have a code" : "🎁 J'ai un code"}</button>
          <span style={{ color: "#1A2F45" }}>·</span>
          <a href={lang === "en" ? "/blog/en/" : "/blog/"} style={{ color: "#C9A84C", ...GL, fontSize: 14, textDecoration: "underline" }}>{lang === "en" ? "📖 Blog" : "📖 Blogue"}</a>
        </div>

        {/* Visite guidée — version DISCRÈTE 2026-06-03 (1 ligne, sans clignotement,
            sous l'action). Ne concurrence plus le CTA. Reste dismissable. */}
        {showTourPrompt && (
          <div style={{ textAlign: "center", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
            <button onClick={startTourFromPrompt} style={{ background: "transparent", border: "none", color: "#9AB8D0", ...GL, fontSize: 13, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
              👋 {lang === "en" ? "First time? Take the 30-sec tour" : "Première visite ? Visite guidée (30 s)"}
            </button>
            <button onClick={dismissTourPrompt} aria-label={lang === "en" ? "Dismiss" : "Ignorer"} style={{ background: "transparent", border: "none", color: "#3A5A72", fontSize: 14, cursor: "pointer", lineHeight: 1, padding: 2 }}>✕</button>
          </div>
        )}

        {usage?.plan === "admin" && (
          <div style={{ textAlign: "center", marginBottom: 12, display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => setShowCodes(true)} style={{ background: "none", border: "1px solid #1A2F45", color: "#7A9BB5", ...GL, fontSize: 13, cursor: "pointer", borderRadius: 8, padding: "5px 10px" }}>🎟️ Générer des codes testeurs</button>
            <button onClick={async () => {
              try {
                const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "maintenance", toggle: true, token: await getToken() }) }).then(x => x.json());
                if (r.ok) alert(r.maintenance ? "🛠️ Mode maintenance ACTIVÉ — les usagers voient un message « temporairement indisponible »." : "✅ Mode maintenance DÉSACTIVÉ — le site est de nouveau ouvert.");
                else alert("Échec : " + (r.error?.message || "?"));
              } catch (e) { alert("Échec : " + e.message); }
            }} style={{ background: "none", border: "1px solid #5A2A2A", color: "#C98080", ...GL, fontSize: 13, cursor: "pointer", borderRadius: 8, padding: "5px 10px" }}>🚧 Mode maintenance ON/OFF</button>
          </div>
        )}
        {showPaywall && <PaywallModal reason={showPaywall === "anon" ? "anon" : "plans"} onClose={() => setShowPaywall(false)} onBeforeAuth={() => saveBeforeAuth(true)} onEnterCode={() => setShowRedeem(true)} />}
        {showRedeem && <RedeemCodeModal workerUrl={WORKER_URL} onClose={() => setShowRedeem(false)} onRedeemed={refreshUsage} />}
        {showCodes && <AdminCodesModal workerUrl={WORKER_URL} onClose={() => setShowCodes(false)} />}
        {showAdmin && <AdminCDMModal onClose={() => setShowAdmin(false)} onGenerate={generateCDM} onSend={sendCDM} loading={cdmLoading} sending={cdmSending} offers={cdmOffers} result={cdmResult} vigie={cdmVigie} setVigie={setCdmVigie} fbPosts={buildFBPosts(cdmOffers)} />}
        {showStats && <AdminStatsModal workerUrl={WORKER_URL} onClose={() => setShowStats(false)} />}
        {showDashboard && <AdminDashboardModal workerUrl={WORKER_URL} onClose={() => setShowDashboard(false)} />}
        {showReturns && (
          <AdminReturnsModal
            getToken={getToken}
            onClose={() => setShowReturns(false)}
            onRelaunch={async (t) => {
              // Recherche AGENT → on ré-extrait à neuf depuis le texte original avec le
              // cerveau actuel (capte les escales, corrige « déjà fait »). Recherche
              // FUNNEL → on charge les choix tels quels. Récap modifiable ensuite.
              let params = { ...(t?.params || {}) };
              if (t?.agent_text) {
                try {
                  const token = await getToken();
                  const r = await fetch(WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "extract", text: t.agent_text, token }) });
                  const d = await r.json();
                  if (d && d.params) params = mergeExtractedParams({}, d.params);
                } catch {}
              }
              setData(params);
              setOffers([]); setCompromiseNote(null); setRefinementOptions(null);
              searchMetaRef.current = { init: t?.agent_text ? "agent" : "funnel", text: t?.agent_text || "" };
              setShowReturns(false);
              try { track("admin_relaunch_from_return"); } catch {}
              go("recap");
            }}
          />
        )}

        <div style={{ textAlign: "center" }}>
          <button data-tour="feedback" onClick={() => setShowFeedback(true)} style={{ background: "transparent", border: "none", color: "#3A5A72", ...GL, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>
            💬 {lang === "en" ? "Feedback or bug report?" : "Suggestion ou bug à signaler ?"}
          </button>
        </div>

        </div>{/* fin corps carte */}
      </div>

      <div style={{ ...GL, fontSize: 12, color: "#2A3F55", marginTop: 16, textAlign: "center", position: "relative", zIndex: 2, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
        <img
          src={lang === "en" ? "/flag-ca.svg" : "/flag-qc.svg"}
          alt={lang === "en" ? "Canada" : "Québec"}
          style={{ height: 14, width: "auto", borderRadius: 2, boxShadow: "0 0 1px rgba(0,0,0,.4)", verticalAlign: "middle" }}
        />
        {lang === "en" ? "Proudly Canadian" : "Fièrement du Québec"}
      </div>

      <div style={{ ...GL, fontSize: 11, color: "#2A3F55", marginTop: 8, textAlign: "center", position: "relative", zIndex: 2 }}>
        © 2026 CroisiereAI — {lang === "en" ? "All rights reserved." : "Tous droits réservés."} · v2.9.0
        {" · "}
        <button onClick={() => setShowWelcome(true)} style={{ background: "transparent", border: "none", color: "#3A5A72", cursor: "pointer", ...GL, fontSize: 11, textDecoration: "underline", padding: 0 }}>
          {lang === "en" ? "About" : "À propos"}
        </button>
        {" · "}
        {/* Tour guidé désormais OPT-IN (plus auto-lancé) — déclenché ici à la demande. */}
        <button onClick={() => setShowWalkthrough(true)} style={{ background: "transparent", border: "none", color: "#3A5A72", cursor: "pointer", ...GL, fontSize: 11, textDecoration: "underline", padding: 0 }}>
          {lang === "en" ? "Quick tour" : "Visite guidée"}
        </button>
        {" · "}
        <button onClick={() => setShowWhatsNew(true)} style={{ background: "transparent", border: "none", color: "#3A5A72", cursor: "pointer", ...GL, fontSize: 11, textDecoration: "underline", padding: 0 }}>
          {lang === "en" ? "What's new" : "Nouveautés"}
          {hasUnseenChangelog() && <span style={{ color: "#C9A84C", marginLeft: 4 }}>●</span>}
        </button>
        {" · "}
        <a href="mailto:contact@croisiereai.ca" style={{ color: "#3A5A72", ...GL, fontSize: 11, textDecoration: "underline" }}>
          {lang === "en" ? "Contact" : "Contact"}
        </a>
        {" · "}
        <a href={lang === "en" ? "/privacy.html" : "/confidentialite.html"} style={{ color: "#3A5A72", ...GL, fontSize: 11, textDecoration: "underline" }}>
          {lang === "en" ? "Privacy" : "Confidentialité"}
        </a>
        {" · "}
        <a href={lang === "en" ? "/terms.html" : "/conditions.html"} style={{ color: "#3A5A72", ...GL, fontSize: 11, textDecoration: "underline" }}>
          {lang === "en" ? "Terms" : "Conditions"}
        </a>
      </div>

      <div style={{ ...GL, fontSize: 11, color: "#2A3F55", marginTop: 4, textAlign: "center", position: "relative", zIndex: 2 }}>
        {lang === "en" ? "A product of " : "Un produit de "}
        <a href="https://kairos-ia.ca" target="_blank" rel="noopener noreferrer" style={{ color: "#3A5A72", ...GL, fontSize: 11, textDecoration: "underline" }}>
          Kairos IA inc.
        </a>
      </div>

      {/* Réseaux sociaux */}
      <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 12, position: "relative", zIndex: 2 }}>
        {SOCIALS.map(s => (
          <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer"
            onClick={() => { try { track("social_click", { network: s.name }); } catch {} }}
            aria-label={s.label} title={s.label}
            style={{ color: "#3A5A72", display: "inline-flex" }}>
            {s.icon}
          </a>
        ))}
      </div>

      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
      {showWelcome  && <WelcomeModal  onClose={closeWelcome} lang={lang} />}
      {showWhatsNew && <WhatsNewModal onClose={() => setShowWhatsNew(false)} lang={lang} />}
      {showCapabilities && <CapabilitiesModal onClose={() => setShowCapabilities(false)} lang={lang} />}
      {showWalkthrough && <Walkthrough onClose={() => setShowWalkthrough(false)} lang={lang} />}
    </div>
  );

  // ── SEARCHING ──────────────────────────────────────────────────────────────
  if (screen === "searching") {
    const searchTarget = data.specific_ship
      ? { ship_name: data.specific_ship }
      : (data.companies || []).length > 0
      ? { cruise_line: (data.companies || []).map(c => CRUISE_COMPANIES.find(x => x.v === c)?.l).filter(Boolean).join(" / ") }
      : {};
    return <SearchingScreen target={searchTarget} data={data} usage={usage} onPaywall={() => setShowPaywall(true)} t={t} />;
  }

  // ── RESULTS ────────────────────────────────────────────────────────────────
  const openChat = () => {
    setChatForceOpen(true);
    setTimeout(() => chatRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  if (screen === "results") return (
    <div style={{
      backgroundImage: "radial-gradient(ellipse 95% 45% at 50% 0%, rgba(201,168,76,0.12), transparent 62%), linear-gradient(rgba(7,17,31,0.72), rgba(7,17,31,0.85)), url(/destinations/sunset.jpg)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundAttachment: "fixed",
      backgroundColor: "#07111F",
      minHeight: "100vh",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;700;900&display=swap" rel="stylesheet" />
      <style>{`* { box-sizing: border-box; } @keyframes ca-glow-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(201,168,76,0); border-color: #C9A84C55; } 50% { box-shadow: 0 0 18px 2px rgba(201,168,76,0.18); border-color: #C9A84C; } } @keyframes ca-offer-rise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } } @media (prefers-reduced-motion: reduce) { .ca-offer-rise { animation: none !important; } }`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "12px 20px", borderBottom: "1px solid #1A2F45", background: "#091929", position: "sticky", top: 0, zIndex: 10 }}>
        <img src="/croisiere-ai-mark-gold.svg" alt="" style={{ height: 24, width: 24, flexShrink: 0 }} />
        <span style={{ ...GF, fontSize: 15, color: "#C9A84C" }}>{lang === "en" ? "Cruise AI" : "Croisière AI"}</span>
        <div style={{ background: DEMO_MODE ? "#C9A84C22" : "#5CB85C22", border: `1px solid ${DEMO_MODE ? "#C9A84C55" : "#5CB85C55"}`, borderRadius: 7, padding: "2px 8px", ...GL, fontSize: 11, color: DEMO_MODE ? "#C9A84C" : "#5CB85C" }}>
          {DEMO_MODE ? "🧪 DEMO" : "🔴 LIVE"}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {savedSearchesBtn}
          <AuthButton usage={usage} workerUrl={WORKER_URL} />
          <button onClick={reset} style={{ background: "transparent", border: "1px solid #1A2F45", color: "#5A7A95", borderRadius: 8, padding: "5px 12px", cursor: "pointer", ...GL, fontSize: 13 }}>
            ⟵ {lang === "en" ? "New" : "Nouvelle"}
          </button>
        </div>
      </div>
      {savedListPanel}
      {showPaywall && <PaywallModal reason={typeof showPaywall === "string" ? showPaywall : "limit"} onClose={() => setShowPaywall(false)} onBeforeAuth={() => saveBeforeAuth(true)} onEnterCode={() => setShowRedeem(true)} />}
      {showRedeem && <RedeemCodeModal workerUrl={WORKER_URL} onClose={() => setShowRedeem(false)} onRedeemed={refreshUsage} />}
      {showCodes && <AdminCodesModal workerUrl={WORKER_URL} onClose={() => setShowCodes(false)} />}
      {showAdmin && <AdminCDMModal onClose={() => setShowAdmin(false)} onGenerate={generateCDM} onSend={sendCDM} loading={cdmLoading} sending={cdmSending} offers={cdmOffers} result={cdmResult} vigie={cdmVigie} setVigie={setCdmVigie} fbPosts={buildFBPosts(cdmOffers)} />}
      <div style={{ maxWidth: 580, margin: "0 auto", padding: "18px 16px 60px" }}>
        {/* Bandeau « inspiration du moment » + CTA conversion (vedette pré-cuisinée). */}
        {inspireMode && (
          <div style={{ marginBottom: 16, background: "linear-gradient(135deg, rgba(201,168,76,0.20), rgba(201,168,76,0.05))", border: "1px solid #C9A84C", borderRadius: 14, padding: "14px 16px" }}>
            <div style={{ ...GF, fontSize: 16, color: "#E8D9A8", fontWeight: 700, marginBottom: 4 }}>
              ✨ {lang === "en" ? "Featured inspiration" : "L'inspiration du moment"}
            </div>
            <div style={{ ...GL, fontSize: 13, color: "#A8C0D0", lineHeight: 1.55, marginBottom: 12 }}>
              {lang === "en"
                ? "A handpicked cruise to spark ideas. Want the best options for YOUR dates, group and budget?"
                : "Une croisière coup de cœur pour vous inspirer. Vous voulez les meilleures options pour VOS dates, votre groupe et votre budget ?"}
            </div>
            <button onClick={exitInspireToSearch} style={{
              width: "100%", padding: "13px 20px", borderRadius: 12, border: "none", cursor: "pointer",
              background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontWeight: 900, fontSize: 15,
            }}>
              {lang === "en" ? "Find the same for MY dates →" : "Trouve la même pour MES dates →"}
            </button>
          </div>
        )}
        {/* Admin : figer ce résultat comme vedette « Inspire-moi ». */}
        {usage?.plan === "admin" && offers.length > 0 && !inspireMode && (
          <div style={{ marginBottom: 14, textAlign: "center" }}>
            <button onClick={publishInspire} disabled={inspirePublishing} style={{
              background: "rgba(201,168,76,0.10)", border: "1px solid #C9A84C66", color: "#C9A84C",
              ...GL, fontSize: 13, fontWeight: 700, cursor: inspirePublishing ? "wait" : "pointer",
              borderRadius: 999, padding: "8px 18px", opacity: inspirePublishing ? 0.6 : 1,
            }}>
              {inspirePublishing ? "⏳…" : "📌 Publier comme inspiration du moment"}
            </button>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <span style={{ ...GF, fontSize: 22, color: "#fff", fontWeight: 700 }}>
            {lang === "en"
              ? (offers.length === 0 ? "No confirmed offer found" : offers.length > 1 ? "Best offers found" : "Your best offer")
              : (offers.length === 0 ? "Aucune offre confirmée trouvée" : offers.length > 1 ? "Meilleures offres trouvées" : "Votre meilleure offre")}
          </span>
          <span style={{ ...GL, fontSize: 13, color: "#5A7A95" }}>
            {DEMO_MODE
              ? (lang === "en" ? "Simulated result based on your criteria" : "Résultat simulé selon vos critères")
              : lang === "en"
              ? `${offers.length} result${offers.length > 1 ? "s" : ""} — real-time web search`
              : `${offers.length} résultat${offers.length > 1 ? "s" : ""} — recherche web en temps réel`}
          </span>
        </div>
        {(() => {
          const chip = (key, content, gold) => (
            <span key={key} style={{ background: gold ? "#C9A84C22" : "#1A2F45", border: gold ? "1px solid #C9A84C44" : "none", borderRadius: 5, padding: "3px 9px", ...GL, fontSize: 11, color: gold ? "#C9A84C" : "#7A9BB5", whiteSpace: "nowrap" }}>{content}</span>
          );
          const label = (txt) => (
            <span style={{ ...GL, fontSize: 9, color: "#3A5A72", fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", alignSelf: "center", flexShrink: 0 }}>{txt}</span>
          );
          const row = (lbl, chips) => chips.length === 0 ? null : (
            <div style={{ display: "flex", gap: 6, alignItems: "flex-start", flexWrap: "wrap" }}>
              {label(lbl)}
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{chips}</div>
            </div>
          );
          const REGION_SHORT = lang === "en"
            ? { carib_east: "E. Caribbean", carib_west: "W. Caribbean", carib_south: "S. Caribbean", bermuda: "Bermuda", europe: "Europe", europe_med: "Mediterranean", europe_north: "Northern Europe", europe_british: "British Isles", alaska: "Alaska", hawaii: "Hawaii", asia: "Asia", repositioning: "Repositioning" }
            : { carib_east: "Caraïbes Est", carib_west: "Caraïbes Ouest", carib_south: "Caraïbes Sud", bermuda: "Bermudes", europe: "Europe", europe_med: "Méditerranée", europe_north: "Europe du Nord", europe_british: "Îles britanniques", alaska: "Alaska", hawaii: "Hawaï", asia: "Asie", repositioning: "Repositionnement" };
          const selectedPorts = Array.isArray(data.departure_port) ? data.departure_port : data.departure_port ? [data.departure_port] : [];
          const selectedRegions = (data.regions || []).filter(r => r !== "any");
          const driveOriginLbl = data.drive_origin ? ({ mtl: "Montréal / QC", ottawa: "Ottawa / ON", toronto: "Toronto / ON", autres: lang === "en" ? "Other region" : "Autre région" }[data.drive_origin] || "") : "";
          const transportLabel = data.departure === "drive_ny" ? (lang === "en" ? "🚗 Drive" : "🚗 Voiture") : data.departure === "either" ? (lang === "en" ? "✈️/🚗 Either" : "✈️/🚗 Au choix") : (lang === "en" ? "✈️ Flight" : "✈️ Avion");
          const transportChip = data.departure ? transportLabel + (data.departure !== "drive_ny" && data.origin_airport && data.origin_airport !== "other" ? ` · ${data.origin_airport}` : "") + (driveOriginLbl ? ` · ${driveOriginLbl}` : "") : null;
          const dateChip = data.dates?.from
            ? `📅 ${fmtDate(data.dates.from, lang)} → ${fmtDate(data.dates.to, lang)}`
            : (data.dates?.month && data.dates?.year)
            ? `📅 ${new Date(data.dates.year, data.dates.month - 1, 1).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", { month: "short", year: "numeric" })}`
            : null;
          // Résumé d'une ligne quand le bandeau est replié (les infos clés en un coup d'œil).
          const sumBits = [];
          if (data.specific_ship) sumBits.push(`⚓ ${data.specific_ship}`);
          if (data.intent === "best_deal") sumBits.push(lang === "en" ? "💰 Best deal" : "💰 Meilleur deal");
          if (data.budget) sumBits.push(`💰 ${data.budget.toLocaleString()} $`);
          if (typeof data.exact_nights === "number" && data.exact_nights >= 2 && data.exact_nights <= 21) {
            sumBits.push(lang === "en" ? `${data.exact_nights} nights (±1)` : `${data.exact_nights} nuits (±1)`);
          } else if (data.duration && data.duration !== "any") sumBits.push(lang === "en" ? ({ short: "3–5 nights", week: "7 nights", long: "8+ nights", xlong: "15+ nights" }[data.duration] || data.duration) : ({ short: "3–5 nuits", week: "7 nuits", long: "8+ nuits", xlong: "15+ nuits" }[data.duration] || data.duration));
          if (data.group) sumBits.push(data.group);
          if (dateChip) sumBits.push(dateChip.replace("📅 ", ""));
          if (selectedRegions.length) sumBits.push(selectedRegions.map(r => REGION_SHORT[r] || r).join(", "));
          if (Array.isArray(data.desired_ports) && data.desired_ports.length) sumBits.push("📍 " + data.desired_ports.join(", "));
          if (transportChip) sumBits.push(transportChip);
          const summaryLine = sumBits.join(" · ");
          return (
            // <details>/<summary> natif : pas de state à gérer. display:flex sur summary = pas de triangle par défaut.
            <details style={{ marginBottom: 18, background: "#091929", border: "1px solid #1A2F45", borderRadius: 12, overflow: "hidden" }}>
              <summary style={{ listStyle: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "11px 13px" }}>
                <span style={{ ...GL, fontSize: 11, color: "#C9A84C", fontWeight: 700, whiteSpace: "nowrap" }}>🎚️ {lang === "en" ? "Your criteria" : "Tes critères"}</span>
                <span style={{ ...GL, fontSize: 11, color: "#7A9BB5", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summaryLine}</span>
                <span style={{ color: "#5A7A95", fontSize: 11, flexShrink: 0 }}>▾</span>
              </summary>
              <div style={{ padding: "8px 13px 12px", borderTop: "1px solid #1A2F4455", display: "flex", flexDirection: "column", gap: 7 }}>
                {/* Chips spéciaux sans catégorie (navire précis, best deal, budget) */}
                {(data.specific_ship || data.intent === "best_deal" || data.budget) && (
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {data.specific_ship && chip("ship", `⚓ ${data.specific_ship}`, true)}
                    {data.intent === "best_deal" && chip("deal", lang === "en" ? "💰 Best deal" : "💰 Meilleur deal")}
                    {data.budget && chip("bud", `💰 ${data.budget.toLocaleString()} $ CAD`)}
                  </div>
                )}
                {row(lang === "en" ? "Duration" : "Durée", data.duration && data.duration !== "any" ? [chip("dur", lang === "en" ? `⏱ ${{ short: "3–5 nights", week: "7 nights", long: "8+ nights", xlong: "15+ nights" }[data.duration] || data.duration}` : `⏱ ${{ short: "3–5 nuits", week: "7 nuits", long: "8+ nuits", xlong: "15+ nuits" }[data.duration] || data.duration}`)] : [])}
                {row(lang === "en" ? "Group" : "Qui", data.group ? [chip("grp", `👥 ${data.group}`)] : [])}
                {row(lang === "en" ? "Period" : "Période", dateChip ? [chip("dt", dateChip)] : [])}
                {row(lang === "en" ? "Lines" : "Compagnies", (data.companies || []).map(c => chip(c, `🚢 ${CRUISE_COMPANIES.find(x => x.v === c)?.l || c}`)))}
                {(() => {
                  const scArr = (Array.isArray(data.ship_class) ? data.ship_class : data.ship_class ? [data.ship_class] : []).filter(v => v !== "any");
                  const allSC = [...GENERIC_SHIP_CLASSES_FR, ...Object.values(SHIP_CLASSES_BY_COMPANY).flat()];
                  return row(lang === "en" ? "Ship type" : "Taille navire", scArr.map(v => { const c = allSC.find(x => x.v === v); return chip(v, c ? `${c.icon} ${c.l}` : v); }));
                })()}
                {row(lang === "en" ? "Destinations" : "Destinations", selectedRegions.map(r => chip(r, REGION_SHORT[r] || r)))}
                {row(lang === "en" ? "Transport" : "Transport", transportChip ? [chip("tr", transportChip)] : [])}
                {row(lang === "en" ? "Ports" : "Ports", selectedPorts.map(pv => chip(pv, DEPARTURE_PORT_LIST.flatMap(g => g.ports).find(p => p.v === pv)?.l || pv)))}
              </div>
            </details>
          );
        })()}
        {offers.length > 0 ? (
          <>
            {compromiseNote && (() => {
              // ⚠️ en tête = contrainte DURE nommée non satisfaite (compagnie/escale) :
              // bandeau déplié par défaut + pulse (3×) pour attirer l'œil. Sinon (compromis
              // mineur) : repliable neutre comme avant, pour ne pas effrayer sur mobile.
              const highSeverity = typeof compromiseNote === "string" && compromiseNote.includes("⚠️");
              return (
              <>
              {highSeverity && <style>{`@keyframes caiPulseWarn{0%,100%{box-shadow:0 0 0 0 rgba(230,160,80,0)}50%{box-shadow:0 0 0 5px rgba(230,160,80,0.22)}}`}</style>}
              <details open={highSeverity} style={{ background: highSeverity ? "rgba(230,140,60,0.12)" : "rgba(201,168,76,0.08)", border: highSeverity ? "1px solid #E6A050" : "1px solid #C9A84C44", borderRadius: 12, marginBottom: 12, overflow: "hidden", animation: highSeverity ? "caiPulseWarn 1.8s ease-in-out 3" : undefined }}>
                <summary style={{ ...GL, fontSize: 13.5, fontWeight: 700, color: highSeverity ? "#F0B070" : "#D4B86A", padding: "12px 14px", cursor: "pointer", listStyle: "none", display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ fontSize: 18, lineHeight: 1.2 }}>{highSeverity ? "⚠️" : "ℹ️"}</span>
                  <span style={{ flex: 1 }}>{highSeverity
                    ? (lang === "en" ? "Important — we couldn't fully match your request. Here's why." : "Important — on n'a pas pu satisfaire pleinement votre demande. Voici pourquoi.")
                    : (lang === "en" ? "Read this — why these results differ slightly from your request" : "À lire — pourquoi ces résultats diffèrent un peu de votre demande")}</span>
                  <span style={{ fontSize: 12, color: "#7AB8E8", whiteSpace: "nowrap" }}>{lang === "en" ? "Read ▾" : "Lire ▾"}</span>
                </summary>
                <div style={{ ...GL, fontSize: 13.5, color: "#D4B86A", lineHeight: 1.55, padding: "0 14px 13px 41px" }}>
                  {compromiseNote}
                  {/* Garde-fou honnêteté (codé, pas IA) : ne JAMAIS laisser un manque se lire
                      comme une absence absolue. Notre vérif couvre les navires principaux —
                      d'autres départs existent souvent qu'on n'a pas encore confirmés. */}
                  <div style={{ ...GL, fontSize: 12, color: "#A8895A", lineHeight: 1.5, marginTop: 7, fontStyle: "italic" }}>
                    {lang === "en"
                      ? "🔎 Our verification covers the main ships and itineraries — other departures may exist that we haven't confirmed yet. This is what we could verify, not the full market."
                      : "🔎 Notre vérification porte sur les navires et itinéraires principaux — d'autres départs peuvent exister que nous n'avons pas encore confirmés. C'est ce qu'on a pu vérifier, pas le marché complet."}
                  </div>
                </div>
              </details>
              </>
              );
            })()}
            {/* Opt-in « jamais vide » : sur résultat élargi/compromis, on offre le suivi courriel. */}
            {compromiseNote && <FollowupOptIn data={data} lang={lang} />}
            <div style={{ marginBottom: 18, textAlign: "center" }}>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  onClick={async () => {
                    if (manuallySaved) return;
                    saveSearchToLocal({ data, offers, compromiseNote, refinementOptions });
                    await saveCurrentSearch();
                    setManuallySaved(true);
                    try { track("manual_save_click", { signed_in: isSignedIn }); } catch {}
                  }}
                  disabled={manuallySaved}
                  style={{
                    background: manuallySaved ? "rgba(122,232,144,0.10)" : "transparent",
                    border: manuallySaved ? "1px solid #7AE89066" : "1px solid #C9A84C66",
                    borderRadius: 10, padding: "7px 13px",
                    color: manuallySaved ? "#7AE890" : "#C9A84C",
                    ...GL, fontSize: 12.5,
                    cursor: manuallySaved ? "default" : "pointer", fontWeight: 600,
                    transition: "all 0.25s", whiteSpace: "nowrap",
                  }}
                >
                  {manuallySaved
                    ? (lang === "en" ? "✓ Saved" : "✓ Sauvegardée")
                    : (lang === "en" ? "💾 Save" : "💾 Sauvegarder")}
                </button>
                <button
                  onClick={() => { try { track("edit_criteria_click", { from: "results" }); } catch {} go("recap"); }}
                  style={{ background: "transparent", border: "1px solid #C9A84C66", borderRadius: 10, padding: "7px 13px", color: "#C9A84C", ...GL, fontSize: 12.5, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  ✏️ {lang === "en" ? "Edit criteria" : "Modifier mes critères"}
                </button>
                {usage?.plan === "admin" && (
                  <button
                    onClick={shareCurrentResult}
                    disabled={shareInfo?.busy}
                    style={{ background: "transparent", border: "1px solid #7AE89066", borderRadius: 10, padding: "7px 13px", color: "#7AE890", ...GL, fontSize: 12.5, cursor: shareInfo?.busy ? "default" : "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                  >
                    {shareInfo?.busy ? "⏳ Génération…" : "🔗 Partager (lien-résultat)"}
                  </button>
                )}
              </div>
              {manuallySaved && !isSignedIn && (
                <button
                  onClick={requestSignupToSave}
                  style={{ marginTop: 10, padding: "10px 18px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
                >
                  💾 {lang === "en" ? "Create a free account to save my searches" : "Créer un compte gratuit pour sauvegarder mes recherches"}
                </button>
              )}
            </div>
            {shareInfo && !shareInfo.busy && (
              <ShareResultModal
                info={shareInfo}
                data={data}
                lang={lang}
                onClose={() => setShareInfo(null)}
                onRetry={shareCurrentResult}
              />
            )}
            {(() => {
              // Rend une offre (onglet de rang + carte). Partagé carrousel mobile / liste desktop.
              const renderOffer = (offer, i) => {
                const isByShip = data.intent === "by_ship" || !!data.specific_ship;
                const byShipLabel0 = data.dates?.from
                  ? `📅 ${fmtDate(data.dates.from, lang)}`
                  : (data.dates?.month && data.dates?.year)
                  ? `📅 ${new Date(data.dates.year, data.dates.month - 1, 1).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", { month: "short", year: "numeric" })}`
                  : (lang === "en" ? "💰 Best price" : "💰 Meilleur prix");
                // Une offre-plancher (reconcileWithStage0) porte TOUJOURS le badge
                // « Prix plancher », même si elle remonte dans un slot #2/#3 parce que
                // Sonnet a renvoyé moins de 3 recommandations. Sinon elle se déguisait en
                // « 🥉 Troisième option » (verdict curaté) alors que c'est juste un départ
                // confirmé le moins cher — d'où l'impression « ça a pas rapport ».
                const floorBadge = { label: lang === "en" ? "💰 Lowest price" : "💰 Prix plancher", bg: "linear-gradient(135deg,#2E6A4A,#1E4A32)", color: "#B8F0CC" };
                const rankBadge = offer._reconciled ? floorBadge : (isByShip ? [
                  { label: byShipLabel0,  bg: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F" },
                  { label: lang === "en" ? "🔄 Alternative"     : "🔄 Alternative",      bg: "linear-gradient(135deg,#4A8CB4,#2A5C7A)", color: "#fff" },
                  { label: lang === "en" ? "🗓️ Other departure" : "🗓️ Autre départ",    bg: "linear-gradient(135deg,#7A6444,#5A4A2A)", color: "#E8C88A" },
                ] : [
                  { label: lang === "en" ? "🥇 Recommended"     : "🥇 Recommandé",       bg: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F" },
                  { label: lang === "en" ? "🥈 Alternative"     : "🥈 Alternative",      bg: "linear-gradient(135deg,#4A8CB4,#2A5C7A)", color: "#fff" },
                  { label: lang === "en" ? "🥉 Third option"    : "🥉 Troisième option", bg: "linear-gradient(135deg,#7A6444,#5A4A2A)", color: "#E8C88A" },
                  { label: lang === "en" ? "💰 Lowest price"      : "💰 Prix plancher",      bg: "linear-gradient(135deg,#2E6A4A,#1E4A32)", color: "#B8F0CC" },
                  { label: lang === "en" ? "💰 Lowest price"      : "💰 Prix plancher",      bg: "linear-gradient(135deg,#2E6A4A,#1E4A32)", color: "#B8F0CC" },
                ])[i] || { label: lang === "en" ? "💰 Lowest price" : "💰 Prix plancher", bg: "linear-gradient(135deg,#2E6A4A,#1E4A32)", color: "#B8F0CC" };
                return (
                  <>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: rankBadge.bg, color: rankBadge.color, borderRadius: "10px 10px 0 0", padding: "5px 14px", ...GL, fontSize: 13, fontWeight: 900, letterSpacing: "0.04em" }}>
                      {rankBadge.label}
                      {offer.over_budget && (
                        <span style={{ background: "#E8524A", color: "#fff", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: "0.02em" }}>
                          ⚠️ {lang === "en" ? "OVER BUDGET" : "HORS BUDGET"}
                        </span>
                      )}
                    </div>
                    <ResultCard offer={offer} data={data} onReset={reset} rank={i} onWatch={() => setWatchOffer(offer)} onAskQuestion={openChat} t={t} priceHistory={offerHistories[`${offer.ship_name}:${offer.departure_date}`] || []} isMobileView={isMobileView} traceId={traceId} />
                  </>
                );
              };

              // On présente 3 offres « focus » par défaut (trop de choix = indécision).
              // Le reste (offres-plancher) est révélé via un bouton « voir plus ».
              const shownOffers = showAllOffers ? offers : offers.slice(0, 3);
              const extraCount  = offers.length - 3;
              const hasMore     = !showAllOffers && extraCount > 0;
              const moreBtn = hasMore ? (
                <div style={{ textAlign: "center", marginTop: 18 }}>
                  <button onClick={() => { setShowAllOffers(true); try { track("show_more_offers", { extra: extraCount }); } catch {} }}
                    style={{ background: "rgba(46,106,74,0.12)", border: "1px solid #2E6A4A", color: "#B8F0CC", borderRadius: 14, padding: "13px 24px", ...GL, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    💰 {lang === "en" ? `Show ${extraCount} more lowest-price option${extraCount > 1 ? "s" : ""}` : `Voir ${extraCount} option${extraCount > 1 ? "s" : ""} à prix plancher`}
                  </button>
                  <div style={{ ...GL, fontSize: 11.5, color: "#5A7A95", marginTop: 8 }}>
                    {lang === "en" ? "Confirmed departures at the lowest fare we found." : "Départs confirmés au tarif le plus bas qu'on a trouvé."}
                  </div>
                </div>
              ) : null;

              // ── MOBILE : carrousel plein écran, swipe horizontal entre les offres ──
              if (isMobileView) {
                return (
                  <>
                    {shownOffers.length > 1 && (
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginBottom: 14 }}>
                        {shownOffers.map((_, i) => (
                          <span key={i}
                            onClick={() => { const ch = offersScrollRef.current?.children[i]; if (ch) offersScrollRef.current.scrollTo({ left: ch.offsetLeft, behavior: "smooth" }); }}
                            style={{ width: i === currentSlide ? 22 : 7, height: 7, borderRadius: 4, background: i === currentSlide ? "#C9A84C" : "#2A4A66", transition: "all .25s", cursor: "pointer" }} />
                        ))}
                        <span style={{ marginLeft: 10, ...GL, fontSize: 12, color: "#7A9BB5", whiteSpace: "nowrap" }}>{currentSlide + 1} / {shownOffers.length}</span>
                      </div>
                    )}
                    <div style={{ position: "relative" }}>
                      <div ref={offersScrollRef}
                        onScroll={(e) => {
                          const el = e.currentTarget;
                          // Slide visible = celui dont le centre est le plus proche du centre du viewport
                          // (robuste au « peek » où les cartes ne font pas 100% de large).
                          const center = el.scrollLeft + el.clientWidth / 2;
                          let idx = 0, best = Infinity;
                          for (let i = 0; i < el.children.length; i++) {
                            const ch = el.children[i];
                            const c = ch.offsetLeft + ch.offsetWidth / 2;
                            const d = Math.abs(c - center);
                            if (d < best) { best = d; idx = i; }
                          }
                          if (idx !== currentSlide) setCurrentSlide(idx);
                          if (!swipeSeen && idx !== 0) markSwipeSeen();   // 1er swipe → on n'aide plus
                        }}
                        style={{ display: "flex", alignItems: "flex-start", overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", paddingBottom: 4 }}>
                        {shownOffers.map((offer, i) => (
                          // « peek » : carte à 90% → on voit le bord de la suivante (signal qu'il y en a d'autres)
                          <div key={i} className="ca-offer-rise" style={{ flex: shownOffers.length > 1 ? "0 0 90%" : "0 0 100%", minWidth: shownOffers.length > 1 ? "90%" : "100%", scrollSnapAlign: "start", paddingRight: shownOffers.length > 1 ? 10 : 0, boxSizing: "border-box", animation: "ca-offer-rise 0.5s cubic-bezier(.16,1,.3,1) both", animationDelay: `${i * 0.07}s` }}>
                            {renderOffer(offer, i)}
                          </div>
                        ))}
                      </div>
                      {/* Coach-mark : visible tant que jamais swipé → disparaît au 1er swipe (définitif) */}
                      {!swipeSeen && shownOffers.length > 1 && (
                        <div style={{ position: "absolute", top: 74, right: 22, zIndex: 5, pointerEvents: "none", background: "rgba(9,25,41,0.94)", border: "1px solid #C9A84C", borderRadius: 20, padding: "8px 14px", ...GL, fontSize: 13, fontWeight: 800, color: "#C9A84C", boxShadow: "0 6px 18px rgba(0,0,0,0.5)", whiteSpace: "nowrap" }}>
                          👉 {lang === "en" ? "Swipe to compare" : "Glisse pour comparer"}
                        </div>
                      )}
                    </div>
                    {shownOffers.length > 1 && (
                      <div style={{ textAlign: "center", ...GL, fontSize: 11.5, color: "#5A7A95", marginTop: 10 }}>
                        {currentSlide + 1 < shownOffers.length
                          ? (lang === "en" ? "‹ swipe to compare the next option ›" : "‹ glisse pour comparer l'option suivante ›")
                          : hasMore
                          ? (lang === "en" ? "↓ more options just below" : "↓ encore des options juste en dessous")
                          : (lang === "en" ? "✓ You've seen all the options" : "✓ Tu as vu toutes les options")}
                      </div>
                    )}
                    {moreBtn}
                  </>
                );
              }

              // ── DESKTOP : liste verticale, pubs entre les 3 premières + bouton « voir plus » ──
              return (
                <>
                  {shownOffers.map((offer, i) => (
                    <div key={i}>
                      <div className="ca-offer-rise" style={{ marginBottom: 28, animation: "ca-offer-rise 0.55s cubic-bezier(.16,1,.3,1) both", animationDelay: `${i * 0.08}s` }}>
                        {renderOffer(offer, i)}
                      </div>
                      {i < Math.min(3, shownOffers.length) - 1 && (
                        <AdBanner slot={ADSENSE_SLOT_TOP} style={{ marginBottom: 20 }} />
                      )}
                    </div>
                  ))}
                  {moreBtn}
                </>
              );
            })()}

            {/* Feedback déplacé PAR OFFRE (dans ResultCard) — individuel : on sait quelle
                offre cloche + plus de points de contact = plus de feedback. */}

            {/* ── MAQUETTE — invitation compte recadrée en BÉNÉFICE (moment des résultats) ──
                Au lieu d'un « mur » avant la valeur, on demande le compte APRÈS que la
                personne a vu ses offres, cadré comme un cadeau. En PROD : seulement aux
                non-connectés. En LOCAL : toujours visible pour que Simon puisse la revoir. */}
            {(!isSignedIn || (typeof window !== "undefined" && window.location.hostname === "localhost")) && (
              <div className="ca-offer-rise" style={{ maxWidth: 600, margin: "8px auto 0", background: "linear-gradient(135deg, rgba(201,168,76,0.11), rgba(201,168,76,0.03))", border: "1px solid #C9A84C66", borderRadius: 18, padding: "22px 24px", boxShadow: "0 18px 40px rgba(0,0,0,0.35)" }}>
                <div style={{ ...GF, fontSize: 19, fontWeight: 700, color: "#E8DCC8", marginBottom: 5 }}>
                  {lang === "en" ? "Keep these results — free" : "Garde ces résultats — gratuitement"}
                </div>
                <div style={{ ...GL, fontSize: 13.5, color: "#A8C0D0", marginBottom: 16, lineHeight: 1.55 }}>
                  {lang === "en" ? "Create your free account in 30 seconds and unlock:" : "Crée ton compte gratuit en 30 secondes et débloque :"}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 11, marginBottom: 18 }}>
                  {[
                    lang === "en" ? ["💾", "Save these offers", "find them on any device"] : ["💾", "Sauvegarde ces offres", "retrouve-les sur tous tes appareils"],
                    lang === "en" ? ["🔔", "Price-drop alerts", "we email you if the price falls"] : ["🔔", "Alerte baisse de prix", "on t'avertit si le prix baisse"],
                    lang === "en" ? ["🔁", "2 free searches / month", "always — no credit card"] : ["🔁", "2 recherches gratuites / mois", "toujours — sans carte de crédit"],
                  ].map((b, i) => (
                    <div key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                      <span style={{ fontSize: 17, flexShrink: 0, lineHeight: 1.3 }}>{b[0]}</span>
                      <div>
                        <div style={{ ...GL, fontSize: 14, fontWeight: 700, color: "#EAF2F8", lineHeight: 1.3 }}>{b[1]}</div>
                        <div style={{ ...GL, fontSize: 12.5, color: "#7A9BB5" }}>{b[2]}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={requestSignupToSave} style={{ width: "100%", padding: "13px 20px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#C9A84C,#A8882A)", color: "#07111F", ...GL, fontSize: 15, fontWeight: 800, cursor: "pointer" }}>
                  {lang === "en" ? "Create my free account →" : "Créer mon compte gratuit →"}
                </button>
                <div style={{ ...GL, fontSize: 11.5, color: "#5A7A95", textAlign: "center", marginTop: 10 }}>
                  {lang === "en" ? "No credit card · Google sign-in available" : "Aucune carte de crédit · Connexion Google disponible"}
                </div>
              </div>
            )}

            {/* AUTO-REFINEMENT — affiché quand Claude signale < 3 offres confirmées
                (toujours visible : desktop tout affiché, mobile carrousel montre tout) */}
            {refinementOptions && (
              <AutoRefinementBanner
                options={refinementOptions}
                lang={lang}
                onApplyLever={leverId => {
                  setRefinementOptions(null);
                  setData(d => {
                    const next = { ...d, is_refinement: true };
                    let changed = false;
                    if (leverId === "dates_shift") {
                      if (d.dates?.from) {
                        const base = new Date(d.dates.from + "T12:00:00");
                        const from = new Date(base); from.setDate(from.getDate() - 14);
                        const to   = new Date(base); to.setDate(to.getDate() + 14);
                        next.when  = "range";
                        next.dates = { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
                        changed = true;
                      } else if (d.dates?.month) {
                        const baseDate = new Date(d.dates.year, d.dates.month - 1, 15);
                        const from = new Date(baseDate); from.setDate(from.getDate() - 45);
                        const to   = new Date(baseDate); to.setDate(to.getDate() + 45);
                        next.when  = "range";
                        next.dates = { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
                        changed = true;
                      } else {
                        next.when  = "any";
                        next.dates = null;
                        changed = true;
                      }
                    }
                    if (leverId === "older_ship")    { next.ship_freshness = "any"; changed = true; }
                    if (leverId === "other_region")  { next.regions = ["any"];      changed = true; }
                    if (leverId === "other_company") { next.companies = [];         changed = true; }
                    if (leverId === "any_duration")  { next.duration = "any";       changed = true; }
                    if (!changed) {
                      if (d.regions && d.regions.length > 0 && !d.regions.includes("any")) {
                        next.regions = ["any"];
                      } else if (d.companies && d.companies.length > 0) {
                        next.companies = [];
                      } else if (d.duration && d.duration !== "any") {
                        next.duration = "any";
                      } else if (d.ship_freshness && d.ship_freshness !== "any") {
                        next.ship_freshness = "any";
                      } else {
                        next.when  = "any";
                        next.dates = null;
                      }
                    }
                    return next;
                  });
                  setTimeout(() => searchRef.current(), 50);
                }}
              />
            )}

            {/* B — Toujours pas satisfait ? (toujours visible — voir note ci-dessus) */}
            {offers.length > 0 && (
              <div style={{ background: "#091929", border: "1px solid #1A2F45", borderRadius: 16, padding: "16px 18px", marginBottom: 16 }}>
                <div style={{ ...GL, fontSize: 14, color: "#7A9BB5", marginBottom: 10 }}>{lang === "en" ? "These offers don't quite fit?" : "Ces offres ne correspondent pas tout à fait ?"}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => setShowRefinement(true)} style={{
                    flex: 1, padding: "11px 14px", borderRadius: 12, border: "1px solid #4A8CB4",
                    background: "rgba(74,140,180,0.1)", color: "#7AB8E8", ...GL, fontSize: 14, fontWeight: 700, cursor: "pointer",
                  }}>
                    {t.results_refine || "🔄 Affiner la recherche"}
                  </button>
                  <button onClick={() => { if (blockAnonIfExhausted()) return; reset(); }} style={{
                    flex: 1, padding: "11px 14px", borderRadius: 12, border: "1px solid #1A2F45",
                    background: "transparent", color: "#5A7A95", ...GL, fontSize: 14, fontWeight: 700, cursor: "pointer",
                  }}>
                    {t.results_new_search || "✏️ Modifier ma recherche"}
                  </button>
                </div>
              </div>
            )}

            <div ref={chatRef}>
              <OfferChat
                workerUrl={WORKER_URL}
                offer={offers[0]}
                data={data}
                forceOpen={chatForceOpen}
                onForceOpenHandled={() => setChatForceOpen(false)}
                onRelaunch={() => { try { track("relaunch_from_chat"); } catch {} setIdx(0); go("funnel"); }}
              />
            </div>
            <AdBanner slot={ADSENSE_SLOT_BTM} style={{ marginTop: 20 }} />
          </>
        ) : (
          <>
            <div style={{ marginBottom: 18, textAlign: "center" }}>
              <button
                onClick={() => { try { track("edit_criteria_click", { from: "no_results" }); } catch {} setIdx(0); go("funnel"); }}
                style={{ background: "transparent", border: "1px solid #C9A84C66", borderRadius: 12, padding: "10px 18px", color: "#C9A84C", ...GL, fontSize: 13.5, cursor: "pointer", fontWeight: 600 }}
              >
                ✏️ {lang === "en" ? "Edit my criteria" : "Modifier mes critères"}
              </button>
              <div style={{ ...GL, fontSize: 11, color: "#5A7A95", marginTop: 6 }}>
                {lang === "en" ? "Reopens the form with all your choices pre-filled" : "Rouvre le formulaire avec tous tes choix pré-remplis"}
              </div>
            </div>

            {/* Résultat VIDE = la plus haute intention (motivé, rien trouvé) → on capture
                l'opt-in (+ alerte instantanée admin) pour rattraper la personne. */}
            <FollowupOptIn data={data} lang={lang} context="empty" />

            {refinementOptions && (
              <AutoRefinementBanner
                options={refinementOptions}
                lang={lang}
                onApplyLever={leverId => {
                  setRefinementOptions(null);
                  setData(d => {
                    const next = { ...d, is_refinement: true };
                    let changed = false;
                    if (leverId === "dates_shift") {
                      if (d.dates?.from) {
                        const base = new Date(d.dates.from + "T12:00:00");
                        const from = new Date(base); from.setDate(from.getDate() - 14);
                        const to   = new Date(base); to.setDate(to.getDate() + 14);
                        next.when  = "range";
                        next.dates = { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
                        changed = true;
                      } else if (d.dates?.month) {
                        const baseDate = new Date(d.dates.year, d.dates.month - 1, 15);
                        const from = new Date(baseDate); from.setDate(from.getDate() - 45);
                        const to   = new Date(baseDate); to.setDate(to.getDate() + 45);
                        next.when  = "range";
                        next.dates = { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
                        changed = true;
                      } else {
                        next.when  = "any";
                        next.dates = null;
                        changed = true;
                      }
                    }
                    if (leverId === "older_ship")    { next.ship_freshness = "any"; changed = true; }
                    if (leverId === "other_region")  { next.regions = ["any"];      changed = true; }
                    if (leverId === "other_company") { next.companies = [];         changed = true; }
                    if (leverId === "any_duration")  { next.duration = "any";       changed = true; }
                    if (!changed) {
                      if (d.regions && d.regions.length > 0 && !d.regions.includes("any")) {
                        next.regions = ["any"];
                      } else if (d.companies && d.companies.length > 0) {
                        next.companies = [];
                      } else if (d.duration && d.duration !== "any") {
                        next.duration = "any";
                      } else if (d.ship_freshness && d.ship_freshness !== "any") {
                        next.ship_freshness = "any";
                      } else {
                        next.when  = "any";
                        next.dates = null;
                      }
                    }
                    return next;
                  });
                  setTimeout(() => searchRef.current(), 50);
                }}
              />
            )}

            <div style={{ background: "#091929", border: "1px solid #1A2F45", borderRadius: 16, padding: "16px 18px" }}>
              <div style={{ ...GL, fontSize: 14, color: "#7A9BB5", marginBottom: 10 }}>{lang === "en" ? "Or change your search:" : "Ou modifiez votre recherche :"}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => setShowRefinement(true)} style={{
                  flex: 1, padding: "11px 14px", borderRadius: 12, border: "1px solid #4A8CB4",
                  background: "rgba(74,140,180,0.08)", color: "#7FB5D5", ...GL, fontSize: 14, fontWeight: 700, cursor: "pointer",
                }}>
                  {t.results_refine || "🔄 Affiner la recherche"}
                </button>
                <button onClick={() => { if (blockAnonIfExhausted()) return; reset(); }} style={{
                  flex: 1, padding: "11px 14px", borderRadius: 12, border: "1px solid #1A2F45",
                  background: "transparent", color: "#5A7A95", ...GL, fontSize: 14, fontWeight: 700, cursor: "pointer",
                }}>
                  {t.results_new_search || "✏️ Modifier ma recherche"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {watchOffer  && <WatchModal offer={watchOffer} searchParams={data} onClose={() => setWatchOffer(null)} t={t} lang={lang} />}
      {showProUpsell && <ProUpsellModal onClose={() => setShowProUpsell(false)} t={t} lang={lang} />}
      {showRefinement && <RefinementModal onClose={() => setShowRefinement(false)} onRefine={r => { setShowRefinement(false); setData(d => ({ ...d, refinement: r })); setTimeout(() => searchRef.current(), 50); }} />}
      <button onClick={() => setShowFeedback(true)} style={{ position: "fixed", bottom: 20, right: 20, background: "#091929", border: "1px solid #C9A84C44", borderRadius: 50, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.5)", zIndex: 50, fontSize: 18 }} title="Suggestion / Bug">💬</button>
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
    </div>
  );

  // ── RÉCAP / MODIFIER MES CRITÈRES ───────────────────────────────────────────
  // Page récap éditable : l'usager voit tout son parcours et touche ✏️ pour corriger
  // UN critère (saut direct à l'étape + retour au récap), au lieu de recommencer le
  // funnel. Évite de gaspiller une recherche sur une simple erreur de saisie.
  if (screen === "recap") {
    const isEN = lang === "en";
    const visR = translatedSteps.filter(s => !WL_HIDDEN_STEPS.has(s.id) && (!s.showIf || s.showIf(data)));
    const pretty = (x) => String(x).replace(/[_-]+/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    const labelFor = (id) => ({
      experience: isEN ? "Experience" : "Expérience", vibe: isEN ? "Vibe" : "Ambiance", intent: isEN ? "Goal" : "Objectif",
      regions: isEN ? "Region" : "Région", port_activities: isEN ? "On shore" : "Aux escales", companies: isEN ? "Cruise lines" : "Compagnies",
      company_for_ship: isEN ? "Cruise line" : "Compagnie", specific_ship: isEN ? "Ship" : "Navire", ship_class: isEN ? "Ship class" : "Classe de navire",
      ship_freshness: isEN ? "Ship age" : "Âge du navire", cabin: isEN ? "Cabin" : "Cabine", priorities: isEN ? "Priorities" : "Priorités",
      duration: isEN ? "Duration" : "Durée", when: isEN ? "When" : "Quand", dates: "Dates",
      group: isEN ? "Travellers" : "Voyageurs", group_size: isEN ? "Group size" : "Taille du groupe", family_detail: isEN ? "Family" : "Famille", cabin_occupancy: isEN ? "Cabins" : "Cabines",
      departure: isEN ? "Getting there" : "Transport", departure_port: isEN ? "Departure port" : "Port de départ", drive_origin: isEN ? "Driving from" : "Départ de",
      origin_airport: isEN ? "Airport" : "Aéroport", overnight: isEN ? "Hotel night before" : "Nuit d'hôtel avant", budget: "Budget",
    }[id] || (id ? pretty(id) : ""));
    const valueFor = (s) => {
      const v = data[s.id];
      if (s.id === "budget") return v ? `${Number(v).toLocaleString()} $ ${isEN ? "(group total)" : "(total groupe)"}` : null;
      if (s.id === "when") {
        // Dates fusionnées dans l'étape « when » → afficher les vraies dates si présentes,
        // sinon le libellé du choix (ex. « La meilleure offre »). Durée fusionnée (2026-06-10)
        // → suffixe « · 7 nuits » pour qu'elle reste visible/éditable au récap.
        const durL = (typeof data.exact_nights === "number" && data.exact_nights >= 2)
          ? `${data.exact_nights} ${isEN ? "nights" : "nuits"}`
          : (data.duration ? (t.steps?.duration?.opts?.[data.duration]?.l || pretty(data.duration)) : null);
        const withDur = (x) => (x && durL) ? `${x} · ${durL}` : (x || durL || null);
        if (data.dates?.from) {
          const f = new Date(data.dates.from + "T12:00:00").toLocaleDateString(isEN ? "en-CA" : "fr-CA", { day: "numeric", month: "short", year: "numeric" });
          const t2 = data.dates.to ? new Date(data.dates.to + "T12:00:00").toLocaleDateString(isEN ? "en-CA" : "fr-CA", { day: "numeric", month: "short", year: "numeric" }) : "";
          return withDur(t2 ? `${f} → ${t2}` : f);
        }
        if (data.dates?.month && data.dates?.year) return withDur(new Date(data.dates.year, data.dates.month - 1, 1).toLocaleDateString(isEN ? "en-CA" : "fr-CA", { month: "long", year: "numeric" }));
        const optsW = Array.isArray(s.opts) ? s.opts : [];
        return withDur(v ? (optsW.find(o => o.v === v)?.l || pretty(v)) : null);
      }
      if (s.id === "group" && v === "group") {
        // Taille + occupation fusionnées (2026-06-10) → visibles au récap sur la ligne groupe.
        const occL = data.cabin_occupancy ? ` · ${t.steps?.cabin_occupancy?.opts?.[data.cabin_occupancy]?.l || ""}` : "";
        return `${t.steps?.group_size?.opts?.[data.group_size]?.l || `${data.group_size || "?"} ${isEN ? "adults" : "adultes"}`}${occL}`;
      }
      if (s.id === "specific_ship") return v || null;
      if (s.id === "ports") { const ps = Array.isArray(data.desired_ports) ? data.desired_ports : []; return ps.length ? ps.join(", ") : null; }
      if (s.id === "companies") { const cs = Array.isArray(data.companies) ? data.companies : (data.companies ? [data.companies] : []); return cs.length ? cs.map(pretty).join(", ") : (isEN ? "Any line" : "Peu importe"); }
      if (s.id === "ship_class") return (Array.isArray(v) && v.length) ? v.map(x => pretty(String(x).replace(/_class$/, ""))).join(", ") : null;
      if (s.id === "origin_airport") return v === "other" ? (data.origin_airport_custom || null) : (v || null);
      if (s.id === "departure_port") { const ps = Array.isArray(v) ? v : v ? [v] : []; return ps.length ? ps.join(", ") : null; }
      if (s.id === "family_detail") {
        const a = v?.adults, c = v?.children?.length;
        const occL = data.cabin_occupancy ? ` · ${t.steps?.cabin_occupancy?.opts?.[data.cabin_occupancy]?.l || ""}` : "";
        return v ? `${a || "?"} ${isEN ? "adults" : "adultes"}${c ? `, ${c} ${isEN ? "kids" : "enfants"}` : ""}${occL}` : null;
      }
      if (s.id === "priorities") return (Array.isArray(v) && v.length) ? v.map(pretty).join(" › ") : null;
      // opts peut être un TABLEAU ou une FONCTION (cabin, etc.) → résoudre avant .find,
      // sinon `(fonction).find` plante (« r.find is not a function ») — vu au relaunch.
      const optsArr = typeof s.opts === "function" ? (s.opts(data) || []) : (s.opts || []);
      if (s.id === "duration") {
        if (typeof data.exact_nights === "number" && data.exact_nights >= 2) return `${data.exact_nights} ${isEN ? "nights" : "nuits"}`;
        return optsArr.find(o => o.v === v)?.l || null;
      }
      if (s.type === "cards" && s.multi) return (Array.isArray(v) && v.length) ? v.map(x => optsArr.find(o => o.v === x)?.l || pretty(x)).join(", ") : null;
      if (s.type === "cards") return v ? (optsArr.find(o => o.v === v)?.l || pretty(v)) : null;
      return v ? String(v) : null;
    };
    const rowsR = visR.map((s, j) => ({ j, label: labelFor(s.id), value: valueFor(s) })).filter(r => r.value);
    return (
      <div style={{ background: ATMOSPHERE, minHeight: "100vh", padding: "26px 14px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ width: "100%", maxWidth: 560 }}>
          <div style={{ ...GL, fontSize: 20, color: "#E8DCC8", fontWeight: 700, marginBottom: 4 }}>✏️ {isEN ? "Edit your criteria" : "Modifier mes critères"}</div>
          <div style={{ ...GL, fontSize: 13, color: "#7A9BB5", marginBottom: 18 }}>{isEN ? "Tap ✏️ on any item to change just that one, then relaunch." : "Touche ✏️ sur un critère pour le corriger, puis relance."}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rowsR.map(({ label, value, j }) => (
              <div key={j} style={{ display: "flex", alignItems: "center", gap: 10, background: "#091929", border: "1px solid #1A2F45", borderRadius: 10, padding: "10px 13px" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...GL, fontSize: 11, color: "#C9A84C", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
                  <div style={{ ...GL, fontSize: 14, color: "#E8DCC8", marginTop: 2 }}>{value}</div>
                </div>
                <button onClick={() => { setEditReturn(true); setIdx(j); go("funnel"); }} style={{ background: "transparent", border: "1px solid #C9A84C66", borderRadius: 9, padding: "6px 11px", color: "#C9A84C", ...GL, fontSize: 13, cursor: "pointer", fontWeight: 600, flexShrink: 0 }}>✏️ {isEN ? "Edit" : "Modifier"}</button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
            <button onClick={() => go("results")} style={{ flex: 1, background: "transparent", border: "1px solid #1A2F45", borderRadius: 11, padding: "12px", color: "#A8C0D0", ...GL, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>← {isEN ? "Back to results" : "Retour aux résultats"}</button>
            <button onClick={() => { try { track("recap_relaunch"); } catch {} search(); }} style={{ flex: 1, background: "#C9A84C", border: "none", borderRadius: 11, padding: "12px", color: "#07111F", ...GL, fontSize: 14, cursor: "pointer", fontWeight: 800 }}>🔍 {isEN ? "Relaunch" : "Relancer"}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── FUNNEL ─────────────────────────────────────────────────────────────────
  const prog = ((idx + 1) / vis.length) * 100;
  return (
    <div style={{ background: ATMOSPHERE, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 14px" }}>
      {coverageGate && (() => {
        // Le gate sert maintenant à PLUSIEURS avis avant recherche : durée hors-scope
        // (court/xlong) ET incohérences ciblées (ex. ligne de luxe × région non couverte).
        const isDuration = coverageGate.reason === "short" || coverageGate.reason === "xlong";
        const title = isDuration
          ? (lang === "en" ? "We focus on 7-night and longer cruises" : "On se concentre sur les croisières de 7 nuits et plus")
          : (lang === "en" ? coverageGate.title_en : coverageGate.title_fr);
        const body = isDuration
          ? (lang === "en"
              ? "Since travelers from Québec and Canada fly or drive to reach a departure port, our tool focuses on cruises of 7 nights and more — that's where the trip is worth the journey."
              : "Étant donné le voyagement depuis le Québec et le Canada pour rejoindre un port de départ, notre outil se concentre sur les croisières de 7 nuits et plus — c'est là que le voyage en vaut la peine.")
          : (lang === "en" ? coverageGate.body_en : coverageGate.body_fr);
        const fixLabel = isDuration
          ? (lang === "en" ? "See 7+ night cruises →" : "Voir les croisières 7 nuits et + →")
          : coverageGate.editStep
            ? (lang === "en" ? "Adjust my criteria →" : "Ajuster mes critères →")
            : (lang === "en" ? coverageGate.fix_en : coverageGate.fix_fr);
        const applyFix = () => {
          // Avis budget → on RAMÈNE l'user à l'étape concernée (il s'organise), sans relancer.
          if (coverageGate.editStep) {
            const bi = vis.findIndex(s => s.id === coverageGate.editStep);
            setCoverageGate(null);
            if (bi >= 0) { setWentBack(true); setIdx(bi); }
            return;
          }
          const patch = isDuration ? { duration: coverageGate.suggest } : (coverageGate.fixPatch || {});
          setCoverageGate(null);
          setData(d => ({ ...d, ...patch }));
          setAutoSearch(true);
        };
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(7,17,31,0.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
            <div style={{ background: "#091929", border: "1px solid #C9A84C55", borderRadius: 18, padding: "24px 22px", maxWidth: 440, boxShadow: "0 24px 48px rgba(0,0,0,0.6)" }}>
              <div style={{ ...GF, fontSize: 18, color: "#E8DCC8", fontWeight: 700, lineHeight: 1.3, marginBottom: 12 }}>{title}</div>
              <div style={{ ...GL, fontSize: 14.5, color: "#9AB8D0", lineHeight: 1.6, marginBottom: coverageGate.levers_fr ? 10 : 18 }}>{body}</div>
              {coverageGate.levers_fr && (
                <ul style={{ margin: "0 0 18px", paddingLeft: 18, ...GL, fontSize: 14, color: "#C8D8E8", lineHeight: 1.7 }}>
                  {(lang === "en" ? coverageGate.levers_en : coverageGate.levers_fr).map((lv, i) => <li key={i}>{lv}</li>)}
                </ul>
              )}
              <button onClick={applyFix}
                style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: "#C9A84C", color: "#07111F", ...GL, fontWeight: 800, fontSize: 15, cursor: "pointer", marginBottom: 10 }}>
                {fixLabel}
              </button>
              <button onClick={() => { setCoverageGate(null); setData(d => ({ ...d, _coverageAck: true })); setAutoSearch(true); }}
                style={{ width: "100%", padding: "11px", borderRadius: 12, border: "1px solid #2A4A66", background: "transparent", color: "#7A9BB5", ...GL, fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}>
                {lang === "en" ? "Search anyway" : "Chercher quand même"}
              </button>
            </div>
          </div>
        );
      })()}
      {showPaywall && <PaywallModal reason={typeof showPaywall === "string" ? showPaywall : "limit"} onClose={() => setShowPaywall(false)} onBeforeAuth={() => saveBeforeAuth(true)} onEnterCode={() => setShowRedeem(true)} />}
      {showRedeem && <RedeemCodeModal workerUrl={WORKER_URL} onClose={() => setShowRedeem(false)} onRedeemed={refreshUsage} />}
      {showCodes && <AdminCodesModal workerUrl={WORKER_URL} onClose={() => setShowCodes(false)} />}
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;700;900&display=swap" rel="stylesheet" />
      <style>{`
        * { box-sizing: border-box; }
        select option { background: #0A1E35; }
        @keyframes funnel-step-in {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .funnel-step { animation: funnel-step-in 0.5s cubic-bezier(0.16,1,0.3,1) both; }
      `}</style>
      <div style={{ width: "100%", maxWidth: 520, background: "#091929", border: "1px solid #1A2F45", borderRadius: 22, overflow: "hidden", boxShadow: "0 40px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ height: 4, background: "#0A1E35" }}><div style={{ height: "100%", width: `${prog}%`, background: "linear-gradient(90deg,#C9A84C,#A8882A)", transition: "width 0.4s" }} /></div>
        <div style={{ padding: "14px 24px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ ...GL, fontSize: 11, color: prog >= 75 ? "#C9A84C" : "#3A5A72", fontWeight: 700, letterSpacing: "0.1em" }}>{prog >= 75 ? (lang === "en" ? "Almost done" : "Presque terminé") : (lang === "en" ? `Step ${idx + 1} / ${vis.length}` : `Étape ${idx + 1} / ${vis.length}`)}</span>
            {!isSignedIn && (() => {
              const cnt = parseInt(localStorage.getItem('anonSearchCount') || '0', 10);
              return (
                <span style={{ ...GL, fontSize: 9, color: cnt >= 1 ? "#C9A84C" : "#5A7A95" }}>
                  {lang === "en"
                    ? (cnt >= 1 ? "⚠️ Sign up to keep searching" : "✓ 1 free search · No account needed")
                    : (cnt >= 1 ? "⚠️ Inscription requise pour continuer" : "✓ 1 recherche gratuite · Aucun compte requis")}
                </span>
              );
            })()}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {cur?.type === "companies"         && <span style={{ ...GL, fontSize: 11, color: "#3A5A72" }}>{t.select_multiple || "Sélection multiple"}</span>}
            {cur?.type === "company_for_ship"  && <span style={{ ...GL, fontSize: 11, color: "#3A5A72" }}>{lang === "en" ? "Optional" : "Optionnel"}</span>}
            {cur?.type === "ports"             && <span style={{ ...GL, fontSize: 11, color: "#3A5A72" }}>{lang === "en" ? "Optional" : "Optionnel"}</span>}
            {cur?.type === "specific_ship"     && <span style={{ ...GL, fontSize: 11, color: "#3A5A72" }}>{lang === "en" ? "Dropdown" : "Menu déroulant"}</span>}
            <AuthButton usage={usage} workerUrl={WORKER_URL} onBeforeAuth={saveBeforeAuth} />
          </div>
        </div>
        <div key={`title-${idx}`} className="funnel-step" style={{ padding: "12px 24px 0" }}>
          <div style={{ ...GF, fontSize: 20, color: "#fff", fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>{typeof cur?.q === "function" ? cur.q(data) : cur?.q}</div>
          <div style={{ ...GL, fontSize: 14, color: "#5A7A95", lineHeight: 1.6, marginBottom: 14 }}>{typeof cur?.sub === "function" ? cur.sub(data) : cur?.sub}</div>
        </div>
        <div key={idx} className="funnel-step" style={{ padding: "0 24px", maxHeight: 360, overflowY: "auto" }}>
          {cur?.type === "cards" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
                {(typeof cur.opts === "function" ? cur.opts(data) : cur.opts).map(o => {
                  const on = cur.multi ? data[cur.id]?.includes(o.v) : data[cur.id] === o.v;
                  return (
                    <button key={o.v} onClick={() => card(o.v)} style={{ background: on ? "rgba(201,168,76,0.12)" : "rgba(255,255,255,0.03)", border: `2px solid ${on ? "#C9A84C" : "rgba(255,255,255,0.08)"}`, borderRadius: 14, padding: "12px 13px", cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ fontSize: 22 }}>{o.icon}</div>
                      <div style={{ ...GF, fontSize: 15, color: on ? "#C9A84C" : "#E8DCC8", fontWeight: 700 }}>{o.l}</div>
                      <div style={{ ...GL, fontSize: 11, color: "#5A7A95", lineHeight: 1.4 }}>{o.s}</div>
                    </button>
                  );
                })}
              </div>
              {cur.id === "regions" && data.regions?.includes("repositioning") && (
                <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(122,184,232,0.06)", border: "1px solid rgba(122,184,232,0.3)", borderRadius: 12 }}>
                  <div style={{ ...GL, fontSize: 12.5, color: "#9FC0D8", lineHeight: 1.55 }}>
                    {lang === "en"
                      ? "🔄 Heads up — repositioning cruises are a niche: one-way, mostly spring/fall, with limited departures. We'll show the closest matches, but expect fewer options than a regular region."
                      : "🔄 Bon à savoir — le repositionnement est un créneau spécifique : à sens unique, surtout printemps/automne, avec peu de départs. On vous montrera ce qui s'en rapproche le plus, mais attendez-vous à moins d'options qu'une région classique."}
                  </div>
                </div>
              )}
              {cur.id === "when" && ["exact", "flexible", "month", "range"].includes(data.when) && (
                <div style={{ marginTop: 14, padding: "14px", background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(201,168,76,0.25)", borderRadius: 12 }}>
                  <div style={{ ...GL, fontSize: 12, color: "#C9A84C", marginBottom: 8 }}>{lang === "en" ? "📅 Enter your dates below" : "📅 Indiquez vos dates ci-dessous"}</div>
                  <DateInput value={data.dates} onChange={v => setData(d => ({ ...d, dates: v }))} when={data.when} isEN={lang === "en"} />
                </div>
              )}
              {cur.id === "when" && (
                <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(201,168,76,0.25)", borderRadius: 12 }}>
                  {/* Durée fusionnée ici (B2, 2026-06-10) — libellés repris de strings.js (clé duration). */}
                  <div style={{ ...GL, fontSize: 12, color: "var(--wl-primary, #C9A84C)", marginBottom: 8 }}>
                    ⏱️ {t.steps?.duration?.q || (lang === "en" ? "How long a cruise?" : "Quelle durée de croisière ?")}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 12 }}>
                    {["short", "week", "long", "xlong", "any"].map(vd => {
                      const on = data.duration === vd && !(typeof data.exact_nights === "number" && data.exact_nights >= 2);
                      return (
                        <button key={vd}
                          onClick={() => setData(d => { const c = { ...d, duration: vd }; delete c.exact_nights; return c; })}
                          style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${on ? "var(--wl-primary, #C9A84C)" : "var(--wl-secondary, #1A2F45)"}`, background: on ? "#C9A84C22" : "transparent", color: on ? "var(--wl-primary, #C9A84C)" : "#A8C0D0", cursor: "pointer", ...GL, fontSize: 13.5, fontWeight: on ? 700 : 400 }}>
                          {t.steps?.duration?.opts?.[vd]?.l || vd}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ ...GL, fontSize: 12, color: "#A8C0D0", marginBottom: 6 }}>
                    {t.duration?.exactLabel || (lang === "en" ? "Or specify exact number of nights:" : "Ou précisez le nombre de nuits :")}
                  </div>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={2}
                    max={21}
                    placeholder={t.duration?.exactPlaceholder || (lang === "en" ? "e.g. 14" : "ex: 14")}
                    value={data.exact_nights ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") { setData(d => { const c = { ...d }; delete c.exact_nights; return c; }); return; }
                      const n = parseInt(raw, 10);
                      if (Number.isFinite(n) && n >= 0 && n <= 99) {
                        setData(d => ({ ...d, exact_nights: n }));
                      }
                    }}
                    onBlur={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isFinite(n) || n < 2) {
                        setData(d => { const c = { ...d }; delete c.exact_nights; return c; });
                      } else if (n > 21) {
                        setData(d => ({ ...d, exact_nights: 21 }));
                      }
                    }}
                    style={{ width: 100, padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(201,168,76,0.3)", background: "#07111F", color: "#E8DCC8", ...GL, fontSize: 16, textAlign: "center" }}
                  />
                </div>
              )}
              {/* Groupe : taille + occupation INLINE (A3/A4, 2026-06-10) — libellés strings.js. */}
              {cur.id === "group" && data.group === "group" && (
                <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(201,168,76,0.25)", borderRadius: 12 }}>
                  <div style={{ ...GL, fontSize: 12, color: "var(--wl-primary, #C9A84C)", marginBottom: 8 }}>
                    👥 {t.steps?.group_size?.q || "Combien d'adultes dans le groupe ?"}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                    {["3", "4", "6", "8", "10"].map(vs => {
                      const on = data.group_size === vs;
                      return (
                        <button key={vs} onClick={() => setData(d => ({ ...d, group_size: vs }))}
                          title={t.steps?.group_size?.opts?.[vs]?.s || ""}
                          style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${on ? "var(--wl-primary, #C9A84C)" : "var(--wl-secondary, #1A2F45)"}`, background: on ? "#C9A84C22" : "transparent", color: on ? "var(--wl-primary, #C9A84C)" : "#A8C0D0", cursor: "pointer", ...GL, fontSize: 13.5, fontWeight: on ? 700 : 400 }}>
                          {t.steps?.group_size?.opts?.[vs]?.l || `${vs} adultes`}
                        </button>
                      );
                    })}
                  </div>
                  {(parseInt(data.group_size, 10) || 0) >= 3 && (
                    <>
                      <div style={{ ...GL, fontSize: 12, color: "var(--wl-primary, #C9A84C)", margin: "14px 0 8px" }}>
                        🛏️ {t.steps?.cabin_occupancy?.q || "Une seule cabine ou plusieurs ?"}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                        {["one", "multiple", "unsure"].map(vo => {
                          const on = data.cabin_occupancy === vo;
                          return (
                            <button key={vo} onClick={() => setData(d => ({ ...d, cabin_occupancy: vo }))}
                              style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${on ? "var(--wl-primary, #C9A84C)" : "var(--wl-secondary, #1A2F45)"}`, background: on ? "#C9A84C22" : "transparent", color: on ? "var(--wl-primary, #C9A84C)" : "#A8C0D0", cursor: "pointer", ...GL, fontSize: 13.5, fontWeight: on ? 700 : 400 }}>
                              {t.steps?.cabin_occupancy?.opts?.[vo]?.l || vo}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
          {cur?.type === "family"        && (
            <>
              <FamilyInput value={data.family_detail} onChange={v => setData(d => ({ ...d, family_detail: v }))} isEN={lang === "en"} />
              {/* Occupation de cabine INLINE dès 3 voyageurs (A4, 2026-06-10). */}
              {((data.family_detail?.adults || 0) + (data.family_detail?.children?.length || 0)) >= 3 && (
                <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(201,168,76,0.25)", borderRadius: 12 }}>
                  <div style={{ ...GL, fontSize: 12, color: "var(--wl-primary, #C9A84C)", marginBottom: 8 }}>
                    🛏️ {t.steps?.cabin_occupancy?.q || "Une seule cabine ou plusieurs ?"}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                    {["one", "multiple", "unsure"].map(vo => {
                      const on = data.cabin_occupancy === vo;
                      return (
                        <button key={vo} onClick={() => setData(d => ({ ...d, cabin_occupancy: vo }))}
                          style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${on ? "var(--wl-primary, #C9A84C)" : "var(--wl-secondary, #1A2F45)"}`, background: on ? "#C9A84C22" : "transparent", color: on ? "var(--wl-primary, #C9A84C)" : "#A8C0D0", cursor: "pointer", ...GL, fontSize: 13.5, fontWeight: on ? 700 : 400 }}>
                          {t.steps?.cabin_occupancy?.opts?.[vo]?.l || vo}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Siège d'avion pour bébé <2 ans, INLINE (A2, 2026-06-10). Défaut « lap ». */}
              {(data.family_detail?.children || []).some(c => (c?.age ?? 99) < 2) && (
                <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(201,168,76,0.25)", borderRadius: 12 }}>
                  <div style={{ ...GL, fontSize: 12, color: "var(--wl-primary, #C9A84C)", marginBottom: 8 }}>
                    👶 {t.steps?.infant_flight_seat?.q || (lang === "en" ? "How will your baby fly?" : "Pour le vol, votre bébé voyagera comment ?")}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                    {["lap", "seat"].map(vi => {
                      const on = (data.infant_flight_seat || "lap") === vi;
                      return (
                        <button key={vi} onClick={() => setData(d => ({ ...d, infant_flight_seat: vi }))}
                          title={t.steps?.infant_flight_seat?.opts?.[vi]?.s || ""}
                          style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${on ? "var(--wl-primary, #C9A84C)" : "var(--wl-secondary, #1A2F45)"}`, background: on ? "#C9A84C22" : "transparent", color: on ? "var(--wl-primary, #C9A84C)" : "#A8C0D0", cursor: "pointer", ...GL, fontSize: 13.5, fontWeight: on ? 700 : 400 }}>
                          {t.steps?.infant_flight_seat?.opts?.[vi]?.l || (vi === "lap" ? "Sur les genoux" : "Siège réservé")}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
          {cur?.type === "dates"         && <DateInput value={data.dates} onChange={v => setData(d => ({ ...d, dates: v }))} when={data.when} isEN={lang === "en"} />}
          {cur?.type === "budget"        && (() => {
            const _ad   = data.group_size ? parseInt(data.group_size, 10) : (data.family_detail?.adults || (data.group === "couple" ? 2 : data.group === "solo" ? 1 : 2));
            const _trav = Math.max(1, _ad + (data.family_detail?.children?.length || 0));
            return <BudgetInput value={data.budget} onChange={v => setData(d => ({ ...d, budget: v }))}
              basis={data.budget_basis || "group"} onBasisChange={b => setData(d => ({ ...d, budget_basis: b }))}
              travelers={_trav} lang={lang} />;
          })()}
          {cur?.type === "ship_class"    && <ShipClassSelector value={data.ship_class} onChange={v => setData(d => ({ ...d, ship_class: v }))} companies={data.companies} isFirstTimer={data.experience === "first"} t={t} />}
          {cur?.type === "companies"         && <CompaniesSelector value={data.companies} onChange={v => setData(d => ({ ...d, companies: v }))} shipValue={data.specific_ship} onShipChange={v => setData(d => ({ ...d, specific_ship: v }))} />}
          {cur?.type === "company_for_ship" && <CompanyForShipSelector value={data.company_for_ship} onChange={v => setData(d => ({ ...d, company_for_ship: v }))} onAutoAdvance={() => goNext()} isEN={lang === "en"} />}
          {cur?.type === "specific_ship"    && <SpecificShipSelector value={data.specific_ship} onChange={v => setData(d => ({ ...d, specific_ship: v }))} companies={data.company_for_ship ? [data.company_for_ship] : []} />}
          {cur?.type === "ports"            && (
            <input
              type="text"
              value={data.desired_ports_raw ?? (Array.isArray(data.desired_ports) ? data.desired_ports.join(", ") : "")}
              onChange={e => {
                const raw = e.target.value;
                const arr = raw.split(",").map(s => s.trim()).filter(Boolean);
                setData(d => ({ ...d, desired_ports_raw: raw, desired_ports: arr }));
              }}
              placeholder={lang === "en" ? "e.g. Aruba, Curaçao, Bonaire" : "ex. Aruba, Curaçao, Bonaire"}
              style={{ width: "100%", padding: "14px 16px", borderRadius: 12, border: "1px solid #1A2F45", background: "#0B1828", color: "#E8DCC8", ...GL, fontSize: 16, boxSizing: "border-box", outline: "none" }}
            />
          )}
          {cur?.type === "priority_rank"  && <PriorityRankSelector value={data.priorities || []} onChange={v => setData(d => ({ ...d, priorities: v }))} opts={typeof cur.opts === "function" ? cur.opts(data) : cur.opts} />}
          {cur?.type === "airport"       && <AirportInput value={data.origin_airport} custom={data.origin_airport_custom} onChange={v => setData(d => ({ ...d, origin_airport: v.origin_airport, origin_airport_custom: v.origin_airport_custom }))} onAutoAdvance={() => goNext()} t={t} />}
          {cur?.type === "port_picker"  && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {DEPARTURE_PORT_LIST.map(group => (
                <div key={group.region}>
                  <div style={{ ...GL, fontSize: 11, color: "#5A7A95", fontWeight: 700, letterSpacing: 1, marginBottom: 7, textTransform: "uppercase" }}>{group.region}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                    {group.ports.map(p => {
                      const ports = Array.isArray(data.departure_port) ? data.departure_port : data.departure_port ? [data.departure_port] : [];
                      const on = ports.includes(p.v);
                      return (
                        <button key={p.v} onClick={() => setData(d => { const cur = Array.isArray(d.departure_port) ? d.departure_port : d.departure_port ? [d.departure_port] : []; return { ...d, departure_port: on ? cur.filter(x => x !== p.v) : [...cur, p.v] }; })} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${on ? "#C9A84C" : "#1A2F45"}`, background: on ? "#C9A84C22" : "transparent", color: on ? "#C9A84C" : "#A8C0D0", cursor: "pointer", ...GL, fontSize: 14, fontWeight: on ? 700 : 400 }}>{p.l}</button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 9, padding: "18px 24px" }}>
          <button onClick={() => { if (idx === 0) go("welcome"); else { setWentBack(true); setIdx(i => i - 1); } }} style={{ background: "transparent", border: "1px solid #1A2F45", color: "#5A7A95", borderRadius: 12, padding: "11px 18px", cursor: "pointer", ...GL, fontSize: 15, fontWeight: 700 }}>{t.back || "← Retour"}</button>
          <button onClick={() => goNext()} disabled={!ok()} style={{ flex: 1, padding: "13px 18px", borderRadius: 12, border: "none", cursor: ok() ? "pointer" : "not-allowed", background: ok() ? "linear-gradient(135deg,#C9A84C,#A8882A)" : "#1A2F45", color: ok() ? "#07111F" : "#3A5A72", ...GL, fontWeight: 900, fontSize: 14 }}>
            {(last || (!wentBack && noQuestionsLeft))
              ? `🔍 ${DEMO_MODE ? (lang === "en" ? "Generate offer" : "Générer l'offre") : (t.search || "Lancer la recherche réelle")}`
              : (((cur?.type === "ports" && !(data.desired_ports?.length)) || (cur?.type === "companies" && !(data.companies?.length) && !data.specific_ship))
                  ? (lang === "en" ? "Skip →" : "Passer →")
                  : (t.next || "Suivant →"))}
          </button>
        </div>
      </div>
      {error && (
        <div style={{ marginTop: 14, background: "#E8524A22", border: "1px solid #E8524A55", borderRadius: 12, padding: "12px 16px", maxWidth: 520, ...GL, fontSize: 14, color: "#E8524A" }}>
          ⚠️ {error}
        </div>
      )}
      <button onClick={() => setShowFeedback(true)} style={{ position: "fixed", bottom: 20, right: 20, background: "#091929", border: "1px solid #C9A84C44", borderRadius: 50, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.5)", zIndex: 50, fontSize: 18 }} title="Suggestion / Bug">💬</button>
      {showFeedback    && <FeedbackModal    onClose={() => setShowFeedback(false)} />}
      {showWalkthrough && <Walkthrough onClose={() => setShowWalkthrough(false)} lang={lang} />}
      {showWelcome     && <WelcomeModal     onClose={closeWelcome} lang={lang} />}
      {showWhatsNew    && <WhatsNewModal    onClose={() => setShowWhatsNew(false)} lang={lang} />}
    </div>
  );
}